// =====================================================================
// Deutschodoro v8 - frontend application
// =====================================================================
//
// One start for every Challenge type (POST /api/challenges) wrapped around
// the 10-question session model: the start returns 10 questions; the
// client submits one at a time to /api/challenges/{cid}/answers, then seals
// with /api/challenges/{cid}/completions.

(function () {
  "use strict";

  const SPA_ROUTES = window.__DT_ROUTES || [];
  if (!SPA_ROUTES.length) console.warn("dt: no SPA route table; every deep link falls back to home");

  // The server writes a per-page <title> for the URL the tab was opened on
  // (static/site.go, #875). In-app navigation changes the URL without a new
  // document, so the tab shows that title only on its boot URL and the
  // brand name (og:site_name) everywhere else. Every URL change goes
  // through pushState / replaceState or popstate.
  // Guarded: some tests run this file against stub globals.
  (function installDocumentTitleSync() {
    try {
      if (typeof location === "undefined" || typeof history === "undefined" || !document.querySelector) return;
      const canonical = (p) => p.replace(/\/+$/, "") || "/";
      const bootTitle = document.title;
      const bootPath = canonical(location.pathname);
      const brandTitle = (document.querySelector('meta[property="og:site_name"]') || {}).content || "Deutschodoro";
      const sync = () => {
        const want = canonical(location.pathname) === bootPath ? bootTitle : brandTitle;
        if (want && document.title !== want) document.title = want;
      };
      ["pushState", "replaceState"].forEach((method) => {
        const original = history[method];
        if (typeof original !== "function") return;
        history[method] = function () {
          const result = original.apply(this, arguments);
          sync();
          return result;
        };
      });
      window.addEventListener("popstate", sync);
    } catch { /* the title stays as served */ }
  })();

  // The shell's asset version (static/static.go). An API response carrying a
  // different X-Asset-Version means a CSS, JS, i18n or shell change shipped
  // while this tab ran; reloadIfAssetsChanged reloads at the next navigation
  // between screens, at most once per version per tab (#1341).
  const ASSET_VERSION = window.__DT_ASSET_VERSION || "";
  const I18N_VERSIONS = window.__DT_I18N || {};
  let staleAssetVersion = "";
  // Writes in flight (answers, chat turns, lesson steps): a reload would abort them.
  let pendingWrites = 0;
  // Entered with in-memory state (a started round, ids the "/" URL cannot
  // restore): a reload never lands on, or leaves, these.
  const IN_MEMORY_SCREENS = ["challenge", "course", "lesson"];
  function noteAssetVersion(res) {
    const v = res && res.headers && res.headers.get("X-Asset-Version");
    if (ASSET_VERSION && v && v !== ASSET_VERSION) staleAssetVersion = v;
  }
  function reloadIfAssetsChanged() {
    if (!staleAssetVersion) return false;
    // The Pomodoro lives in memory only: a reload would reset a running or
    // paused timer, so wait until it is idle.
    const session = window.Alpine && window.Alpine.store("session");
    if (session && (session.pomodoroActive || session.pomodoroElapsed > 0)) return false;
    if (pendingWrites > 0) return false;
    // A preference save waiting out its backoff (3s / 10s / 20s) has no
    // request in flight, and a live edit keeps no durable copy, so a reload
    // there would drop the value (#1342).
    if (prefSaves.size > 0) return false;
    try {
      if (sessionStorage.getItem("dt_asset_reload") === staleAssetVersion) return false;
      sessionStorage.setItem("dt_asset_reload", staleAssetVersion);
    } catch {
      return false;
    }
    window.location.reload();
    return true;
  }

  const LEVELS = [
    { id: "A1", name: "Beginner" },
    { id: "A2", name: "Elementary" },
    { id: "B1", name: "Intermediate" },
    { id: "B2", name: "Upper-Int." },
    { id: "C1", name: "Advanced" },
    { id: "C2", name: "Mastery" },
  ];
  const GERMAN_ONLY_LEVELS = ["B2", "C1", "C2"];

  // Daily points goals that gate streak maintenance. The points value and emoji
  // must stay in sync with lib/users/identity.DailyGoals and the
  // users_users.daily_goal_points CHECK constraint; the key names the label
  // (settings.learning.goal.<key>). Slider order is ascending.
  const DAILY_GOALS = [
    { points: 25,  key: "sleepyhead", emoji: "\u{1F62A}" },
    { points: 50,  key: "casual",     emoji: "\u{1F60E}" },
    { points: 75,  key: "serious",    emoji: "\u{1FAE1}" },
    { points: 100, key: "fire",       emoji: "\u{1F525}" },
  ];
  // The goal the SERVER assumes for a learner whose daily_goal_points has
  // never been written. Mirrors lib/users/identity.DefaultDailyGoal and the
  // users_users.daily_goal_points column default, and is therefore the right
  // fallback for the local mirror: before onboarding's PATCH lands (first run,
  // offline, a write that exhausted its retries) the streak is gated on THIS
  // number, so showing anything else would let the progress bar lie about how
  // far the day still has to go.
  const SERVER_DEFAULT_DAILY_GOAL = 25;
  // The tier onboarding writes for a new learner ("Casual"). Deliberately
  // above SERVER_DEFAULT_DAILY_GOAL: this is a product choice about where to
  // start people, not a mirror of a server default.
  const ONBOARDING_DAILY_GOAL = 50;
  // Pomodoro dial presets (minutes). The large knobs on the left arc set the
  // TOTAL session length; the small knobs on the upper-right arc set the
  // PRACTICE (break) length. Focus time is the remainder (total - practice),
  // so e.g. total 30 + practice 5 = 25 min focus (design plan res/design/v2,
  // "Rotlicht v2"). The pixel positions in the markup are tied to these exact
  // values and the 204px dial box.
  const POMO_TOTAL_OPTS = [30, 45, 60];
  const POMO_BREAK_OPTS = [5, 10, 15];
  // note is deliberately written in the language it describes rather than
  // translated: the onboarding language picker is the one screen where the
  // learner may not yet be reading a language they understand.
  const LANGS = [
    { id: "en", label: "English", flag: "🇬🇧", note: "Hints in English" },
    { id: "de", label: "Deutsch", flag: "🇩🇪", note: "Einsprachig - nur Deutsch" },
    { id: "hu", label: "Magyar", flag: "🇭🇺", note: "Tippek magyarul" },
  ];
  // Age bands asked by the 18+ opt-in. Mirrors lib/users/identity.AgeBrackets
  // and the users_users.age_bracket CHECK constraint, in the same order.
  // Labels are digits and symbols on purpose - they read the same in every
  // supported gloss language, so the step needs no catalogue entries.
  const AGE_BRACKETS = [
    { id: "under13", label: "<13" },
    { id: "13-17",   label: "13-17" },
    { id: "18-24",   label: "18-24" },
    { id: "25-34",   label: "25-34" },
    { id: "35-44",   label: "35-44" },
    { id: "45-54",   label: "45-54" },
    { id: "55-64",   label: "55-64" },
    { id: "65+",     label: "65+" },
  ];
  // Mirrors lib/users/identity.MinorAgeBrackets. "" (unknown) is not a
  // minor here either - see the Go comment for why.
  const MINOR_AGE_BRACKETS = ["under13", "13-17"];
  function isMinorAgeBracket(bracket) {
    return MINOR_AGE_BRACKETS.includes(bracket);
  }

  // The two app themes (custom grey-forward light/dark, defined in
  // static/src/input.css). Red is the shared `secondary` accent.
  const THEMES = ["dark", "light"];

  // Curated IANA zones for the Settings picker. Browser-detected zone is
  // always prepended when absent so onboarding works everywhere.
  const TIMEZONE_IDS = [
    "UTC",
    "America/Los_Angeles",
    "America/Denver",
    "America/Chicago",
    "America/New_York",
    "America/Sao_Paulo",
    "Europe/London",
    "Europe/Berlin",
    "Europe/Paris",
    "Europe/Moscow",
    "Africa/Johannesburg",
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Bangkok",
    "Asia/Shanghai",
    "Asia/Tokyo",
    "Australia/Sydney",
    "Pacific/Auckland",
  ];

  // Product default when the browser cannot report a zone (issue #161).
  // users_users.timezone defaults to the same value server-side.
  const DEFAULT_TIMEZONE = "Europe/Berlin";

  // Memoized so the Intl lookup + its log run at most once per page load.
  // Callers must still skip this when a zone is already stored, keeping an
  // already-configured user at zero detection runs.
  let detectedTimezone; // undefined until the first real attempt
  function detectBrowserTimezone() {
    if (detectedTimezone !== undefined) return detectedTimezone;
    try {
      detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      console.log("[timezone] detected browser timezone:", detectedTimezone || "(none)");
    } catch {
      detectedTimezone = "";
      console.log("[timezone] detection failed; falling back");
    }
    return detectedTimezone;
  }

  function timezoneLabel(id) {
    if (!id) return "";
    try {
      const parts = new Intl.DateTimeFormat("en", {
        timeZone: id,
        timeZoneName: "shortOffset",
      }).formatToParts(new Date());
      const offset = (parts.find((p) => p.type === "timeZoneName") || {}).value || "";
      return offset ? `${id} (${offset})` : id;
    } catch {
      return id;
    }
  }

  function timezoneOptions(current) {
    const ids = [...TIMEZONE_IDS];
    const preferred = current || detectBrowserTimezone();
    if (preferred && !ids.includes(preferred)) ids.unshift(preferred);
    return ids.map((id) => ({ id, label: timezoneLabel(id) }));
  }

  // Best-effort preference save with incremental backoff (issue #403).
  // Preference PATCHes (home level, daily goal, maturity, gloss lang, timezone)
  // retry up to 3 times, waiting 3s / 10s / 20s between attempts; resolves on
  // the first success, rejects with the last error once retries are exhausted so
  // callers can log or surface it. Client errors (4xx) are not retried - a value
  // the backend rejected will not become valid on a resend.
  //
  // Latest-value-wins: there is one save chain per field. A save requested
  // while the field's chain is in flight just updates `desired` and shares the
  // chain's promise; every (re)send reads the current desired value, and a
  // chain that lands a superseded value resends the newest one with a fresh
  // retry budget. A stale retry can therefore never overwrite a newer save.
  const PREF_SAVE_BACKOFF_MS = [3000, 10000, 20000];
  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  const prefSaves = new Map(); // field -> { uid, desired, promise }
  // Fields whose save is parked behind another one (the 18+ answer behind the
  // age save): the local value is newer than any row until the save starts, so
  // hydrateUser must not overwrite it with the row the first save returns.
  const heldPrefs = new Set();
  // Newest users_users.updated_at applied by hydrateUser, per user. Single-field
  // saves answer out of order (the lang response can land after the home_level
  // one), and a finished save is no longer in prefSaves, so only the row's
  // timestamp tells that it is older than what the store already shows.
  const appliedRow = { uid: null, at: 0 };
  // True for a row older than the newest applied one. Only hydrateUser records
  // the mark: a row that is merely looked at (heartbeat) must not move it, or a
  // save response handled earlier but landing later would be dropped whole.
  // Rows without a parsable updated_at (profile shapes) are never stale.
  function rowIsStale(user) {
    const at = Date.parse(user.updated_at);
    // ponytail: equal timestamps apply (two writes in the same ms cannot be ordered); per-field versions if that ever shows up
    return appliedRow.uid === user.user_id && at < appliedRow.at;
  }
  // Preference values are compared by value: dialect_regions is an array, and
  // a fresh array with the same keys is the same pick.
  function samePref(a, b) {
    if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((v, i) => v === b[i]);
    return a === b;
  }
  function patchUserRetry(uid, body, delays = PREF_SAVE_BACKOFF_MS) {
    const fields = Object.keys(body);
    if (fields.length !== 1) return api.patchUser(uid, body); // pref savers are single-field; anything else gets no retry machinery
    const field = fields[0];
    // Keep any durable queue copy pointing at the value we are currently
    // trying to persist. Without this a pick made while a save chain is
    // already running (or after one gave up) leaves the older value in
    // localStorage, and the next boot would restore that stale pick. Never
    // creates a copy - a live edit with a resolved userId needs no queue.
    refreshPendingPref(field, body[field]);
    const running = prefSaves.get(field);
    if (running) {
      running.uid = uid;
      running.desired = body[field];
      return running.promise;
    }
    const entry = { uid, desired: body[field] };
    entry.promise = runPrefSave(field, entry, delays).finally(() => prefSaves.delete(field));
    prefSaves.set(field, entry);
    return entry.promise;
  }

  async function runPrefSave(field, entry, delays) {
    let attempt = 0;
    for (;;) {
      const sent = entry.desired;
      try {
        const user = await api.patchUser(entry.uid, { [field]: sent });
        if (!samePref(entry.desired, sent)) {
          attempt = 0; // landed a superseded value; send the newest one
          continue;
        }
        // The value is now on the server, so the durable queue copy (if this
        // field had one) has done its job. Clearing here rather than at flush
        // time is what makes the queue survive a save that exhausts its
        // retries: an offline boot keeps the pick for the next one. Field
        // names match PENDING_PREF_KEYS; a field without a queue is a no-op.
        clearPendingPref(field);
        reconcileUser(user);
        return user;
      } catch (e) {
        if (!samePref(entry.desired, sent)) {
          attempt = 0; // a stale value failed; only the newest matters
          continue;
        }
        // Stop on a genuine client rejection (validation/auth/conflict) - it
        // won't pass on resend. Keep retrying network errors, all 5xx, and the
        // transient 408 (timeout) / 429 (rate limited) statuses.
        if (
          e instanceof ApiError &&
          e.status < 500 &&
          e.status !== 408 &&
          e.status !== 429
        ) {
          // The server will reject this value on every resend, so a durable
          // queue copy would just fail once per boot forever. Drop it; only
          // retryable failures are worth carrying to the next boot.
          clearPendingPref(field);
          throw e;
        }
        if (attempt >= delays.length) throw e;
        await sleep(delays[attempt]);
        if (!samePref(entry.desired, sent)) {
          attempt = 0;
          continue;
        }
        attempt += 1;
      }
    }
  }

  // The PATCH response is the authoritative user row; fold it back into the ui
  // store so an overwrite from elsewhere (another tab / device) shows up now
  // instead of on the next full hydration. Fields with a save chain still in
  // flight are skipped - their local value is newer than the row.
  function reconcileUser(user) {
    if (!window.Alpine || !window.Alpine.store) return;
    const ui = window.Alpine.store("ui");
    if (!ui || typeof ui.hydrateUser !== "function") return;
    if (!ui.hydrateUser(user, new Set(prefSaves.keys()))) return;
    // auth.me carries the saved timezone the midnight stats refetch keys on.
    const auth = window.Alpine.store("auth");
    if (auth && auth.me && user.timezone) auth.me.timezone = user.timezone;
  }

  // The DB (users_users.timezone) is the single source of truth; the frontend
  // keeps no persisted copy. Called only from the two user actions that change
  // the value: submitting onboarding and saving on Settings.
  async function persistTimezone(uid, timezone) {
    if (!uid || !timezone) return;
    await patchUserRetry(uid, { timezone });
  }

  // Seed value for the picker widgets (the DB owns the real value). A learner
  // still in onboarding gets the browser-detected zone; a returning learner
  // gets the product default, which Settings overwrites from the DB on load.
  // Gating on the onboarding flag keeps detection out of the returning path.
  function initialPickerTimezone() {
    if (lsGet("dt_home_level_set", false)) return DEFAULT_TIMEZONE;
    return detectBrowserTimezone() || DEFAULT_TIMEZONE;
  }

  function normalizeLang(lang) {
    return LANGS.some((l) => l.id === lang) ? lang : "en";
  }

  // Seeds the onboarding gloss-language picker from the browser's ordered
  // Accept-Language list. English is the absolute fallback: of the three
  // supported glosses it is the one a speaker of an unsupported language is
  // most likely to read.
  //
  //   - Hungarian anywhere in the list wins outright. It is the only gloss a
  //     Hungarian speaker would prefer, and a Hungarian speaker who also
  //     accepts English or German still reads Hungarian best.
  //   - German is chosen only when the list asks for nothing but German and
  //     English. German glosses mean monolingual study, which is the right
  //     default for a German speaker and the wrong one for anybody else - so
  //     a list that also mentions e.g. Portuguese or Spanish lands on English
  //     even though German is in it.
  //   - Everything else is English.
  //
  // Only ever a seed: the learner picks explicitly on step 1, and the choice
  // (including an untouched default) is persisted when the wizard finishes.
  function detectBrowserLang() {
    let tags = [];
    try {
      const nav = window.navigator || {};
      tags = nav.languages && nav.languages.length ? [...nav.languages] : [nav.language];
    } catch { /* no navigator (tests, exotic embeds) */ }
    const primary = tags
      .filter(Boolean)
      .map((tag) => String(tag).toLowerCase().split("-")[0]);
    if (primary.includes("hu")) return "hu";
    if (primary.includes("de") && primary.every((l) => l === "de" || l === "en")) return "de";
    return "en";
  }

  function effectiveLang(lang, level) {
    const root = window.__dtRoot;
    if (root && root.flags && root.flags.german_only_above_b1 && GERMAN_ONLY_LEVELS.includes(level)) {
      return "de";
    }
    return normalizeLang(lang);
  }

  function currentEffectiveLang() {
    try {
      const ui = window.Alpine && window.Alpine.store && window.Alpine.store("ui");
      return normalizeLang(ui && ui.effectiveLang);
    } catch {
      return "en";
    }
  }

  function decodePathSegment(value) {
    try { return decodeURIComponent(value); }
    catch { return null; }
  }

  function matchSpaPattern(pattern, path) {
    const p = pattern.split("/");
    const a = path.split("/");
    if (p.length !== a.length) return null;
    const params = {};
    for (let i = 0; i < p.length; i++) {
      const spec = p[i];
      const got = a[i];
      if (spec.charAt(0) === "{" && spec.charAt(spec.length - 1) === "}") {
        if (!got) return null;
        params[spec.slice(1, -1)] = got;
      } else if (spec !== got) {
        return null;
      }
    }
    return params;
  }

  function matchSpaRoute(path) {
    for (let i = 0; i < SPA_ROUTES.length; i++) {
      const route = SPA_ROUTES[i];
      const params = matchSpaPattern(route.pattern, path);
      if (params) return { route, params };
    }
    return null;
  }

  function pathFromRoute(route, value) {
    if (!route.param) return route.pattern;
    return route.pattern.replace("{" + route.param + "}", encodeURIComponent(value));
  }

  function interpolate(message, params) {
    if (!params) return message;
    return message.replace(/\{(\w+)\}/g, (_, key) =>
      Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : `{${key}}`);
  }

  // langOption looks up a gloss language in the catalogue (flag + label).
  // Null for an unknown id, so callers degrade to no flag rather than to the
  // wrong one.
  function langOption(id) {
    return LANGS.find((l) => l.id === id) || null;
  }

  // promptLang maps a question's direction + the learner's gloss lang to the
  // language the prompt itself is written in: German when the German side is
  // the cue, the learner's language when the gloss is. Blank for a
  // missing/unknown direction (types that carry no direction).
  function promptLang(direction, lang) {
    if (direction === "de-to-lang") return "de";
    if (direction === "lang-to-de") return lang || "";
    return "";
  }

  async function fetchCatalog(lang) {
    try {
      const v = I18N_VERSIONS[lang];
      const res = await fetch(`/i18n/${lang}.json` + (v ? `?v=${v}` : ""));
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  async function syncI18nLang(lang) {
    try {
      const i18n = window.Alpine && window.Alpine.store && window.Alpine.store("i18n");
      if (i18n) await i18n.use(lang);
    } catch { /* catalog fallback stays English */ }
  }

  // The runnable challenge types + one meta "random" entry. `challenge_type`
  // is the CHALLENGE_TYPE value POST /api/challenges starts (the one table
  // from page id to API value); `slug` is the user-facing URL segment
  // (/challenges/<slug>), decoupled from `id` so the public URL reads
  // naturally without disturbing the server API surface.
  //
  // "random" is a UI-only meta-type with no endpoint: callers resolve it to a
  // real id first (see pickRandomChallengeType / _performStartChallenge).
  // `input_mode` selects the challengePlayer renderer ("multi-choice" or
  // "audio-to-text" dictation). `requires_speech` keeps audio-dependent types
  // out of the Random pool so a surprise audio prompt can't land mid-Random.
  const GAME_TYPES = [
    { id: "vocabulary",  slug: "vocabulary",             label: "Vocabulary",  icon: "💬", desc: "DE ↔ EN",           input_mode: "multi-choice", challenge_type: "VOCABULARY" },
    { id: "article",     slug: "articles",              label: "Articles",    icon: "📖", desc: "der / die / das",   input_mode: "multi-choice", challenge_type: "ARTICLES" },
    { id: "plural",      slug: "plurals",               label: "Plurals",     icon: "🔢", desc: "Singular → plural", input_mode: "multi-choice", challenge_type: "PLURALS" },
    { id: "separable",   slug: "separable-verbs",       label: "Separable verbs", icon: "✂️", desc: "Separable or not",  input_mode: "multi-choice", levels: ["A1", "A2", "B1", "B2"], challenge_type: "SEPARABLE_VERBS" },
    { id: "reflexive",   slug: "reflexive-pronouns",    label: "Reflexive verbs", icon: "🔁", desc: "Pick the pronoun", input_mode: "multi-choice", levels: ["A1", "A2", "B1", "B2", "C1"], challenge_type: "REFLEXIVE_VERBS" },
    { id: "object-pronoun", slug: "object-pronouns",   label: "Object pronouns", icon: "👤", desc: "Pick the object pronoun", input_mode: "multi-choice", levels: ["A1", "A2", "B1", "B2", "C1"], challenge_type: "OBJECT_PRONOUNS" },
    { id: "conjugation", slug: "verb-conjugations",     label: "Conjugation", icon: "⚡", desc: "Verb forms",        input_mode: "multi-choice", challenge_type: "CONJUGATION" },
    { id: "auxiliary",   slug: "auxiliaries",           label: "haben or sein", icon: "🤝", desc: "Auxiliaries",       input_mode: "multi-choice", challenge_type: "AUXILIARIES" },
    { id: "past-tense",  slug: "past-tenses",           label: "Past tenses", icon: "⏳", desc: "Präteritum & Perfekt", input_mode: "multi-choice", challenge_type: "PAST_TENSES" },
    { id: "passive",     slug: "passive",               label: "Passive voice", icon: "🔄", desc: "Turn active sentences into the passive", input_mode: "multi-choice", levels: ["B1", "B2", "C1", "C2"], challenge_type: "PASSIVE_VOICE" },
    { id: "adjective",   slug: "adjective-declensions", label: "Adjective endings", icon: "🎨", desc: "Declension",        input_mode: "multi-choice", challenge_type: "ADJECTIVE_ENDINGS" },
    { id: "n-declension", slug: "n-declension",         label: "N-declension", icon: "🧑", desc: "Weak masculine nouns", input_mode: "multi-choice", levels: ["B1", "B2", "C1", "C2"], challenge_type: "N_DECLENSION" },
    { id: "preposition", slug: "prepositions",          label: "Prepositions", icon: "🧩", desc: "Fill in the gap",  input_mode: "multi-choice", challenge_type: "PREPOSITIONS" },
    { id: "conjunction", slug: "conjunctions",          label: "Conjunctions", icon: "🔗", desc: "Connect clauses", input_mode: "multi-choice", challenge_type: "CONJUNCTIONS" },
    { id: "fixed-expression", slug: "fixed-expressions", label: "Fixed expressions", icon: "💡", desc: "Match the meaning", input_mode: "multi-choice", levels: ["A2", "B1", "B2", "C1", "C2"], challenge_type: "FIXED_EXPRESSIONS" },
    // Regional words asks for the learner's Dialect regions first (the region
    // picker), so `needs_regions` keeps it out of Random and Compete; it stays
    // in the shared list. Out of Course Practice by not being in
    // AllowedPracticeChallengeTypes (lib/courses/admin/builder.go).
    { id: "regional-word", slug: "regional-words",     label: "Regional words", icon: "🗺️", desc: "Words from German-speaking regions", input_mode: "multi-choice", needs_regions: true, challenge_type: "REGIONAL_WORDS" },
    { id: "word-order",  slug: "word-order",            label: "Word order",   icon: "🧱", desc: "Build the sentence", input_mode: "word-order", challenge_type: "WORD_ORDER" },
    { id: "matching",    slug: "matching",              label: "Matching",    icon: "🔀", desc: "Match the pairs",   input_mode: "matching", challenge_type: "MATCHING" },
    { id: "dictation",   slug: "dictation",             label: "Dictation",   icon: "🎧", desc: "Hear it, type it",  input_mode: "audio-to-text", requires_speech: true, levels: ["A1", "A2", "B1", "B2"], challenge_type: "DICTATION" },
    { id: "pronunciation", slug: "pronunciation",       label: "Pronunciation", icon: "🎤", desc: "Hear it, say it", input_mode: "speech", requires_speech: true, challenge_type: "PRONUNCIATION" },
    { id: "trivia",      slug: "trivia",                label: "Trivia",      icon: "🏆", desc: "German trivia",     input_mode: "multi-choice", standalone: true, levels: ["A2", "B1", "B2"], challenge_type: "TRIVIA" },
    // Comprehension questions on the 3 most recently read Pieces, started
    // from the Lesen "Was jetzt?" screen, never from a picker.
    { id: "reading-quiz", slug: "reading-quiz",         label: "Reading questions", icon: "📖", desc: "Questions on your texts", input_mode: "multi-choice", standalone: true, challenge_type: "COMPREHENSION_QUESTIONS" },
    // Chat is a whole screen of its own (/chat), not a challengePlayer
    // renderer: `standalone` keeps it out of Random, out of Compete and out
    // of the shared challenge list. It stays in the catalogue so the label,
    // icon and slug have one home.
    { id: "chat",        slug: "chat",                  label: "Chat",        icon: "🗣️", desc: "Talk in German",    input_mode: "chat", standalone: true },
    // Spelling Bee sits in the normal catalogue at every level, but stays out
    // of Random: `requires_speech` marks the audio-dependent types, and a
    // surprise audio prompt must not land mid-Random. It is out of Compete by
    // being non-multi-choice, and out of Course Practice by not being in
    // AllowedPracticeChallengeTypes (lib/courses/admin/builder.go). Grading
    // needs no provider - the clips are the pre-generated dictionary audio and
    // the scorer is the deterministic CER compare.
    { id: "spelling-bee", slug: "spelling-bee",         label: "Spelling", icon: "🐝", desc: "Hear it, spell it", input_mode: "audio-to-text", requires_speech: true, challenge_type: "SPELLING" },
    { id: "random",      slug: "random",                label: "Random",      icon: "🎲", desc: "Surprise me",       input_mode: "multi-choice" },
  ];

  // Line-icon SVG path per challenge type (design plan res/design/v2). Rendered
  // as a single <path> with stroke-linecap round; keyed by GAME_TYPES id.
  const CHALLENGE_ICON_PATHS = {
    vocabulary:  "M21 11.5a8.38 8.38 0 0 1-9 8.4 8.5 8.5 0 0 1-3.4-.7L3 21l1.9-5.4A8.4 8.4 0 1 1 21 11.5z",
    article:     "M2 4h6a4 4 0 0 1 4 4v12a3 3 0 0 0-3-3H2zM22 4h-6a4 4 0 0 0-4 4v12a3 3 0 0 1 3-3h7z",
    plural:      "M4 9h16M4 15h16M10 3 8 21M16 3l-2 18",
    separable:   "M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM20 4 8.1 15.9M14.5 14.5 20 20M8.1 8.1 12 12",
    reflexive:   "M17 2l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 22l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3",
    "object-pronoun": "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
    conjugation: "M13 2 3 14h9l-1 8 10-12h-9l1-8z",
    auxiliary:   "M8 7h8M8 7a4 4 0 0 0-4 4v6M16 7a4 4 0 0 1 4 4v6M4 17h4M16 17h4M12 3v8M9 11h6",
    "past-tense": "M12 3a9 9 0 1 0 9 9M12 7v5l3 2M12 3 9 5.5M12 3l3 2.5",
    passive:     "M16 3l4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16",
    adjective:   "M12 2.7s6 6.3 6 10.8a6 6 0 0 1-12 0C6 9 12 2.7 12 2.7z",
    "n-declension": "M4 5h16M8 10h8M6 15h12M9 20h6",
    preposition: "M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7",
    conjunction: "M8 12h8M5 8h3v8H5M19 8h-3v8h3",
    "fixed-expression": "M9 18h6M10 22h4M8 14a7 7 0 1 1 8 0c-1 1-1.5 2-1.5 3h-5C9.5 16 9 15 8 14z",
    "regional-word": "M12 22s7-6.2 7-12a7 7 0 0 0-14 0c0 5.8 7 12 7 12zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z",
    "word-order": "M3 6h7v5H3zM14 6h7v5h-7zM7 14h10v5H7z",
    matching:    "M4 6h5M4 12h5M4 18h5M15 6h5M15 12h5M15 18h5M9 6l6 6M9 12l6-6M9 18l6-6",
    dictation:   "M3 18v-6a9 9 0 0 1 18 0v6M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z",
    pronunciation: "M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM5 11a7 7 0 0 0 14 0M12 18v3",
    "spelling-bee": "M12 3c2.5 0 4 1.8 4 4v7c0 2.2-1.8 4-4 4s-4-1.8-4-4V7c0-2.2 1.5-4 4-4zM8 8h8M8 12h8M4 7c1.5-1.5 3-2 4-2M20 7c-1.5-1.5-3-2-4-2",
    trivia:      "M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0zM7 6H4v2a4 4 0 0 0 4 4M17 6h3v2a4 4 0 0 1-4 4",
    "reading-quiz": "M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z",
    chat:        "M21 11.5a8.4 8.4 0 0 1-11.9 7.6L3 21l1.9-6A8.4 8.4 0 1 1 21 11.5zM8.5 10h7M8.5 13.5h4",
    random:      "M3 3h18v18H3zM8.5 8.5h.01M15.5 8.5h.01M8.5 15.5h.01M15.5 15.5h.01M12 12h.01",
  };

  const ACHIEVEMENT_ICON_PATHS = {
    total_challenges: "M9 12l2 2 4-4M21 12a9 9 0 1 1-9-9",
    streak: "M12 22c4 0 7-3 7-7 0-3-2-6-5-9 0 3-2 4-3 5 0-4-2-7-2-7-3 3-4 7-4 11 0 4 3 7 7 7z",
    compete_wins: "M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0zM7 6H4v2a4 4 0 0 0 4 4M17 6h3v2a4 4 0 0 1-4 4",
    compete_win_streak: "M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0zM7 6H4v2a4 4 0 0 0 4 4M17 6h3v2a4 4 0 0 1-4 4",
    friends: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
    perfect_rounds: "M22 11.1V12a10 10 0 1 1-5.9-9.1M22 4 12 14l-3-3",
    courses_completed: "M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 4v15.5M6.5 4H20v13H6.5A2.5 2.5 0 0 0 4 19.5",
    level_first: "M5 22V4M5 4h12l-2 4 2 4H5",
    discovery: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM16 8l-2 6-6 2 2-6z",
    hidden: "M12 2l3 6 6 .9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 8.9 9 8z",
  };

  const ACHIEVEMENT_GROUPS = [
    { key: "practice", kinds: ["total_challenges", "perfect_rounds"] },
    { key: "streaks", kinds: ["streak"] },
    { key: "social", kinds: ["friends"] },
    { key: "competition", kinds: ["compete_wins", "compete_win_streak"] },
    { key: "courses", kinds: ["courses_completed"] },
    { key: "exploration", kinds: ["level_first", "discovery", "hidden"] },
  ];

  // Reverse lookup for the URL router: slug → id. Built from
  // GAME_TYPES so the catalogue stays the single source of truth.
  const CHALLENGE_TYPE_FROM_SLUG = Object.fromEntries(
    GAME_TYPES.map((g) => [g.slug, g.id])
  );

  // The 14 trivia categories, mirroring the res/seed/trivia directory names
  // (the backend `category` filter). Names live in the i18n catalog.
  // icon_path follows the CHALLENGE_ICON_PATHS convention: a single stroked
  // <path> in a 24x24 viewBox.
  const TRIVIA_CATEGORIES = [
    { id: "animals-nature", icon_path: "M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10zM2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" },
    { id: "art",            icon_path: "M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM8.5 8.5h.01M21 15l-5-5L5 21" },
    { id: "athletes",       icon_path: "M12 15a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM8.21 13.89 7 23l5-3 5 3-1.21-9.12" },
    { id: "fashion",        icon_path: "M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z" },
    { id: "food",           icon_path: "M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2M7 2v20M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zM21 15v7" },
    { id: "history",        icon_path: "M5 22h14M5 2h14M17 22v-4.17a2 2 0 0 0-.59-1.42L12 12l-4.41 4.41A2 2 0 0 0 7 17.83V22M7 2v4.17a2 2 0 0 0 .59 1.42L12 12l4.41-4.41A2 2 0 0 0 17 6.17V2" },
    { id: "landmarks",      icon_path: "M3 22h18M6 18v-7M10 18v-7M14 18v-7M18 18v-7M12 2l9 5H3z" },
    { id: "movies",         icon_path: "M4 3h16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM7 3v18M17 3v18M2 8h5M2 16h5M17 8h5M17 16h5M2 12h20" },
    { id: "music",          icon_path: "M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" },
    { id: "mythology",      icon_path: "m2 5 4.5 4.5L12 3l5.5 6.5L22 5l-2.5 14h-15zM5 22h14" },
    { id: "scientists",     icon_path: "M10 2v7.53a2 2 0 0 1-.21.9L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45l-5.07-10.13a2 2 0 0 1-.21-.89V2M8.5 2h7M7 16h10" },
    { id: "space",          icon_path: "M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09zM12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2zM9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" },
    { id: "tv",             icon_path: "M4 7h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zM17 2l-5 5-5-5" },
    { id: "video-games",    icon_path: "M6 11h4M8 9v4M15 12h.01M18 10h.01M17.32 5H6.68a4 4 0 0 0-3.98 3.59L2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.41-1.41A2 2 0 0 1 9.83 16h4.34a2 2 0 0 1 1.41.59L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3l-.7-7.41A4 4 0 0 0 17.32 5z" },
  ];

  // Rolls a runnable challenge id. `excludeId` is dropped from the pool so a
  // Random session doesn't re-roll the type just played (falling back to the
  // full pool when the exclusion would empty it). Speech-gated types stay out
  // so Random never lands on a type that can't run with speech disabled.
  function challengeAvailability(type, level) {
    if (!type) return null;
    if (!type.levels || type.levels.includes(level)) return null;
    return {
      range: `${type.levels[0]}-${type.levels[type.levels.length - 1]}`,
      reasonKey: `challenge.unavailable.${type.id}.${level}`,
    };
  }

  function selectableChallengeTypes(types, level) {
    return types.filter((type) => !challengeAvailability(type, level));
  }

  function pickRandomChallengeType(excludeId, level) {
    const real = selectableChallengeTypes(GAME_TYPES, level).filter((g) =>
      g.id !== "random" && !g.requires_speech && !g.standalone && !g.needs_regions);
    const pool = real.filter((g) => g.id !== excludeId);
    const candidates = pool.length ? pool : real;
    return candidates[Math.floor(Math.random() * candidates.length)].id;
  }

  // Stored Dialect region keys that are still offered (GET
  // /regional-word-regions), in the server's order; a region can be retired.
  function liveDialectRegionKeys(stored, live) {
    const keys = Array.isArray(stored) ? stored : [];
    return (live || []).map((r) => r.key).filter((k) => keys.includes(k));
  }

  const SPA_PATH_PARAM = {
    tv_show: "activeShowId",
    wordgroup: "activeWordgroupSlug",
    chat: "activeChatSessionId",
    profile: "activeProfileUsername",
    compete: "competeJoinCode",
    lesen_piece: "activePieceId",
  };

  function wordgroupQueryFromLocation() {
    try {
      return new URLSearchParams(window.location.search).get("q") || "";
    } catch {
      return "";
    }
  }

  const SPA_ROUTE_HANDLERS = {
    tv() {
      this.restoreTvFiltersFromURL();
      this.activeShowId = null;
      this.setScreen("tv");
    },
    tv_show(param) {
      if (param === null) return false;
      this.restoreTvFiltersFromURL();
      this.activeShowId = param;
      this.setScreen("tv_show");
    },
    wordgroups() {
      this.activeWordgroupSlug = null;
      this.wordgroupBand = null;
      const next = wordgroupQueryFromLocation();
      const was = this.screen === "wordgroups";
      this.wordgroupQuery = next;
      this.setScreen("wordgroups");
      if (was) this.$dispatch("dt-wordgroup-query", next);
    },
    wordgroup(param) {
      if (param === null) return false;
      const wasWordgroup = this.screen === "wordgroup";
      this.activeWordgroupSlug = param;
      const next = wordgroupQueryFromLocation();
      this.wordgroupQuery = next;
      this.setScreen("wordgroup");
      if (wasWordgroup) {
        this.$dispatch("dt-wordgroup-changed", param);
        this.$dispatch("dt-wordgroup-query", next);
      }
    },
    friends() { this.setScreen("friends"); },
    lesen_superhirn() { this.setScreen("lesen_superhirn"); },
    lesen_news() { this.setScreen("lesen_news"); },
    lesen_choice() { this.setScreen("lesen_choice"); },
    lesen_piece(param) {
      if (param === null) return false;
      const wasPiece = this.screen === "lesen_piece";
      this.activePieceId = param;
      // Unknown until getPiece answers; the band and sidebar stay neutral.
      this.readingPieceOrigin = null;
      this.setScreen("lesen_piece");
      if (wasPiece) this.$dispatch("dt-piece-changed", param);
    },
    compete(param) {
      this.competeJoinCode = param;
      this.setScreen("compete");
    },
    settings() { this.setScreen("settings"); },
    chat(param) {
      const wasChat = this.screen === "chat";
      this.activeChatSessionId = param;
      this.setScreen("chat");
      if (wasChat) this.$dispatch("dt-chat-session-changed", param);
    },
    profile(param) {
      if (param === null) return false;
      const wasProfile = this.screen === "profile";
      this.activeProfileUsername = param;
      this.setScreen("profile");
      if (wasProfile) this.$dispatch("dt-profile-changed", param);
    },
    home() {
      this.setScreen("home");
      try { history.replaceState({}, "", "/"); } catch { /* ignore */ }
    },
    trivia() {
      const params = new URLSearchParams(window.location.search);
      const category = (params.get("category") || "").toLowerCase();
      this.activeTriviaCategory = TRIVIA_CATEGORIES.some((c) => c.id === category) ? category : null;
      if (params.has("level")) {
        params.delete("level");
        const qs = params.toString();
        try { history.replaceState({}, "", qs ? `/trivia?${qs}` : "/trivia"); } catch { /* ignore */ }
      }
      this.randomMode = false;
      this.activeChallengeWordgroupId = this.activeChallengeWordgroupSlug = null;
      const wasMounted = this.screen === "challenge";
      this.pendingChallengeType = "trivia";
      const trivia = GAME_TYPES.find((g) => g.id === "trivia");
      const unavailable = challengeAvailability(trivia, this.$store.ui.level);
      this.setScreen(unavailable ? "challenge_unavailable" : "challenge");
      if (wasMounted && !unavailable) this.$dispatch("dt-challenge-type-changed", "trivia");
    },
    challenge(param) {
      const wasMounted = this.screen === "challenge";
      this.activeChallengeWordgroupId = this.activeChallengeWordgroupSlug = null;
      if (param === null) return false;
      const type = CHALLENGE_TYPE_FROM_SLUG[param];
      if (type === "chat") {
        this.setScreen("chat");
        try { history.replaceState({}, "", "/chat"); } catch { /* ignore */ }
        return;
      }
      if (!(type && (window.dt && window.dt.GAME_TYPES || []).some((g) => g.id === type))) {
        return false;
      }
      const query = new URLSearchParams(window.location.search);
      const list = type === "vocabulary" ? query.get("list") || "" : "";
      if (list) {
        this.randomMode = false;
        this.pendingChallengeType = type;
        this.resolveChallengeList(list);
        return;
      }
      if (type === "random") {
        this.randomMode = true;
        this.pendingChallengeType = pickRandomChallengeType(undefined, this.$store.ui.level);
      } else {
        this.randomMode = false;
        this.pendingChallengeType = type;
      }
      const selected = GAME_TYPES.find((g) => g.id === this.pendingChallengeType);
      this.setScreen(challengeAvailability(selected, this.$store.ui.level)
        ? "challenge_unavailable"
        : "challenge");
      // An ignored ?list= must not linger in the address bar.
      if (query.has("list")) {
        try { history.replaceState({}, "", this.pathForScreen()); } catch { /* ignore */ }
      }
      // The player stayed mounted across a popstate: restart it for the new type/scope.
      if (wasMounted && this.screen === "challenge") this.$dispatch("dt-challenge-scope-changed");
    },
  };

  const BOTTOM_NAV = [
    { id: "home",    labelKey: "mode.home",    icon: "home" },
    { id: "courses", labelKey: "mode.courses", icon: "book" },
    { id: "wordgroups", labelKey: "mode.wordgroups", icon: "cards" },
    { id: "tv",      labelKey: "mode.tv",      icon: "monitor" },
    { id: "friends", labelKey: "mode.friends", icon: "users" },
    { id: "profile", labelKey: "mode.profile", icon: "user" },
  ];

  // Notification kinds for the Settings + bell-drawer preference grids.
  // Canonical list lives in notification-kinds.js (loaded before this
  // script); mirrored in lib/notifications/model.go (AllKinds).
  // Resolved at call time so script or import order cannot freeze fallbacks.
  const notificationKinds = () => window.NOTIFICATION_KINDS || [];
  const notificationKindLabel = (...a) =>
    (window.notificationKindLabel || ((kind) => kind))(...a);
  const notificationKindMessage = (...a) =>
    (window.notificationKindMessage || (() => ""))(...a);
  const notificationKindIsAlert = (...a) =>
    (window.notificationKindIsAlert || (() => false))(...a);

  // Pivots the server's flat {kind, channel, enabled} rows into one row per
  // kind with separate IN_APP / PUSH / DISCORD booleans for the grid. Order follows
  // NOTIFICATION_KINDS; unknown kinds are skipped so a server-side addition
  // can't break the UI before the catalogue here is updated.
  function groupNotificationPrefs(prefs) {
    const byKind = {};
    for (const p of prefs || []) {
      (byKind[p.kind] || (byKind[p.kind] = {}))[p.channel] = p.enabled;
    }
    return notificationKinds()
      .filter((k) => byKind[k.id])
      .map((k) => ({
        id: k.id,
        label: k.label,
        social: k.social,
        inApp: !!byKind[k.id].IN_APP,
        push: !!byKind[k.id].PUSH,
        discord: !!byKind[k.id].DISCORD,
      }));
  }

  function fmtTime(s) {
    const m = Math.floor(s / 60);
    return `${m}:${(s % 60).toString().padStart(2, "0")}`;
  }
  function levelNameOf(id) {
    const lvl = LEVELS.find((l) => l.id === id);
    return lvl ? lvl.name : "";
  }
  // t() is the non-Alpine entry point to the catalogue (template bindings call
  // $store.i18n.t directly). It echoes the key when the store is missing
  // (called before alpine:init) or throws; #root stays hidden until the
  // store is ready, so the echo never reaches the learner.
  function t(key, params) {
    try {
      const store = window.Alpine && window.Alpine.store && window.Alpine.store("i18n");
      if (store) return store.t(key, params);
    } catch { /* store missing or broken */ }
    return key;
  }

  // Until dtApp.init re-anchors it, ui.userId is the dt_user_id cached in
  // localStorage, which a sign-in merge or an expired anon row leaves stale,
  // and /users/{stale}/... 403s (#984). Own-user reads that can fire while
  // screens mount read the id through settledUserId(). It never
  // settles on a halted ("unavailable") boot (the page reloads once auth is
  // back) or a closed sign-up (the visitor has no row to read).
  let settleUserId;
  const userIdSettled = new Promise((resolve) => { settleUserId = resolve; });
  const settledUserId = async () => {
    await userIdSettled;
    return window.Alpine.store("ui").userId;
  };

  const authChannel = typeof BroadcastChannel === "function" ? new BroadcastChannel("dt-auth") : null;
  let authPosted = false;
  let pendingAuthIdentity = null;
  function postAuthIdentity(identity) {
    if (!authChannel) return;
    authChannel.postMessage(identity);
    authPosted = true;
  }
  function checkAuthChange(identity) {
    if (!authPosted) {
      pendingAuthIdentity = identity;
      return;
    }
    if (!identity) return;
    const auth = window.Alpine.store("auth");
    const ui = window.Alpine.store("ui");
    if (identity.signedIn === auth.signedIn && (identity.userId == null || identity.userId === ui.userId)) return;
    auth.crossTabNotice = identity.signedIn ? "auth.signedInAnotherTab" : "auth.signedOutAnotherTab";
    window.dispatchEvent(new CustomEvent("dt-auth-changed"));
  }
  if (authChannel) {
    authChannel.onmessage = (event) => checkAuthChange(event.data);
    window.addEventListener("pageshow", async (event) => {
      if (!event.persisted || !authPosted) return;
      try {
        const identity = await api.getMe();
        checkAuthChange({ userId: identity.user_id, signedIn: true });
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) checkAuthChange({ userId: null, signedIn: false });
      }
    });
  }
  // A page restored from the back/forward cache checks the asset version
  // (apiFetch notes it); the sign-in check above covers it when it runs.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted && !(authChannel && authPosted)) api.getPublicFlags().catch(() => {});
  });

  function wordReviewReason(word, translate, now = Date.now()) {
    if (word.reason === "recent_lapse") {
      if (word.lapse_count > 1) return translate("profile.reviewMissedTimes", { count: word.lapse_count });
      const days = Math.max(0, Math.floor((now - new Date(word.last_wrong_at).getTime()) / 86_400_000));
      if (days === 0) return translate("profile.reviewMissedToday");
      if (days === 1) return translate("profile.reviewMissedYesterday");
      return translate("profile.reviewMissedDaysAgo", { count: days });
    }
    if (word.reason === "overdue" && word.next_due_at) {
      const days = Math.max(1, Math.floor((now - new Date(word.next_due_at).getTime()) / 86_400_000));
      return translate(days === 1 ? "profile.reviewOverdueDay" : "profile.reviewOverdueDays", { count: days });
    }
    return translate("profile.reviewNeedsRefresh");
  }

  // The progress table is keyed by (word, challenge type), but the review
  // surface is word-based. Collapse duplicate rows for display while retaining
  // every source type so one vocabulary answer can update all matching keys.
  function coalesceFadingWords(words) {
    const byWord = new Map();
    for (const word of Array.isArray(words) ? words : []) {
      if (!word || !word.word_id) continue;
      const existing = byWord.get(word.word_id);
      if (!existing) {
        byWord.set(word.word_id, {
          ...word,
          review_types: word.challenge_type ? [word.challenge_type] : [],
        });
        continue;
      }
      if (word.challenge_type && !existing.review_types.includes(word.challenge_type)) {
        existing.review_types.push(word.challenge_type);
      }
    }
    return [...byWord.values()];
  }

  function reviewPairs(words) {
    return (Array.isArray(words) ? words : []).flatMap((word) => {
      const types = word.review_types && word.review_types.length
        ? word.review_types
        : (word.challenge_type ? [word.challenge_type] : []);
      return types.map((challengeType) => ({
        word_id: word.word_id,
        challenge_type: challengeType,
      }));
    });
  }

  // Matches the TV taxonomy in lib/tv/admin/viewmodel/types.go and db/schema.sql.
  const TV_CONTENT_TYPES = [
    "cartoons", "news", "documentaries", "podcasts", "popular_music_videos",
    "crime", "drama", "comedy", "family", "thriller", "historical", "sci_fi", "action",
  ];
  const TV_LEVEL_COHORTS = ["A2-B2", "B1-C1", "B2-C1"];

  // Every Show is a German-language programme, so its original title is the
  // German one; the title field can carry an English name (#1153).
  function tvShowTitle(show) {
    return show.original_title || show.title;
  }

  // TV_SERVICE_LABELS preserves each brand's canonical capitalisation
  // for the link badges in the detail popup. Mirrors the operator-
  // facing list in lib/admin/tv.go (TvExternalURLServices); keep
  // them in sync when a new service lands.
  const TV_SERVICE_LABELS = {
    imdb: "IMDb",
    wikipedia: "Wikipedia",
    netflix: "Netflix",
    "amazon-prime": "Amazon Prime",
    hbo: "HBO",
    "disney-plus": "Disney+",
    "ard-plus": "ARD+",
    joyn: "Joyn",
    "3sat": "3sat",
    youtube: "YouTube",
    mubi: "Mubi",
    criterion: "Criterion",
    filmingo: "Filmingo",
    cinefile: "Cinefile",
    tubi: "Tubi",
    indiecinema: "Indiecinema",
  };
  function prettyServiceLabel(s) {
    return s === "other" ? t("tv.watchExternally") : TV_SERVICE_LABELS[s] || s;
  }

  // Turns a watch / youtu.be / embed YouTube URL into its /embed/ form.
  // Returns "" for unrecognised input so the modal can fall back to the hero
  // image instead of loading an iframe with a broken src.
  function ytEmbedSrc(raw) {
    if (!raw) return "";
    try {
      const u = new URL(raw);
      let id = "";
      if (u.hostname === "youtu.be") {
        id = u.pathname.replace(/^\//, "");
      } else if (u.hostname.endsWith("youtube.com")) {
        if (u.pathname.startsWith("/embed/")) {
          id = u.pathname.slice("/embed/".length);
        } else {
          id = u.searchParams.get("v") || "";
        }
      }
      if (!id) return "";
      // Encode the id so an attacker-controlled URL can't escape the
      // attribute. rel=0 keeps suggested videos confined to the same
      // channel, modestbranding hides the YouTube logo, playsinline
      // keeps mobile autoplay-on-tap inline.
      return "https://www.youtube-nocookie.com/embed/" +
             encodeURIComponent(id) +
             "?rel=0&modestbranding=1&playsinline=1";
    } catch (e) {
      return "";
    }
  }
  window.tvShowTitle = tvShowTitle;
  window.prettyServiceLabel = prettyServiceLabel;
  window.ytEmbedSrc = ytEmbedSrc;

  // ------------------------------------------------------------------
  // Device-identity bootstrap + API client
  // ------------------------------------------------------------------

  function ensureDeviceId() {
    let id = localStorage.getItem("dt_device_id");
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : "dev-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("dt_device_id", id);
    }
    return id;
  }

  // The device id normally travels in the X-Device-Id header. The one flow
  // that can't send a header is /auth/callback: a top-level GET that AuthKit
  // redirects to, with no JS in the loop. So just before we hand off to the
  // sign-in redirect we mirror the id into a short-lived SameSite=Lax cookie;
  // the callback reads it and then clears it server-side (see oauth
  // Controller), so it never lingers for a logged-out user. We deliberately
  // do NOT write this on every API call - an anonymous visitor keeps zero
  // cookies. Secure on HTTPS so an MITM can't lift the id over plain HTTP and
  // link their own WorkOS identity to the victim's local row (P1, Greptile).
  function armDeviceIdCookieForSignIn() {
    const id = ensureDeviceId();
    const secure = location.protocol === "https:" ? "; Secure" : "";
    // 30 min is ample for an OAuth round-trip (incl. MFA/password reset);
    // an abandoned sign-in self-clears well before it could matter.
    document.cookie = "dt_device_id=" + encodeURIComponent(id) +
      "; Path=/; Max-Age=1800; SameSite=Lax" + secure;
  }

  // The OAuth round trip loses the page the visitor was on, so mirror it into
  // a cookie /auth/callback can read (issue #677). The server treats anything
  // that is not a plain same-origin path as the home screen.
  function armReturnToCookieForSignIn() {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    const target = location.pathname + location.search;
    document.cookie = "dt_return_to=" + encodeURIComponent(target) +
      "; Path=/; Max-Age=1800; SameSite=Lax" + secure;
  }

  class ApiError extends Error {
    constructor(problem) {
      super(problem.title || "Request failed");
      this.problem = problem;
      this.status = problem.status;
    }
  }

  const MAX_API_REPORTS = 10;
  let apiReportCount = 0;
  let currentScreen = "";
  const reportedApiFailures = new Set();

  // Whatever follows one of these is an id, a slug or a @handle: usernames and
  // word ids are as identifying as a uuid and must never reach the log message.
  // "chat", "lesen", "tv" and "u" are SPA shell prefixes (the screen fallback
  // runs location.pathname through here); no API route uses them as a parent.
  // "challenges" is not one: /challenges/<type> is a page, and the uuid of a
  // Challenge in /api/challenges/... is collapsed as a uuid.
  const ID_PARENTS = [
    "chat", "chats", "courses", "duels", "favorite-word-lists", "follows", "lesen",
    "lessons", "level-votes", "messages", "notifications", "passages", "pieces",
    "push-subscriptions", "quality-votes", "tv", "tv-shows",
    "u", "username-availabilities", "users", "word-groups", "word-lists", "words",
  ];
  // Literal sub-resources that sit in an id slot, scoped to their parent so a
  // @handle spelled "active" is still collapsed.
  // Object.create(null) so a handle spelled "constructor" cannot reach Object.prototype.
  const ID_LITERALS = Object.assign(Object.create(null),
    {
      chats: ["current"], duels: ["current"],
      users: ["me"], "level-votes": ["me"], "quality-votes": ["me"],
    });

  function apiRouteTemplate(path) {
    const segments = path.split(/[?#]/)[0].split("/");
    return segments.map((segment, i) => {
      if (!segment || ID_LITERALS[segments[i - 1]]?.includes(segment)) return segment;
      const isId = ID_PARENTS.includes(segments[i - 1])
        || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
        || /^\d+$/.test(segment)
        || (/^[A-Za-z0-9_-]{16,}$/.test(segment) && /\d/.test(segment));
      return isId ? ":id" : segment;
    }).join("/");
  }

  // Expected failures stay local; cap unexpected failures so polling cannot flood logs.
  // challengeType tells the 23 types of POST /api/challenges apart: they share
  // one route, and one report per shape would hide all but the first.
  function reportApiFailure(method, path, status, problem, silence = [], challengeType = "") {
    try {
      if ([401, 409, 429].includes(status) || silence.includes(status)
          || apiReportCount >= MAX_API_REPORTS) return;
      if (typeof window.Sentry?.logger?.error !== "function") return;
      path = path.split(/[?#]/)[0];
      const route = apiRouteTemplate(path);
      // A retry or a poll repeats the same line, so one report per shape is
      // plenty; count only what Sentry actually took.
      const key = `${method} ${route} ${status} ${challengeType}`;
      if (reportedApiFailures.has(key)) return;
      window.Sentry.logger.error(`API ${status} ${method} ${route}`, {
        method, path, route, status,
        ...(challengeType ? { challenge_type: challengeType } : {}),
        ...(problem.type !== undefined ? { type: problem.type } : {}),
        ...(problem.detail !== undefined ? { detail: problem.detail } : {}),
        screen: currentScreen || apiRouteTemplate(location.pathname),
      });
      reportedApiFailures.add(key);
      apiReportCount++;
    } catch { /* reporting is best-effort */ }
  }

  // Default envelope keeps `status` = the HTTP code even when the body is
  // missing, non-JSON, or problem JSON without a status field. Merge (not
  // overwrite) so a parsed title / detail flows through with res.status as
  // the floor.
  async function parseApiError(res, method, path, silence, challengeType) {
    let problem = { title: "Request failed", status: res.status };
    try {
      const body = await res.json();
      if (body && typeof body === "object") {
        problem = { ...problem, ...body, status: body.status || res.status };
      }
    } catch { /* not JSON */ }
    reportApiFailure(method, path, res.status, problem, silence, challengeType);
    return new ApiError(problem);
  }

  // A fetch rejection (offline, DNS, CORS) never reaches parseApiError, so
  // report it as status 0 and rethrow it untouched, so the error a caller
  // sees is unchanged. No silence array is passed on purpose - those list HTTP
  // statuses the call site handles, and none of them handles a dead network.
  async function apiFetch(method, path, init) {
    const write = method !== "GET";
    if (write) pendingWrites++;
    try {
      const res = await fetch(path, { ...init, method });
      noteAssetVersion(res);
      return res;
    } catch (e) {
      reportApiFailure(method, path, 0, {});
      throw e;
    } finally {
      if (write) pendingWrites--;
    }
  }

  // call retries once through registerAnonymous when the device-id
  // middleware answers 401 puzzle-required: the device row is gone (or
  // never existed) while the sign-up challenge is on (issue #578).
  async function call(method, path, body, opts = {}) {
    return retryUnregistered(path, () => callOnce(method, path, body, opts));
  }

  async function retryUnregistered(path, attempt) {
    try {
      return await attempt();
    } catch (e) {
      if (!isUnregisteredDevice(e) || path.startsWith("/api/anonymous-users") || path === "/api/sign-up-puzzles") throw e;
      await registerOnce();
      return attempt();
    }
  }

  // The device-id middleware answers 503 session-store-unavailable before any
  // handler runs, so nothing was saved and an answer submit is safe to send
  // once more (issue #1270). Other 503s can come after the handler.
  const SESSION_RETRY_MS = 1000;
  // current() is an optional guard: false after the wait (round torn down) skips the resend.
  async function retrySessionUnavailable(attempt, current = () => true) {
    try {
      return await attempt();
    } catch (e) {
      if (!isSessionStoreUnavailable(e)) throw e;
      await new Promise((resolve) => setTimeout(resolve, SESSION_RETRY_MS));
      if (!current()) throw e;
      return attempt();
    }
  }

  async function callOnce(method, path, body, { silence = [] } = {}) {
    const headers = {
      "X-Device-Id": ensureDeviceId(),
      "X-Requested-With": "deutschodoro-spa",
    };
    if (body !== undefined) headers["Content-Type"] = method === "PATCH" ? "application/merge-patch+json" : "application/json";
    const res = await apiFetch(method, path, {
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw await parseApiError(res, method, path, silence, path === "/api/challenges" ? body?.challenge_type : undefined);
    if (res.status === 204) return null;
    return res.json();
  }

  // pickRecMime returns the first MediaRecorder container the browser supports,
  // preferring opus in webm/ogg. "" lets MediaRecorder pick its own default.
  // The server MIME allowlist strips codec params, so "audio/webm;codecs=opus"
  // is accepted as "audio/webm".
  function pickRecMime() {
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported) return "";
    const cands = [
      "audio/webm;codecs=opus", "audio/webm",
      "audio/ogg;codecs=opus", "audio/ogg", "audio/mp4",
    ];
    for (const c of cands) if (MediaRecorder.isTypeSupported(c)) return c;
    return "";
  }

  // Mic capture shared by the Pronunciation Question and Chat. Owns the
  // getUserMedia grant, the MediaRecorder and the superseded-attempt token
  // (issue #673: a second tap must not open a second stream); callers keep
  // their own screen state. start() resolves true once recording, false when
  // teardown() cancelled the attempt, and throws the getUserMedia error.
  function createMicCapture() {
    let gen = 0, starting = false, stream = null, recorder = null, chunks = [], mime = "audio/webm";
    const stopStream = () => {
      if (stream) { stream.getTracks().forEach((track) => track.stop()); stream = null; }
    };
    return {
      get starting() { return starting; },
      async start(onStop) {
        starting = true;
        const my = ++gen;
        try {
          const s = await navigator.mediaDevices.getUserMedia({ audio: true });
          if (my !== gen) { s.getTracks().forEach((track) => track.stop()); return false; }
          stream = s;
          const picked = pickRecMime();
          mime = picked || "audio/webm";
          const mr = picked ? new MediaRecorder(s, { mimeType: picked }) : new MediaRecorder(s);
          chunks = [];
          mr.addEventListener("dataavailable", (e) => { if (e.data && e.data.size) chunks.push(e.data); });
          mr.addEventListener("stop", () => {
            if (recorder !== mr) return; // torn down or superseded: drop the late clip
            stopStream();
            const blob = new Blob(chunks, { type: mime });
            chunks = [];
            onStop(blob.size ? blob : null);
          });
          recorder = mr;
          mr.start();
          return true;
        } catch (e) {
          if (my !== gen) return false; // a superseded attempt fails quietly
          stopStream();
          throw e;
        } finally {
          if (my === gen) starting = false; // only the attempt in charge lifts the guard
        }
      },
      // True when a stop event (and onStop) will follow.
      stop() {
        if (recorder && recorder.state !== "inactive") { recorder.stop(); return true; }
        stopStream();
        return false;
      },
      // Hard-stop: discard any in-flight clip and any pending grant.
      teardown() {
        gen++;
        starting = false;
        const mr = recorder;
        recorder = null;
        if (mr && mr.state !== "inactive") { try { mr.stop(); } catch { /* already stopped */ } }
        stopStream();
      },
    };
  }

  // extForMime maps a recorded blob's MIME type to a file extension for the
  // upload filename (the server also reads the part's Content-Type).
  function extForMime(mime) {
    const m = (mime || "").split(";")[0];
    if (m === "audio/ogg") return "ogg";
    if (m === "audio/mp4") return "m4a";
    if (m === "audio/mpeg") return "mp3";
    if (m === "audio/wav" || m === "audio/x-wav") return "wav";
    return "webm";
  }

  // Problem-report screenshots: mirrors the server limits (the server stays the
  // authority and sniffs the bytes).
  const REPORT_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
  const REPORT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

  // callForm posts multipart/form-data (recorded audio, report screenshots). The
  // browser sets the Content-Type + boundary from the FormData body, so we must
  // NOT set it here. Error handling and the unregistered-device retry mirror
  // call(); a FormData body can be sent again as is.
  async function callForm(method, path, formData, opts = {}) {
    return retryUnregistered(path, () => callFormOnce(method, path, formData, opts));
  }

  async function callFormOnce(method, path, formData, { silence = [] } = {}) {
    const res = await apiFetch(method, path, {
      headers: {
        "X-Device-Id": ensureDeviceId(),
        "X-Requested-With": "deutschodoro-spa",
      },
      body: formData,
    });
    if (!res.ok) throw await parseApiError(res, method, path, silence);
    if (res.status === 204) return null;
    return res.json();
  }

  // callBlob is call() for endpoints that stream bytes back (chat reply
  // audio). Error handling is identical; only the success body differs.
  async function callBlob(method, path) {
    const res = await apiFetch(method, path, {
      headers: {
        "X-Device-Id": ensureDeviceId(),
        "X-Requested-With": "deutschodoro-spa",
      },
    });
    if (!res.ok) throw await parseApiError(res, method, path);
    return res.blob();
  }

  // leadingZeroBits counts the zero bits at the start of a digest.
  function leadingZeroBits(bytes) {
    let bits = 0;
    for (const b of bytes) {
      if (b === 0) { bits += 8; continue; }
      return bits + Math.clz32(b) - 24;
    }
    return bits;
  }

  // solveSignupChallenge is the proof-of-work POST /anonymous-users asks for
  // when an operator turns on anonymous_signup_challenge_enabled (issue #578;
  // proof-of-work because it needs no third-party captcha account). It finds
  // the first decimal nonce whose SHA-256("<challenge>:<nonce>") starts with
  // `bits` zero bits, which is what lib/users/signupgate checks. It yields to
  // the event loop every 2048 digests and gives up once expiresAt has passed.
  async function solveSignupChallenge(challenge, bits, expiresAt) {
    const enc = new TextEncoder();
    const deadline = expiresAt ? Date.parse(expiresAt) : NaN;
    const limit = 2 ** Math.min(bits + 6, 40);
    for (let nonce = 0; nonce < limit; nonce++) {
      if (nonce % 2048 === 0) {
        if (nonce > 0) await new Promise((r) => setTimeout(r, 0));
        if (Date.now() > deadline) throw new Error("sign-up challenge expired");
      }
      const digest = await crypto.subtle.digest("SHA-256", enc.encode(`${challenge}:${nonce}`));
      if (leadingZeroBits(new Uint8Array(digest)) >= bits) return String(nonce);
    }
    throw new Error("sign-up challenge not solved");
  }

  function problemSlug(e) {
    const type = (e && e.problem && e.problem.type) || "";
    return type.slice(type.lastIndexOf("/") + 1);
  }

  function isSignupChallengeError(e) {
    const slug = problemSlug(e);
    return slug === "puzzle-required" || slug === "puzzle-invalid";
  }

  // isSignupClosed: the server is not creating guest accounts right now
  // (daily ceiling spent, or the gate cannot reach Redis).
  function isSignupClosed(e) {
    const slug = problemSlug(e);
    return slug === "signups-paused" || slug === "signups-unavailable";
  }

  function isUnregisteredDevice(e) {
    return !!e && e.status === 401 && problemSlug(e) === "puzzle-required";
  }

  function isSessionStoreUnavailable(e) {
    return problemSlug(e) === "session-store-unavailable";
  }

  // registerAnonymous posts without a proof first, so nothing changes while
  // the challenge flag is off. A puzzle-required answer fetches, solves and
  // retries; one more round covers a challenge that expired mid-solve.
  async function registerAnonymous() {
    try {
      return await call("POST", "/api/anonymous-users", {}, { silence: [403, 503] });
    } catch (e) {
      if (!isSignupChallengeError(e)) throw e;
    }
    for (let attempt = 0; ; attempt++) {
      try {
        const ch = await call("POST", "/api/sign-up-puzzles", {}, { silence: [503] });
        const nonce = await solveSignupChallenge(ch.puzzle, ch.bits, ch.expires_at);
        return await call("POST", "/api/anonymous-users",
          { puzzle: ch.puzzle, nonce }, { silence: [403, 503] });
      } catch (e) {
        if (attempt >= 1 || isSignupClosed(e) || (e instanceof ApiError && !isSignupChallengeError(e))) throw e;
      }
    }
  }

  // registerOnce shares one in-flight registration between concurrent calls
  // that all hit 401 puzzle-required, so the page solves one challenge.
  let pendingRegistration = null;
  function registerOnce() {
    pendingRegistration ||= registerAnonymous().finally(() => { pendingRegistration = null; });
    return pendingRegistration;
  }

  const api = {
    registerAnon: () => registerOnce(),
    heartbeat: (userId) => call("POST", `/api/users/${userId}/heartbeats`),
    // sounds defaults to true to preserve existing behaviour; Phase 19
    // Settings flips it to false when the user mutes audio so the server
    // skips ElevenLabs synthesis on the next quiz. scope: a Passive scope
    // for this round only (deep links), never saved.
    startChallenge: (type, level, sounds = true, wordgroupId = "", category = "", wordIds = [], regions = [], scope = 0) => {
      // The reading module picks the Pieces, the level and the questions.
      if (type === "reading-quiz") return api.startReadingQuestions();
      const g = GAME_TYPES.find((x) => x.id === type);
      const body = { challenge_type: g && g.challenge_type, level, lang: currentEffectiveLang() };
      if (!sounds) body.sounds = false;
      if (wordgroupId) body.word_list_id = wordgroupId;
      if (category) body.topic = category;
      if (wordIds.length) body.word_ids = wordIds;
      if (regions.length) body.dialect_regions = regions;
      if (scope) body.scope = scope;
      return call("POST", "/api/challenges", body);
    },
    getScopeMarker: (challengeType, level) =>
      call("GET", `/api/users/me/scope-markers/${challengeType}/${level}`),
    patchScopeMarker: (challengeType, level, chosen) =>
      call("PATCH", `/api/users/me/scope-markers/${challengeType}/${level}`, { chosen }),
    listDialectRegions: () => call("GET", "/api/regional-word-regions"),
    listWordgroups: (query = "", theme = "") => {
      const params = new URLSearchParams({ lang: currentEffectiveLang() });
      if (query) params.set("q", query);
      if (theme) params.set("theme", theme);
      return call("GET", `/api/word-lists?${params.toString()}`);
    },
    searchWordgroupHeadwords: (query = "", theme = "", limit = 0) => {
      const params = new URLSearchParams({ lang: currentEffectiveLang(), in_public_word_lists: "true", embed: "word_lists" });
      if (query) params.set("q", query);
      if (theme) params.set("theme", theme);
      if (limit) params.set("limit", String(limit));
      return call("GET", `/api/words?${params.toString()}`);
    },
    getWordgroup: (slug, limit = 500) => {
      const params = new URLSearchParams({ lang: currentEffectiveLang(), limit: String(limit) });
      return call("GET", `/api/word-lists/${encodeURIComponent(slug)}?${params.toString()}`, undefined, { silence: [404] });
    },
    setWordgroupSelected: (wordListId, selected) =>
      call(selected ? "PUT" : "DELETE", `/api/users/me/favorite-word-lists/${encodeURIComponent(wordListId)}`),
    listPieces: ({ origin, topic = "", scope = "", limit, offset = 0 }) => {
      const params = new URLSearchParams({ origin, limit: String(limit), offset: String(offset) });
      if (topic) params.set("topic", topic);
      if (scope) params.set("scope", scope);
      return call("GET", `/api/pieces?${params.toString()}`);
    },
    listRegions: () => call("GET", "/api/regions"),
    getPiece: (id) => call("GET", `/api/pieces/${encodeURIComponent(id)}`),
    markPassageRead: (passageId) => call("POST", `/api/passages/${encodeURIComponent(passageId)}/read-marks`),
    getReadyPieces: () => call("GET", "/api/users/me/ready-pieces"),
    startReadingQuestions: () => call("POST", "/api/challenges", { challenge_type: "COMPREHENSION_QUESTIONS" }),
    startPieceChat: (pieceId, kind) => call("POST", "/api/chats", { piece_id: pieceId, scenario_kind: kind }),
    submitAnswer: (challengeId, questionId, choice, current, durationMs) =>
      // durationMs: time from showing the question to the tap (logged, not shown).
      retrySessionUnavailable(() => call("POST", `/api/challenges/${challengeId}/answers`,
        typeof durationMs === "number"
          ? { question_id: questionId, choice, duration_ms: durationMs }
          : { question_id: questionId, choice }), current),
    // Pronunciation: upload the recorded blob as multipart/form-data; the
    // server transcribes it (STT) and grades the transcript by CER.
    submitAnswerAudio: (challengeId, questionId, blob, durationMs, current) => {
      const fd = new FormData();
      fd.append("question_id", questionId);
      fd.append("audio", blob, `recording.${extForMime(blob.type)}`);
      if (durationMs) fd.append("duration_ms", String(durationMs));
      return retrySessionUnavailable(() => callForm("POST", `/api/challenges/${challengeId}/answers`, fd), current);
    },
    completeChallenge: (challengeId) =>
      call("POST", `/api/challenges/${challengeId}/completions`, {}),
    // Read-only: hands back a still-cached round so a reload continues it
    // (issue #666). 404s once the 30-min server-side TTL lapses, or once
    // the round has been sealed by /completions.
    resumeChallenge: (challengeId) =>
      call("GET", `/api/challenges/${challengeId}`, undefined, { silence: [403, 404] }),
    stats: (period) =>
      call("GET", `/api/users/me/stats?period=${period}`),
    streaks: () =>
      call("GET", "/api/users/me/streaks"),
    fadingWords: (limit) => {
      const params = new URLSearchParams({ lang: currentEffectiveLang() });
      if (limit) params.set("limit", String(limit));
      return call("GET", `/api/users/me/fading-words?${params.toString()}`);
    },
    achievements: (userId) =>
      call("GET", `/api/users/${userId}/achievements`),
    getWord: (id) => call("GET", `/api/words/${encodeURIComponent(id)}?lang=${currentEffectiveLang()}`),
    // SR drill passes `word_ids` so the server seeds the session with
    // those words first; remaining slots fill from the normal random
    // pool at the requested level.
    drillFadingWords: (level, words, sounds = true) => {
      const body = { challenge_type: "VOCABULARY", level, lang: currentEffectiveLang() };
      const pairs = reviewPairs(words);
      if (pairs.length) {
        body.word_ids = pairs.map((pair) => pair.word_id);
        body.review_types = pairs.map((pair) => pair.challenge_type);
      }
      if (!sounds) body.sounds = false;
      return call("POST", "/api/challenges", body);
    },
    listNotifications: (limit) =>
      call("GET", `/api/users/me/notifications${limit ? `?limit=${limit}` : ""}`),
    patchNotification: (id, action) =>
      call("PATCH", `/api/users/me/notifications/${id}`, action),
    markAllNotificationsSeen: () =>
      call("POST", "/api/users/me/seen-notification-markers", {}),
    getNotificationPrefs: () =>
      call("GET", "/api/users/me/notification-preferences"),
    putNotificationPref: (kind, channel, enabled) =>
      call("PUT", `/api/users/me/notification-preferences/${kind}/${channel}`, { enabled }),
    pushConfig: () => call("GET", "/api/push-config"),
    subscribePush: (userId, endpoint, p256dh, auth) =>
      call("POST", `/api/users/${userId}/push-subscriptions`, { endpoint, p256dh, auth }),
    unsubscribePush: (subscriptionId) =>
      call("DELETE", `/api/users/me/push-subscriptions/${subscriptionId}`, undefined, { silence: [404] }),
    // 404: no DISCORD_* config or the kill flag is off; 403: not signed in.
    getDiscordLink: () => call("GET", "/api/users/me/discord", undefined, { silence: [403, 404] }),
    unlinkDiscord: () => call("DELETE", "/api/users/me/discord"),
    setDiscordCelebrate: (enabled) => call("PUT", "/api/users/me/discord/celebrate", { enabled }),
    retryDiscordDM: () => call("POST", "/api/users/me/discord/dm-retry"),

    follow: (targetUserId) =>
      call("PUT", `/api/users/me/follows/${targetUserId}`),
    unfollow: (targetUserId) =>
      call("DELETE", `/api/users/me/follows/${targetUserId}`),
    listFollows: () =>
      call("GET", "/api/users/me/follows"),
    listFriends: () =>
      call("GET", "/api/users/me/friends"),
    listFollowers: (cursor, limit) => {
      const params = new URLSearchParams();
      if (cursor) params.set("cursor", cursor);
      if (limit) params.set("limit", String(limit));
      const qs = params.toString();
      return call("GET", `/api/users/me/followers${qs ? `?${qs}` : ""}`);
    },
    // userRef is a UUID, a @username (the /u/{username} page) or "me".
    // Only the public profile screen treats a 404 as normal (unknown handle,
    // or an anonymous Learner, who has no username); on your own profile row
    // it is a bug, so the silence is the caller's to ask for.
    getProfile: (userRef, opts) =>
      call("GET", `/api/users/${encodeURIComponent(userRef)}?lang=${encodeURIComponent(currentEffectiveLang())}`, undefined, opts),
    getLeaderboard: (limit = 10) =>
      call("GET", `/api/leaderboard?limit=${limit}`),
    listCourses: () => call("GET", "/api/courses"),
    getCourse: (courseId) => call("GET", `/api/courses/${courseId}`),
    getLesson: (courseId, lessonId) =>
      call("GET", `/api/courses/${courseId}/lessons/${lessonId}?lang=${encodeURIComponent(currentEffectiveLang())}`),
    lessonVisit: (courseId, lessonId) =>
      call("POST", `/api/courses/${courseId}/lessons/${lessonId}/visits`),
    // answer is {answer_type, ...}: one route for every Lesson step.
    lessonAnswer: (courseId, lessonId, answer) =>
      call("POST", `/api/courses/${courseId}/lessons/${lessonId}/answers`, answer),
    lessonComplete: (courseId, lessonId) =>
      call("POST", `/api/courses/${courseId}/lessons/${lessonId}/completions`),
    lessonContinue: () =>
      call("GET", "/api/users/me/current-lesson", undefined, { silence: [404] }),
    listTvShows: (params) => {
      const qs = new URLSearchParams();
      if (params && params.content_type) qs.set("content_type", params.content_type);
      if (params && params.level_cohort) qs.set("level_cohort", params.level_cohort);
      if (params && params.sort) qs.set("sort", params.sort);
      if (params && params.limit) qs.set("limit", String(params.limit));
      const s = qs.toString();
      return call("GET", `/api/tv-shows${s ? `?${s}` : ""}`);
    },
    // Curated allowlist of feature flags the public app needs to
    // know about (e.g. whether to render the TV "Most voted" rail).
    // Unauthenticated; safe to call before device-id bootstrap.
    getPublicFlags: () => call("GET", "/api/public-flags"),

    // OAuth surface. GET /sessions/current returns the signed-in identity
    // (resolved via the dt_session cookie set by /auth/callback) and
    // 401s when the cookie is missing or stale. DELETE /sessions/current is
    // the matching cookie-clearing endpoint (204). The sign-in entry point is a
    // full-page nav to /auth/sign-in (302 → AuthKit), not a fetch.
    getMe: () => call("GET", "/api/sessions/current"),
    signOut: () => call("DELETE", "/api/sessions/current"),
    patchUser: (userId, body) => call("PATCH", `/api/users/${userId}`, body),
    deleteUser: (userId) => call("DELETE", `/api/users/${userId}`),
    // Public availability probe used by the first-login handle picker
    // to bump the auto-derived number suffix until the slug is free.
    // 200 { username, available }; false for an invalid or taken handle.
    checkUsernameAvailability: (candidate) =>
      call("GET", `/api/username-availabilities/${encodeURIComponent(candidate)}`),
    getTvShow: (showId) => call("GET", `/api/tv-shows/${encodeURIComponent(showId)}`, undefined, { silence: [404] }),
    voteTvQuality: (showId, stars) =>
      call("PUT", `/api/tv-shows/${showId}/quality-votes/me`, { stars }),
    voteTvLevel: (showId, levelRange) =>
      call("PUT", `/api/tv-shows/${showId}/level-votes/me`,
        { level_range: levelRange }),
    // JSON unless a screenshot File is attached; then multipart with the same fields.
    createReport: (userId, challengeId, questionId, { screenshot, ...payload }) => {
      const fields = { user_id: userId, challenge_id: challengeId, question_id: questionId, ...payload };
      if (!screenshot) return call("POST", "/api/reports", fields);
      const fd = new FormData();
      for (const [k, v] of Object.entries(fields)) if (v != null) fd.append(k, v);
      fd.append("screenshot", screenshot, screenshot.name || "screenshot");
      return callForm("POST", "/api/reports", fd);
    },

    // Duels (real-time head-to-head). Sign-in-only; the server also
    // master-switches everything behind the compete_enabled flag.
    // challengeType is the CHALLENGE_TYPE value.
    competeCreateRoom: (mode, challengeType, level, invitedUserId) =>
      call("POST", "/api/duels", {
        mode, challenge_type: challengeType, level,
        ...(invitedUserId ? { invited_user_id: invitedUserId } : {}),
      }),
    competeJoinRoom: (duelRef) =>
      call("POST", `/api/duels/${encodeURIComponent(duelRef)}/players`, undefined, { silence: [404] }),
    competeQuickMatch: (mode, challengeType, level) =>
      call("POST", "/api/matchmaking-entries",
        { mode, challenge_type: challengeType, level }),
    competeQueueStatus: () => call("GET", "/api/matchmaking-entries/current", undefined, { silence: [404] }),
    competeLeaveQueue: () => call("DELETE", "/api/matchmaking-entries/current"),
    competeActiveMatch: () => call("GET", "/api/duels/current", undefined, { silence: [404] }),
    competeMatchState: (duelId) => call("GET", `/api/duels/${duelId}`, undefined, { silence: [403, 404] }),
    competeReady: (duelId) =>
      call("POST", `/api/duels/${duelId}/readiness`),
    competeAnswer: (duelId, questionIndex, choice) =>
      call("POST", `/api/duels/${duelId}/answers`,
        { question_index: questionIndex, choice }),
    competeLeave: (duelId) =>
      call("POST", `/api/duels/${duelId}/leaves`, undefined, { silence: [403, 404] }),

    // Chat (LLM role-play). Device-id auth like the rest; the server
    // master-switches everything behind chat_enabled and re-checks the
    // per-user allowance on every route.
    // 403 is the sign-in panel: the level watcher re-lists scenarios while a
    // signed-out visitor sits on it, and chat-signin-required is the only 403
    // this route can produce.
    chatScenarios: (level) =>
      call("GET", `/api/scenarios?level=${encodeURIComponent(level)}`, undefined, { silence: [403] }),
    chatStart: (scenarioId) => call("POST", "/api/chats", { scenario_id: scenarioId }),
    // 404 is "nothing to recover" and 403 is the sign-in panel: both are
    // screen-mount states for a signed-out visitor, not failures.
    chatActive: () => call("GET", "/api/chats/current", undefined, { silence: [403, 404] }),
    chatState: (chatId) =>
      call("GET", `/api/chats/${encodeURIComponent(chatId)}`, undefined, { silence: [403, 404] }),
    chatMessage: (chatId, clientMessageId, text) =>
      call("POST", `/api/chats/${encodeURIComponent(chatId)}/messages`,
        { client_message_id: clientMessageId, text }),
    chatComplete: (chatId) =>
      call("POST", `/api/chats/${encodeURIComponent(chatId)}/completions`),
    // Spoken turn: the upload is transcribed and thrown away server-side;
    // the transcript comes back for the confirm step. "" is a normal answer.
    chatTranscribe: (chatId, blob) => {
      const fd = new FormData();
      fd.append("audio", blob, `turn.${extForMime(blob.type)}`);
      return callForm("POST", `/api/chats/${encodeURIComponent(chatId)}/transcriptions`, fd);
    },
    // Render-on-tap Partner Message speech: streamed mp3 bytes, not JSON.
    chatReplyAudio: (chatId, messageId) =>
      callBlob("GET", `/api/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/audio`),
  };

  // debugWordLookupId extracts the ?word= override from a query string.
  // Internal verification affordance (issue #286): only honoured together
  // with ?debug=1 so deep-linking the word-detail modal stays non-public.
  // The id may be the DB uuid or the CSV-side source id; GET /words/{id}
  // accepts both.
  function debugWordLookupId(search) {
    try {
      const qs = new URLSearchParams(search);
      if (qs.get("debug") !== "1") return null;
      const id = (qs.get("word") || "").trim();
      return id || null;
    } catch {
      return null;
    }
  }

  // Chat launch promo for the right rail, fronted by the cast of the first
  // course ("Raub in Potsdam"). One variant per page load so the whole set
  // gets seen while the feature is free. TEMPORARY: when chat stops being
  // free, delete this list, root.pickChatAd(), the rail block in index.html,
  // the right.chatAd.* strings, and static/public/images/heist/.
  // `id` names the i18n key suffix and the poster plate; `name` is a proper
  // noun, so it is not translated.
  const CHAT_AD_VARIANTS = [
    { id: "aisha", name: "Aisha „Hexe“ Johnson", img: "aisha" },
    { id: "jan", name: "Jan „Der Bäcker“ Müller", img: "jan" },
    { id: "memo", name: "Mehmet „Memo“ Yılmaz", img: "mehmet" },
    { id: "katrin", name: "Katrin „Crash“ Schneider", img: "katrin" },
    { id: "lukas", name: "Lukas „Pfau“ Woell", img: "lukas" },
    { id: "basti", name: "Detektiv Bastian Fischer", img: "basti" },
    { id: "paywall", name: "Mehmet „Memo“ Yılmaz", img: "memo-alt" },
    { id: "crew", name: "Die Crew", img: "crew" },
  ];

  // dayGoalProgress projects today's points onto the learner's daily goal, so
  // the home hero bar and the round summary describe the day with the same
  // numbers instead of each doing its own arithmetic.
  //
  // `percent` is clamped to 100 because points keep accruing past the goal
  // (the streak advances on the first crossing, but the day's score does not
  // stop there) and a <progress> value above its max renders as an
  // indeterminate bar - the one state that must never appear on a finished
  // goal. A missing or nonsensical goal degrades to "no goal" rather than
  // dividing by zero; callers hide the bar on `goal === 0`.
  function dayGoalProgress(points, goal) {
    const earned = Math.max(0, Number(points) || 0);
    const target = Math.max(0, Number(goal) || 0);
    if (!target) return { points: earned, goal: 0, percent: 0, remaining: 0, met: false };
    const met = earned >= target;
    return {
      points: earned,
      goal: target,
      percent: met ? 100 : Math.round((earned / target) * 100),
      remaining: met ? 0 : target - earned,
      met,
    };
  }

  // isoLocalDay formats a Date as the browser-local YYYY-MM-DD: the fallback
  // when no usable profile zone is known (see zoneToday).
  function isoLocalDay(d) {
    return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  }

  // zoneToday is today's calendar day in the IANA zone, as a Date at that
  // day's UTC midnight: step it with UTC arithmetic and read it with
  // dayKey. The server projects activity_dates onto the profile zone, so
  // "today" must come from the same zone, not the browser's (#1483). An
  // empty zone or one this browser's Intl rejects falls back to the
  // browser's own day.
  function zoneToday(zone, now = new Date()) {
    let ymd;
    try {
      ymd = now.toLocaleDateString("en-CA", { timeZone: zone || undefined });
    } catch {
      ymd = isoLocalDay(now);
    }
    return new Date(ymd + "T00:00:00Z");
  }

  // dayKey is the YYYY-MM-DD of a zoneToday-style UTC-midnight Date.
  function dayKey(d) {
    return d.toISOString().slice(0, 10);
  }

  // weekStrip lays the rolling last seven days (today rightmost, today in
  // `zone`) over the streak activity_dates, which only mark days the daily
  // goal was met. Today also counts as filled once `day` (a dayGoalProgress
  // result) is met, before the next streaks refresh lands, and as in
  // progress while it has points.
  function weekStrip(activityDates, day, lang, now = new Date(), zone = "") {
    const met = new Set(activityDates || []);
    const base = zoneToday(zone, now);
    const days = [];
    for (let back = 6; back >= 0; back--) {
      const d = new Date(base.getTime() - back * 86_400_000);
      const key = dayKey(d);
      const today = back === 0;
      const filled = met.has(key) || (today && !!day && day.met);
      days.push({
        key,
        label: d.toLocaleDateString(lang || undefined, { weekday: "narrow", timeZone: "UTC" }),
        today,
        filled,
        inProgress: today && !filled && !!day && day.points > 0,
      });
    }
    return days;
  }

  // Expose for tests + dev-console inspection. The slugify / anon-name helpers
  // are declared further down but hoisted, so the references here resolve.
  window.dt = {
    api, LEVELS, DAILY_GOALS, LANGS, THEMES, GAME_TYPES, BOTTOM_NAV,
    SPA_ROUTES, SPA_ROUTE_HANDLERS, SPA_PATH_PARAM,
    CHAT_AD_VARIANTS,
    TRIVIA_CATEGORIES,
    ApiError, ensureDeviceId, patchUserRetry, samePref, liveDialectRegionKeys,
    call, apiRouteTemplate,
    resetApiReportCount: () => { apiReportCount = 0; reportedApiFailures.clear(); },
    settleUserId,
    slugifyHandle, generateAnonName, bumpHandleNumber,
    groupNotificationPrefs,
    notificationKindLabel, subscribePush, mapIdentityError,
    debugWordLookupId, playbackRateForLevel, newLevelAudio, audioSpeed,
    detectBrowserLang, AGE_BRACKETS, isMinorAgeBracket,
    dayGoalProgress, weekStrip, isoLocalDay, zoneToday, SERVER_DEFAULT_DAILY_GOAL, ONBOARDING_DAILY_GOAL,
    t, diffTokens,
  };

  // Tie this browser's PostHog person to the stable device id so events +
  // session replays group per device. Only identify an already loaded library;
  // otherwise the bootstrap's loaded callback identifies after window.dt exists.
  if (window.posthog && window.posthog.__loaded && typeof window.posthog.identify === "function") {
    try { window.posthog.identify(ensureDeviceId()); } catch { /* analytics is best-effort */ }
  }

  // ------------------------------------------------------------------
  // localStorage helper - Alpine.$persist doesn't compose with
  // Alpine.store(obj) (values come back as `false` because the magic only
  // hydrates inside x-data scopes). Roll our own; the API is dirt-simple.
  // ------------------------------------------------------------------

  function lsGet(key, fallback) {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return fallback;
      return JSON.parse(raw);
    } catch {
      return fallback;
    }
  }
  function lsSet(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
  }
  function lsRemove(key) {
    try { localStorage.removeItem(key); } catch { /* private mode */ }
  }

  // The round currently in progress, so a reload can ask the server to hand
  // it back instead of silently starting a new 1/10 (issue #666). Only the id
  // and the round's scope are stored: every answer already lives on the cached
  // session, and the server is the only thing allowed to say how far along
  // the round is.
  //
  // sessionStorage, not localStorage: a reload keeps it, but a second tab gets
  // its own. The session is shared server-side, so two tabs resuming the same
  // id would submit into one round and seal it out from under each other.
  const ACTIVE_ROUND_KEY = "dt_active_round";
  // A round is only the same round if everything that shaped its questions
  // matches. start() passes type, level, word group, trivia category and the
  // course-practice word list to the API; resuming skips that call entirely,
  // so all of them have to be in the key or a scoped round leaks into an
  // unscoped one (a Tiere vocabulary round resuming as plain vocabulary).
  function roundScope(type, level, wordgroupId, triviaCategory, practiceWordIDs) {
    return [
      type || "",
      String(level || ""),
      wordgroupId || "",
      triviaCategory || "",
      (practiceWordIDs || []).join("."),
    ].join("|");
  }
  function ssGet(key, fallback) {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw === null) return fallback;
      return JSON.parse(raw);
    } catch { return fallback; }
  }
  // profileLevel is the header level the round started under and headerDriven
  // says the round's level came from it (the notice needs both after a reload);
  // neither is part of the scope.
  function rememberRound(challengeID, scope, profileLevel, headerDriven) {
    if (!challengeID || !scope) return;
    try {
      sessionStorage.setItem(ACTIVE_ROUND_KEY, JSON.stringify({
        challenge_id: challengeID, scope, profile_level: profileLevel, header_driven: headerDriven,
      }));
    }
    catch { /* quota / private mode */ }
  }
  // Pointers from before header_driven: course practice has a level in its
  // scope, reading-quiz is picked by the server.
  function pointerHeaderDriven(saved) {
    if (typeof saved.header_driven === "boolean") return saved.header_driven;
    const [type, level] = saved.scope.split("|");
    return type !== "reading-quiz" && !level;
  }
  function forgetRound() {
    try { sessionStorage.removeItem(ACTIVE_ROUND_KEY); } catch { /* private mode */ }
  }
  // Returns the stored pointer only for an exactly matching scope, so switching
  // type, word group or category starts that round clean.
  function rememberedRound(scope) {
    const saved = ssGet(ACTIVE_ROUND_KEY, null);
    if (!saved || saved.scope !== scope || !saved.challenge_id) return null;
    return saved;
  }

  // Pending preference keys survive a reload between Get started (or a
  // picker touch) and the boot flush. In-memory pending* alone is lost when
  // registerAnon fails or the tab closes before flush, and the next boot's
  // hydrateUser then permanently stamps the server defaults over the pick
  // because dt_home_level_set already hides onboarding.
  const PENDING_PREF_KEYS = {
    timezone: "dt_pending_timezone",
    daily_goal_points: "dt_pending_daily_goal",
    mature_content: "dt_pending_mature_content",
    age_bracket: "dt_pending_age_bracket",
    lang: "dt_pending_lang",
    home_level: "dt_pending_home_level",
    dialect_regions: "dt_pending_dialect_regions",
  };
  function queuePendingPref(field, value) {
    const key = PENDING_PREF_KEYS[field];
    if (key) lsSet(key, value);
  }
  function clearPendingPref(field) {
    const key = PENDING_PREF_KEYS[field];
    if (key) lsRemove(key);
  }
  // Update an existing durable copy in place; a field without one stays
  // unqueued (see the call in patchUserRetry).
  function refreshPendingPref(field, value) {
    const key = PENDING_PREF_KEYS[field];
    if (key && lsGet(key, null) !== null) lsSet(key, value);
  }
  function clearAllPendingPrefs() {
    for (const key of Object.values(PENDING_PREF_KEYS)) lsRemove(key);
  }
  // Also empties the root's in-memory copies, which were read from storage at
  // construction (#1244).
  function dropPendingPrefs() {
    clearAllPendingPrefs();
    const root = window.__dtRoot;
    if (!root) return;
    for (const n of ["Timezone", "DailyGoal", "MatureContent", "AgeBracket", "Lang", "Level", "DialectRegions"]) {
      root[`pending${n}Save`] = null;
    }
  }

  // meaningLines splits a ";"-joined translation/definition into one line
  // per meaning for stacked display.
  window.meaningLines = function (s) {
    return String(s || "").split(";").map((m) => m.trim()).filter(Boolean);
  };

  window.glossEntry = function (s) {
    const meanings = window.meaningLines(s);
    const first = meanings[0] || "";
    const term = first.match(/^([^:]+):\s*(.*)$/);
    if (!term) return { term: "", meanings };
    return { term: term[1], meanings: [term[2], ...meanings.slice(1)].filter(Boolean) };
  };

  // glossCard shapes a question's hint for the gloss card: the headword term
  // plus one Meaning per line with level and registers. translation_meanings
  // is the rich form and names its term; the flat text is the fallback for
  // question types that only send translation.
  window.glossCard = function (q) {
    const rich = (q && q.translationMeanings) || [];
    if (rich.length) return { term: q.translationTerm || "", meanings: rich };
    const flat = window.glossEntry(q && q.translation);
    return { term: flat.term, meanings: flat.meanings.map((text) => ({ text })) };
  };

  // meaningVulgar: a vulgar Meaning shown to a learner who opted into 18+
  // content; styled with the shared .meaning-vulgar class.
  window.meaningVulgar = function (m) {
    return Boolean(m && (m.registers || []).includes("vulgar") && window.Alpine.store("ui").matureContent);
  };

  // glossParts splits one meaning into its headline sense and its
  // parenthetical qualifiers ("clever (of a person)"), so the qualifiers can
  // be rendered quieter than the sense itself.
  window.glossParts = function (s) {
    return String(s || "")
      .split(/(\([^)]*\))/g)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => ({ text: t, paren: t.startsWith("(") }));
  };

  function fillArticlePrompt(prompt, answer) {
    if (!answer) return prompt;
    return String(prompt || "").replace(
      /(<span class="article-cue">)[\s\S]*?(<\/span>)/,
      (_match, open, close, offset) =>
        open + (offset === 0 ? answer.charAt(0).toUpperCase() + answer.slice(1) : answer) + close);
  }
  if (typeof window !== "undefined") window.__dtFillArticlePrompt = fillArticlePrompt; // test hook

  // levelBadgeStyle colours CEFR level indicators from green (A1) to red (C2).
  const LEVEL_COLORS = {
    A1: "#16a34a", A2: "#84cc16", B1: "#eab308",
    B2: "#f97316", C1: "#ef4444", C2: "#b91c1c",
  };
  window.levelBadgeStyle = function (level) {
    const c = LEVEL_COLORS[String(level || "").toUpperCase()];
    return c ? `background-color:${c};border-color:transparent;color:#fff` : "";
  };

  // ------------------------------------------------------------------
  // Level-aware audio playback speed
  // ------------------------------------------------------------------
  // TTS clips are generated once at native pace; lower CEFR levels slow
  // playback via HTMLMediaElement.playbackRate instead of rendering
  // per-level audio assets. Only spoken German content goes through this;
  // the learner's own recording preview stays at 1x.
  const LEVEL_PLAYBACK_RATES = {
    A1: 0.75, A2: 0.8, B1: 0.9, B2: 1, C1: 1, C2: 1,
  };
  function playbackRateForLevel(level) {
    return LEVEL_PLAYBACK_RATES[String(level || "").toUpperCase()] || 1;
  }
  // newLevelAudio builds an Audio element whose speed matches the level.
  // preservesPitch is the standard property (on by default in modern
  // browsers); webkitPreservesPitch covers older Safari. Both are set
  // explicitly so slowed speech never comes out pitched down.
  function newLevelAudio(url, level) {
    const a = new Audio(url);
    a.playbackRate = audioSpeedOverride !== null ? audioSpeedOverride : playbackRateForLevel(level);
    try { a.preservesPitch = true; a.webkitPreservesPitch = true; } catch { /* best-effort */ }
    return a;
  }

  // Console escape hatch for the playback speed, exposed as dt.audioSpeed:
  //   dt.audioSpeed()      -> { override, levelRate, effective } for the current level
  //   dt.audioSpeed(0.6)   -> pin every spoken clip to 0.6x (any level)
  //   dt.audioSpeed(null)  -> clear the pin, back to level-derived rates
  // The override lives in memory only, so a reload always restores normal
  // behaviour. 0.25-4 matches the range browsers pitch-correct reliably.
  let audioSpeedOverride = null;
  function audioSpeed(rate) {
    if (rate !== undefined) {
      if (rate === null) {
        audioSpeedOverride = null;
      } else {
        const n = Number(rate);
        if (!Number.isFinite(n) || n < 0.25 || n > 4) {
          throw new Error("audioSpeed: pass a rate between 0.25 and 4, or null to clear the override");
        }
        audioSpeedOverride = n;
      }
    }
    let level = "";
    try { level = window.Alpine.store("ui").level; } catch { /* store not ready */ }
    const levelRate = playbackRateForLevel(level);
    return {
      override: audioSpeedOverride,
      levelRate,
      effective: audioSpeedOverride !== null ? audioSpeedOverride : levelRate,
    };
  }

  // ------------------------------------------------------------------
  // Alpine stores
  // ------------------------------------------------------------------

  const SAFE_HTML_CLASSES = new Set(["verb-split", "word-link", "cue-link", "article-cue"]);

  document.addEventListener("alpine:init", () => {
    // $sanitize magic helper: neutralises server-provided HTML before it is
    // rendered via x-html. Challenge text (conjugation/adjective) legitimately
    // carries a small set of formatting tags, but the interpolated word data is
    // admin/CSV/LLM-sourced and must not be trusted. DOMPurify strips anything
    // outside the formatting allow-list. See docs/security.md.
    //
    // `class` is allow-listed only so the trusted challenge markup keeps its
    // <span class="verb-split"> highlight and its <span class="word-link">
    // noun. Because DOMPurify does not constrain attribute *values*, an
    // untrusted word field could otherwise smuggle an arbitrary class (e.g.
    // "fixed inset-0 z-40 ...") to overlay/spoof the UI. This hook pins the
    // only class values the app emits, closing that content-injection vector.
    if (typeof DOMPurify !== "undefined") {
      // removeHook first so a re-fired alpine:init (test resets, Alpine
      // re-init) re-registers rather than stacking duplicate hooks.
      DOMPurify.removeHook("uponSanitizeAttribute");
      DOMPurify.addHook("uponSanitizeAttribute", (_node, data) => {
        if (data.attrName === "class" && !SAFE_HTML_CLASSES.has(data.attrValue)) {
          data.keepAttr = false;
        }
      });
    }

    window.Alpine.magic("sanitize", () => {
      return (html) => {
        if (!html || typeof html !== "string") return "";

        if (typeof DOMPurify === "undefined") {
          console.error("DOMPurify is not loaded");
          return "";
        }

        return DOMPurify.sanitize(html, {
          ALLOWED_TAGS: ["i", "u", "b", "strong", "em", "br", "span"],
          // `class` is allowed so the trusted conjugation markup
          // (<span class="verb-split">) keeps its highlight; it is not an XSS
          // execution vector and every other attribute (event handlers, style,
          // href, data-*) is still stripped.
          ALLOWED_ATTR: ["class"],
          KEEP_CONTENT: true,
          RETURN_DOM: false,
          RETURN_DOM_FRAGMENT: false,
        });
      };
    });

    window.Alpine.store("i18n", {
      // English is fetched like every other catalogue; #root stays hidden
      // until `ready` so no raw key reaches the screen.
      catalogs: {},
      lang: "en",
      requestedLang: "en",
      ready: false,
      failed: false, // English never arrived: the shell shows a reload notice
      t(key, params) {
        // Touch reactive fields so Alpine refreshes x-text bindings after
        // use() loads a catalog or switches language.
        const lang = this.lang;
        const ready = this.ready;
        void ready;
        const active = this.catalogs[lang] || {};
        return interpolate(active[key] || (this.catalogs.en || {})[key] || key, params);
      },
      async use(lang) {
        const next = normalizeLang(lang);
        this.requestedLang = next;
        await Promise.all([...new Set(["en", next])].map(async (l) => {
          if (this.catalogs[l]) return;
          const loaded = (await fetchCatalog(l)) || (await fetchCatalog(l)); // one retry
          if (loaded) this.catalogs[l] = loaded;
        }));
        if (this.requestedLang !== next) return;
        // Without English every label would be a raw key: keep #root hidden.
        this.failed = !this.catalogs.en;
        if (this.failed) return;
        this.lang = next;
        this.ready = true;
        // The shell declares lang="en" (the default UI language); once a
        // catalogue is active the document speaks that language (#875).
        document.documentElement.lang = next;
      },
    });

    // Tailwind's lg / xl breakpoints. Desktop-only subtrees render behind
    // <template x-if="$store.ui.lg"> so a phone never builds them (#797).
    // Without matchMedia the flag stays true: the chrome is built and CSS
    // hides it, as before #797. Old Safari only has addListener.
    const viewportFlag = (query, key) => {
      const mq = window.matchMedia ? window.matchMedia(query) : null;
      if (!mq) return true;
      const update = (e) => { window.Alpine.store("ui")[key] = e.matches; };
      if (mq.addEventListener) mq.addEventListener("change", update);
      else mq.addListener(update);
      return mq.matches;
    };
    window.Alpine.store("ui", {
      lg: viewportFlag("(min-width: 64rem)", "lg"),
      xl: viewportFlag("(min-width: 80rem)", "xl"),
      deviceId: ensureDeviceId(),
      userId: lsGet("dt_user_id", null),
      level: lsGet("dt_level", "A1"),
      // Daily points goal (25/50/75/100). Local mirror of
      // users_users.daily_goal_points for instant UI; the DB owns the value.
      dailyGoal: lsGet("dt_daily_goal", SERVER_DEFAULT_DAILY_GOAL),
      // Content-maturity gate. false = PG (default), true = 18+ accepted.
      // Local mirror of users_users.mature_content; the DB owns the value.
      matureContent: lsGet("dt_mature_content", false),
      // The mature_content value of the last user row the server returned.
      // Pages that hide 18+ content refetch when it changes: matureContent
      // flips at the click, before the PATCH lands, so a refetch keyed on it
      // would get the hidden data again.
      matureContentSaved: lsGet("dt_mature_content", false),
      // Coarse age band from the 18+ opt-in; "" means unknown, the default
      // for every new learner (#1386). Local mirror of users_users.age_bracket.
      // Read only to decide whether the 18+ choice may be offered at all.
      ageBracket: lsGet("dt_age_bracket", ""),
      // Regional words Dialect region keys; null until the learner first
      // picks. Local mirror of users_users.dialect_regions.
      dialectRegions: lsGet("dt_dialect_regions", null),
      // No stored language means a first run: seed the picker from the
      // browser instead of assuming English (see detectBrowserLang).
      lang: normalizeLang(lsGet("dt_lang", null) ?? detectBrowserLang()),
      theme: lsGet("dt_theme", "dark"),
      zoom: lsGet("dt_zoom", 100),
      focusChallengeType: lsGet("dt_focus_challenge_type", "vocabulary"),
      // prepDetail controls how much of the grammar-rule result card
      // (preposition, article, plural quizzes) the learner wants to see,
      // and (via the player) how long the auto-advance waits before
      // moving on:
      //   "none"     - rule card body hidden, 5s timer (status quo speed)
      //   "rules"    - rule shown, mistakes hidden, 15s timer (default)
      //   "mistakes" - rule + common mistakes shown, 30s timer
      // Stored under dt_prep_detail; defaults to "rules" for new users.
      prepDetail: (() => {
        const v = lsGet("dt_prep_detail", "rules");
        return (v === "none" || v === "rules" || v === "mistakes") ? v : "rules";
      })(),
      // Focus is the remainder of total minus practice; the defaults give a
      // 30 min total split into 25 focus + 5 practice (the dial's first knobs).
      pomodoroDuration: lsGet("dt_pomodoro_duration", 1500),
      pomodoroBreak: lsGet("dt_pomodoro_break", 300),
      // Right-rail Pomodoro dial visibility. Hidden by default; toggled from
      // the top-bar chip's chevron and persisted so the choice survives reloads.
      pomoWidgetOpen: lsGet("dt_pomo_widget_open", false),
      onboardingDone: lsGet("dt_home_level_set", false),
      // In-memory only: drives the picker <select>s. Not persisted - the DB
      // owns the value (see initialPickerTimezone / persistTimezone).
      timezone: initialPickerTimezone(),
      // audioMode is the global audio directive. Tri-state:
      //   "off"     - all playback suppressed (button, A shortcut, autoplay)
      //   "manual"  - playback only on explicit user action (button or A)
      //   "auto"    - same as "manual" PLUS autoplay on every new question
      // Stored under dt_audio_mode. Legacy boolean dt_audio_enabled is
      // migrated once at boot (true → manual, false → off).
      audioMode: (() => {
        const explicit = lsGet("dt_audio_mode", null);
        if (explicit === "off" || explicit === "manual" || explicit === "auto") return explicit;
        const legacy = lsGet("dt_audio_enabled", null);
        if (legacy === false) return "off";
        return "manual"; // legacy true OR no prior preference
      })(),
      get audioEnabled() { return this.audioMode !== "off"; },
      get audioAuto() { return this.audioMode === "auto"; },
      get effectiveLang() { return effectiveLang(this.lang, this.level); },

      get needsOnboarding() { return !this.onboardingDone; },
      // The 18+ opt-in button's rule: 18+ is hidden, and the bracket is not a
      // minor's (the backend refuses 18+ there).
      get matureOptInShown() { return !this.matureContent && !isMinorAgeBracket(this.ageBracket); },
      // Drop an 18+ answer the server did not take (held behind a lost age
      // save, refused, or out of retries) back to the last saved value, so the
      // opt-in button and Settings do not claim 18+ is on while the content
      // stays hidden. The durable pending copy stays for the next boot. A
      // newer answer (matureContent no longer `v`) is left alone.
      revertMatureContent(v) {
        if (this.matureContent !== v) return;
        this.matureContent = this.matureContentSaved && !isMinorAgeBracket(this.ageBracket);
        lsSet("dt_mature_content", this.matureContent);
      },
      // skip: optional Set of field names to leave alone (fields whose own
      // save is still in flight - the local value is newer than this row).
      // In-flight prefSaves are always skipped too, so a Settings/profile
      // hydrate during retry backoff cannot stomp a newer local value.
      // Returns false when the row was dropped as older than one already applied.
      hydrateUser(user, skip) {
        if (!user || rowIsStale(user)) return false;
        const at = Date.parse(user.updated_at);
        if (!Number.isNaN(at)) { appliedRow.uid = user.user_id; appliedRow.at = at; }
        const skipped = (f) => !!(skip && skip.has(f)) || prefSaves.has(f) || heldPrefs.has(f);
        if (user.home_level && !skipped("home_level")) {
          this.level = user.home_level;
          lsSet("dt_level", this.level);
        }
        if (user.lang && !skipped("lang")) {
          this.lang = normalizeLang(user.lang);
          lsSet("dt_lang", this.lang);
        }
        if (typeof user.mature_content === "boolean") this.matureContentSaved = user.mature_content;
        if (typeof user.mature_content === "boolean" && !skipped("mature_content")) {
          this.matureContent = user.mature_content;
          lsSet("dt_mature_content", user.mature_content);
        }
        if (typeof user.age_bracket === "string" && !skipped("age_bracket")) {
          this.ageBracket = user.age_bracket;
          lsSet("dt_age_bracket", user.age_bracket);
        }
        if ((user.dialect_regions === null || Array.isArray(user.dialect_regions)) && !skipped("dialect_regions")) {
          this.dialectRegions = user.dialect_regions;
          lsSet("dt_dialect_regions", user.dialect_regions);
        }
        // The daily goal is the number the home progress bar measures against
        // and the one the server gates the streak on, so a local mirror that
        // never reconciles makes the bar lie on any browser whose
        // localStorage does not already hold the learner's tier - a new
        // device, a cleared profile, a goal changed elsewhere. Both boot
        // paths (registerAnon for anonymous, auth.refresh for signed-in) hand
        // a user row through here, so hydrating it here covers both.
        // Guarded on the known tier set: a value from outside it (a row
        // written by a newer client) would render a goal this build cannot
        // offer in Settings, leaving the learner unable to change it back.
        if (typeof user.daily_goal_points === "number"
            && DAILY_GOALS.some((g) => g.points === user.daily_goal_points)
            && !skipped("daily_goal_points")) {
          this.dailyGoal = user.daily_goal_points;
          lsSet("dt_daily_goal", user.daily_goal_points);
        }
        syncI18nLang(this.effectiveLang);
        return true;
      },
      setLang(lang) {
        this.lang = normalizeLang(lang);
        lsSet("dt_lang", this.lang);
        syncI18nLang(this.effectiveLang);
      },
      completeOnboarding() {
        this.onboardingDone = true;
        lsSet("dt_home_level_set", true);
      },
      setFocusType(t) {
        this.focusChallengeType = t;
        lsSet("dt_focus_challenge_type", t);
      },
      setPrepDetail(v) {
        this.prepDetail = v;
        lsSet("dt_prep_detail", v);
      },
      cycleAudio() {
        // off → manual → auto → off. Single-button cycle is simpler
        // than a dropdown and matches the existing toggle pattern.
        const order = ["off", "manual", "auto"];
        const i = order.indexOf(this.audioMode);
        this.setAudioMode(order[(i + 1) % order.length]);
      },
      // Direct set, for the mobile overflow menu's segmented control (the
      // desktop topbar has room for one button only, so it cycles).
      setAudioMode(mode) {
        if (!["off", "manual", "auto"].includes(mode)) return;
        this.audioMode = mode;
        lsSet("dt_audio_mode", this.audioMode);
      },
    });

    // Notifications store. unseenCount + items are populated by sendHeartbeat
    // (the heartbeat response piggybacks the in-app feed). pushOptInDismissed
    // is the permanent "never ask again" flag; pushOptInSnoozeUntil is an
    // epoch-ms cutoff for the softer "remind me in 1 week" path. sessionsStarted
    // counts "play challenge" clicks to gate the opt-in prompt (after 3).
    window.Alpine.store("notifications", {
      unseenCount: 0,
      items: [],
      // False until the first heartbeat answers, so the bell does not claim
      // "all caught up" before it has read anything.
      loaded: false,
      pushOptInDismissed: lsGet("dt_push_optin_dismissed", false),
      pushOptInSnoozeUntil: lsGet("dt_push_optin_snooze_until", 0),
      pushSubscribed: lsGet("dt_push_subscribed", false),
      // True while the challenge screen shows a completed round's results:
      // one of the two natural breaks the push opt-in may open at (#986).
      roundDone: false,
      sessionsStarted: lsGet("dt_sessions_started", 0),
      preferences: [],

      bumpSessions() {
        this.sessionsStarted += 1;
        lsSet("dt_sessions_started", this.sessionsStarted);
      },
      // Permanent "never ask again" - only Settings can re-enable.
      markPushOptInDismissed() {
        this.pushOptInDismissed = true;
        lsSet("dt_push_optin_dismissed", true);
      },
      // Soft snooze (default 7 days). Backdrop-click / escape use this too so
      // an accidental dismiss doesn't lock the user out forever.
      snoozePushOptIn(days = 7) {
        const ms = days * 24 * 60 * 60 * 1000;
        this.pushOptInSnoozeUntil = Date.now() + ms;
        lsSet("dt_push_optin_snooze_until", this.pushOptInSnoozeUntil);
      },
      shouldPromptForPush() {
        // Ask after the user has played 3 sessions and hasn't already
        // dismissed-forever, isn't currently snoozed, and isn't
        // already subscribed.
        return (
          this.sessionsStarted >= 3 &&
          !this.pushOptInDismissed &&
          this.pushOptInSnoozeUntil < Date.now() &&
          !this.pushSubscribed &&
          "serviceWorker" in navigator &&
          "PushManager" in window
        );
      },
      async markSeen(id) {
        try {
          await api.patchNotification(id, { seen: true });
          this.items = this.items.filter((n) => n.id !== id);
          this.unseenCount = Math.max(0, this.unseenCount - 1);
        } catch (e) {
          console.warn("markSeen failed:", e);
        }
      },
      async dismiss(id) {
        try {
          await api.patchNotification(id, { dismissed: true });
          this.items = this.items.filter((n) => n.id !== id);
          this.unseenCount = Math.max(0, this.unseenCount - 1);
        } catch (e) {
          console.warn("dismiss failed:", e);
        }
      },
      // Bulk "mark all as seen" for the bell drawer. Clears the local feed
      // optimistically once the server has flipped every open in_app row.
      async markAllSeen() {
        try {
          await api.markAllNotificationsSeen();
          this.items = [];
          this.unseenCount = 0;
        } catch (e) {
          console.warn("markAllSeen failed:", e);
        }
      },
      prefLoading: false,
      prefError: null,
      // null while the Discord column is hidden (anonymous, no Discord config,
      // kill flag off), else { linked, username, celebrate, dm_failed }.
      discordLink: null,
      discordLoading: null,
      // Concurrent callers (bell modal and Settings mount) share one request.
      loadDiscordLink(signedIn) {
        if (!signedIn) {
          this.discordLink = null;
          return Promise.resolve();
        }
        this.discordLoading ||= api.getDiscordLink()
          .then((link) => { this.discordLink = link; }, () => { this.discordLink = null; })
          .finally(() => { this.discordLoading = null; });
        return this.discordLoading;
      },
      async loadPreferences() {
        const userId = await settledUserId();
        if (!userId) return;
        this.loadDiscordLink(window.Alpine.store("auth").signedIn);
        this.prefLoading = true;
        this.prefError = null;
        try {
          const resp = await api.getNotificationPrefs();
          this.preferences = (resp && resp.preferences) || [];
        } catch (e) {
          this.prefError = e.message || "Failed to load preferences";
          console.warn("loadPreferences failed:", e);
        } finally {
          this.prefLoading = false;
        }
      },
      get notificationRows() {
        return groupNotificationPrefs(this.preferences);
      },
      async toggleKindChannel(kind, channel, signedIn) {
        if (notificationKinds().some((k) => k.id === kind && k.social) && !signedIn) return false;
        const p = (this.preferences || [])
          .find((x) => x.kind === kind && x.channel === channel);
        if (!p) return false;
        this.prefError = null;
        const next = !p.enabled;
        try {
          await api.putNotificationPref(p.kind, p.channel, next);
          p.enabled = next;
          return true;
        } catch (e) {
          this.prefError = (e.problem && e.problem.detail) || e.message || "Failed to save preference";
          console.warn("toggle pref failed:", e);
          return false;
        }
      },
    });

    // Word-detail modal (category-aware; verbs show the conjugation table).
    window.Alpine.store("wordDetail", {
      data: null, // GET /words/{id} payload, or {error} / {loading}
      pronouns: ["ich", "du", "er/sie/es", "wir", "ihr", "sie/Sie"],
      async open(id) {
        if (!id) return;
        this.data = { loading: true };
        try {
          this.data = await api.getWord(id);
        } catch (e) {
          this.data = { error: (e && e.title) || "Could not load details" };
        }
      },
      close() { this.data = null; },
    });

    window.Alpine.store("session", {
      currentChallenge: null,
      pomodoroElapsed: 0,
      pomodoroActive: false,
      isBreak: false,
      statsToday: { period: "today", challenges_count: 0, score: 0, overall_score: 0 },
      statsWeek: { period: "week", challenges_count: 0, score: 0, overall_score: 0 },
      statsMonth: { period: "month", challenges_count: 0, score: 0, overall_score: 0 },
      statsYear: { period: "year", challenges_count: 0, score: 0, overall_score: 0 },
      statsOverall: { period: "overall", challenges_count: 0, score: 0, overall_score: 0 },
      streaks: { current_streak: 0, max_streak: 0, at_risk: false },
      statsHydrated: false,
      socialHydrated: false,
      leaderboardHydrated: false,
      fadingWordsHydrated: false,
      fadingWords: [],
      friends: [],
      following: [],
      followers: [],
      followersTotal: 0,
      leaderboard: [],

      // BreakModal trigger - set when the focus/break window rolls over.
      showBreakModal: false,

      togglePomodoro() { this.pomodoroActive = !this.pomodoroActive; },
      openBreakModal() { this.showBreakModal = true; },
      closeBreakModal() { this.showBreakModal = false; },
      tickPomodoro() {
        // Mirror the prototype: the ticker runs whether the user toggled
        // focus on or not, but only counts up while active. This keeps the
        // 1Hz interval cheap (no setInterval churn on pause).
        if (!this.pomodoroActive) return;
        const ui = window.Alpine.store("ui");
        const total = this.isBreak ? ui.pomodoroBreak : ui.pomodoroDuration;
        const next = this.pomodoroElapsed + 1;
        if (next >= total) {
          this.pomodoroElapsed = 0;
          this.isBreak = !this.isBreak;
          this.showBreakModal = true;
        } else {
          this.pomodoroElapsed = next;
        }
      },
    });

    // Signed-in identity. identity is the /api/sessions/current payload (null while
    // anonymous or while the first call is in flight).
    window.Alpine.store("auth", {
      identity: null,
      // me is the current User row (anon OR signed-in), populated once
      // bootstrap completes. The Settings Account card reads display_name /
      // username / username_set_at from here so anon users can edit their
      // generated name without a special not-yet-signed-in path. identity
      // stays null for anon (it's the /api/sessions/current payload specifically).
      me: null,
      signInModalOpen: false,
      signInReason: "",
      accountDeleteModalOpen: false,
      lockToast: null, // null | { reason: 'notice', game, message }
      crossTabNotice: null,
      reload() { window.location.reload(); },
      // Session-scoped dismissal of the username picker so the modal
      // doesn't re-pop on the same browser tab after the user clicks
      // "Maybe later". sessionStorage (not localStorage) so a fresh
      // tab / fresh browser brings the prompt back.
      usernamePickerDismissed: (() => {
        try { return sessionStorage.getItem("dt_username_skipped") === "1"; }
        catch { return false; }
      })(),

      get signedIn() { return this.identity !== null; },
      get displayName() {
        // Fall back to the anon me row so the topbar pill shows the generated
        // KlugElephant43-style name instead of "Account" for anon visitors.
        const src = this.identity || this.me;
        if (!src) return "";
        return src.display_name || src.username || "Account";
      },
      // True when the user is signed in but hasn't claimed a handle yet
      // and hasn't dismissed the picker this session. Drives the
      // dtUsernamePicker modal.
      get needsUsername() {
        return (
          this.signedIn &&
          !this.identity.username &&
          !this.usernamePickerDismissed
        );
      },
      dismissUsernamePicker() {
        this.usernamePickerDismissed = true;
        try { sessionStorage.setItem("dt_username_skipped", "1"); }
        catch { /* private mode / quota: dismiss is in-memory only */ }
      },
      // reason names the locked feature the visitor came from ("chat"), so
      // the modal pitches that instead of the generic sync line. Empty for
      // every plain "Sign in" entry point.
      openSignIn(reason = "") { this.signInReason = reason; this.signInModalOpen = true; },
      closeSignIn() { this.signInModalOpen = false; this.signInReason = ""; },
      openAccountDelete() { this.accountDeleteModalOpen = true; },
      closeAccountDelete() { this.accountDeleteModalOpen = false; },
      // reason 'notice' shows `message` (a server problem detail) with the
      // sign-in CTA, e.g. when guest sign-ups are paused.
      showLockToast(reason, game, message = "") {
        this.lockToast = { reason, game, message };
      },
      hideLockToast() { this.lockToast = null; },
      async refresh({ quiet = false } = {}) {
        let identity;
        try {
          // Always probe the HttpOnly session cookie. The JS-readable
          // dt_authed hint is only an optimization and can be missing for
          // sessions minted before it existed or after selective cookie
          // eviction; skipping /api/sessions/current would split UI state from the real
          // session user.
          identity = await api.getMe();
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            this.identity = null;
            return "signed-out";
          }
          console.warn("auth refresh failed:", e);
          if (!quiet) {
            this.showLockToast("notice", null, e?.problem?.detail || e?.problem?.title ||
              t("auth.unavailable"));
          }
          return "unavailable";
        }
        // identity doubles as the canonical User row; mirror into `me` so
        // anon-vs-identified code paths can read from one place. /api/sessions/current
        // carries no username_set_at, so keep the other fields of a same-user
        // row already in `me` (from registerAnon, a PATCH or the heartbeat)
        // instead of dropping them until the next heartbeat.
        const prevMe = identity && this.me && this.me.user_id === identity.user_id ? this.me : null;
        // A response older than a save already applied (a slow /api/sessions/current landing
        // after a PATCH) must not roll the preference fields in `me` back.
        const stale = !!identity && rowIsStale(identity);
        this.identity = identity;
        if (this.identity) this.me = stale && prevMe ? prevMe : prevMe ? { ...prevMe, ...this.identity } : this.identity;
        // Re-anchor $store.ui.userId to the session-resolved id.
        // After a cross-browser sign-in, the server merges this
        // browser's anon row (u-B) into the survivor (u-A); the SPA's
        // localStorage still holds the stale u-B until we overwrite
        // it here. Without this, every subsequent heartbeat, profile, patch and delete call
        // names the stale id and 403s.
        if (this.identity && this.identity.user_id) {
          const ui = window.Alpine.store("ui");
          ui.hydrateUser(this.identity);
          // A signed-in account is onboarded by definition.
          ui.completeOnboarding();
          if (this.identity.user_id !== ui.userId) {
            // The account's preferences (hydrated above) win over anything the
            // anonymous session had queued; replaying it would overwrite them.
            if (ui.userId) dropPendingPrefs();
            ui.userId = this.identity.user_id;
            lsSet("dt_user_id", this.identity.user_id);
          }
        }
        return "signed-in";
      },
      async signOut() {
        let signedOut = false;
        try { await api.signOut(); signedOut = true; }
        catch (e) { console.warn("sign-out failed:", e); }
        // Nulling identity isn't enough: the session stores would keep the
        // prior account's stats / friends / etc. until a manual reload. Drop
        // the user_id anchor and reload to / so every store rebuilds against
        // the fresh anon row /anonymous-users hands back on bootstrap.
        this.identity = null;
        this.closeSignIn();
        try { localStorage.removeItem("dt_user_id"); }
        catch { /* private mode: best effort */ }
        // Undrained preference queues must not outlive the session that made
        // them - the next visitor on this browser gets a fresh anon row.
        clearAllPendingPrefs();
        if (signedOut) postAuthIdentity({ userId: null, signedIn: false });
        window.location.assign("/");
      },
    });
  });

  // ------------------------------------------------------------------
  // Helper to reach the root component. We stash the dtApp instance on
  // window on first init so any nested component (challengePlayer) can
  // reach it without DOM-walking past its own x-data scope.
  // ------------------------------------------------------------------

  function getRoot() {
    return window.__dtRoot || null;
  }

  // ------------------------------------------------------------------
  // Root component
  // ------------------------------------------------------------------

  window.dtApp = function () {
    return {
      screen: "home",
      pendingChallengeType: null,
      // True while inside the sticky "Random" mode (set by a Random-tile click
      // or a /challenges/random landing; cleared when a real type is picked).
      // While true, restart() rerolls a fresh type instead of replaying the
      // last one - that's what makes Random a mode rather than a one-shot pick.
      randomMode: false,
      // Set true by challengePlayer.pick() once >=1 answer is submitted;
      // cleared on start() / finish(). While true, go() and startChallenge()
      // defer cross-screen / cross-type navigation to the leave-confirm modal.
      challengeDirty: false,
      // Set when a round reaches its summary, cleared by the next start().
      challengeFinished: false,
      // Level the loaded round runs at, uppercase; "" when none is loaded. The
      // header level can move mid-round, the band must keep naming this one.
      // challengeStartLevel is the header level the round started under;
      // challengeHeaderDriven says the round's level came from it (not course
      // practice, reading-quiz or a drill of a word with its own level).
      challengeRoundLevel: "",
      challengeStartLevel: "",
      challengeHeaderDriven: false,
      leaveModalOpen: false,
      leaveModalPendingAction: null,
      // Routing state - which course / lesson is currently active.
      activeCourseId: null,
      activeLessonId: null,
      activeCourseDemo: false,
      courseBandTitle: "",
      coursePractice: null,
      lessonResumeStep: null,
      activeShowId: null,
      tvContentType: "",
      tvLevelCohort: "",
      tvLevelCohorts: TV_LEVEL_COHORTS,
      // The chat session the /chat/<id> URL points at. Null means the picker.
      activeChatSessionId: null,
      // A chat started outside the chat screen (Lesen picker), for the chat
      // screen to play like its own start.
      pendingChatState: null,
      activeWordgroupSlug: null,
      // The Piece the /lesen/<id> URL points at, and its Origin once loaded
      // (the sidebar highlights Superhirn or News from it).
      activePieceId: null,
      readingPieceOrigin: null,
      // German-word search from the address. Theme chips stay off the URL.
      wordgroupQuery: "",
      activeChallengeWordgroupId: null,
      // The list slug in the practice URL (?list=); the id above is its
      // resolved form and is only set once the slug has been validated.
      activeChallengeWordgroupSlug: null,
      challengeListState: "loading",
      challengeListRequest: 0,
      wordgroupBand: null,
      // Trivia routing state: the active category id (null = all categories)
      // for /trivia?category=.. deep links. Trivia gates on the profile
      // level like every other challenge type; unsupported levels render
      // the shared unavailable screen.
      activeTriviaCategory: null,
      triviaCategoriesExpanded: false,
      wordgroupStats: null,
      wordgroupSelectionPending: {},
      selectedWordgroups: [],
      // False until the first profile read lands, so the sidebar does not
      // claim an empty selection it has not seen yet. Until then the
      // heartbeat tick retries the read every 30 s (retryFirstReads).
      selectedWordgroupsLoaded: false,
      selectedWordgroupsRequest: 0,
      socialRequest: 0,
      fadingWordsRequest: 0,
      // Per surface: when retryFirstReads may next retry a failed first read.
      firstReadRetryAt: {},
      challengeTypesExpanded: false,
      sidebarWordgroupsExpanded: false,
      // @username currently shown on the public profile screen. Set by
      // openProfile() + applyRouteFromURL(); drives the /u/{username}
      // URL and the dtUserProfile component's fetch.
      activeProfileUsername: null,
      friendSearchUsername: "",
      // Flipped true once the first /api/sessions/current probe resolves. Until then the
      // account-only screens render neither their data nor their sign-in
      // card, and the admin-only bounce is a no-op, so a signed-in
      // deep-linker never sees the signed-out state of a screen.
      authReady: false,
      // Set once the auth probe settled, also when it was unavailable.
      authSettled: false,
      // The round's Dialect region snapshot, published by the Regional words player.
      roundRegions: null,
      // The first-run wizard waits for the auth probe: a signed-in Learner with
      // wiped storage must not answer it before /api/sessions/current marks onboarding done.
      get showWizard() { return (this.authReady && this.$store.ui.needsOnboarding) || !!this.wizOptInStart; },
      // pushBreak gates the push opt-in to a natural break (#986): Home, or a
      // completed round's results. Never mid-question, in chat, during
      // onboarding, or over another blocking modal.
      pushBreak() {
        const s = this.$store;
        const atBreak = this.screen === "home" || (this.screen === "challenge" && s.notifications.roundDone);
        return atBreak && !this.showWizard && !this.leaveModalOpen && !s.wordDetail.data &&
          !s.session.showBreakModal && !s.auth.needsUsername && !s.auth.accountDeleteModalOpen && !s.auth.signInModalOpen;
      },
      continueLesson: null, // populated by refreshContinue(); home card hides when null
      levels: LEVELS,
      dailyGoals: DAILY_GOALS,
      langs: LANGS,
      themes: THEMES,
      get allChallengeTypes() {
        return GAME_TYPES.map((g) => {
          const unavailable = challengeAvailability(g, this.$store.ui.level);
          return {
            ...g,
            label: t(`challengeType.${g.id}.label`),
            desc: t(`challengeType.${g.id}.desc`),
            icon_path: CHALLENGE_ICON_PATHS[g.id] || "",
            unavailable,
          };
        });
      },
      get challengeTypes() {
        // Standalone types (trivia) have their own sidebar section and stay
        // out of the shared challenge list + count badge.
        return this.allChallengeTypes.filter((g) => !g.standalone);
      },
      get sidebarChallengeTypes() {
        if (this.challengeTypesExpanded) return this.challengeTypes;
        const visible = this.challengeTypes.slice(0, 5);
        const active = this.challengeTypes.find((g) => g.id === this.pendingChallengeType);
        if (active && !visible.some((g) => g.id === active.id)) visible.push(active);
        return visible;
      },
      get unavailableChallenge() {
        return this.allChallengeTypes.find((g) => g.id === this.pendingChallengeType) || null;
      },
      get unavailableReasonKey() {
        return this.unavailableChallenge?.unavailable?.reasonKey || "challenge.unavailable.noContent";
      },
      get triviaCategories() {
        return TRIVIA_CATEGORIES;
      },
      get triviaUnavailable() {
        const trivia = this.allChallengeTypes.find((g) => g.id === "trivia");
        return trivia && trivia.unavailable;
      },
      // Sidebar TRIVIA section list: collapsed to 5 categories + expand,
      // mirroring sidebarChallengeTypes (the active category is always kept
      // visible so the highlight can't vanish into the collapsed tail).
      get sidebarTriviaCategories() {
        if (this.triviaCategoriesExpanded) return TRIVIA_CATEGORIES;
        const visible = TRIVIA_CATEGORIES.slice(0, 5);
        const active = TRIVIA_CATEGORIES.find((c) => c.id === this.activeTriviaCategory);
        if (active && !visible.some((c) => c.id === active.id)) visible.push(active);
        return visible;
      },
      // Trivia tile active state; category null means the "all categories"
      // tile is the active one.
      isTriviaTileActive(categoryId) {
        if (this.screen !== "challenge" || this.pendingChallengeType !== "trivia") return false;
        return (this.activeTriviaCategory || null) === (categoryId || null);
      },
      // Timezone choices for the first-run onboarding picker. Seeded from the
      // same in-memory value as $store.ui.timezone so the current zone is
      // always present in the list (issue #161).
      timezoneOptions: timezoneOptions(initialPickerTimezone()),
      // Restored from localStorage so a Get-started / picker choice made
      // before userId resolved still flushes after a reload (see
      // PENDING_PREF_KEYS). null / missing means nothing to drain.
      pendingTimezoneSave: lsGet(PENDING_PREF_KEYS.timezone, null),
      pendingDailyGoalSave: lsGet(PENDING_PREF_KEYS.daily_goal_points, null),
      pendingMatureContentSave: lsGet(PENDING_PREF_KEYS.mature_content, null),
      pendingAgeBracketSave: lsGet(PENDING_PREF_KEYS.age_bracket, null),
      pendingDialectRegionsSave: lsGet(PENDING_PREF_KEYS.dialect_regions, null),
      // Live Dialect regions (GET /regional-word-regions), loaded on first use.
      dialectRegionOptions: null,
      // The region picker: first start of Regional words, its "Change" link
      // and Settings. startsRound labels the button Start instead of Save.
      regionPicker: { open: false, selected: [], startsRound: false },

      // ── Onboarding wizard (Rotlicht v2) ──
      // The first run asks two steps, gloss language and CEFR level, and
      // leaves the age unknown and 18+ off (#1386). Timezone and daily goal
      // are not asked for either (both are editable in Settings); the wizard
      // writes the browser-detected zone and the product default goal on
      // finish.
      //
      // The 18+ opt-in (openMatureOptIn) reopens the same modal on its second
      // half: step 3 age bracket, then step 4 18+ content. It starts on step 4
      // when the age is already known, and a minor's bracket ends it on step 3
      // with 18+ forced off.
      //
      // Answers stay local until the last step, so an abandoned wizard leaves
      // no preferences behind. Language is the exception: it applies live, so
      // step 2 renders in the language just picked.
      wizStep: 1,
      // null on the first run; 3 or 4 (the step it opened on) while the 18+
      // opt-in is open.
      wizOptInStart: null,
      // Deliberately null rather than a CEFR default: the level has to be
      // chosen, so the wizard cannot be clicked through into a level nobody
      // picked.
      wizLevel: null,
      wizAge: null,
      wizMature: null,
      get wizIsAdult() {
        return this.wizAge !== null && !isMinorAgeBracket(this.wizAge);
      },
      // The steps of the open flow. While the age is still unanswered the
      // opt-in counts two, so the count doesn't shrink under the reader.
      get wizSteps() {
        if (this.wizOptInStart === 4) return [4];
        if (this.wizOptInStart === 3) return this.wizAge !== null && !this.wizIsAdult ? [3] : [3, 4];
        return [1, 2];
      },
      get wizStepNumber() { return this.wizSteps.indexOf(this.wizStep) + 1; },
      get wizTotalSteps() { return this.wizSteps.length; },
      get wizIsLastStep() { return this.wizStepNumber === this.wizTotalSteps; },
      get wizCanAdvance() {
        if (this.wizStep === 2) return this.wizLevel !== null;
        if (this.wizStep === 3) return this.wizAge !== null;
        if (this.wizStep === 4) return this.wizMature !== null;
        return true; // step 1 always has a language: stored or browser-detected
      },
      wizAgeBrackets: AGE_BRACKETS,
      // A deep link on a device that has not answered the wizard yet: the
      // wizard says why it is in the way (#734). Keyed off the routed screen.
      get wizDeepLink() {
        return !this.wizOptInStart && this.screen !== "home" && this.screen !== "not_found";
      },
      // Opened by the 18+ opt-in button. Never for a minor's bracket: the
      // backend refuses 18+ there, and the button is hidden.
      openMatureOptIn() {
        const age = this.$store.ui.ageBracket;
        if (isMinorAgeBracket(age)) return;
        this.wizAge = age || null;
        this.wizMature = null;
        this.wizOptInStart = age ? 4 : 3;
        this.wizStep = this.wizOptInStart;
      },
      wizClose() { this.wizOptInStart = null; },
      wizBack() {
        const i = this.wizSteps.indexOf(this.wizStep);
        if (i > 0) this.wizStep = this.wizSteps[i - 1];
        else this.wizClose(); // a no-op on the first run
      },
      wizPickAge(id) {
        this.wizAge = id;
        // Re-answering can flip adult -> minor, which retires step 4's answer.
        if (isMinorAgeBracket(id)) this.wizMature = null;
      },
      wizNext() {
        if (!this.wizCanAdvance) return;
        if (this.wizIsLastStep) { this.wizFinish(); return; }
        this.wizStep = this.wizSteps[this.wizStepNumber];
      },
      // setLang runs even when the learner never touched step 1: the seeded
      // value is a guess from the browser, and leaving it unsaved would let
      // the next hydrateUser stamp the server default over it.
      wizFinish() {
        if (this.wizOptInStart) { this.wizFinishOptIn(); return; }
        // Queue every answer durably BEFORE any of the saves start. The
        // setters only queue when userId is still unresolved, and by the time
        // anyone has clicked through the steps it long since resolved - so
        // without this the whole wizard runs on the unqueued path. The saves
        // are fire-and-forget, and completeOnboarding() below dismisses the
        // modal for good, so a PATCH that exhausts its retries would otherwise
        // lose the answer silently and let the next hydrateUser stamp the
        // server default over it. patchUserRetry keeps these copies in step
        // and drops each one as its write lands; whatever is left is replayed
        // by the flushPending* drains on the next boot.
        queuePendingPref("lang", this.$store.ui.lang);
        queuePendingPref("home_level", this.wizLevel);
        queuePendingPref("timezone", this.$store.ui.timezone);
        queuePendingPref("daily_goal_points", ONBOARDING_DAILY_GOAL);
        this.setLang(this.$store.ui.lang);
        this.setLevel(this.wizLevel);
        this.setTimezone(this.$store.ui.timezone);
        this.setDailyGoal(ONBOARDING_DAILY_GOAL);
        this.$store.ui.completeOnboarding();
      },
      // Same durable queue as the first run. setAgeBracket must precede
      // setMatureContent - see setMatureContent. The age is only written when
      // the opt-in asked for it.
      wizFinishOptIn() {
        const mature = this.wizIsAdult && !!this.wizMature;
        const askedAge = this.wizOptInStart === 3;
        if (askedAge) queuePendingPref("age_bracket", this.wizAge);
        queuePendingPref("mature_content", mature);
        if (askedAge) this.setAgeBracket(this.wizAge);
        this.setMatureContent(mature);
        this.wizClose();
      },
      pendingLangSave: lsGet(PENDING_PREF_KEYS.lang, null),
      pendingLevelSave: lsGet(PENDING_PREF_KEYS.home_level, null),
      reviewStarting: false,
      get bottomNav() {
        return BOTTOM_NAV.map((m) => ({ ...m, label: t(m.labelKey) }));
      },
      lastInputAt: Date.now(),
      // statsDayKey of the last successful stats read, the earliest retry of
      // a failed midnight refetch, and its one follow-up read
      // (refreshStatsOnNewDay).
      statsDay: "",
      statsRetryAt: 0,
      statsFollowUpAt: 0,
      statsReads: 0, // successful refreshAllStats reads; tells a retried follow-up it landed

      // Public feature flags, fetched once at boot. The catalogue + endpoint
      // allowlist live on the server (lib/admin/flags_catalogue.go +
      // /public-flags).
      flags: {},
      flagsLoaded: false,
      chatAdClickPending: false,

      async init() {
        window.__dtRoot = this;
        this.chatAd = this.pickChatAd();
        this.chatAdAlt = this.pickChatAd(this.chatAd);
        // Start the flags request now, in parallel with the catalogue: the
        // chat-ad banner (the LCP element) waits for it. Settled at once so a
        // failure is not an unhandled rejection while i18n loads.
        const flagsRequest = api.getPublicFlags().then((flags) => ({ flags }), (error) => ({ error }));
        await syncI18nLang(this.$store.ui.effectiveLang);
        // Restore screen + activeShowId from the URL so /tv and
        // /tv/<show-id> are addressable + reloadable. Other screens
        // share '/' - they're state-only by design and don't push
        // history entries.
        this.applyRouteFromURL();
        // ?word=<uuid-or-csv-id>&debug=1 opens the word-detail modal on
        // load - an internal verification affordance (issue #286).
        const debugWord = debugWordLookupId(window.location.search);
        if (debugWord) this.$store.wordDetail.open(debugWord);
        window.addEventListener("popstate", (event) => {
          const target = event && event.state && event.state.screen;
          if (!IN_MEMORY_SCREENS.includes(this.screen) && !IN_MEMORY_SCREENS.includes(target) && reloadIfAssetsChanged()) return;
          this.applyRouteFromURL();
          // Back/forward onto an admin-only screen bounces home, same as a
          // direct nav.
          this.guardCurrentScreen();
        });
        // Public flags - best-effort; on failure the rails fall back
        // to their default-disabled state.
        try {
          const { flags, error } = await flagsRequest;
          if (error) throw error;
          this.flags = flags || {};
          await syncI18nLang(this.$store.ui.effectiveLang);
        }
        catch (e) { console.warn("public-flags fetch failed:", e); }
        finally { this.flagsLoaded = true; }
        // Local-only Pomodoro tick (1 Hz) and input activity.
        setInterval(() => this.$store.session.tickPomodoro(), 1000);
        ["pointerdown", "keydown", "scroll"].forEach((ev) =>
          window.addEventListener(ev, () => (this.lastInputAt = Date.now()), { passive: true })
        );
        // Resolve the signed-in identity FIRST: cookies can outlive
        // localStorage, so a valid dt_session may exist even when ui.userId is
        // unset. Minting an anon row before this probe would leave an orphan.
        // refresh() also re-anchors ui.userId, so a signed-in browser skips
        // the anon branch below entirely.
        const authStatus = await this.$store.auth.refresh();
        if (authStatus === "unavailable") {
          const retryAuth = async () => {
            if (await this.$store.auth.refresh({ quiet: true }) !== "unavailable") {
              window.location.reload();
            } else {
              setTimeout(retryAuth, 30_000);
            }
          };
          setTimeout(retryAuth, 30_000);
          this.authSettled = true;
          return;
        }
        // The auth probe has finished; screen and admin guards can run.
        this.authReady = true;
        this.authSettled = true;
        if (this.chatAdClickPending) {
          this.chatAdClickPending = false;
          this.openChatAd();
        }
        this.guardCurrentScreen();
        // Before the pending-pref drains and the first reads: a queued opt-in
        // that lands during boot must still refresh what it unlocks.
        this.$watch("$store.ui.matureContentSaved", () => this.refreshAfterMatureSave());
        // Anonymous boot: reconcile ui.userId against the server's
        // device-resolved identity. registerAnon is idempotent on the
        // X-Device-Id header, so it returns THIS device's canonical anon row.
        // We adopt that id unconditionally, because a stale-but-truthy
        // dt_user_id otherwise sticks around as the heartbeat path param
        // and 403s (expired dt_session, or a pre-merge anon id) while
        // completions still write against the device-resolved user - so stats
        // silently never render. Signed-in boots skip this: auth.refresh()
        // already anchored ui.userId, and registerAnon would adopt an orphan.
        // A closed sign-up (ceiling spent or gate down) leaves this visitor
        // without a row; the home fetches would only fail, so they are skipped.
        let signupClosed = false;
        let identitySettled = authStatus === "signed-in";
        if (authStatus === "signed-out") {
          try {
            const u = await api.registerAnon();
            if (u && u.user_id) {
              if (u.user_id !== this.$store.ui.userId) {
                this.$store.ui.userId = u.user_id;
                lsSet("dt_user_id", u.user_id);
              }
              // Mirror the anon row (with its server-generated display name)
              // into auth.me so the topbar pill + Settings editor render
              // before the user signs in.
              this.$store.auth.me = u;
              this.$store.ui.hydrateUser(u);
              identitySettled = true;
            }
          } catch (e) {
            // Rate-limited is not closed: the first-read retries create the
            // row a minute later, so only tell the learner.
            if (isSignupClosed(e) || problemSlug(e) === "signups-rate-limited") {
              signupClosed = isSignupClosed(e);
              this.$store.auth.showLockToast("notice", null, e.problem.detail || e.problem.title);
            } else {
              console.warn("anon register failed:", e);
            }
          }
        }
        if (!signupClosed) settleUserId();
        if (identitySettled) {
          postAuthIdentity({ userId: this.$store.ui.userId, signedIn: this.$store.auth.signedIn });
          checkAuthChange(pendingAuthIdentity);
          pendingAuthIdentity = null;
        }
        // Drain a timezone the learner picked before ui.userId was known.
        // Runs for both boot paths so a signed-in browser whose localStorage
        // was wiped doesn't strand the pending zone (registerAnon never runs
        // for it).
        await this.flushPendingTimezone();
        this.flushPendingDailyGoal();
        this.flushPendingAgeBracket();
        this.flushPendingMatureContent();
        this.flushPendingDialectRegions();
        this.flushPendingLang();
        this.flushPendingLevel();
        if (!signupClosed) await this.refreshHomeSurfaces();
        this.$watch("$store.ui.effectiveLang", () => this.refreshSelectedWordgroups());

        // Heartbeat: 30 s while interacting, 60 s while idle. Paused via visibility.
        let hbTick = 0;
        setInterval(() => {
          if (document.hidden) return;
          if (!signupClosed) this.refreshStatsOnNewDay();
          if (!signupClosed) this.retryFirstReads();
          const recentInput = Date.now() - this.lastInputAt < 60_000;
          if ((recentInput && hbTick % 30 === 0) || (!recentInput && hbTick % 60 === 0)) {
            this.sendHeartbeat();
          }
          hbTick++;
        }, 1000);
      },

      async sendHeartbeat() {
        const uid = this.$store.ui.userId;
        if (!uid) return;
        try {
          // Heartbeat returns the user row + piggybacked unseen notifications.
          // Pipe them into the store so the bell badge updates without a
          // separate fetch, and refresh auth.me so a rename on another device
          // lands here without a manual reload.
          const resp = await api.heartbeat(uid);
          if (resp && resp.user_id && !rowIsStale(resp)) {
            this.$store.auth.me = resp;
          }
          // The server sends the list (empty included) on every successful
          // read, so an empty one also clears items seen on another device;
          // a failed read (notifications_failed) keeps what is shown and
          // never claims "all caught up" (#1079).
          if (resp && Array.isArray(resp.notifications)) {
            this.$store.notifications.unseenCount = resp.unseen_count || 0;
            this.$store.notifications.items = resp.notifications;
            this.$store.notifications.loaded = true;
          }
        } catch { /* best-effort */ }
      },

      // refreshAllStats re-reads every stats window plus the streak row, and
      // reports whether THIS refresh is the one that took the day past the
      // learner's goal - the finish on which the server advances the streak
      // (stats.advanceStreak fires when cumulative DayScore first reaches it).
      // The comparison has to live here because the "before" score survives
      // only until the assignments below overwrite it, and this is the one
      // place holding both halves. Callers that do not care ignore the result.
      // `now` dates a successful read (statsDay) for refreshStatsOnNewDay.
      async refreshAllStats(now = new Date()) {
        const uid = this.$store.ui.userId;
        if (!uid) return false;
        const day = this.statsDayKey(now);
        const before = this.$store.session.statsToday.score || 0;
        const goal = this.$store.ui.dailyGoal || 0;
        try {
          // One stats read carries every window under `periods` (issue #689);
          // the top level still holds the period we asked for.
          const [snapshot, streaks] = await Promise.all([
            api.stats("today"),
            api.streaks(),
          ]);
          const periods = snapshot.periods || {};
          this.$store.session.statsToday = periods.today || snapshot;
          this.$store.session.statsWeek = periods.week || {};
          this.$store.session.statsMonth = periods.month || {};
          this.$store.session.statsYear = periods.year || {};
          this.$store.session.statsOverall = periods.overall || {};
          this.$store.session.streaks = streaks;
          this.$store.session.statsHydrated = true;
          this.statsDay = day;
          this.statsReads++;
        } catch (e) {
          console.warn("stats refresh failed:", e);
          return false; // no fresh numbers: nothing to claim a crossing from
        }
        const after = this.$store.session.statsToday.score || 0;
        return goal > 0 && before < goal && after >= goal;
      },

      // The server rotates the daily stats at local midnight in the profile
      // timezone. Nothing else refetches them on a page left open across it,
      // so the heartbeat tick re-reads them once that day changes, retrying a
      // failed read (the boot one included) every 30 s: statsDay only moves on
      // a successful read. /stats does not say which day it counted, so a
      // clock more than a minute fast would read before the server rotates;
      // one follow-up read five minutes after the day-change read covers that
      // without polling.
      // The day change is checked first (#1479): a follow-up left overdue
      // by a hidden page or a sleeping device must not consume it, or a fast
      // clock's early read would stand for the new day with no follow-up.
      // A failed follow-up read retries every 30 s like the day-change read.
      async refreshStatsOnNewDay(now = new Date()) {
        const t = now.getTime();
        if (this.statsDayKey(now) !== this.statsDay) {
          if (t < this.statsRetryAt) return;
          this.statsRetryAt = t + 30_000;
          this.statsFollowUpAt = t + 5 * 60_000;
          return this.refreshAllStats(now);
        }
        if (!this.statsFollowUpAt || t < this.statsFollowUpAt) return;
        const due = (this.statsFollowUpAt = t + 30_000);
        const reads = this.statsReads;
        const crossed = await this.refreshAllStats(now);
        // Clear only this retry: a day change during the read set its own.
        if (this.statsReads !== reads && this.statsFollowUpAt === due) this.statsFollowUpAt = 0;
        return crossed;
      },

      // "zone|day": the calendar day, in the saved profile timezone, of one
      // minute before `now`. The zone comes from auth.me, the server's user
      // row (/api/sessions/current carries it since #1244); ui.timezone (only the picker
      // seed) covers the moments before any row landed. Keying on the zone
      // too makes a late zone force one re-read. The minute keeps a client
      // clock running up to 60 s fast from reading before the server rotates.
      // A zone this browser's Intl rejects falls back to the browser's own day.
      statsDayKey(now = new Date()) {
        const zone = this.$store.auth?.me?.timezone || this.$store.ui.timezone || "";
        const at = new Date(now.getTime() - 60_000);
        let day;
        try {
          day = at.toLocaleDateString("en-CA", { timeZone: zone || undefined });
        } catch {
          day = isoLocalDay(at);
        }
        return `${zone}|${day}`;
      },

      // Stats, social, continue, word groups, and the leaderboard do not
      // depend on each other; awaiting them in sequence delayed the home
      // hero (issue #710).
      async refreshHomeSurfaces() {
        const tasks = [
          this.refreshAllStats(),
          this.refreshLeaderboard(),
          this.refreshContinue(),
          this.refreshSelectedWordgroups(),
        ];
        if (this.$store.auth.signedIn) tasks.push(this.refreshSocial());
        await Promise.all(tasks);
      },

      async refreshSelectedWordgroups() {
        const uid = this.$store.ui.userId;
        if (!uid) return;
        const request = ++this.selectedWordgroupsRequest;
        try {
          const profile = await api.getProfile(uid);
          if (request === this.selectedWordgroupsRequest) {
            this.selectedWordgroups = profile.favorite_word_lists || [];
            this.selectedWordgroupsLoaded = true;
          }
        } catch (e) {
          console.warn("word group selection refresh failed:", e);
        }
      },

      // Re-runs each home-surface read whose first attempt failed, at most
      // every 30 s per surface, until one lands.
      retryFirstReads() {
        const t = Date.now();
        const s = this.$store.session;
        const pending = [
          ["wordgroups", !this.selectedWordgroupsLoaded, this.refreshSelectedWordgroups],
          ["social", this.$store.auth.signedIn && !s.socialHydrated, this.refreshSocial],
          ["leaderboard", this.isAdmin && !s.leaderboardHydrated, this.refreshLeaderboard],
        ];
        return Promise.all(pending.map(([name, due, refresh]) => {
          if (!due || t < (this.firstReadRetryAt[name] || 0)) return;
          this.firstReadRetryAt[name] = t + 30_000;
          return refresh.call(this);
        }));
      },

      get sidebarWordgroups() {
        return this.sidebarWordgroupsExpanded
          ? this.selectedWordgroups
          : this.selectedWordgroups.slice(0, 5);
      },

      // Points shown for a stats period. Overall carries all-time points
      // under overall_score; the narrower windows use the window score.
      _periodPoints(period) {
        const s = this.$store.session;
        switch (period) {
          case "today": return s.statsToday.score || 0;
          case "week": return s.statsWeek.score || 0;
          case "month": return s.statsMonth.score || 0;
          case "year": return s.statsYear.score || 0;
          case "overall": return s.statsOverall.overall_score || 0;
          default: return 0;
        }
      },

      // dayGoal is the home hero's view of the day: today's points against
      // the learner's goal tier. Kept as a method (not a cached field) so the
      // bar repaints as soon as refreshAllStats() lands the new score.
      dayGoal() {
        return dayGoalProgress(this.$store.session.statsToday.score, this.$store.ui.dailyGoal);
      },

      // weekDays feeds the home hero's 7-day strip; the template hides it
      // until at least one day is filled so a new learner sees no row of misses.
      weekDays() {
        return weekStrip(this.$store.session.streaks.activity_dates, this.dayGoal(), this.$store.i18n.lang, new Date(),
          this.$store.auth?.me?.timezone || this.$store.ui.timezone || "");
      },

      // Persistent red header band content per screen (design plan
      // res/design/v2). Returns { o: overline, t: title, stat, sub }.
      // The band renders once above the scroll area for every screen.
      band() {
        if (this.adminOnlyScreen(this.screen) && !this.isAdmin) return { o: "", t: "" };
        const level = this.$store.ui.level;
        const roundLevel = this.challengeRoundLevel || level;
        const streak = this.$store.session.streaks.current_streak || 0;
        const ct = (this.screen === "challenge_unavailable"
          ? this.unavailableChallenge
          : this.allChallengeTypes.find((g) => g.id === this.pendingChallengeType)) || {};
        const desc = ct.desc ? " · " + ct.desc : "";
        // A reading-quiz round mixes Pieces of different levels: each question shows its own.
        const challengeOverline = (lvl) => this.pendingChallengeType === "reading-quiz"
          ? t("band.challenge.overlineNoLevel", { desc })
          : t("band.challenge.overline", { level: lvl, desc });
        const map = {
          home:     { o: t("band.home.overline", { level }), t: t("band.home.title"), stat: streak ? t(streak === 1 ? "band.home.streakDaysOne" : "band.home.streakDays", { count: streak }) : "", sub: streak ? t("band.home.sub") : "" },
          challenge:{ o: challengeOverline(roundLevel), t: ct.label || t("band.challenge.title"), stat: this.randomMode ? t("band.challenge.randomStat") : "", sub: this.randomMode ? t("band.challenge.randomSub") : "" },
          challenge_list:{ o: challengeOverline(level), t: ct.label || t("band.challenge.title"), stat: "", sub: "" },
          challenge_unavailable:{ o: t("challenge.unavailable.overline", { name: ct.label || "" }), t: ct.label || t("band.challenge.title"), stat: ct.unavailable ? ct.unavailable.range : "", sub: t("challenge.unavailable.range") },
          courses:  { o: t("band.courses.overline"), t: t("band.courses.title") },
          course:   { o: t("band.course.overline"), t: this.courseBandTitle || t("band.course.title") },
          lesson:   { o: t("band.lesson.overline"), t: this.courseBandTitle || t("band.lesson.title") },
          tv:       { o: t("band.tv.overline"), t: t("band.tv.title") },
          tv_show:  { o: t("band.tv.overline"), t: t("band.tv.title") },
          wordgroups:{
            o: t("band.wordgroups.overline"),
            t: t("band.wordgroups.title"),
            stat: this.wordgroupStats ? `${this.wordgroupStats.selected} ${t("wordgroups.active")}` : "",
            sub: this.wordgroupStats ? t(this.wordgroupStats.total === 1 ? "wordgroups.ofGroupsOne" : "wordgroups.ofGroups", { count: this.wordgroupStats.total }) : "",
          },
          wordgroup: this.wordgroupBand ? {
            o: t(`wordgroups.category.${this.wordgroupBand.theme}`),
            t: this.wordgroupBand.name,
            stat: `${this.wordgroupBand.word_count} ${t(this.wordgroupBand.word_count === 1 ? "wordgroups.wordsOne" : "wordgroups.words")}`,
            sub: t("wordgroups.detailSubtitle"),
          } : { o: t("band.wordgroups.overline"), t: t("band.wordgroups.title") },
          friends:  { o: t("band.friends.overline"), t: t("band.friends.title") },
          profile:  { o: t("band.profile.overline"), t: t("band.profile.title") },
          compete:  { o: t("band.compete.overline"), t: t("band.compete.title") },
          chat:     { o: t("band.chat.overline"), t: t("band.chat.title") },
          lesen_superhirn: { o: t("band.lesenSuperhirn.overline"), t: t("band.lesenSuperhirn.title") },
          lesen_news: { o: t("band.lesenNews.overline"), t: t("band.lesenNews.title") },
          lesen_piece: this.readingPieceOrigin === "news"
            ? { o: t("band.lesenNews.overline"), t: t("band.lesenNews.title") }
            : this.readingPieceOrigin === "trivia"
              ? { o: t("band.lesenSuperhirn.overline"), t: t("band.lesenSuperhirn.title") }
              : { o: t("band.lesenPiece.overline"), t: t("band.lesenPiece.title") },
          lesen_choice: { o: t("band.lesenChoice.overline"), t: t("band.lesenChoice.title") },
          settings: { o: t("band.settings.overline"), t: t("band.settings.title") },
        };
        return map[this.screen] || { o: "", t: "", stat: "", sub: "" };
      },

      // showPeriod hides a longer window while it still equals the next
      // shorter one, so a new learner sees just "Today" instead of five
      // identical point totals. Today always shows.
      showPeriod(period) {
        const chain = ["today", "week", "month", "year", "overall"];
        const i = chain.indexOf(period);
        if (i <= 0) return true;
        return this._periodPoints(period) !== this._periodPoints(chain[i - 1]);
      },

      async refreshFadingWords() {
        const uid = await settledUserId();
        if (!uid) return;
        const request = ++this.fadingWordsRequest;
        try {
          const resp = await api.fadingWords(5);
          if (request !== this.fadingWordsRequest) return;
          this.$store.session.fadingWords = coalesceFadingWords(resp.words);
          this.$store.session.fadingWordsHydrated = true;
        } catch (e) {
          console.warn("fading words fetch failed:", e);
        }
      },

      async refreshContinue() {
        // Course content is admin-only; skip the fetch (it 403s) and keep
        // the home continue-lesson card hidden for everyone else.
        if (!this.isAdmin) {
          this.continueLesson = null;
          return;
        }
        const uid = this.$store.ui.userId;
        if (!uid) return;
        try {
          // 404 when there is no in-progress lesson.
          this.continueLesson = await api.lessonContinue();
        } catch (e) {
          this.continueLesson = null;
        }
      },

      openContinueLesson() {
        if (!this.continueLesson) return;
        this.activeCourseId = this.continueLesson.course_id;
        this.activeLessonId = this.continueLesson.lesson_id;
        this.activeCourseDemo = false;
        this.setScreen("lesson");
      },

      async refreshSocial() {
        const uid = this.$store.ui.userId;
        if (!uid) return;
        const request = ++this.socialRequest;
        try {
          const [friends, follows, followers] = await Promise.all([
            api.listFriends(),
            api.listFollows(),
            api.listFollowers(),
          ]);
          if (request !== this.socialRequest) return;
          this.$store.session.friends = friends.friends || [];
          this.$store.session.following = follows.follows || [];
          this.$store.session.followers = followers.followers || [];
          this.$store.session.followersTotal = followers.total || 0;
          this.$store.session.socialHydrated = true;
        } catch (e) {
          console.warn("social refresh failed:", e);
        }
      },

      async refreshLeaderboard() {
        // Global leaderboard is admin-only; skip the fetch (it 403s) and
        // keep the sidebar rail hidden for everyone else.
        if (!this.isAdmin) {
          this.$store.session.leaderboard = [];
          return;
        }
        try {
          const resp = await api.getLeaderboard(10);
          this.$store.session.leaderboard = resp.entries || [];
          this.$store.session.leaderboardHydrated = true;
        } catch (e) {
          console.warn("leaderboard fetch failed:", e);
        }
      },

      async unfollow(targetUserId) {
        const uid = this.$store.ui.userId;
        if (!uid) return;
        try {
          await api.unfollow(targetUserId);
          await this.refreshSocial();
        } catch (e) {
          console.warn("unfollow failed:", e);
        }
      },

      // Friends-screen "Compete" button: carry the friend onto the
      // Compete screen so the setup form pre-targets them for a room
      // invite. competePlayer.init() reads + clears the handoff.
      competeInviteFriend: null,
      // /compete/{room_code} deep link: competePlayer.init() reads + clears
      // it and attempts the join. Signed out, it stays in the URL so the
      // sign-in return-to brings the visitor back to it.
      competeJoinCode: null,
      competeChallengeFriend(friend) {
        this.competeInviteFriend = friend || null;
        this.go("compete");
      },

      wordClassLabel(category) {
        const label = this.$store.i18n.t(`wordClass.${category}`);
        return label === `wordClass.${category}` ? category : label;
      },

      reviewReason(word) {
        return wordReviewReason(word, (key, params) => this.$store.i18n.t(key, params));
      },

      async drillFadingWords(word = null) {
        if (this.reviewStarting) return;
        const fading = word ? [word] : (this.$store.session.fadingWords || []);
        if (fading.length === 0) {
          alert(t("profile.drillNone"));
          return;
        }
        const headerDriven = !(word && word.level);
        const level = headerDriven ? this.$store.ui.level : word.level;
        this.reviewStarting = true;
        try {
          const sounds = window.Alpine.store("ui").audioEnabled;
          const challenge = await api.drillFadingWords(level, fading, sounds);
          this.$store.session.currentChallenge = {
            ...challenge, headerDriven, startLevel: String(level).toUpperCase(),
          };
          this.activeChallengeWordgroupId = this.activeChallengeWordgroupSlug = null;
          this.pendingChallengeType = "vocabulary";
          this.setScreen("challenge");
        } catch (e) {
          console.warn("drill these failed:", e);
          alert(t("profile.drillFailed"));
        } finally {
          this.reviewStarting = false;
        }
      },

      // Defers `action` behind a confirmation modal when a challenge is in
      // progress (>=1 answer submitted); otherwise runs it synchronously.
      // action MUST be idempotent since the user can cancel and re-trigger.
      confirmLeaveChallenge(action) {
        if (typeof action !== "function") return;
        if (!this.challengeDirty || this.screen !== "challenge") {
          action();
          return;
        }
        // Stash and surface the modal. Any prior pending action is
        // replaced - only one nav prompt is in-flight at a time.
        this.leaveModalPendingAction = action;
        this.leaveModalOpen = true;
      },

      // User confirmed leaving. Clear the dirty flag so a follow-up nav in the
      // same tick doesn't re-prompt, then run the deferred action.
      leaveChallengeConfirm() {
        const action = this.leaveModalPendingAction;
        this.leaveModalOpen = false;
        this.leaveModalPendingAction = null;
        this.challengeDirty = false;
        // The modal promises the round's progress is lost, so make that
        // true: this round must not come back on the next visit. A reload
        // or a Back press never reaches here, and those DO resume (#666).
        forgetRound();
        if (typeof action === "function") action();
      },

      // Modal action: user backed out. Discard the deferred action so
      // it doesn't fire if the modal re-opens later.
      leaveChallengeCancel() {
        this.leaveModalOpen = false;
        this.leaveModalPendingAction = null;
      },

      go(name) {
        if (name === "challenge") {
          this._performGo(name);
          return;
        }
        // The Profile nav always targets the signed-in user's own profile,
        // regardless of whichever profile was last viewed.
        if (name === "profile") {
          const u = this.$store.auth.identity?.username;
          if (u) { this.openProfile(u); return; }
        }
        this.confirmLeaveChallenge(() => this._performGo(name));
      },

      _performGo(name) {
        // Course content is admin-only until it ships publicly. A
        // non-admin who reaches a course screen (stale state, direct
        // call) is bounced to home; the backend gates /courses* too.
        if (this.adminOnlyScreen(name) && !this.isAdmin) {
          this.go("home");
          return;
        }
        // Reset transient routing state when leaving deep screens.
        if (!["course", "lesson"].includes(name) && !(name === "challenge" && this.coursePractice)) {
          this.activeCourseId = null;
          this.activeCourseDemo = false;
        }
        if (name !== "lesson") this.activeLessonId = null;
        if (name !== "challenge" && name !== "lesson") this.coursePractice = null;
        if (name !== "tv_show") this.activeShowId = null;
        if (name !== "chat") {
          this.activeChatSessionId = null;
          this.pendingChatState = null;
        }
        if (name !== "wordgroup") this.activeWordgroupSlug = null;
        if (name !== "wordgroup") this.wordgroupBand = null;
        if (name !== "wordgroups" && name !== "wordgroup") this.wordgroupQuery = "";
        if (name !== "profile") this.activeProfileUsername = null;
        if (name !== "lesen_piece") this.activePieceId = null;
        this.competeJoinCode = null;
        const left = this.screen !== name;
        this.setScreen(name);
        // Update the browser URL so /tv etc. are reloadable. Other
        // screens normalise to '/' - they're state-only.
        this.pushRouteForScreen();
        // A screen without its own URL would reload onto home instead.
        if (left && !IN_MEMORY_SCREENS.includes(name) && !this.handsOffInMemory(name)
            && (name === "home" || this.pathForScreen() !== "/")) reloadIfAssetsChanged();
      },

      // A hand-off the target screen's init reads from memory: the started
      // Lesen chat (opener audio, announcement, composer focus) and the
      // friend a compete invite opens for. A reload would drop it (#1342).
      handsOffInMemory(name) {
        return (name === "chat" && !!this.pendingChatState)
          || (name === "compete" && !!this.competeInviteFriend);
      },

      // Account-only screens: their content needs a WorkOS session.
      // They stay reachable and addressable while signed out - the
      // screen renders a sign-in card instead of its data (see the
      // signed-out template in index.html), so /friends and /compete
      // never bounce a visitor to home.
      // Home, courses, TV, the challenge player, AND Settings stay
      // open to anonymous device-only users - Settings hosts theme /
      // level / appearance and the solo notification toggles, which
      // are useful without an account (the account-only bits inside
      // Settings gate themselves). Friends and Compete are wholly
      // social and need a session. The Lesen screens and the reading-quiz
      // work device-only; only the Piece chat start needs an account.
      screenRequiresAuth(name) {
        return name === "friends" || name === "compete";
      },

      // isAdmin reflects the signed-in user's role from /api/sessions/current. Course
      // content (the Courses nav entry, home cards, and the
      // courses/course/lesson screens) and the global overall leaderboard
      // rail are admin-only until those features ship publicly; the backend
      // enforces the same gates on /courses* and /leaderboard.
      get isAdmin() {
        const id = this.$store.auth.identity;
        return !!id && id.role === "admin";
      },

      // Right-sidebar "Friends today" rail: current user plus mutual
      // friends, sorted by today's challenge count then streak. Per-friend
      // today counts aren't on the wire yet, so friends sort by streak only.
      get friendsRailRows() {
        if (!this.$store.auth.signedIn) return [];
        const uid = this.$store.ui.userId;
        if (!uid) return [];
        const self = {
          user_id: uid,
          username: this.$store.auth.identity?.username || "",
          display_name: this.$store.auth.displayName || "You",
          current_streak: this.$store.session.streaks.current_streak || 0,
          today_count: this.$store.session.statsToday.challenges_count || 0,
          isSelf: true,
        };
        const friends = (this.$store.session.friends || []).map((f) => ({
          user_id: f.user_id,
          username: f.username || "",
          display_name: f.display_name || "Friend",
          current_streak: f.current_streak || 0,
          today_count: 0,
          isSelf: false,
        }));
        return [self, ...friends].sort((a, b) => {
          const byToday = (b.today_count || 0) - (a.today_count || 0);
          if (byToday !== 0) return byToday;
          return (b.current_streak || 0) - (a.current_streak || 0);
        });
      },

      // Public overall leaderboard for the sidebar rail below Friends today.
      get globalLeaderboardRows() {
        const uid = this.$store.ui.userId;
        return (this.$store.session.leaderboard || []).map((row) => ({
          user_id: row.user_id,
          username: row.username || "",
          display_name: row.display_name || "Learner",
          current_streak: row.current_streak || 0,
          overall_count: row.overall_challenges_count || 0,
          overall_score: row.overall_score || 0,
          isSelf: !!uid && row.user_id === uid,
        }));
      },

      // The promo card this page load shows, picked once in init().
      chatAd: CHAT_AD_VARIANTS[0],
      // The second card, for screens that carry two promos at once: below the
      // rail's breakpoint home runs a banner above and below the content.
      // Also picked once in init(), and never the same card as chatAd.
      chatAdAlt: CHAT_AD_VARIANTS[1],

      // pickChatAd returns one promo card at random: the rail is long-lived,
      // and a fixed pick would mean a learner only ever meets one of the
      // eight. Pass the card already on screen as `exclude` to draw a
      // different one - by character, not by card, since two cards front the
      // same Memo and the same face twice on one screen reads as a rendering
      // bug. See CHAT_AD_VARIANTS.
      pickChatAd(exclude) {
        const pool = exclude ? CHAT_AD_VARIANTS.filter((v) => v.name !== exclude.name) : CHAT_AD_VARIANTS;
        return pool[Math.floor(Math.random() * pool.length)];
      },

      chatAdBannerVisible() {
        return !this.flagsLoaded || !!this.flags.chat_enabled;
      },

      // Before authReady the right ask is unknown; remember the click and
      // let init carry it out once auth is known.
      openChatAd() {
        if (!this.authReady) {
          this.chatAdClickPending = true;
          return;
        }
        if (this.$store.auth.signedIn) this.go("chat");
        else this.$store.auth.openSignIn("chat");
      },

      // bannerChatAd returns the card for one of the mobile banner slots. The
      // rail poster is xl-only, so narrower viewports run the promo as a
      // horizontal banner under the content, plus a second one above it on
      // home. Two banners on one screen must not wear the same face, so the
      // lower slot takes the alternate card there.
      bannerChatAd(slot) {
        return slot === "bottom" && this.screen === "home" ? this.chatAdAlt : this.chatAd;
      },

      // adminOnlyScreen marks the course screens that only admins may see.
      adminOnlyScreen(name) {
        return name === "courses" || name === "course" || name === "lesson";
      },

      // guardCurrentScreen bounces a non-admin off a course screen back
      // to home. No-op until auth state is known (authReady) so a signed-in
      // deep-linker isn't redirected before /api/sessions/current resolves. Called after
      // the initial auth probe and on every popstate (via applyRouteFromURL).
      // Returns true when it redirected.
      //
      // Account-only screens (friends, compete) are NOT bounced: they render
      // their own signed-out sign-in card, so /friends and /compete stay on
      // the URL the visitor asked for instead of silently landing on home.
      guardCurrentScreen() {
        if (!this.authReady) return false;
        if (this.adminOnlyScreen(this.screen) && !this.isAdmin) {
          this.setScreen("home");
          try { history.replaceState({}, "", "/"); } catch { /* ignore */ }
          return true;
        }
        return false;
      },

      // setScreen swaps the visible screen, tearing the challengePlayer
      // down first when we are leaving it. The player is mounted under
      // x-if="screen === 'challenge'", so it stops existing the moment
      // screen changes - the abort has to be dispatched BEFORE the
      // assignment or its auto-advance timer keeps ticking with nothing
      // left to render. Every screen change goes through here; popstate
      // in particular never reaches go().
      setScreen(name) {
        if (name !== "tv" && name !== "tv_show") {
          this.tvContentType = "";
          this.tvLevelCohort = "";
        }
        if (this.screen === "challenge" && name !== "challenge") {
          this.$dispatch("dt-challenge-aborted");
        }
        if (name !== "challenge") {
          this.challengeRoundLevel = "";
        }
        // Any move off the list check orphans its pending lookup.
        if (name !== "challenge_list") this.challengeListRequest++;
        // The practice list scope lives only while a round (or its list
        // check) is on screen.
        if (name !== "challenge" && name !== "challenge_list") {
          this.activeChallengeWordgroupId = this.activeChallengeWordgroupSlug = null;
        }
        // Route handlers set wordgroupQuery before entering either Word list
        // screen. Every other screen, including practice, drops it.
        if (name !== "wordgroups" && name !== "wordgroup") this.wordgroupQuery = "";
        // The loaded feed outlives a Piece visit ("Zurück zur Liste", "Weiter
        // zum nächsten Text") and is dropped once the learner leaves Lesen.
        if (!READING_SCREENS.includes(name)) forgetReadingFeed();
        this.screen = name;
        currentScreen = name;
      },

      restoreTvFiltersFromURL() {
        const params = new URLSearchParams(window.location.search);
        const type = params.get("content_type");
        const cohort = params.get("level_cohort");
        this.tvContentType = TV_CONTENT_TYPES.includes(type) ? type : "";
        this.tvLevelCohort = TV_LEVEL_COHORTS.includes(cohort) ? cohort : "";
      },

      tvHref(showId = null) {
        const path = showId ? "/tv/" + encodeURIComponent(showId) : "/tv";
        const params = new URLSearchParams();
        if (this.tvContentType) params.set("content_type", this.tvContentType);
        if (this.tvLevelCohort) params.set("level_cohort", this.tvLevelCohort);
        const qs = params.toString();
        return path + (qs ? "?" + qs : "");
      },

      // pathForScreen returns the URL path that represents the
      // current screen + any deep-screen identifier (active show
      // for TV, pending type for challenge). Screens without their
      // own URL collapse to '/'. Built from SPA_ROUTES
      // (static/public/routes.json, inlined as window.__DT_ROUTES).
      pathForScreen() {
        if (this.screen === "tv" || this.screen === "tv_show") {
          return this.tvHref(this.screen === "tv_show" ? this.activeShowId : null);
        }
        if ((this.screen === "challenge" || this.screen === "challenge_unavailable")
            && this.pendingChallengeType === "trivia") {
          if (!this.activeTriviaCategory) return "/trivia";
          return "/trivia?" + new URLSearchParams({ category: this.activeTriviaCategory }).toString();
        }
        if (["challenge", "challenge_unavailable", "challenge_list"].includes(this.screen) && this.pendingChallengeType) {
          if (this.randomMode) return "/challenges/random";
          const g = GAME_TYPES.find((g) => g.id === this.pendingChallengeType);
          const slug = (g && g.slug) || this.pendingChallengeType;
          const path = "/challenges/" + encodeURIComponent(slug);
          return this.activeChallengeWordgroupSlug
            ? path + "?" + new URLSearchParams({ list: this.activeChallengeWordgroupSlug }).toString()
            : path;
        }
        if (this.screen === "home") return "/";
        const key = this.screen;
        const paramName = SPA_PATH_PARAM[key];
        const paramVal = paramName ? this[paramName] : "";
        let found = null;
        for (let i = 0; i < SPA_ROUTES.length; i++) {
          const route = SPA_ROUTES[i];
          if (route.screen !== key) continue;
          if (route.param) {
            if (paramVal) found = pathFromRoute(route, paramVal);
          } else if (!found) {
            found = route.pattern;
          }
        }
        let path = found || "/";
        if ((this.screen === "wordgroups" || this.screen === "wordgroup") && this.wordgroupQuery) {
          path += "?" + new URLSearchParams({ q: this.wordgroupQuery }).toString();
        }
        return path;
      },

      // Typing updates the address without adding a history entry per key.
      replaceRouteForScreen() {
        const want = this.pathForScreen();
        if (window.location.pathname + window.location.search === want) return;
        try { history.replaceState({}, "", want); } catch { /* ignore */ }
      },

      wordgroupHref(slug) {
        const path = "/word-groups/" + encodeURIComponent(slug || "");
        if (!this.wordgroupQuery) return path;
        return path + "?" + new URLSearchParams({ q: this.wordgroupQuery }).toString();
      },

      // pushRouteForScreen syncs window.history with the current
      // screen. Only pushes a new entry when the path actually
      // changes so the back button doesn't fill up with no-ops.
      pushRouteForScreen() {
        const want = this.pathForScreen();
        // Compare pathname + search: trivia carries its level/category in
        // the query string, so two trivia screens can share one pathname.
        if (window.location.pathname + window.location.search === want) return;
        try { history.pushState({ screen: this.screen }, "", want); } catch { /* ignore */ }
      },

      // applyRouteFromURL reads window.location.pathname and sets
      // screen + activeShowId accordingly. Called on init and on
      // popstate (back / forward / address bar reload). Unknown
      // paths show the not_found screen.
      applyRouteFromURL() {
        // A trailing slash means the same screen; the server allowlists both
        // forms (issue #676), so the router has to agree.
        const raw = window.location.pathname;
        let path = raw.length > 1 ? raw.replace(/\/+$/, "") || "/" : raw;
        // The server also serves the shell at /index.html (static.Handler).
        if (path === "/index.html") path = "/";
        const hit = matchSpaRoute(path);
        if (hit) {
          const handler = SPA_ROUTE_HANDLERS[hit.route.screen];
          if (handler) {
            let param = null;
            if (hit.route.param) {
              param = decodePathSegment(hit.params[hit.route.param] || "");
            }
            if (handler.call(this, param) !== false) return;
          }
        }
        // Root is home. Screens that carry no URL of their own (stats,
        // courses) also collapse to '/', so Back out of one of them lands
        // on home rather than restoring it - the history entry does not
        // record which one it was. At boot screen is already 'home', so
        // this is a no-op there.
        if (path === "/") {
          // Clear the deep-screen ids, exactly as _performGo does when it
          // leaves them. coursePractice is the one that bites: left set, the
          // next round started from home silently reuses the old lesson's
          // level and word list.
          this.activeShowId = null;
          this.activeWordgroupSlug = null;
          this.wordgroupBand = null;
          this.wordgroupQuery = "";
          this.activeChatSessionId = null;
          this.activeProfileUsername = null;
          this.activePieceId = null;
          this.competeJoinCode = null;
          this.activeCourseId = null;
          this.activeCourseDemo = false;
          this.activeLessonId = null;
          this.coursePractice = null;
          this.setScreen("home");
          return;
        }
        // Unknown path (no route, or a handler rejected its param): say so
        // and keep the URL, so a mistyped or stale link never looks like home.
        this.setScreen("not_found");
      },

      // Sidebar / home challenge-tile active state. The Random tile
      // owns the active class for the whole sticky Random mode, even
      // though the underlying pendingChallengeType is a real id -
      // otherwise both Random and the rolled type's tiles would
      // highlight at once, which misrepresents what the user picked.
      isChallengeTileActive(g) {
        if (!["challenge", "challenge_unavailable", "challenge_list"].includes(this.screen)) return false;
        if (g.id === "random") return this.randomMode;
        return !this.randomMode && this.pendingChallengeType === g.id;
      },

      // Web sidebar Modes menu (TV/Friends/Compete/Stats) shares this with the
      // mobile bottom nav. The 'tv_show' deep screen highlights as 'tv'.
      activeNav(id) {
        if (id === "tv") return this.screen === "tv" || this.screen === "tv_show";
        if (id === "wordgroups") return this.screen === "wordgroups" || this.screen === "wordgroup";
        if (id === "courses") return ["courses", "course", "lesson"].includes(this.screen);
        if (this.screen === "lesen_piece") {
          return (id === "lesen_news" && this.readingPieceOrigin === "news")
            || (id === "lesen_superhirn" && this.readingPieceOrigin === "trivia");
        }
        if (this.screen === "lesen_choice") return id === readingFeedScreen();
        if (this.screen === "challenge" && this.pendingChallengeType === "reading-quiz" && id === readingFeedScreen()) return true;
        return this.screen === id;
      },

      goHomeFromBrand() {
        this.go("home");
      },

      // openProfile navigates to a user's public profile at /u/{username}.
      // Used by the avatar menu (own profile) and the friend / follower /
      // following lists. Honours the leave-challenge guard. When already on
      // a profile (clicking through from one profile to another), the
      // mounted dtUserProfile component re-fetches via the dt-profile-changed
      // event since the screen value itself doesn't change.
      openProfile(username) {
        if (!username) return;
        this.confirmLeaveChallenge(() => {
          const wasProfile = this.screen === "profile";
          this.activeProfileUsername = username;
          this._performGo("profile");
          if (wasProfile) this.$dispatch("dt-profile-changed", username);
        });
      },

      openWordgroup(slug, ev) {
        if (ev && typeof ev.preventDefault === "function") ev.preventDefault();
        if (!slug) return;
        this.confirmLeaveChallenge(() => {
          const wasWordgroup = this.screen === "wordgroup";
          this.activeWordgroupSlug = slug;
          this._performGo("wordgroup");
          if (wasWordgroup) this.$dispatch("dt-wordgroup-changed", slug);
        });
      },

      openPiece(id, ev) {
        if (ev && typeof ev.preventDefault === "function") ev.preventDefault();
        if (!id) return;
        this.confirmLeaveChallenge(() => {
          const wasPiece = this.screen === "lesen_piece";
          this.activePieceId = id;
          this._performGo("lesen_piece");
          if (wasPiece) this.$dispatch("dt-piece-changed", id);
        });
      },

      // playReadingQuiz hands a started reading-quiz Challenge to the player,
      // the way drillFadingWords hands over its prefetched round.
      playReadingQuiz(challenge) {
        this.$store.session.currentChallenge = challenge;
        this.randomMode = false;
        this.activeChallengeWordgroupId = this.activeChallengeWordgroupSlug = null;
        this.pendingChallengeType = "reading-quiz";
        this.$store.notifications.bumpSessions();
        this._performGo("challenge");
      },

      startWordgroupChallenge(groupId, slug) {
        if (!groupId || !slug) return;
        this.confirmLeaveChallenge(() => this._performStartChallenge("vocabulary", groupId, slug));
      },

      // A landed 18+ opt-in: the sidebar lists and a hidden list's round load
      // again in place (the catalog and list screens watch it themselves).
      // A request still in flight started before the opt-in; the new one
      // supersedes it by token, so its late 404 is dropped.
      refreshAfterMatureSave() {
        this.refreshSelectedWordgroups();
        const state = this.challengeListState;
        if (this.screen === "challenge_list" && (state === "notfound" || state === "loading")) {
          this.resolveChallengeList(this.activeChallengeWordgroupSlug);
        }
      },

      // resolveChallengeList validates the ?list= slug before any round
      // starts: the player mounts only once the id is known, so a reload
      // resumes the scoped round and a missing list never degrades into
      // plain vocabulary practice.
      async resolveChallengeList(slug) {
        const request = ++this.challengeListRequest;
        this.activeChallengeWordgroupSlug = slug;
        this.challengeListState = "loading";
        this.setScreen("challenge_list");
        try {
          const group = await api.getWordgroup(slug, 1);
          if (request !== this.challengeListRequest) return;
          if (!group || !group.word_list_id) { this.challengeListState = "notfound"; return; }
          this.activeChallengeWordgroupId = group.word_list_id;
          this.setScreen("challenge");
        } catch (e) {
          if (request !== this.challengeListRequest) return;
          this.challengeListState = e && e.status === 404 ? "notfound" : "error";
        }
      },

      // onStart runs once the start goes ahead (not on a cancelled
      // leave-challenge modal); the bell CTA uses it to mark its item seen.
      startChallenge(type, ev, onStart) {
        // <a>-as-card launches: cancel default navigation so the SPA
        // (not the browser) handles the screen change. The href is
        // still there for middle-click / right-click → open / copy
        // link, which serves the SPA shell at /challenges/<type>.
        if (ev && typeof ev.preventDefault === "function") ev.preventDefault();
        // Same-mode re-clicks inside a running round: nothing to start (a finished round deals a fresh one).
        // The "current mode" is "random" while randomMode is true (no
        // matter what real type happens to be running underneath); a
        // user inside Random who clicks the resolved type's own tile
        // is asking to *leave* Random mode, so that case must NOT be
        // short-circuited.
        const currentMode = this.randomMode ? "random" : this.pendingChallengeType;
        const sameMode = this.screen === "challenge" && type === currentMode;
        // The mounted player ignores a type-changed event for its own type,
        // also when Random mode resolved to it, so it needs a rescope.
        const samePlayer = this.screen === "challenge" && type === this.pendingChallengeType;
        if (sameMode && !this.challengeFinished) {
          if (onStart) onStart();
          return;
        }
        this.confirmLeaveChallenge(() => {
          if (onStart) onStart();
          this._performStartChallenge(type);
          if (samePlayer) this.$dispatch("dt-challenge-scope-changed");
        });
      },

      startCoursePractice(view) {
        const configured = view && view.lesson && view.lesson.practice_challenge_type;
        const game = GAME_TYPES.find((g) => g.challenge_type === configured);
        if (!game) return;
        const type = game.id;
        const levels = LEVELS.map((level) => level.id);
        const wanted = levels.indexOf(this.$store.ui.level);
        const min = levels.indexOf(view.course.level_min);
        const max = levels.indexOf(view.course.level_max);
        const level = levels[Math.min(Math.max(wanted, min), max)];
        this.coursePractice = {
          course_id: view.lesson.course_id,
          lesson_id: view.lesson.lesson_id,
          challenge_type: configured,
          level,
          word_ids: (view.words || []).map((word) => word.word_id),
          completed: false,
          ...(type === "passive" && view.lesson.practice_passive_scope
            ? { scope: view.lesson.practice_passive_scope } : {}),
        };
        this.randomMode = false;
        this.pendingChallengeType = type;
        this.setScreen("challenge");
        this.$store.notifications.bumpSessions();
        this.$dispatch("dt-challenge-type-changed", type);
        this.pushRouteForScreen();
      },

      returnToLessonPractice(completed) {
        if (!this.coursePractice) return;
        this.activeCourseId = this.coursePractice.course_id;
        this.activeLessonId = this.coursePractice.lesson_id;
        this.lessonResumeStep = completed ? "after-practice" : "practice";
        this.coursePractice = null;
        this._performGo("lesson");
      },

      // startTrivia launches a trivia session for one category (null = all).
      // Mirrors startChallenge: cancel the <a> default, skip same-category
      // re-clicks, honour the leave-challenge guard.
      startTrivia(categoryId, ev) {
        if (ev && typeof ev.preventDefault === "function") ev.preventDefault();
        const category = TRIVIA_CATEGORIES.some((c) => c.id === categoryId) ? categoryId : null;
        if (this.screen === "challenge" && this.pendingChallengeType === "trivia"
            && (this.activeTriviaCategory || null) === category) return;
        this.confirmLeaveChallenge(() => this._performStartTrivia(category));
      },

      _performStartTrivia(category) {
        const trivia = GAME_TYPES.find((g) => g.id === "trivia");
        this.randomMode = false;
        this.pendingChallengeType = "trivia";
        this.activeChallengeWordgroupId = this.activeChallengeWordgroupSlug = null;
        this.activeTriviaCategory = category;
        const unavailable = challengeAvailability(trivia, this.$store.ui.level);
        this.setScreen(unavailable ? "challenge_unavailable" : "challenge");
        if (!unavailable) this.$store.notifications.bumpSessions();
        // The listener restarts on every trivia dispatch (not only on a type
        // change) so switching categories mid-trivia starts a fresh session.
        if (!unavailable) this.$dispatch("dt-challenge-type-changed", "trivia");
        this.pushRouteForScreen();
      },

      _performStartChallenge(type, wordgroupId = null, wordgroupSlug = null) {
        const requested = GAME_TYPES.find((g) => g.id === type);
        if (challengeAvailability(requested, this.$store.ui.level)) {
          this.randomMode = false;
          this.pendingChallengeType = type;
          this.activeChallengeWordgroupId = this.activeChallengeWordgroupSlug = null;
          this.setScreen("challenge_unavailable");
          this.pushRouteForScreen();
          return;
        }
        this.coursePractice = null;
        // Picking "random" enters sticky Random mode and resolves to a real
        // id (excluding the current type so consecutive Random sessions don't
        // repeat). Any real type exits Random mode.
        this.randomMode = (type === "random");
        const resolved = this.randomMode
          ? pickRandomChallengeType(this.pendingChallengeType, this.$store.ui.level)
          : type;
        this.pendingChallengeType = resolved;
        this.activeChallengeWordgroupId = wordgroupId;
        this.activeChallengeWordgroupSlug = wordgroupSlug;
        this.setScreen("challenge");
        // Count sessions so dtPushOptIn can gate the opt-in modal (any type).
        this.$store.notifications.bumpSessions();
        // When the challengePlayer is already mounted (sidebar type-switch
        // mid-challenge), fire a DOM-bubbling event so it cancels and restarts.
        // On first mount it reads pendingChallengeType in init() instead, so a
        // missed dispatch here is harmless.
        this.$dispatch("dt-challenge-type-changed", resolved);
        // Update the browser URL so /challenges/<type> is reloadable
        // and shareable. _performGo handles this for the regular nav
        // path; the challenge-start path bypasses _performGo so it
        // has to push here itself.
        this.pushRouteForScreen();
      },

      setLevel(id) {
        // Local mirror first so the UI updates without the server round-trip;
        // a failed PATCH keeps the local value and just warns (Settings
        // re-reads from the user row on next mount, so it self-heals).
        this.$store.ui.level = id;
        lsSet("dt_level", id);
        if (this.screen === "challenge_unavailable") {
          const type = GAME_TYPES.find((g) => g.id === this.pendingChallengeType);
          if (type && !challengeAvailability(type, id)) {
            if (type.id === "trivia") {
              this._performStartTrivia(this.activeTriviaCategory);
            } else {
              this._performStartChallenge(type.id);
            }
          }
        }
        syncI18nLang(this.$store.ui.effectiveLang);
        const uid = this.$store.ui.userId;
        if (!uid) {
          // Onboarding can render before the boot auth/anon round-trip
          // resolves an id; queue like pendingLangSave so the chosen CEFR
          // level still reaches the backend row after hydrateUser.
          this.pendingLevelSave = id;
          queuePendingPref("home_level", id);
          return;
        }
        patchUserRetry(uid, { home_level: id }).catch((e) => {
          console.warn("home_level PATCH failed:", e);
        });
      },

      // Daily-goal slider handler. Mirrors setLevel: local value first so the
      // slider + labels react instantly, then a best-effort PATCH. A failed
      // write leaves the local value in place and self-heals on next mount.
      // Out-of-tier values are ignored client-side; the backend rejects them
      // with a 422 regardless.
      setDailyGoal(points) {
        const n = Number(points);
        if (!DAILY_GOALS.some((g) => g.points === n)) return;
        this.$store.ui.dailyGoal = n;
        lsSet("dt_daily_goal", n);
        const uid = this.$store.ui.userId;
        if (!uid) {
          // Onboarding can render before the boot auth/anon round-trip
          // resolves an id; queue the goal like pendingTimezoneSave so it
          // still reaches the backend row.
          this.pendingDailyGoalSave = n;
          queuePendingPref("daily_goal_points", n);
          return;
        }
        patchUserRetry(uid, { daily_goal_points: n }).catch((e) => {
          console.warn("daily_goal_points PATCH failed:", e);
        });
      },
      // Content-maturity toggle (PG vs 18+). Mirrors setDailyGoal: local value
      // first for instant UI, then a best-effort PATCH; queued if the anon id
      // isn't resolved yet (false is a valid value, so the queue uses null).
      async setMatureContent(on) {
        const v = !!on;
        this.$store.ui.matureContent = v;
        lsSet("dt_mature_content", v);
        const uid = this.$store.ui.userId;
        if (!uid) {
          this.pendingMatureContentSave = v;
          queuePendingPref("mature_content", v);
          return;
        }
        if (v) this.retryLostAge();
        if (this.ageBracketSave && !(await this.ageAllowsMatureSave(v))) {
          // The age save did not land: 18+ ON must not go out with the age
          // unknown (the wizard's queued copy replays on the next boot).
          this.$store.ui.revertMatureContent(v);
          return;
        }
        patchUserRetry(uid, { mature_content: v }).catch((e) => {
          console.warn("mature_content PATCH failed:", e);
          this.$store.ui.revertMatureContent(v);
        });
      },
      // The backend refuses 18+ for a minor's age bracket, judged against the
      // row as stored, and while the age is unknown. The 18+ opt-in sets both
      // in the same breath, so the maturity save waits for the age save (and
      // is held, so the age response cannot reset the local answer meanwhile).
      // False: 18+ ON must not go out because the age save failed. Failures
      // are the age save's to report.
      async ageAllowsMatureSave(on) {
        heldPrefs.add("mature_content");
        try {
          return (await this.ageBracketSave) || !on;
        } finally {
          heldPrefs.delete("mature_content");
        }
      },
      // Age bracket (18+ opt-in, Settings). Mirrors setMatureContent; one of
      // AGE_BRACKETS (the backend 422s on anything else, including ""). Moving
      // to a minor bracket also clears 18+ server-side, so mirror that locally.
      setAgeBracket(bracket) {
        if (!AGE_BRACKETS.some((b) => b.id === bracket)) return;
        this.$store.ui.ageBracket = bracket;
        lsSet("dt_age_bracket", bracket);
        if (isMinorAgeBracket(bracket) && this.$store.ui.matureContent) {
          this.$store.ui.matureContent = false;
          lsSet("dt_mature_content", false);
        }
        const uid = this.$store.ui.userId;
        if (!uid) {
          this.pendingAgeBracketSave = bracket;
          queuePendingPref("age_bracket", bracket);
          return;
        }
        // Held so setMatureContent can wait on it (see ageAllowsMatureSave). Resolves
        // to whether the age landed and never rejects: nothing else awaits
        // this handle, so a rethrow would surface as an unhandled rejection
        // whenever the learner never reaches the 18+ step.
        this.lostAgeBracket = null;
        this.ageBracketSave = patchUserRetry(uid, { age_bracket: bracket }).then(() => true, (e) => {
          console.warn("age_bracket PATCH failed:", e);
          this.lostAgeBracket = bracket;
          return false;
        });
      },
      // The bracket whose save exhausted its retries. The next 18+ ON attempt
      // sends it again first instead of treating the loss as permanent.
      lostAgeBracket: null,
      retryLostAge() {
        if (this.lostAgeBracket) this.setAgeBracket(this.lostAgeBracket);
      },
      // Settled or in-flight age-bracket PATCH (resolves true once it landed),
      // or null before the first one.
      ageBracketSave: null,
      setLang(lang) {
        this.$store.ui.setLang(lang);
        if (this.screen === "profile") this.refreshFadingWords();
        const uid = this.$store.ui.userId;
        if (!uid) {
          this.pendingLangSave = this.$store.ui.lang;
          queuePendingPref("lang", this.$store.ui.lang);
          return;
        }
        patchUserRetry(uid, { lang: this.$store.ui.lang }).catch((e) => {
          console.warn("lang PATCH failed:", e);
        });
      },
      // The flushes below clear only the in-memory queue. The durable copy is
      // dropped by runPrefSave once the PATCH lands, so a flush whose save
      // exhausts its retries still leaves the pick for the next boot.
      flushPendingLang() {
        if (!this.pendingLangSave || !this.$store.ui.userId) return;
        const pending = this.pendingLangSave;
        this.pendingLangSave = null;
        this.setLang(pending);
      },

      // Onboarding timezone picker handler. Updates the in-memory <select>
      // value and writes it straight to the DB (the source of truth, issue
      // #161). Called on every change and on onboarding submit so the shown
      // zone is persisted even when the learner left the default.
      async setTimezone(tz) {
        if (!tz) return;
        this.$store.ui.timezone = tz;
        if (!this.timezoneOptions.some((o) => o.id === tz)) {
          this.timezoneOptions = timezoneOptions(tz);
        }
        const uid = this.$store.ui.userId;
        if (!uid) {
          this.pendingTimezoneSave = tz;
          queuePendingPref("timezone", tz);
          return;
        }
        try {
          await persistTimezone(uid, tz);
        } catch (e) {
          console.warn("timezone save failed:", e);
        }
      },
      // Persist a timezone queued by setTimezone while ui.userId was still
      // null (picker touched during the boot auth/anon round-trip). Called
      // once boot resolves an id, for both signed-in and anon visitors.
      async flushPendingTimezone() {
        if (!this.pendingTimezoneSave || !this.$store.ui.userId) return;
        const pendingTZ = this.pendingTimezoneSave;
        this.pendingTimezoneSave = null;
        await this.setTimezone(pendingTZ);
      },
      // Same drain for a daily goal picked before ui.userId was known.
      flushPendingDailyGoal() {
        if (!this.pendingDailyGoalSave || !this.$store.ui.userId) return;
        const pending = this.pendingDailyGoalSave;
        this.pendingDailyGoalSave = null;
        this.setDailyGoal(pending);
      },
      // Drained before the maturity queue: setMatureContent waits on the
      // promise this leaves behind, so ordering only holds if the bracket
      // save has already been started.
      flushPendingAgeBracket() {
        if (!this.pendingAgeBracketSave || !this.$store.ui.userId) return;
        const pending = this.pendingAgeBracketSave;
        this.pendingAgeBracketSave = null;
        this.setAgeBracket(pending);
      },
      flushPendingMatureContent() {
        if (this.pendingMatureContentSave === null || !this.$store.ui.userId) return;
        const pending = this.pendingMatureContentSave;
        this.pendingMatureContentSave = null;
        this.setMatureContent(pending);
      },
      // Regional words Dialect regions. Mirrors setDailyGoal: local value
      // first, then a best-effort PATCH, queued while the user id is unknown.
      setDialectRegions(keys) {
        this.$store.ui.dialectRegions = keys;
        lsSet("dt_dialect_regions", keys);
        const uid = this.$store.ui.userId;
        if (!uid) {
          this.pendingDialectRegionsSave = keys;
          queuePendingPref("dialect_regions", keys);
          return;
        }
        patchUserRetry(uid, { dialect_regions: keys }).catch((e) => {
          console.warn("dialect_regions PATCH failed:", e);
        });
      },
      flushPendingDialectRegions() {
        if (!this.pendingDialectRegionsSave || !this.$store.ui.userId) return;
        const pending = this.pendingDialectRegionsSave;
        this.pendingDialectRegionsSave = null;
        this.setDialectRegions(pending);
      },
      async loadDialectRegionOptions() {
        this.dialectRegionOptions ||= await api.listDialectRegions();
        return this.dialectRegionOptions;
      },
      // The keys a Regional words round uses; empty means "ask first".
      // Read after every await (live list, initial hydration) so the round
      // uses one fresh snapshot; the caller keeps it for the request, label
      // and saved-round scope.
      async regionalWordRegions() {
        const live = await this.loadDialectRegionOptions();
        // Bounded: a hung probe must not block the round forever.
        for (let i = 0; !this.authSettled && i < 50; i++) await sleep(100);
        return liveDialectRegionKeys(this.$store.ui.dialectRegions, live);
      },
      dialectRegionLabel(r) {
        const key = `dialectRegion.${r.key}`;
        const label = t(key);
        return label && label !== key ? label : (r.region || r.key);
      },
      // "Austria, Switzerland" for the stored keys; "" while none are set.
      get dialectRegionNames() {
        return this.dialectRegionNamesOf(this.$store.ui.dialectRegions || []);
      },
      dialectRegionNamesOf(stored) {
        const regions = this.dialectRegionOptions
          ? this.dialectRegionOptions.filter((r) => stored.includes(r.key))
          : stored.map((key) => ({ key }));
        return regions.map((r) => this.dialectRegionLabel(r)).join(", ");
      },
      // Every live region starts checked on the first pick.
      async openRegionPicker() {
        let live;
        try {
          live = await this.loadDialectRegionOptions();
        } catch (e) {
          console.warn("dialect regions load failed:", e);
          return;
        }
        const startsRound = this.screen === "challenge" && this.pendingChallengeType === "regional-word";
        const current = liveDialectRegionKeys((startsRound && this.roundRegions?.length ? this.roundRegions : null) || this.$store.ui.dialectRegions, live);
        this.regionPicker = {
          open: true,
          selected: current.length ? current : live.map((r) => r.key),
          startsRound,
        };
      },
      toggleRegionPick(key) {
        const sel = this.regionPicker.selected;
        this.regionPicker.selected = sel.includes(key) ? sel.filter((k) => k !== key) : [...sel, key];
      },
      closeRegionPicker() {
        this.regionPicker.open = false;
      },
      saveRegionPicker() {
        const keys = liveDialectRegionKeys(this.regionPicker.selected, this.dialectRegionOptions);
        if (!keys.length) return;
        this.regionPicker.open = false;
        const playing = this.regionPicker.startsRound && this.roundRegions;
        if (samePref(keys, playing || this.$store.ui.dialectRegions)) return;
        if (!this.regionPicker.startsRound) {
          this.setDialectRegions(keys);
          return;
        }
        // Cancelling the leave modal changes nothing: no save, old label.
        this.confirmLeaveChallenge(() => {
          this.setDialectRegions(keys);
          this.$dispatch("dt-challenge-scope-changed");
        });
      },
      // Same drain for a home level picked before ui.userId was known.
      flushPendingLevel() {
        if (!this.pendingLevelSave || !this.$store.ui.userId) return;
        const pending = this.pendingLevelSave;
        this.pendingLevelSave = null;
        this.setLevel(pending);
      },
      setTheme(t) { this.$store.ui.theme = t; lsSet("dt_theme", t); },
      setZoom(z) { this.$store.ui.zoom = z; lsSet("dt_zoom", z); },
      levelNameOf(id) { return t(`level.${id}`) || levelNameOf(id); },
      // The desktop topbar cycles audio off → manual → auto from one button.
      // The mobile overflow menu has room for a segmented control instead, so
      // it needs the three modes as data plus a direct setter.
      get audioModes() {
        return [
          { id: "off", label: t("nav.audio.off") },
          { id: "manual", label: t("nav.audio.manual") },
          { id: "auto", label: t("nav.audio.auto") },
        ];
      },
      setAudioMode(mode) { this.$store.ui.setAudioMode(mode); },
      // Header language selector: at B2+ (with the german_only_above_b1 flag
      // on) the pivot language is forced to German and the picker is inert.
      get langEnforced() {
        return !!(this.flags.german_only_above_b1 && GERMAN_ONLY_LEVELS.includes(this.$store.ui.level));
      },
      langFlagOf(id) { return (LANGS.find((l) => l.id === id) || LANGS[0]).flag; },

      // Pomodoro display state - derived from the session store ticker.
      pomodoroTotal() {
        return this.$store.session.isBreak
          ? this.$store.ui.pomodoroBreak
          : this.$store.ui.pomodoroDuration;
      },
      pomodoroPct() {
        const total = this.pomodoroTotal();
        if (!total) return 0;
        return Math.min(100, Math.round((this.$store.session.pomodoroElapsed / total) * 100));
      },
      pomodoroLabel() {
        return fmtTime(Math.max(0, this.pomodoroTotal() - this.$store.session.pomodoroElapsed));
      },
      // "TOTAL" / "PRAXIS" badge above the dial's countdown. The focus phase
      // is labelled by the total session length the big knobs select.
      pomodoroModeLabel() {
        return this.$store.i18n.t(
          this.$store.session.isBreak ? "pomodoro.practice" : "pomodoro.total",
        );
      },
      // Dial ring geometry: r=56 circle in the 204px SVG box (matches markup).
      pomodoroRingCirc() {
        return 2 * Math.PI * 56;
      },
      pomodoroRingOffset() {
        const c = this.pomodoroRingCirc();
        const total = this.pomodoroTotal();
        if (!total) return c;
        const frac = Math.min(1, this.$store.session.pomodoroElapsed / total);
        return c * (1 - frac);
      },
      pomoFocusMin() {
        return Math.round(this.$store.ui.pomodoroDuration / 60);
      },
      pomoBreakMin() {
        return Math.round(this.$store.ui.pomodoroBreak / 60);
      },
      // Total session length = focus + practice; the large knobs select it.
      pomoTotalMin() {
        return this.pomoFocusMin() + this.pomoBreakMin();
      },
      // Preset knobs positioned around the dial. Pixel coords are anchored to
      // the 204px box and the specific option values (design plan
      // res/design/v2); keep them in sync with POMO_*_OPTS. The large knobs
      // reflect the TOTAL length, the small knobs the PRACTICE length.
      pomoTotalKnobs() {
        const cur = this.pomoTotalMin();
        const pos = { 30: [84, 164], 45: [4, 84], 60: [84, 4] };
        return POMO_TOTAL_OPTS.map((v) => ({
          val: v, left: pos[v][0], top: pos[v][1], selected: v === cur,
        }));
      },
      pomoBreakKnobs() {
        const cur = this.pomoBreakMin();
        const pos = { 5: [129, 20], 10: [158, 49], 15: [169, 89] };
        return POMO_BREAK_OPTS.map((v) => ({
          val: v, left: pos[v][0], top: pos[v][1], selected: v === cur,
        }));
      },
      // Selecting a total keeps practice fixed and puts the rest into focus.
      setPomoTotal(min) {
        if (!POMO_TOTAL_OPTS.includes(min)) return;
        const focus = Math.max(1, min - this.pomoBreakMin());
        this.$store.ui.pomodoroDuration = focus * 60;
        lsSet("dt_pomodoro_duration", focus * 60);
        // Restart the current window so the ring reflects the new split.
        this.$store.session.pomodoroElapsed = 0;
      },
      // Selecting a practice length keeps the total fixed and shrinks focus.
      setPomoBreak(min) {
        if (!POMO_BREAK_OPTS.includes(min)) return;
        const total = this.pomoTotalMin();
        const focus = Math.max(1, total - min);
        this.$store.ui.pomodoroBreak = min * 60;
        this.$store.ui.pomodoroDuration = focus * 60;
        lsSet("dt_pomodoro_break", min * 60);
        lsSet("dt_pomodoro_duration", focus * 60);
        this.$store.session.pomodoroElapsed = 0;
      },
      resetPomodoro() {
        const s = this.$store.session;
        s.pomodoroActive = false;
        s.isBreak = false;
        s.pomodoroElapsed = 0;
      },
      togglePomoWidget() {
        this.$store.ui.pomoWidgetOpen = !this.$store.ui.pomoWidgetOpen;
        lsSet("dt_pomo_widget_open", this.$store.ui.pomoWidgetOpen);
      },
    };
  };

  // ------------------------------------------------------------------
  // Notifications bell + drawer
  // ------------------------------------------------------------------

  // Convert the URL-safe base64 string the server returns into the Uint8Array
  // the PushManager.subscribe API expects. Standard helper from the W3C
  // Web Push docs.
  function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
    return out;
  }

  // subscribePush runs the full browser + backend push subscription
  // dance once. Callers map the structured result to their own UX
  // (opt-in modal snooze vs Settings inline errors).
  async function subscribePush() {
    // The permission prompt can outlast a sign-in in another tab: a captured
    // id makes the final write a 403 instead of saving for the new account.
    const userId = window.Alpine.store("ui").userId;
    const cfg = await api.pushConfig();
    if (!cfg || !cfg.enabled || !cfg.vapid_public_key) {
      return { ok: false, reason: "not_configured" };
    }
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return { ok: false, reason: "unsupported" };
    }
    const reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { ok: false, reason: "denied" };
    }
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(cfg.vapid_public_key),
    });
    const json = sub.toJSON();
    const created = await api.subscribePush(
      userId,
      json.endpoint,
      json.keys && json.keys.p256dh,
      json.keys && json.keys.auth,
    );
    window.Alpine.store("notifications").pushSubscribed = true;
    lsSet("dt_push_subscribed", true);
    const subscriptionId = (created && created.subscription_id) || null;
    if (subscriptionId) lsSet("dt_push_subscription_id", subscriptionId);
    return { ok: true, subscriptionId };
  }

  // mapIdentityError turns PATCH /api/users/{user_ref} identity failures into
  // per-field messages. fieldFallback pins 409/422 when the server
  // detail doesn't name a field (single-field saves from Settings).
  function mapIdentityError(e, fieldFallback) {
    const errors = { displayName: "", username: "", general: "" };
    if (!(e instanceof ApiError)) {
      errors.general = t("settings.error.network");
      return errors;
    }
    const detail = (e.problem && e.problem.detail) || "";
    if (e.status === 403) {
      errors.general = detail || t("settings.error.handleLocked");
      return errors;
    }
    if (e.status === 409) {
      if (/display/i.test(detail)) {
        errors.displayName = detail;
      } else {
        errors[fieldFallback] = detail || t("settings.error.handleTaken");
      }
      return errors;
    }
    if (e.status === 422) {
      if (/moder/i.test(detail)) {
        errors.general = detail;
      } else if (/display/i.test(detail)) {
        errors.displayName = detail;
      } else {
        errors[fieldFallback] = detail || (
          fieldFallback === "username"
            ? t("settings.error.usernameNotAllowed")
            : t("settings.error.notAllowed")
        );
      }
      return errors;
    }
    errors.general = detail || t("settings.error.requestFailed", { status: e.status });
    return errors;
  }

  // formatNotificationTime renders an RFC3339 timestamp as a short relative
  // string for the bell drawer, in the Helper language (`t`, `lang` come from
  // the i18n store). Returns "" for empty/unparseable input so the drawer
  // just omits the line rather than showing "Invalid Date".
  function formatNotificationTime(iso, t, lang) {
    if (!iso) return "";
    const ms = Date.parse(iso);
    if (isNaN(ms)) return "";
    const sec = Math.round((Date.now() - ms) / 1000);
    if (sec < 45) return t("notification.time.now");
    const ago = (unit, count) => t(`notification.time.${unit}${count === 1 ? "One" : ""}`, { count });
    const min = Math.round(sec / 60);
    if (min < 60) return ago("minutes", min);
    const hr = Math.round(min / 60);
    if (hr < 24) return ago("hours", hr);
    const day = Math.round(hr / 24);
    if (day < 7) return ago("days", day);
    const wk = Math.round(day / 7);
    if (wk < 5) return ago("weeks", wk);
    try { return new Date(ms).toLocaleDateString(lang); } catch { return ""; }
  }

  window.dtAccountMenu = function () {
    return {
      open: false,
      toggle() {
        this.open = !this.open;
        if (this.open) this.$refs.trigger.focus();
      },
      onEscape(event) {
        if (!this.open) return;
        event.stopPropagation();
        event.preventDefault();
        this.close(true);
      },
      close(focusTrigger = false) {
        this.open = false;
        if (focusTrigger) this.$refs.trigger.focus();
      },
      focusOut() {
        this.$nextTick(() => {
          if (!this.$el.contains(document.activeElement)) this.close();
        });
      },
    };
  };

  // dtNotifications powers the bell icon + dropdown. The store is populated
  // by sendHeartbeat (server piggybacks unseen rows onto the heartbeat
  // response), so this component is mostly a thin view + action wrapper.
  window.dtNotifications = function () {
    return {
      // The Helper-language translator the shared notification-kinds.js
      // helpers take.
      translator() {
        return (key, params) => this.$store.i18n.t(key, params);
      },
      labelFor(item) {
        return notificationKindLabel(item.kind, this.translator());
      },
      // The descriptive body line under the title. Built from the kind +
      // the payload the heartbeat piggybacks (a JSON string); the drawer
      // used to show only the category label, so the actual message was
      // never visible.
      messageFor(item) {
        return notificationKindMessage(item.kind, item.payload, this.translator());
      },
      // Time-sensitive kinds (streak / compete invite) get the warning
      // tint; everything else uses the neutral card style.
      isAlert(item) {
        return notificationKindIsAlert(item.kind);
      },
      // Friendly relative timestamp ("just now", "3h ago") instead of the
      // raw RFC3339 string the drawer rendered before.
      timeFor(item) {
        return formatNotificationTime(item.created_at, this.translator(), this.$store.i18n.lang);
      },
      // The drawer is a native daisyUI dropdown that stays open while it is
      // :focus-within. Acting on a per-item button removes that button from
      // the DOM (the row is filtered out), which drops focus to <body> and
      // collapses the drawer. keepOpen() parks focus on the panel (tabindex=0)
      // so the drawer survives the row removal - both up front and again once
      // the async store action settles.
      keepOpen() {
        const panel = this.$refs.panel;
        if (panel) panel.focus();
      },
      hrefFor(item) {
        return notificationKindHref(item.kind);
      },
      // The CTA link opens the item's challenge in the SPA, marks the item
      // seen and closes the drawer / sheet. Without the root (or a
      // challenge) the plain href navigates instead.
      openItem(item, ev) {
        const root = window.__dtRoot;
        const challenge = notificationKindChallenge(item.kind);
        if (!root || !challenge) return;
        ev.preventDefault();
        const uid = this.$store.ui.userId;
        root.startChallenge(challenge, null, () => {
          if (uid) this.$store.notifications.markSeen(item.id);
          const sheet = document.getElementById("notif-modal");
          if (sheet) sheet.checked = false;
          if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
        });
      },
      onItemClick(item) {
        const uid = this.$store.ui.userId;
        if (!uid) return;
        this.keepOpen();
        Promise.resolve(this.$store.notifications.markSeen(item.id)).finally(
          () => this.keepOpen(),
        );
      },
      onDismiss(item) {
        const uid = this.$store.ui.userId;
        if (!uid) return;
        this.keepOpen();
        Promise.resolve(this.$store.notifications.dismiss(item.id)).finally(
          () => this.keepOpen(),
        );
      },
      onMarkAllSeen() {
        const uid = this.$store.ui.userId;
        if (!uid) return;
        this.keepOpen();
        Promise.resolve(this.$store.notifications.markAllSeen()).finally(
          () => this.keepOpen(),
        );
      },
    };
  };

  // Sign-in modal Alpine component. State is local (mode); the open/close
  // flag lives on the auth store. Every provider button and the email
  // button funnel to one path: a full-page nav to /auth/sign-in, which
  // 302s to AuthKit's hosted picker. AuthKit covers
  // Google/Microsoft/Apple/email from a single landing page and asks for
  // the email itself, so the modal collects none (#985).
  window.dtSignInModal = function () {
    return {
      mode: "signin", // 'signin' | 'register'
      go() {
        // Mirror the device id into a transient cookie so /auth/callback can
        // read it (the redirect carries no X-Device-Id header).
        armDeviceIdCookieForSignIn();
        armReturnToCookieForSignIn();
        window.location.href = "/auth/sign-in";
      },
    };
  };

  // Username picker. Auto-opens after first sign-in (when /api/sessions/current's
  // username is still null). Mirrors the server-side regexes from
  // lib/users/identity so the user gets immediate feedback instead of
  // a round-trip 422. Display name is required on this surface; the
  // handle is auto-derived from the display name (slugified) and the
  // user can tweak it manually.
  const USERNAME_RE = /^[a-z][a-z0-9_]{2,19}$/;
  const DISPLAY_NAME_RE = /^[A-Za-z][A-Za-z0-9_]{0,29}$/;

  // Anon-name pools mirror lib/users/identity/anonname.go and MUST stay
  // in sync with it: this powers the dice 🎲 suggestion, so anything
  // here can be proposed directly as a display name. The Go pool's
  // curation rules apply (no profanity / slur substrings / homophones;
  // animals <= 10 chars). The server still owns the authoritative
  // generator for inserts at user-create time.
  const ANON_ADJECTIVES = [
    "Anon","Brave","Bright","Calm","Clever","Cosy","Curious","Eager",
    "Fluffy","Funny","Gentle","Happy","Jolly","Kind","Lucky","Merry",
    "Mighty","Nimble","Playful","Proud","Quick","Quiet","Smart","Snug",
    "Sunny","Swift","Witty","Zesty",
    "Echt","Edel","Fein","Flink","Frech","Froh","Gut","Heiter","Klug",
    "Kuehn","Lieb","Mutig","Nett","Pfiffig","Schlau","Schnell","Stark",
    "Suess","Tapfer","Treu","Weise","Wild","Wonnig",
  ];
  const ANON_ANIMALS = [
    "Bear","Bunny","Cat","Cheetah","Chipmunk","Cub","Deer","Dolphin",
    "Duckling","Elephant","Fawn","Ferret","Fox","Frog","Gosling","Hamster",
    "Hedgehog","Hippo","Husky","Kitten","Koala","Lamb","Llama","Mole",
    "Mouse","Otter","Owl","Panda","Penguin","Piglet","Polecat","Puppy",
    "Quokka","Rabbit","Seal","Sloth","Squirrel","Tiger","Turtle","Walrus",
    "Wombat",
    "Stinktier","Igel","Reh","Hase","Eule","Robbe","Dachs","Pinguin",
    "Spatz","Pony","Biene","Kaninchen","Maus","Wolf","Hund","Katze",
    "Baer","Loewe","Pferd","Adler","Falke","Rabe","Schwan","Hirsch",
    "Elch","Hai","Giraffe","Zebra","Taube","Moewe","Hummel","Kaefer",
  ];

  // generateAnonName returns a fresh PascalCase name in the
  // <Adjective><Animal><NN> form. Always passes DISPLAY_NAME_RE.
  function generateAnonName() {
    const adj = ANON_ADJECTIVES[Math.floor(Math.random() * ANON_ADJECTIVES.length)];
    const animal = ANON_ANIMALS[Math.floor(Math.random() * ANON_ANIMALS.length)];
    const n = 10 + Math.floor(Math.random() * 90); // 10..99
    return `${adj}${animal}${n}`;
  }

  // slugifyHandle converts a freeform display name into a candidate
  // @handle that satisfies USERNAME_RE. Steps:
  //   1. lowercase + strip diacritics
  //   2. non-[a-z0-9] runs collapse to a single underscore
  //   3. trim leading/trailing underscores
  //   4. if the result starts with a digit, prefix "a_" (USERNAME_RE
  //      requires a letter first)
  //   5. if shorter than 3 chars, pad with "_x"
  //   6. cap to 20 chars
  // The result is a *candidate* - the caller still probes
  // /api/username-availabilities/{x} and bumps a numeric suffix on taken.
  function slugifyHandle(displayName) {
    if (!displayName) return "";
    let s = String(displayName)
      .toLowerCase()
      .normalize("NFKD")
      // Strip combining diacritics.
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/_{2,}/g, "_");
    if (s.length === 0) return "";
    if (/^[0-9]/.test(s)) s = "a_" + s;
    while (s.length < 3) s += "_x";
    if (s.length > 20) s = s.slice(0, 20);
    // After truncation, re-trim a trailing underscore so we don't end
    // mid-collapse with a dangling `_`.
    s = s.replace(/_+$/, "");
    // Truncation may have dropped us back below 3 chars; pad again.
    while (s.length < 3) s += "_x";
    return s;
  }

  // bumpHandleNumber takes a candidate handle and either appends a
  // numeric suffix or increments an existing trailing one. Used by
  // the availability-probe loop in the picker to find the lowest free
  // suffix without losing the user's slug prefix. Always returns a
  // string that satisfies USERNAME_RE when the input does.
  function bumpHandleNumber(candidate) {
    const m = candidate.match(/^(.*?)([0-9]+)$/);
    if (m) {
      const base = m[1];
      const next = (parseInt(m[2], 10) || 0) + 1;
      const out = `${base}${next}`;
      return out.length > 20 ? `${base.slice(0, 20 - String(next).length)}${next}` : out;
    }
    const out = `${candidate}2`;
    return out.length > 20 ? `${candidate.slice(0, 19)}2` : out;
  }
  window.dtUsernamePicker = function () {
    return {
      // Display name is auto-slugified into the handle; handleTouched freezes
      // that auto-derive once the user edits the handle manually.
      displayName: "",
      username: "",
      handleTouched: false,
      busy: false,
      checking: false,
      // Per-field error keyed by field name; general catches
      // server-side failures we can't pin to a single field
      // (moderation, network, etc.).
      errors: { username: "", displayName: "", general: "" },

      init() {
        // Seed with the user's existing name (e.g. server-generated
        // KlugElephant43) so first sign-in shows something to confirm-or-edit,
        // falling back to a fresh suggestion if /api/sessions/current hasn't returned one.
        const me = this.$store.auth.identity || this.$store.auth.me;
        const seed = (me && me.display_name) || generateAnonName();
        this.displayName = seed;
        this.username = slugifyHandle(seed);
        this.validateDisplayName();
        this.validateUsername();
      },

      // Roll a fresh suggested display name and re-derive the handle. Wired to
      // the 🎲 button next to the display name field.
      rollSuggestion() {
        this.displayName = generateAnonName();
        this.handleTouched = false;
        this.errors.general = "";
        this.validateDisplayName();
        this.onDisplayNameInput();
      },

      // Called on every keystroke in the display name field. Re-runs
      // validation and, when the user hasn't manually edited the
      // handle, regenerates the slug + queues an availability probe.
      onDisplayNameInput() {
        this.validateDisplayName();
        if (this.handleTouched) return;
        this.username = slugifyHandle(this.displayName);
        this.validateUsername();
        this._queueAvailabilityCheck();
      },

      onHandleInput() {
        this.handleTouched = true;
        this.validateUsername();
        this._queueAvailabilityCheck();
      },

      validateUsername() {
        if (!this.username) {
          this.errors.username = t("settings.account.error.pickHandle");
          return false;
        }
        if (!USERNAME_RE.test(this.username)) {
          this.errors.username = t("settings.account.error.handleInvalid");
          return false;
        }
        this.errors.username = "";
        return true;
      },
      validateDisplayName() {
        if (!this.displayName) {
          this.errors.displayName = t("settings.account.error.pickDisplayName");
          return false;
        }
        if (!DISPLAY_NAME_RE.test(this.displayName)) {
          this.errors.displayName = t("settings.account.error.displayNameInvalid");
          return false;
        }
        this.errors.displayName = "";
        return true;
      },

      // _queueAvailabilityCheck debounces a /api/username-availabilities/{x}
      // probe; on "taken" we bump the numeric suffix and re-probe up
      // to a few times so the user lands on the lowest free slug. We
      // deliberately keep this in-component (no global throttler) so
      // every modal instance gets its own timer.
      _queueAvailabilityCheck() {
        if (this._availTimer) clearTimeout(this._availTimer);
        if (!USERNAME_RE.test(this.username)) return;
        this._availTimer = setTimeout(() => this._runAvailabilityCheck(), 250);
      },
      async _runAvailabilityCheck() {
        const startedFor = this.username;
        this.checking = true;
        try {
          let candidate = startedFor;
          // Up to 5 bumps - more than enough to skip past common
          // "frieda", "frieda2", ... clusters without spamming the
          // server.
          for (let i = 0; i < 5; i++) {
            const resp = await api.checkUsernameAvailability(candidate);
            if (this.username !== startedFor) {
              // User typed more while a request was in flight; bail out
              // on any iteration and let the latest debounce drive the
              // next probe. (Checking only on i === 0 would let a later
              // "available" response overwrite a freshly derived handle
              // with a stale bumped candidate.)
              return;
            }
            if (!resp) return;
            if (resp.available) {
              if (!this.handleTouched) this.username = candidate;
              return;
            }
            if (resp.reason === "invalid") {
              // Server agrees this slug is bad; defer to the inline
              // regex hint already shown.
              return;
            }
            // "taken" - bump and try again, but only if we're still
            // in auto-derive mode. Manual edits don't get auto-bumped.
            if (this.handleTouched) {
              this.errors.username = t("settings.error.handleTaken");
              return;
            }
            candidate = bumpHandleNumber(candidate);
            if (!USERNAME_RE.test(candidate)) return;
          }
        } catch (e) {
          // Probe failure is non-fatal - the actual claim still
          // happens via PATCH and surfaces 409 cleanly.
          console.warn("availability check failed:", e);
        } finally {
          this.checking = false;
        }
      },

      async submit() {
        this.errors.general = "";
        const okD = this.validateDisplayName();
        const okU = this.validateUsername();
        if (!okU || !okD || this.busy) return;
        const userId = this.$store.ui.userId;
        if (!userId) {
          this.errors.general = t("settings.error.noUser");
          return;
        }
        this.busy = true;
        try {
          await api.patchUser(userId, {
            username: this.username,
            display_name: this.displayName,
          });
          // Refresh /api/sessions/current so the topbar pill switches to the new
          // handle and the modal auto-closes via needsUsername.
          await this.$store.auth.refresh();
        } catch (e) {
          this.handleApiError(e);
        } finally {
          this.busy = false;
        }
      },
      handleApiError(e) {
        const mapped = mapIdentityError(e, "username");
        this.errors.displayName = mapped.displayName;
        this.errors.username = mapped.username;
        this.errors.general = mapped.general;
      },
      skip() {
        this.$store.auth.dismissUsernamePicker();
      },
    };
  };

  // Account-delete typed-confirmation. Opened from the avatar
  // dropdown. The user must type the exact phrase before the
  // destructive button enables; on success we wipe local state and
  // reload so the SPA boots back to anon. Backend hard-deletes the
  // row + cascades to all dependent tables.
  window.dtAccountDelete = function () {
    return {
      typed: "",
      busy: false,
      error: "",

      get canConfirm() {
        return (
          !this.busy &&
          this.typed.trim().toLowerCase() === t("accountDelete.phrase").toLowerCase()
        );
      },

      async confirm() {
        if (!this.canConfirm) return;
        const userId = this.$store.ui.userId;
        if (!userId) {
          this.error = t("settings.error.noUser");
          return;
        }
        this.busy = true;
        this.error = "";
        try {
          await api.deleteUser(userId);
        } catch (e) {
          this.busy = false;
          if (e instanceof ApiError) {
            this.error = e.problem.detail || t("settings.error.requestFailed", { status: e.status });
          } else {
            this.error = t("settings.error.network");
          }
          return;
        }
        // Clear the session cookie (best-effort - the user_id it points
        // at is gone, so subsequent /api/sessions/current calls would 401 anyway).
        try { await api.signOut(); }
        catch (e) { console.warn("post-delete sign-out failed:", e); }
        // Drop the now-stale anon bootstrap id so the next load re-registers
        // under a fresh user_id (device-id can stay; the backend makes a new
        // anon row keyed on it). Full reload avoids lingering Alpine state
        // pointing at the deleted row.
        try { localStorage.removeItem("dt_user_id"); }
        catch { /* private mode: best effort */ }
        postAuthIdentity({ userId: null, signedIn: false });
        window.location.assign("/");
      },
    };
  };

  // dtPushOptIn is the modal that asks for push permission after the user's
  // third session. Service-worker registration happens on demand when the user
  // accepts; declining sets the dismissed flag so we don't re-prompt.
  window.dtPushOptIn = function () {
    return {
      busy: false,
      async accept() {
        const uid = this.$store.ui.userId;
        if (!uid || this.busy) return;
        this.busy = true;
        try {
          const result = await subscribePush();
          if (result.ok) {
            this.$store.notifications.snoozePushOptIn(0);
            return;
          }
          if (result.reason === "not_configured") {
            console.info("push not configured server-side; snoozing prompt");
            this.$store.notifications.snoozePushOptIn(7);
            return;
          }
          if (result.reason === "denied") {
            this.$store.notifications.markPushOptInDismissed();
            return;
          }
          console.warn("push opt-in failed:", result.reason);
          this.$store.notifications.snoozePushOptIn(7);
        } catch (e) {
          console.warn("push opt-in failed:", e);
          this.$store.notifications.snoozePushOptIn(7);
        } finally {
          this.busy = false;
        }
      },
      // Permanent - sets the dismissed flag; only Settings can re-enable.
      declineForever() {
        this.$store.notifications.markPushOptInDismissed();
      },
      // Soft snooze. Default 7 days; backdrop / escape use this too so an
      // accidental click doesn't lock the user out of the prompt forever.
      snooze(days = 7) {
        this.$store.notifications.snoozePushOptIn(days);
      },
    };
  };

  // dtSettings powers the /settings screen: mostly a thin wrapper over the UI
  // store + dtPushOptIn helpers, plus two settings-only concerns: a push
  // subscribe/unsubscribe pair (full SW + Notification API dance) and a toggle
  // that clears the permanent "Never ask again" flag.
  window.dtSettings = function () {
    return {
      busyPush: false,
      pushError: null,
      patchError: null,
      timezoneError: null,
      timezoneSaved: false,
      prefError: null,
      prefSaved: false,
      LEVELS,
      DAILY_GOALS,
      THEMES,
      LANGS,
      GAME_TYPES,
      AGE_BRACKETS,
      isMinorAgeBracket,
      // Placeholder list; init() immediately fetches the server-saved timezone
      // and rebuilds it from that authoritative value (no browser guess).
      timezoneOptions: timezoneOptions(initialPickerTimezone()),

      async init() {
        // Seed the identity form fields so the inputs render pre-filled. After
        // the gate, so a /settings deep link seeds from the booted auth.me;
        // initIdentity keeps anything the learner typed while boot ran.
        this.readDiscordReturn();
        const uid = await settledUserId();
        this.initIdentity();
        if (uid) {
          try {
            const profile = await api.getProfile(uid);
            // Skip fields whose own save is still in flight (retry backoff can
            // outlive this mount); otherwise a stale profile row stomps the
            // newer local value and reconcile will not repair it.
            if (profile.private && profile.private.timezone && !prefSaves.has("timezone")) {
              this.$store.ui.timezone = profile.private.timezone;
              this.timezoneOptions = timezoneOptions(profile.private.timezone);
            }
            if (profile.private && profile.private.daily_goal_points != null && !prefSaves.has("daily_goal_points")) {
              this.$store.ui.dailyGoal = profile.private.daily_goal_points;
              lsSet("dt_daily_goal", profile.private.daily_goal_points);
            }
            this.$store.ui.hydrateUser(profile);
          } catch (e) {
            console.warn("settings: getProfile failed:", e);
          }
          await this.$store.notifications.loadPreferences();
        }
      },

      async toggleKindChannel(kind, channel) {
        const uid = this.$store.ui.userId;
        if (!uid) return;
        this.prefError = null;
        this.prefSaved = false;
        const ok = await this.$store.notifications.toggleKindChannel(
          kind, channel, this.$store.auth.signedIn,
        );
        if (ok) {
          this.flashSaved("prefSaved");
        } else if (this.$store.notifications.prefError) {
          this.prefError = this.$store.notifications.prefError;
        }
      },

      get notificationRows() {
        return this.$store.notifications.notificationRows;
      },

      // flashSaved shows a transient "saved" confirmation for the given
      // boolean flag, clearing it after a few seconds so the settings page
      // gives visible feedback on a successful persist (issue #161).
      flashSaved(flag) {
        this[flag] = true;
        if (this._savedTimers && this._savedTimers[flag]) {
          clearTimeout(this._savedTimers[flag]);
        }
        this._savedTimers = this._savedTimers || {};
        this._savedTimers[flag] = setTimeout(() => { this[flag] = false; }, 3000);
      },


      // togglePushSubscription: top-level "Push notifications" toggle.
      // Subscribes when off; unsubscribes when on. The unsubscribe path
      // does both: DELETE the backend row AND call pushManager.unsubscribe()
      // so the browser's permission UI matches our stored state (per the
      // Phase 19 open question #3 - verified empirically: re-subscribe
      // without unsubscribe() leaves a stale browser subscription that
      // can't be re-used by our backend's endpoint check).
      async togglePushSubscription() {
        const uid = this.$store.ui.userId;
        if (!uid || this.busyPush) return;
        this.busyPush = true;
        this.pushError = null;
        try {
          if (this.$store.notifications.pushSubscribed) {
            await this._unsubscribePush();
          } else {
            await this._subscribePush();
          }
        } finally {
          this.busyPush = false;
        }
      },

      async _subscribePush() {
        try {
          const result = await subscribePush();
          if (!result.ok) {
            if (result.reason === "not_configured") {
              this.pushError = t("settings.error.pushNotConfigured");
            } else if (result.reason === "unsupported") {
              this.pushError = t("settings.error.pushUnsupported");
            } else if (result.reason === "denied") {
              this.pushError = t("settings.error.pushDenied");
            } else {
              this.pushError = t("settings.error.pushFailed");
            }
            return;
          }
          this.reenablePushPrompt();
        } catch (e) {
          this.pushError = t("settings.error.pushFailed");
          console.warn("settings: subscribePush failed:", e);
        }
      },

      async _unsubscribePush() {
        // Track whether the backend delete actually succeeded (or the
        // row was already gone). On any other failure we leave the
        // stored subscription id + pushSubscribed flag in place so the
        // user can retry; otherwise we'd lose the id and the stale
        // backend row would survive forever.
        const subId = lsGet("dt_push_subscription_id", null);
        let backendCleared = !subId; // nothing to delete → already clean
        if (subId) {
          try {
            await api.unsubscribePush(subId);
            backendCleared = true;
          } catch (e) {
            if (e.problem && e.problem.status === 404) {
              backendCleared = true; // row already gone - treat as success
            } else {
              this.pushError = t("settings.error.pushRemoveFailed");
              console.warn("settings: unsubscribePush failed:", e);
              return;
            }
          }
        }
        // Only clear the browser-level subscription + local state once
        // the backend row is gone (or was never there). Reversing this
        // order would orphan the backend row if the SW call hung.
        try {
          if ("serviceWorker" in navigator) {
            const reg = await navigator.serviceWorker.getRegistration("/sw.js");
            if (reg) {
              const sub = await reg.pushManager.getSubscription();
              if (sub) {
                await sub.unsubscribe();
              }
            }
          }
        } catch (e) {
          console.warn("pushManager.unsubscribe failed (continuing):", e);
        }
        if (backendCleared) {
          this.$store.notifications.pushSubscribed = false;
          lsSet("dt_push_subscribed", false);
          lsSet("dt_push_subscription_id", null);
        }
      },

      // Clears the permanent "Never ask again" flag so the modal can
      // resurface on the regular Phase 14a §3 gate (3+ sessions, not
      // snoozed). Also clears any active snooze.
      reenablePushPrompt() {
        this.$store.notifications.pushOptInDismissed = false;
        this.$store.notifications.pushOptInSnoozeUntil = 0;
        lsSet("dt_push_optin_dismissed", false);
        lsSet("dt_push_optin_snooze_until", 0);
      },

      // Like getRoot().setLevel() but surfaces errors inline (next to the
      // control) instead of swallowing them.
      async saveHomeLevel(level) {
        const uid = this.$store.ui.userId;
        this.$store.ui.level = level;
        lsSet("dt_level", level);
        syncI18nLang(this.$store.ui.effectiveLang);
        this.patchError = null;
        if (!uid) {
          // Queue like saveDailyGoal so a level picked before boot resolves
          // an id still reaches the DB (drained by flushPendingLevel).
          const root = getRoot();
          if (root) {
            root.pendingLevelSave = level;
            queuePendingPref("home_level", level);
          }
          return;
        }
        try {
          await patchUserRetry(uid, { home_level: level });
        } catch (e) {
          this.patchError = (e.problem && e.problem.detail) || e.message || t("settings.error.saveLevel");
        }
      },

      // PATCH the daily points goal. Local mirror first, then a server write
      // that surfaces errors inline (the backend rejects any non-tier value).
      async saveDailyGoal(points) {
        const n = Number(points);
        if (!DAILY_GOALS.some((g) => g.points === n)) return;
        const uid = this.$store.ui.userId;
        this.$store.ui.dailyGoal = n;
        lsSet("dt_daily_goal", n);
        this.patchError = null;
        if (!uid) {
          // Queue like saveLang so a goal picked before boot resolves an id
          // still reaches the DB (drained by flushPendingDailyGoal).
          const root = getRoot();
          if (root) {
            root.pendingDailyGoalSave = n;
            queuePendingPref("daily_goal_points", n);
          }
          return;
        }
        try {
          await patchUserRetry(uid, { daily_goal_points: n });
        } catch (e) {
          this.patchError = (e.problem && e.problem.detail) || e.message || t("settings.error.saveDailyGoal");
        }
      },

      async setMatureContent(on) {
        const v = !!on;
        const uid = this.$store.ui.userId;
        this.$store.ui.matureContent = v;
        lsSet("dt_mature_content", v);
        this.patchError = null;
        if (!uid) {
          const root = getRoot();
          if (root) {
            root.pendingMatureContentSave = v;
            queuePendingPref("mature_content", v);
          }
          return;
        }
        const root = getRoot();
        if (root && v) root.retryLostAge();
        if (root && root.ageBracketSave && !(await root.ageAllowsMatureSave(v))) {
          // Same as the root setter: 18+ ON never goes out ahead of its age.
          root.pendingMatureContentSave = v;
          queuePendingPref("mature_content", v);
          this.$store.ui.revertMatureContent(v);
          return;
        }
        try {
          await patchUserRetry(uid, { mature_content: v });
        } catch (e) {
          this.$store.ui.revertMatureContent(v);
          this.patchError = (e.problem && e.problem.detail) || e.message || t("settings.error.saveContent");
        }
      },

      async saveTimezone(timezone) {
        const uid = this.$store.ui.userId;
        this.timezoneError = null;
        this.timezoneSaved = false;
        if (!timezone) return;
        if (!uid) {
          const root = getRoot();
          if (root) {
            root.pendingTimezoneSave = timezone;
            queuePendingPref("timezone", timezone);
          }
          this.$store.ui.timezone = timezone;
          this.timezoneOptions = timezoneOptions(timezone);
          return;
        }
        try {
          await persistTimezone(uid, timezone);
          this.$store.ui.timezone = timezone;
          this.timezoneOptions = timezoneOptions(timezone);
          this.flashSaved("timezoneSaved");
        } catch (e) {
          this.timezoneError = (e.problem && e.problem.detail) || e.message || t("settings.error.saveTimezone");
        }
      },
      async saveLang(lang) {
        const uid = this.$store.ui.userId;
        this.patchError = null;
        this.$store.ui.setLang(lang);
        if (!uid) {
          const root = getRoot();
          if (root) {
            root.pendingLangSave = this.$store.ui.lang;
            queuePendingPref("lang", this.$store.ui.lang);
          }
          return;
        }
        try {
          await patchUserRetry(uid, { lang: this.$store.ui.lang });
        } catch (e) {
          this.patchError = (e.problem && e.problem.detail) || e.message || t("settings.error.saveLang");
        }
      },

      // Local-only setters (no server round-trip needed).
      setTheme(t) { this.$store.ui.theme = t; lsSet("dt_theme", t); },
      setZoom(z) { this.$store.ui.zoom = z; lsSet("dt_zoom", z); },
      setFocusType(t) { this.$store.ui.setFocusType(t); },
      cycleAudio() { this.$store.ui.cycleAudio(); },
      audioLabel() {
        const m = this.$store.ui.audioMode;
        if (m === "off") return t("settings.learning.audioOff");
        if (m === "manual") return t("settings.learning.audioManual");
        return t("settings.learning.audioAuto");
      },

      // Reset-onboarding gate. Visible behind ?debug=1 only; clears the
      // home-level flag so the intro flow re-fires.
      get debugVisible() {
        try { return new URLSearchParams(window.location.search).get("debug") === "1"; }
        catch { return false; }
      },
      resetOnboarding() {
        lsSet("dt_home_level_set", false);
        this.$store.ui.onboardingDone = false;
        window.location.assign("/");
      },

      // ─── Identity editors (Account card) ───
      // editDisplayName / editUsername are scratch fields; on submit we PATCH
      // /api/users/{user_ref} and refresh /api/sessions/current so the topbar pill + identity pane
      // update in place. Inline errors mirror the picker's mapping.
      editDisplayName: "",
      editUsername: "",
      identityBusy: false,
      identityErrors: { displayName: "", username: "", general: "" },

      initIdentity() {
        // Read from auth.me (the unified User row) so anon users see
        // their generated display name pre-filled too. identity is
        // the /api/sessions/current payload and is null for anon.
        const me = this.$store.auth.me || this.$store.auth.identity;
        this.editDisplayName ||= (me && me.display_name) || "";
        this.editUsername ||= (me && me.username) || "";
        this.identityErrors = { displayName: "", username: "", general: "" };
      },

      // remainingHandleDays returns days until the rename window
      // closes; null when the handle has never been set (the user
      // can claim one any time), 0 when the window has already
      // closed. Mirrors lib/users.RenameWindow (30 days from
      // username_set_at).
      get remainingHandleDays() {
        const me = this.$store.auth.me || this.$store.auth.identity;
        if (!me || !me.username_set_at) return null;
        const set = new Date(me.username_set_at).getTime();
        if (Number.isNaN(set)) return null;
        const ms = 30 * 24 * 60 * 60 * 1000 - (Date.now() - set);
        return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
      },
      get handleRenameOpen() {
        const me = this.$store.auth.me || this.$store.auth.identity;
        if (!me) return false;
        if (!me.username_set_at) return true; // never claimed
        return this.remainingHandleDays > 0;
      },
      get discordUrl() {
        // Public flags live on the root component; pull from there.
        const root = window.__dtRoot;
        return (root && root.flags && root.flags.community_discord_url) || "";
      },

      // ─── Discord link (#23) ───
      // The state lives on the notifications store (the bell modal reads it too).
      get discordLink() { return this.$store.notifications.discordLink; },
      set discordLink(v) { this.$store.notifications.discordLink = v; },
      discordBusy: false,
      discordNotice: "",
      discordNoticeError: false,

      // The OAuth callback lands on /settings?discord=linked|error&reason=..
      readDiscordReturn() {
        const params = new URLSearchParams(window.location.search);
        const outcome = params.get("discord");
        if (!outcome) return;
        try { history.replaceState({}, "", "/settings"); } catch { /* ignore */ }
        // denied: the person cancelled on Discord, so there is nothing to report.
        const reason = params.get("reason");
        if (outcome === "error" && reason === "denied") return;
        const linked = outcome === "linked";
        this.discordNoticeError = !linked;
        this.discordNotice = t(linked ? "settings.discord.linked"
          : reason === "taken" ? "settings.discord.errorTaken" : "settings.discord.error");
      },

      async unlinkDiscord() {
        if (this.discordBusy) return;
        this.discordBusy = true;
        this.discordNotice = "";
        try {
          this.discordLink = await api.unlinkDiscord();
        } catch {
          this.discordNoticeError = true;
          this.discordNotice = t("settings.discord.unlinkFailed");
        } finally {
          this.discordBusy = false;
        }
      },

      // el is the checkbox: a failed save puts it back to the stored value.
      async setDiscordCelebrate(on, el) {
        if (this.discordBusy) return;
        this.discordBusy = true;
        this.discordNotice = "";
        try {
          this.discordLink = await api.setDiscordCelebrate(on);
        } catch {
          this.discordNoticeError = true;
          this.discordNotice = t("settings.discord.celebrateFailed");
          if (el) el.checked = !!this.discordLink?.celebrate;
        } finally {
          this.discordBusy = false;
        }
      },

      // Clears the "couldn't DM you" mark so notification DMs are tried again.
      async retryDiscordDM() {
        if (this.discordBusy) return;
        this.discordBusy = true;
        this.prefError = null;
        try {
          this.discordLink = await api.retryDiscordDM();
        } catch {
          this.prefError = t("settings.notifications.discordDMRetryFailed");
        } finally {
          this.discordBusy = false;
        }
      },

      async saveDisplayName() {
        const uid = this.$store.ui.userId;
        if (!uid || this.identityBusy) return;
        this.identityErrors.displayName = "";
        this.identityErrors.general = "";
        if (!this.editDisplayName) {
          this.identityErrors.displayName = t("settings.account.error.pickDisplayName");
          return;
        }
        if (!/^[A-Za-z][A-Za-z0-9_]{0,29}$/.test(this.editDisplayName)) {
          this.identityErrors.displayName = t("settings.account.error.displayNameInvalid");
          return;
        }
        this.identityBusy = true;
        try {
          const updated = await api.patchUser(uid, { display_name: this.editDisplayName });
          // Mirror the returned row to auth.me so the topbar pill updates
          // immediately (anon users have no /api/sessions/current to refresh against). A
          // stale row still carries the field just saved, which is never older.
          if (updated) this._mirrorSaved(updated, ["display_name"]);
          await this.$store.auth.refresh();
        } catch (e) {
          this._mapIdentityError(e, "displayName");
        } finally {
          this.identityBusy = false;
        }
      },

      async saveUsername() {
        const uid = this.$store.ui.userId;
        if (!uid || this.identityBusy) return;
        this.identityErrors.username = "";
        this.identityErrors.general = "";
        if (!this.editUsername) {
          this.identityErrors.username = t("settings.account.error.pickHandle");
          return;
        }
        if (!/^[a-z][a-z0-9_]{2,19}$/.test(this.editUsername)) {
          this.identityErrors.username = t("settings.account.error.handleInvalid");
          return;
        }
        this.identityBusy = true;
        try {
          const updated = await api.patchUser(uid, { username: this.editUsername });
          if (updated) this._mirrorSaved(updated, ["username", "username_set_at"]);
          await this.$store.auth.refresh();
        } catch (e) {
          this._mapIdentityError(e, "username");
        } finally {
          this.identityBusy = false;
        }
      },

      _mirrorSaved(updated, fields) {
        const auth = this.$store.auth;
        if (this.$store.ui.hydrateUser(updated)) auth.me = updated;
        else auth.me = { ...auth.me, ...Object.fromEntries(fields.map((f) => [f, updated[f]])) };
      },

      _mapIdentityError(e, fieldFallback) {
        const mapped = mapIdentityError(e, fieldFallback);
        this.identityErrors.displayName = mapped.displayName;
        this.identityErrors.username = mapped.username;
        this.identityErrors.general = mapped.general;
      },
    };
  };

  // ------------------------------------------------------------------
  // Challenge-screen component
  // ------------------------------------------------------------------

  // Normalize a server Question (flat `prompt` / `choices` / `question_id`)
  // into the {q, options, ...} shape the HTML template binds to. q[0] is the
  // full prompt.
  function normalizeQuestion(serverQ, sessionLevel) {
    return {
      questionId: serverQ.question_id,
      q: [serverQ.prompt || "", "", ""],
      options: serverQ.choices || [],
      answer: null, // filled in once the solution endpoint responds
      level: serverQ.level || sessionLevel,
      translation: serverQ.translation || "",
      translationMeanings: serverQ.translation_meanings || [],
      translationTerm: serverQ.translation_term || "",
      help: serverQ.help || "",
      // Grammar-context badges (case / family / degree / number).
      badges: serverQ.badges || [],
      audioUrl: serverQ.audio_url || "",
      imageUrl: serverQ.image_url || "",
      // The shown prompt and, for vocabulary, which side it is on.
      challenge: serverQ.prompt || "",
      direction: serverQ.direction || "",
      // The tested word's id; the graded result links to its word-detail modal.
      wordId: serverQ.word_id || null,
      // An optional second prompt word, rendered as .word-link.
      nounId: serverQ.noun_id || null,
    };
  }

  // grammarLabelKey maps a stable grammar identifier from the server (a
  // case, gender, article family, degree, or number name, as found in question badges and
  // rule-table row/column keys) onto the i18n key carrying its label.
  // Cases and genders reuse the existing word-detail namespaces;
  // everything else (article families, degrees, number, predicative, form) lives under
  // grammar.*. Unknown terms fall through to grammar.<term>, which t()
  // renders as the raw key - visible, not broken.
  function grammarLabelKey(term) {
    if (["nominative", "accusative", "dative", "genitive"].includes(term)) return "case." + term;
    if (["male", "female", "neutral"].includes(term)) return "gender." + term;
    return "grammar." + term;
  }
  if (typeof window !== "undefined") window.__dtGrammarLabelKey = grammarLabelKey; // test hook

  // localizedRule resolves one field of a grammar rule card through the
  // learner's catalogue (rule.<key>.title / .badge / .text / .mistakes, with
  // rule.params filling the {name} placeholders) and falls back to the
  // English text the server shipped when the family has no entry - the
  // rule-backed types without a key send the English only.
  function localizedRule(rule, field, fallback) {
    if (!rule || !rule.key) return fallback;
    const key = "rule." + rule.key + "." + field;
    const value = t(key, rule.params);
    return value === key ? fallback : value;
  }
  function ruleTitleText(rule) {
    return localizedRule(rule, "title", rule && rule.title);
  }
  function ruleBodyText(rule) {
    return localizedRule(rule, "text", rule && rule.rule);
  }
  // ruleBadgeText: the rule's own badge entry, else the grammar-term label
  // of the server badge (cases, genders, ...).
  function ruleBadgeText(rule, label) {
    return localizedRule(rule, "badge", null) || label(rule.badge);
  }
  // ruleWarningText: rule.warning_key (filled with rule.params), else the
  // English warning the server shipped.
  function ruleWarningText(rule) {
    if (!rule) return "";
    if (!rule.warning_key) return rule.warning || "";
    const value = t(rule.warning_key, rule.params);
    return value === rule.warning_key ? rule.warning || "" : value;
  }
  // localizedAchievement: achievement.<key>.name / .desc, else the English the
  // server shipped. field is "name" or "description".
  function localizedAchievement(a, field) {
    const key = "achievement." + a.key + "." + (field === "name" ? "name" : "desc");
    const value = t(key);
    return value === key ? a[field] : value;
  }
  // ruleMistakeLines: mistakes are one catalogue string, one mistake per line.
  function ruleMistakeLines(rule) {
    const localized = localizedRule(rule, "mistakes", null);
    if (localized) return localized.split("\n").filter(Boolean);
    return (rule && rule.mistakes) || [];
  }

  // After a graded answer, the player auto-advances after N seconds.
  // Tunable here so the countdown display + tests can reference the
  // same constants. The duration scales with how much of the
  // grammar rule card (preposition / article / plural) is on screen
  // (see autoAdvanceSeconds): a bare result keeps the original 5s, a
  // visible rule buys 15s, and rule + common mistakes buys 30s.
  const AUTO_ADVANCE_SECONDS = 5;        // base (no rule card)
  const RULES_ADVANCE_SECONDS = 15;      // rule visible
  const MISTAKES_ADVANCE_SECONDS = 30;   // rule + common mistakes visible

  // charSegments returns the `expected` string split into runs of unchanged /
  // changed characters versus `got`, so the dictation correction can underline
  // only the characters that were off (case-insensitive; original case shown).
  // Runs a full character LCS and emits ordered runs tagged by kind:
  //   keep - the char is correct (shown plain)
  //   del  - an expected char the learner omitted (shown underlined)
  //   ins  - an extra char the learner typed
  // The recap (dictSegments) shows only keep/del on the expected sentence and
  // skips ins runs: the typed answer stands on its own line (#987).
  function charSegments(expected, got) {
    const a = [...expected], b = [...got];
    const al = a.map((c) => c.toLowerCase()), bl = b.map((c) => c.toLowerCase());
    const n = a.length, m = b.length;
    const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= m; j++) {
        dp[i][j] = al[i - 1] === bl[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
    const rev = [];
    let i = n, j = m;
    while (i > 0 || j > 0) {
      if (i > 0 && j > 0 && al[i - 1] === bl[j - 1]) { rev.push({ ch: a[i - 1], kind: "keep" }); i--; j--; }
      else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) { rev.push({ ch: b[j - 1], kind: "ins" }); j--; }
      else { rev.push({ ch: a[i - 1], kind: "del" }); i--; }
    }
    const segs = [];
    for (let k = rev.length - 1; k >= 0; k--) {
      const last = segs[segs.length - 1];
      if (last && last.kind === rev[k].kind) last.text += rev[k].ch;
      else segs.push({ text: rev[k].ch, kind: rev[k].kind });
    }
    return segs;
  }
  if (typeof window !== "undefined") window.__dictCharSegments = charSegments; // test hook

  // diffTokens compares two sentences word by word (LCS over whitespace
  // tokens) and returns both in order: shared tokens unmarked, tokens only in
  // `a` marked as errors, tokens only in `b` as successes. The Passive wrong-tap
  // lines show it twice, once without the `b` tokens and once without the `a` ones.
  const DIFF_ERROR = "underline decoration-2 decoration-error";
  const DIFF_SUCCESS = "underline decoration-2 decoration-success";
  function diffTokens(a, b) {
    const x = String(a || "").split(/\s+/).filter(Boolean);
    const y = String(b || "").split(/\s+/).filter(Boolean);
    // dp[i][j]: LCS length of x[i..] and y[j..], so the walk below runs forward.
    const dp = Array.from({ length: x.length + 1 }, () => new Array(y.length + 1).fill(0));
    for (let i = x.length - 1; i >= 0; i--) {
      for (let j = y.length - 1; j >= 0; j--) {
        dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
    const out = [];
    let i = 0, j = 0;
    while (i < x.length || j < y.length) {
      if (i < x.length && j < y.length && x[i] === y[j]) { out.push({ text: x[i], cls: "" }); i++; j++; }
      else if (i < x.length && (j >= y.length || dp[i + 1][j] >= dp[i][j + 1])) out.push({ text: x[i++], cls: DIFF_ERROR });
      else out.push({ text: y[j++], cls: DIFF_SUCCESS });
    }
    return out;
  }

  // The `scope` query param of the page (/challenges/passive?scope=3): a scope
  // for one Passive round, from a deep link or a course. 0 when absent or invalid.
  function urlPassiveScope() {
    const n = Number(new URLSearchParams(window.location.search).get("scope"));
    return Number.isInteger(n) && n > 0 ? n : 0;
  }

  window.challengePlayer = function () {
    const reset = function () { this._resetDictation(); };
    const begin = function () { this.maybeAutoplay(); };
    const textKey = function (e) {
      if (e.target && e.target === this.$refs?.dictInput && e.key === "Enter") {
        e.preventDefault();
        this.onDictEnter();
      }
    };
    return {
      state: "idle", // idle → loading → playing → graded → summary
      type: null,
      regions: [], // the round's Dialect region snapshot (request, label, saved scope)
      get regionNames() {
        const root = getRoot();
        return root && this.regions.length ? root.dialectRegionNamesOf(this.regions) : "";
      },
      // ---- Passive voice ----
      // scopeParam: the page's ?scope= value, read at mount and sent with
      // Passive rounds until one completes (a deep link runs one round). A
      // course step's lesson scope is read at start time by runScope.
      scopeParam: 0,
      passiveScope: null, // GET .../scope-markers payload: forms, marker, chosen, effective, stats
      passiveScopeOpen: false,
      passiveScopeError: "",
      _shownAt: 0, // when the current question showed; pick() sends the elapsed ms
      get runScope() {
        if (this.type !== "passive") return 0;
        const course = getRoot()?.coursePractice;
        return course ? (course.completed ? 0 : course.scope || 0) : this.scopeParam;
      },
      challenge: null,
      questions: [],
      idx: 0,
      currentChoice: null,
      lastOutcome: null,
      correctCount: 0,
      summary: null,
      error: null,
      roundGone: false,
      readingBlocked: false,
      // True when THIS round is the one that carried the day's cumulative
      // score over the learner's goal (see finish()). Drives the summary's
      // goal-reached banner: crossing the line is the moment the streak is
      // secured, and it used to pass with nothing on screen to mark it.
      // Rounds finished later the same day leave it false - the goal is met,
      // but it was not met just now.
      goalJustMet: false,

      // ---- dictation (audio-to-text) state ----
      // dictPhase drives the passage flow: intro (play the whole passage) ->
      // sentences (per-part looping audio + type-and-Enter) -> scoring (grading
      // wait) -> then state flips to "summary" for the done screen. dictReview
      // accumulates each part's {verdict, reference, typed} for the results
      // list, since the completion summary carries no per-sentence detail.
      dictPhase: "intro",
      dictReview: [],     // one entry per submitted segment {typed, verdict, reference, diff, pending}
      dictPlaying: false, // a clip is audibly playing (drives the speaker pulse)
      typedAnswer: "",
      _dictationAudio: null,
      // Spelling Bee: whether the current word's definition hint is showing.
      // Free (no scoring effect) and reset on every advance, so a revealed
      // hint never carries into the next word.
      defRevealed: false,
      _introIdx: 0,
      _pending: [],       // in-flight background grade promises; finish() awaits them
      _failedSubmits: [], // submits that errored; finish() retries before scoring
      _lastSkipAt: 0,     // debounce timestamp for the skip button
      _readyTimer: null,  // the ~2s "get ready" beat after full-text playback
      // Monotonic session-generation token bumped by beginSentences and
      // _resetDictation. Async continuations (intro loop, submitPart, finish)
      // capture it and no-op if it changed, so a torn-down / restarted session
      // is never mutated by a stale clip event or late API response.
      _dictRun: 0,
      _resumePointer: null,
      _picking: false, // a pick() submit (and its retry) is in flight
      _finishing: false, // finish() is running; a second click must not complete the round
      // A new run token orphans in-flight pick()/finish() calls, so it also
      // releases their guards (their own finally blocks skip stale runs).
      _newRun() {
        this._picking = false;
        this._finishing = false;
        return ++this._dictRun;
      },
      _current(run) { return this._dictRun === run; },

      // ---- matching state ----
      // 4 sets of 5 word-translation pairs. matchResults accumulates
      // questionId -> {correct, correctAnswer}; a set advances once all 5
      // of its questions are resolved. First try counts: a wrong pick
      // fails the word and reveals+locks its correct translation.
      matchSetStart: 0,
      matchSelected: null,   // index within the current set
      matchFlash: null,      // translation that just flashed wrong
      matchResults: {},
      _matchSubmitting: false,

      // ---- word-order state ----
      orderSelected: [],

      // ---- pronunciation (speech) recording state ----
      // recording: mic is live. recReady: a clip is captured and awaiting submit.
      // recError: a friendly message for denied permission / unsupported browser.
      recording: false,
      recReady: false,
      recError: "",
      _mic: null,          // createMicCapture(), made on first use
      _recBlob: null,
      _recUrl: null,       // object URL for the playback preview
      _recStartedAt: 0,
      _recDurationMs: 0,

      // Auto-advance countdown: starts at AUTO_ADVANCE_SECONDS when the
      // player enters the graded state, ticks down once per second, and
      // calls next() at 0. Surfaces in the Next button label.
      nextCountdown: 0,
      // Pause state for that countdown, toggled by the pause button next to
      // Next. Paused keeps the remaining seconds on screen and only stops
      // the ticking; every countdown starts unpaused.
      nextPaused: false,
      _nextTimer: null,

      // Per-question reveals + report dialog. `showHelp` toggles the
      // help icon copy below the prompt (the translation is always
      // visible when present). `reportOpen` drives the report dialog
      // overlay; `reportCategory` + `reportDetails` carry the form state.
      showHelp: false,
      reportOpen: false,
      reportCategory: "wrong",
      reportDetails: "",
      reportPending: false,
      reportError: null,
      reportSent: false,
      reportImage: null,
      reportImageUrl: "",

      get currentQuestion() {
        return this.questions[this.idx] || { q: ["", "", ""], options: [] };
      },

      get hasAudio() { return !!(this.currentQuestion && this.currentQuestion.audioUrl); },
      get hasTranslation() { return !!(this.currentQuestion && this.currentQuestion.translation); },
      get hasHelp() { return !!(this.currentQuestion && this.currentQuestion.help); },

      // Delegated prompt click: .word-link opens the optional noun and
      // .cue-link opens the tested word. Anything else is ignored.
      openPromptWord(ev) {
        const q = this.currentQuestion;
        if (!q || !ev.target || !ev.target.closest) return;
        const id = ev.target.closest(".word-link") ? q.nounId
          : (ev.target.closest(".cue-link") ? q.wordId : null);
        if (id) Alpine.store("wordDetail").open(id);
      },

      playAudio() {
        // Honour the global mute here so every path (manual click, autoplay)
        // respects it without per-call guards.
        if (!Alpine.store("ui").audioEnabled) return;
        const url = this.currentQuestion && this.currentQuestion.audioUrl;
        if (!url) return;
        const a = newLevelAudio(url, this.playbackLevel);
        a.play().catch((e) => console.warn("audio play blocked:", e));
      },
      // The level driving the playback rate: the running session's level
      // (server-confirmed on the challenge, mirrored onto every question),
      // falling back to the ui store before a session exists.
      get playbackLevel() {
        return (this.challenge && this.challenge.level)
          || (this.currentQuestion && this.currentQuestion.level)
          || Alpine.store("ui").level;
      },
      // Invoked at the start of each new question. Only fires in "auto" mode.
      // The browser may still block the first play on a cold page until the
      // user interacts - playAudio's .catch handles that silently.
      maybeAutoplay() {
        // Audio-prompt types never autoplay: the first play must come from a
        // user tap (mobile autoplay policies, and so the replay counter only
        // counts deliberate plays).
        if (this.isAudioPrompt) return;
        if (Alpine.store("ui").audioAuto) this.playAudio();
      },

      // ---- audio-to-text (dictation) renderer ----

      // Resolves the renderer from GAME_TYPES (keyed by the running type id),
      // defaulting to multi-choice for any unmapped / legacy type.
      get inputMode() {
        const g = GAME_TYPES.find((x) => x.id === this.type);
        return (g && g.input_mode) || "multi-choice";
      },
      _modeHooks: {
        "multi-choice": {
          begin,
          reset,
          key(e) {
            const idx = ["1", "2", "3", "4"].indexOf(e.key);
            const opts = this.currentQuestion && this.currentQuestion.options;
            if (idx < 0 || !opts || idx >= opts.length) return;
            e.preventDefault();
            this.pick(opts[idx]);
          },
        },
        "word-order": {
          begin,
          reset() { this._resetDictation(); this.orderSelected = []; },
          key(e) {
            if (!this.orderSelected.length || this._picking) return;
            if (e.key === "Enter") {
              e.preventDefault();
              this.orderCheck();
            } else if (e.key === "Backspace") {
              e.preventDefault();
              this.orderSelected.pop();
            }
          },
        },
        matching: {
          begin,
          reset() {
            this._resetDictation();
            this.matchSetStart = 0;
            this.matchSelected = null;
            this.matchFlash = null;
            this.matchResults = {};
            this._matchSubmitting = false;
          },
        },
        "audio-to-text": {
          begin() { if (this.isSpellingBee) this.beginSentences(); },
          reset,
          key: textKey,
        },
        speech: { reset },
      },
      // An unknown mode (a type with no renderer) behaves like choice.
      get _hooks() { return this._modeHooks[this.inputMode] || this._modeHooks["multi-choice"]; },
      get isChoice() { return this.inputMode === "multi-choice"; },
      get isAudioToText() { return this.inputMode === "audio-to-text"; },
      // Spelling Bee shares the audio-to-text renderer but starts on word 1
      // (no full-text intro), counts words rather than sentences, and offers
      // the free definition hint.
      get isSpellingBee() { return this.type === "spelling-bee"; },
      // Pronunciation: hear the prompt, record yourself, upload the blob.
      get isSpeech() { return this.inputMode === "speech"; },
      // isAudioPrompt is the shared audio-prompt family (dictation + pronunciation):
      // both play a per-part clip, use the passage/segment flow, and skip autoplay.
      get isAudioPrompt() { return this.isAudioToText || this.isSpeech; },
      // isMatching: two-column tap-to-pair renderer (matching).
      get isMatching() { return this.inputMode === "matching"; },
      get isWordOrder() { return this.inputMode === "word-order"; },
      get orderTokens() {
        return (this.currentQuestion.options || []).map((text, id) => ({ id, text }));
      },
      get orderSoup() {
        return this.orderTokens.filter((token) => !this.orderSelected.includes(token.id));
      },
      // Selected tokens in tap order. The answer strip must use this (not a
      // filter over orderTokens) so the displayed sentence matches orderAnswer.
      get orderAnswerTokens() {
        return this.orderSelected.map((id) => this.orderTokens[id]);
      },
      get orderAnswer() {
        return this.orderAnswerTokens.map((token) => token.text).join(" ");
      },

      orderAdd(id) {
        if (this.state === "playing" && !this._picking && !this.orderSelected.includes(id)) this.orderSelected.push(id);
      },
      orderRemove(id) {
        if (this.state === "playing" && !this._picking) this.orderSelected = this.orderSelected.filter((selected) => selected !== id);
      },
      orderClear() {
        if (this.state === "playing" && !this._picking) this.orderSelected = [];
      },
      orderCheck() {
        if (this.orderSelected.length) this.pick(this.orderAnswer);
      },
      backToLessonPractice() {
        const root = getRoot();
        if (root) root.confirmLeaveChallenge(() => root.returnToLessonPractice(false));
      },
      continueLessonPractice() {
        const root = getRoot();
        if (root) root.returnToLessonPractice(true);
      },
      get inCoursePractice() {
        const root = getRoot();
        return !!(root && root.coursePractice);
      },
      get roundLevel() {
        const root = getRoot();
        return (root && root.challengeRoundLevel) || "";
      },
      // Only a round whose level came from the header level can be "switched
      // away from". A course practice level is clamped to the course range, a
      // reading Passage or drilled word sets its own: none of those is a switch.
      get levelMismatch() {
        const root = getRoot();
        return !!this.roundLevel && root.challengeHeaderDriven
          && root.challengeStartLevel !== String(window.Alpine.store("ui").level).toUpperCase();
      },

      // ---- matching renderer ----

      get matchBusy() { return this._matchSubmitting; },
      get matchSet() {
        return this.questions.slice(this.matchSetStart, this.matchSetStart + 5);
      },
      // Every question in a set carries the same shuffled translations, so
      // the first one's options ARE the right column.
      get matchRight() {
        return (this.matchSet[0] && this.matchSet[0].options) || [];
      },
      get matchSetNumber() { return this.matchSetStart / 5 + 1; },
      get matchSetCount() { return Math.ceil(this.questions.length / 5) || 1; },
      matchResultFor(q) { return this.matchResults[q.questionId] || null; },
      // A translation is consumed once the question it belongs to is
      // resolved - either matched correctly or revealed after a miss.
      matchUsed(translation) {
        return this.matchSet.some((q) => {
          const r = this.matchResultFor(q);
          return r && r.correctAnswer === translation;
        });
      },
      // Right-column tint: mirrors its owning pair's outcome.
      matchRightState(translation) {
        for (const q of this.matchSet) {
          const r = this.matchResultFor(q);
          if (r && r.correctAnswer === translation) return r.correct ? "correct" : "wrong";
        }
        return this.matchFlash === translation ? "flash" : "";
      },
      matchLeftState(q, i) {
        const r = this.matchResultFor(q);
        if (r) return r.correct ? "correct" : "wrong";
        return this.matchSelected === i ? "selected" : "";
      },
      matchPickLeft(i) {
        if (this.state !== "playing" || this._matchSubmitting) return;
        if (this.matchResultFor(this.matchSet[i])) return;
        this.matchSelected = this.matchSelected === i ? null : i;
      },
      async matchPickRight(translation) {
        if (this.state !== "playing" || this._matchSubmitting) return;
        if (this.matchSelected === null || this.matchUsed(translation)) return;
        const q = this.matchSet[this.matchSelected];
        this.error = null;
        this._matchSubmitting = true;
        // _dictRun is the shared session-generation token (bumped by
        // _resetDictation on start/abort): a submit or advance timer from a
        // torn-down round must not mutate its replacement.
        const run = this._dictRun;
        try {
          const out = await api.submitAnswer(this.challenge.challenge_id, q.questionId, translation, () => this._current(run));
          if (!this._current(run)) return;
          this.matchResults[q.questionId] = {
            correct: !!(out && out.correct),
            correctAnswer: (out && out.correct_answer) || "",
          };
          if (out && out.correct) {
            this.correctCount++;
          } else {
            this.matchFlash = translation;
            setTimeout(() => { if (this._current(run) && this.matchFlash === translation) this.matchFlash = null; }, 700);
          }
          this.matchSelected = null;
          const root = getRoot();
          if (root) root.challengeDirty = true;
          if (this.matchSet.every((sq) => this.matchResultFor(sq))) {
            setTimeout(() => { if (this._current(run)) this._matchAdvance(); }, 900);
          }
        } catch (e) {
          if (!this._current(run)) return;
          console.error("submitAnswer:", e);
          this.error = this.submitError(e, "Failed to submit");
        } finally {
          // A stale submit must not release the replacement round's guard.
          if (this._current(run)) this._matchSubmitting = false;
        }
      },
      _matchAdvance() {
        if (this.state !== "playing" || !this.isMatching) return;
        if (this.matchSetStart + 5 < this.questions.length) {
          this.matchSetStart += 5;
          this.matchSelected = null;
          this.matchFlash = null;
        } else {
          this.finish();
        }
      },
      // The language the current prompt is written in, and its flag + name.
      // Rendered as a flag before the question: vocabulary flips between a
      // German cue and a gloss cue at random, so without it the learner has
      // to infer from the word alone which side is being asked for. Empty
      // (and the template renders nothing) for types carrying no direction.
      get questionLang() {
        return promptLang(this.currentQuestion.direction, Alpine.store("ui").effectiveLang);
      },
      get questionFlag() {
        const opt = langOption(this.questionLang);
        return opt ? opt.flag : "";
      },
      get questionFlagLabel() {
        const opt = langOption(this.questionLang);
        return opt ? opt.label : "";
      },

      get dictNo() { return Math.min(this.idx + 1, this.questions.length); },
      get dictTotal() { return this.questions.length; },
      dictEmoji(v) { return v === "pass" ? "👍" : v === "partial" ? "🤏" : v === "fail" ? "👎" : ""; },
      // Progress dots: answered parts keep their verdict colour; the current
      // part is a longer grey bar (width, not colour); upcoming parts muted.
      dictDotClass(i) {
        if (i < this.dictReview.length) {
          const entry = this.dictReview[i];
          const v = entry.pending ? null : entry.verdict;
          if (v === "pass") return "bg-success";
          if (v === "partial") return "bg-warning";
          if (v === "fail") return "bg-error";
          return "bg-base-300"; // pending or unknown (e.g. 409): neutral, not a red fail
        }
        if (i === this.idx) return "bg-base-content/40";
        return "bg-base-300";
      },

      // Plays one clip and resolves when it ends. A blocked/failed play
      // resolves false immediately (dictPlaying stays false) so the flow, and
      // the intro sequence, never dead-end - the learner can hit replay.
      _playClip(url, loop) {
        this._stopClip();
        if (!url) return Promise.resolve(false);
        const a = newLevelAudio(url, this.playbackLevel);
        a.loop = !!loop;
        this._dictationAudio = a;
        return new Promise((resolve) => {
          // A superseded clip (skip / replay started a newer one) must not flip
          // dictPlaying: only the current clip owns the pulse state.
          // Resolve true only on a genuine 'ended'; a blocked/errored play
          // resolves false so the intro does not race past an unplayed part.
          const done = (ok) => { if (this._dictationAudio === a) this.dictPlaying = false; resolve(ok); };
          a.addEventListener("ended", () => done(true));
          a.addEventListener("error", () => done(false));
          const p = a.play();
          this.dictPlaying = true;
          if (p && typeof p.catch === "function") p.catch(() => done(false));
        });
      },
      _stopClip() {
        const a = this._dictationAudio;
        if (a) { try { a.pause(); a.currentTime = 0; } catch { /* not yet ready */ } }
        this.dictPlaying = false;
      },
      // Intro: play the whole passage once, part by part, then drop into the
      // per-sentence flow. Triggered by tapping the intro speaker (a real
      // gesture, so autoplay policies honour it - start() runs too late).
      // Skippable at any point.
      async startDictation() {
        const run = this._newRun();
        this.dictReview = [];
        this.dictPhase = "intro";
        for (let i = 0; i < this.questions.length; i++) {
          if (!this._current(run)) return; // superseded: skip / reset / restart
          this._introIdx = i;
          const played = await this._playClip(this.questions[i].audioUrl, false);
          if (!this._current(run)) return;
          if (!played) return; // blocked/errored: hold on intro; user taps the speaker to replay or skips
        }
        if (!this._current(run)) return;
        // Whole text finished: a short "get ready" beat, then the first segment.
        this.dictPhase = "ready";
        clearTimeout(this._readyTimer);
        this._readyTimer = setTimeout(() => { if (this._current(run)) this.beginSentences(); }, 2000);
      },
      // START skips (or interrupts) the full-text listen and begins right away.
      skipIntro() { clearTimeout(this._readyTimer); this._stopClip(); this.beginSentences(); },
      beginSentences() {
        this._newRun(); // end the intro run so its awaited loop cannot resume
        this.dictPhase = "sentences";
        this.idx = 0;
        this.dictReview = [];
        this.typedAnswer = "";
        this.defRevealed = false;
        this.playPart();
      },
      toggleDefinition() { this.defRevealed = !this.defRevealed; },
      // Play the current sentence once and focus the input. No auto-repeat -
      // the learner replays deliberately with replayPart().
      playPart() {
        this._playClip(this.currentQuestion.audioUrl, false);
        this.$nextTick(() => { if (this.$refs.dictInput) this.$refs.dictInput.focus(); });
      },
      replayPart() { this._playClip(this.currentQuestion.audioUrl, false); },
      onDictEnter() { if (this.typedAnswer.trim()) this.submitPart(); },
      // Keep the running transcript scrolled to the newest entry (it sits at
      // the bottom, next to the input).
      _scrollLog() {
        this.$nextTick(() => { const el = this.$refs.dictLog; if (el) el.scrollTop = el.scrollHeight; });
      },
      // Optimistic submit: record the typed text, clear the input, and advance
      // to the next segment immediately - no blocking wait. The (fast) grade
      // runs in the background and annotates its entry (verdict / reference /
      // diff) when it lands, guarded by _dictRun so a torn-down session is not
      // mutated. The blank path (skip button) flows through here too.
      submitPart() {
        const run = this._dictRun;
        this._stopClip(); // stop the current clip; the last segment goes straight to finish (no playPart)
        const q = this.questions[this.idx];
        const typed = this.typedAnswer.trim();
        this.dictReview.push({ typed, verdict: null, reference: "", diff: null, pending: true });
        // Grab the pushed element back, not the raw literal: _gradeSegment mutates
        // this reference later, and only the reactive proxy re-renders the live log.
        const entry = this.dictReview[this.dictReview.length - 1];
        this.typedAnswer = "";
        // Mark the session dirty synchronously so the leave-confirmation guard
        // fires even while the grade is still in flight (optimistic advance).
        const root = getRoot();
        if (root) root.challengeDirty = true;
        this._pending.push(this._gradeSegment(run, q.questionId, typed, entry));
        this.advancePart();
        this._scrollLog();
      },
      // The skip button allows blank submits, so debounce it: one tap must not
      // submit the current sentence AND a blank for the next. Enter self-guards
      // (the input is cleared on submit).
      skipPart() {
        const now = Date.now();
        if (now - this._lastSkipAt < 400) return;
        this._lastSkipAt = now;
        this.submitPart();
      },
      // Grade one segment in the background and annotate its entry. A failed
      // request is remembered so finish() can retry it before completeChallenge
      // reads the server-persisted score (a dropped answer would otherwise be
      // silently scored 0).
      async _gradeSegment(run, questionId, typed, entry) {
        try {
          const out = await api.submitAnswer(this.challenge.challenge_id, questionId, typed, () => this._current(run));
          if (!this._current(run)) return;
          entry.verdict = (out && out.verdict) || "fail";
          entry.reference = (out && (out.reference || out.correct_answer)) || "";
          entry.diff = (out && out.diff) || null;
          entry.pending = false;
          if (out && out.correct) this.correctCount++;
        } catch (e) {
          if (!this._current(run)) return;
          console.error("submitPart:", e);
          // Leave the entry pending (spinner, not a false 👎) - finish() retries
          // it and resolves the verdict there.
          this._failedSubmits.push({ questionId, typed, entry });
        }
      },

      // ---- pronunciation (speech) recording ----
      // A denied permission / unsupported browser sets a friendly recError
      // instead of throwing.
      async startRecording() {
        if (this.recording || (this._mic && this._mic.starting)) return;
        this.recError = "";
        this._clearRecording();
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || !window.MediaRecorder) {
          this.recError = t("challenge.pronunciation.micUnsupported");
          return;
        }
        this._mic ||= createMicCapture();
        try {
          if (!await this._mic.start((blob) => this._onRecStop(blob))) return; // skip, reset or leave cancelled it
          this._recStartedAt = Date.now();
          this.recording = true;
        } catch (e) {
          this.recError = (e && e.name === "NotAllowedError")
            ? t("challenge.pronunciation.micDenied")
            : t("challenge.pronunciation.micError");
        }
      },
      stopRecording() {
        this.recording = false;
        if (this._mic) this._mic.stop(); // fires _onRecStop when a clip is live
      },
      // Toggle handler for the single record/stop button.
      toggleRecording() {
        if (this.recording) this.stopRecording();
        else this.startRecording();
      },
      _onRecStop(blob) {
        this._recDurationMs = Date.now() - (this._recStartedAt || Date.now());
        this._recBlob = blob;
        if (this._recUrl) { URL.revokeObjectURL(this._recUrl); this._recUrl = null; }
        if (blob) this._recUrl = URL.createObjectURL(blob);
        this.recReady = !!blob;
        this.recording = false;
      },
      // Hard-stop the mic and recorder, discarding any in-flight clip.
      _teardownRecorder() {
        if (this._mic) this._mic.teardown();
        this.recording = false;
      },
      // Play back the just-recorded clip so the learner can check it.
      playRecording() {
        if (!this._recUrl) return;
        const a = new Audio(this._recUrl);
        a.play().catch(() => { /* preview blocked; harmless */ });
      },
      reRecord() { this.startRecording(); },
      _clearRecording() {
        if (this._recUrl) { URL.revokeObjectURL(this._recUrl); this._recUrl = null; }
        this._recBlob = null;
        this.recReady = false;
        this._recDurationMs = 0;
      },
      // Optimistic submit of the recorded clip: mirror submitPart, but upload
      // the blob for STT+CER grading. entry.typed is filled with the returned
      // transcription when the grade lands (there is no typed text).
      submitRecording() {
        if (!this._recBlob) return;
        const run = this._dictRun;
        this._stopClip();
        const q = this.questions[this.idx];
        const blob = this._recBlob;
        const durationMs = this._recDurationMs || 0;
        this.dictReview.push({ typed: "", verdict: null, reference: "", diff: null, pending: true });
        const entry = this.dictReview[this.dictReview.length - 1];
        this._clearRecording();
        const root = getRoot();
        if (root) root.challengeDirty = true;
        this._pending.push(this._gradeSegmentAudio(run, q.questionId, blob, durationMs, entry));
        this.advancePart();
        this._scrollLog();
      },
      // Skip a sentence without recording: no audio to grade, so record a local
      // fail entry (the server scores the unsubmitted part 0 at completion).
      skipRecording() {
        const now = Date.now();
        if (now - this._lastSkipAt < 400) return;
        this._lastSkipAt = now;
        this._stopClip();
        this._teardownRecorder(); // may be mid-recording: stop the mic before advancing
        this._clearRecording();
        this.dictReview.push({ typed: "", verdict: "fail", reference: "", diff: null, pending: false });
        this.advancePart();
        this._scrollLog();
      },
      async _gradeSegmentAudio(run, questionId, blob, durationMs, entry) {
        try {
          const out = await api.submitAnswerAudio(this.challenge.challenge_id, questionId, blob, durationMs, () => this._current(run));
          if (!this._current(run)) return;
          entry.verdict = (out && out.verdict) || "fail";
          entry.reference = (out && (out.reference || out.correct_answer)) || "";
          entry.diff = (out && out.diff) || null;
          entry.typed = (out && out.transcription) || "";
          entry.pending = false;
          if (out && out.correct) this.correctCount++;
        } catch (e) {
          if (!this._current(run)) return;
          console.error("submitRecording:", e);
          this._failedSubmits.push({ questionId, blob, durationMs, entry, audio: true });
        }
      },
      advancePart() {
        this.typedAnswer = "";
        this.defRevealed = false;
        if (this.idx + 1 >= this.questions.length) {
          this.dictPhase = "scoring";
          this.finish(); // awaits the pending grades, then completeChallenge -> summary
          return;
        }
        this.idx++;
        this.playPart();
      },
      // Insert a German character at the input caret (for English keyboards).
      insertChar(ch) {
        const el = this.$refs.dictInput;
        if (!el) { this.typedAnswer += ch; return; }
        const s = el.selectionStart ?? this.typedAnswer.length;
        const e = el.selectionEnd ?? s;
        this.typedAnswer = this.typedAnswer.slice(0, s) + ch + this.typedAnswer.slice(e);
        this.$nextTick(() => { el.focus(); const pos = s + ch.length; el.setSelectionRange(pos, pos); });
      },
      // Render one graded entry's expected sentence as coloured segments: the
      // complete expected text, with missing words and the characters a
      // substituted word got wrong underlined. Extra typed words and
      // characters are not interleaved into it (#987): the typed answer is
      // shown on its own line, exactly as typed.
      dictSegments(r) {
        const diff = r && r.diff;
        if (!Array.isArray(diff) || !diff.length) {
          if (r && r.reference) return [{ text: r.reference, cls: "" }];
          return r && r.typed ? [{ text: r.typed, cls: "opacity-60" }] : []; // unknown verdict (e.g. 409): show what was typed
        }
        const segs = [];
        for (const t of diff) {
          if (t.op === "match") segs.push({ text: t.expected + " ", cls: "" });
          else if (t.op === "del") segs.push({ text: t.expected + " ", cls: "underline decoration-error decoration-2 text-error" });
          else if (t.op === "sub") {
            const runs = charSegments(t.expected || "", t.got || "").filter((cs) => cs.kind !== "ins");
            if (!runs.some((cs) => cs.kind === "del")) {
              // Only extra (or differently cased) characters: no expected
              // letter is missing, so mark the whole word lightly instead.
              segs.push({ text: t.expected || "", cls: "underline decoration-dotted decoration-error decoration-2" });
            } else {
              for (const cs of runs) segs.push({ text: cs.text, cls: cs.kind === "del" ? "underline decoration-error decoration-2" : "" });
            }
            segs.push({ text: " ", cls: "" });
          }
        }
        return segs;
      },
      // Clears dictation state on start()/restart() so nothing bleeds across
      // sessions. next() is multi-choice only; dictation advances via advancePart.
      _resetDictation() {
        this._newRun(); // invalidate any in-flight intro coroutine / background grade
        clearTimeout(this._readyTimer);
        this._stopClip();
        this._dictationAudio = null;
        this.typedAnswer = "";
        this.dictReview = [];
        this._pending = [];
        this._failedSubmits = [];
        this.dictPhase = "intro";
        this.defRevealed = false;
        // Tear down any live mic capture / captured clip so it never bleeds
        // across sessions (abort / restart / type switch).
        this._teardownRecorder();
        this._clearRecording();
        this.recError = "";
      },

      toggleHelp() { this.showHelp = !this.showHelp; },

      // Auto-advance: scheduled when the player enters the graded
      // state (see pick()). Ticks via re-scheduled setTimeout so each
      // tick can re-check state - if the player is no longer in
      // 'graded' (next() / start() / finish() / openReport() called,
      // or the challengePlayer was unmounted via dt-challenge-aborted),
      // the loop self-cancels on the next tick.
      // autoAdvanceSeconds picks the countdown length for the current
      // result. Only results carrying a grammar rule card (preposition,
      // article, plural: lastOutcome.rule present) get the longer reading
      // windows; every other type keeps the original 5s. The window
      // follows the persisted prepDetail preference.
      get autoAdvanceSeconds() {
        const rule = this.lastOutcome && this.lastOutcome.rule;
        if (!rule) return AUTO_ADVANCE_SECONDS;
        const pref = Alpine.store("ui").prepDetail;
        if (pref === "none") return AUTO_ADVANCE_SECONDS;
        if (pref === "mistakes" && this.ruleMistakes(rule).length) {
          return MISTAKES_ADVANCE_SECONDS;
        }
        return RULES_ADVANCE_SECONDS;
      },
      // Persists the chosen detail tier and, from a graded result with a
      // rule card, restarts the countdown at the new tier's (longer) length
      // so revealing mistakes buys the learner the extra reading time.
      setPrepDetail(v) {
        Alpine.store("ui").setPrepDetail(v);
        if (this.state === "graded" && this.lastOutcome && this.lastOutcome.rule) {
          this.startAutoAdvance();
        }
      },
      // grammarLabel resolves a badge / rule-table key to its localized
      // label (case, gender, degree, number names). Terms without a
      // catalog entry (article/preposition rule badges like "der" or
      // "+ Dativ") pass through unchanged.
      grammarLabel(term) {
        const key = grammarLabelKey(term);
        const label = Alpine.store("i18n").t(key);
        return label === key ? term : label;
      },
      questionPrompt() {
        const q = this.currentQuestion;
        if (!q || this.state !== "graded") return q ? q.q[0] : "";
        return fillArticlePrompt(q.q[0], this.currentChoice);
      },
      // recapPrompt is the review-row sentence: an article Question shows its
      // correct answer in the blank, like the graded prompt.
      recapPrompt(w) {
        return fillArticlePrompt(w.prompt || "", w.correct_german);
      },
      // Rule-card text, in the learner's language where the catalogue has
      // it (see localizedRule). Mistakes are one catalogue string, one
      // mistake per line.
      ruleTitle(rule) { return ruleTitleText(rule); },
      ruleText(rule) { return ruleBodyText(rule); },
      ruleMistakes(rule) { return ruleMistakeLines(rule); },
      ruleBadge(rule) { return ruleBadgeText(rule, (term) => this.grammarLabel(term)); },
      ruleWarning(rule) { return ruleWarningText(rule); },
      // The adjective quiz ships its hint as an i18n key, not prose.
      helpText() {
        const help = this.currentQuestion && this.currentQuestion.help;
        return help ? Alpine.store("i18n").t(help) : "";
      },
      // ruleItems splits a numbered rule string ("1. ... 2. ... 3. ...")
      // into its parts so the card can render an ordered list. Falls back
      // to a single-item array when the text is not numbered. Lookahead
      // only (no lookbehind) for broad browser support.
      ruleItems(text) {
        if (!text) return [];
        const segs = text.match(/\d+\.\s+[\s\S]*?(?=\s+\d+\.\s|$)/g);
        if (!segs || segs.length < 2) return [text.trim()];
        return segs.map((s) => s.replace(/^\d+\.\s+/, "").trim());
      },
      startAutoAdvance() {
        this.clearAutoAdvance();
        // Trivia and reading-quiz prompts are prose: the learner moves on.
        if (this.type === "trivia" || this.type === "reading-quiz") return;
        this.nextCountdown = this.autoAdvanceSeconds;
        this._tickAutoAdvance();
      },
      // canPauseAutoAdvance mirrors the Next button's own condition: the
      // pause control is only live while a graded result is on screen with a
      // countdown left to stop (or already stopped by a previous pause).
      get canPauseAutoAdvance() {
        return this.state === "graded" && (this.nextPaused || this.nextCountdown > 0);
      },
      // Stop / resume the countdown without losing the remaining seconds -
      // unlike clearAutoAdvance (used by openReport / next / finish), which
      // ends it. Resuming continues from where it stopped.
      toggleAutoAdvancePause() {
        if (!this.canPauseAutoAdvance) return;
        if (this.nextPaused) {
          this.nextPaused = false;
          this._tickAutoAdvance();
          return;
        }
        this.nextPaused = true;
        if (this._nextTimer) {
          clearTimeout(this._nextTimer);
          this._nextTimer = null;
        }
      },
      _tickAutoAdvance() {
        this._nextTimer = setTimeout(() => {
          if (this.state !== "graded") {
            this.clearAutoAdvance();
            return;
          }
          this.nextCountdown -= 1;
          if (this.nextCountdown <= 0) {
            this.next();
            return;
          }
          this._tickAutoAdvance();
        }, 1000);
      },
      clearAutoAdvance() {
        if (this._nextTimer) {
          clearTimeout(this._nextTimer);
          this._nextTimer = null;
        }
        this.nextCountdown = 0;
        this.nextPaused = false;
      },

      openReport() {
        // Pause the auto-advance while the user types - losing the
        // current question mid-report would erase context. For dictation,
        // silence the clip so it does not play under the modal.
        this.clearAutoAdvance();
        this._stopClip();
        this.reportOpen = true;
        this.reportCategory = "wrong";
        this.reportDetails = "";
        this.reportError = null;
        this.reportSent = false;
        this.clearReportImage();
      },
      closeReport() {
        this.reportOpen = false;
        this.clearReportImage();
        // Replay the current segment the report interrupted (submit is instant
        // now, so there is no deferred advance to resume).
        if (this.isAudioPrompt && this.dictPhase === "sentences") this.playPart();
      },

      setReportImage(file) {
        if (!file) return;
        const t = (key) => Alpine.store("i18n").t(key);
        if (!REPORT_IMAGE_TYPES.includes(file.type)) {
          this.reportError = t("challenge.report.screenshotType");
          return;
        }
        if (file.size > REPORT_IMAGE_MAX_BYTES) {
          this.reportError = t("challenge.report.screenshotSize");
          return;
        }
        this.clearReportImage();
        this.reportError = null;
        this.reportImage = file;
        // blob: URL, not data:, so session replay never sees the bytes in the DOM.
        this.reportImageUrl = URL.createObjectURL(file);
      },
      clearReportImage() {
        if (this.reportImageUrl) URL.revokeObjectURL(this.reportImageUrl);
        this.reportImage = null;
        this.reportImageUrl = "";
      },
      onReportFile(event) {
        this.setReportImage(event.target.files && event.target.files[0]);
        event.target.value = "";
      },
      // Takes the first image on the clipboard; plain text pastes pass through.
      onReportPaste(event) {
        if (this.reportSent) return;
        const items = Array.from((event.clipboardData && event.clipboardData.items) || []);
        // A mixed clipboard (text + image from an office app) pastes its text into the textarea.
        if (event.target && event.target.tagName === "TEXTAREA" && items.some((it) => it.type === "text/plain")) return;
        const item = items.find((it) => it.kind === "file" && it.type.startsWith("image/"));
        if (!item) return;
        event.preventDefault();
        this.setReportImage(item.getAsFile());
      },

      // Screenshot rejections get translated text; anything else shows the server detail.
      reportErrorText(e) {
        const t = (key) => Alpine.store("i18n").t(key);
        const slug = problemSlug(e);
        if (slug === "report-screenshot-too-large") return t("challenge.report.screenshotSize");
        if (slug === "report-screenshot-limit") return t("challenge.report.screenshotLimit");
        if (slug === "report-screenshot-type") return t("challenge.report.screenshotType");
        return (e && e.problem && e.problem.detail) || (e && e.message) || "Failed to send report";
      },

      async submitReport() {
        const userId = window.Alpine.store("ui").userId;
        const q = this.currentQuestion;
        if (!userId || !q || !this.challenge) {
          this.reportError = "Cannot send report right now.";
          return;
        }
        const details = (this.reportDetails || "").trim();
        if (!details) {
          this.reportError = "Please describe the problem.";
          return;
        }
        this.reportPending = true;
        this.reportError = null;
        try {
          await api.createReport(userId, this.challenge.challenge_id, q.questionId, {
            solution: this.currentChoice || null,
            category: this.reportCategory,
            details,
            screenshot: this.reportImage,
          });
          this.reportSent = true;
        } catch (e) {
          console.warn("createReport:", e);
          this.reportError = this.reportErrorText(e);
        } finally {
          this.reportPending = false;
        }
      },

      async init() {
        const root = getRoot();
        this.scopeParam = urlPassiveScope();
        if (root && root.pendingChallengeType) {
          this.type = root.pendingChallengeType;
          // Note: we deliberately do NOT null root.pendingChallengeType.
          // The GAME screen heading reads it for the title / icon and
          // would go blank if we cleared it. Switching types is now
          // handled by the dt-challenge-type-changed event listener
          // (see index.html), so re-entry doesn't depend on the value
          // resetting.
          // Review actions prefetch a seeded challenge before mounting this
          // component. Consume it exactly once instead of replacing it with
          // an unrelated unseeded session.
          const session = window.Alpine.store("session");
          const prefetched = session.currentChallenge;
          session.currentChallenge = null;
          await this.start(prefetched);
        }
      },

      // Keyboard shortcuts:
      //   1..4    picks the matching option while playing
      //   A       (re)plays the current question's audio, in both
      //           playing and graded states - replay after grading is
      //           when learners most want to re-hear pronunciation.
      //           Honours the audio master toggle inside playAudio().
      //   Space   advances to the next question while graded. Deliberately
      //           inert on the summary: a press that lands just after the
      //           last question auto-finishes used to restart the round and
      //           skip the results screen (GH #599).
      //   P       pauses / resumes the auto-advance countdown, whenever
      //           the on-screen pause button is live
      // Bound via x-on:keydown.window on the challengePlayer root in
      // index.html. Ignored when a text field is focused (report
      // textarea), or when modifier keys are held.
      handleKey(e) {
        if (e.target && e.target === this.$refs?.dictInput) {
          if (this.state === "playing") this._hooks.key?.call(this, e);
          return;
        }
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.target && /^(INPUT|TEXTAREA|SELECT)$/i.test(e.target.tagName)) return;
        if (this.passiveScopeOpen) return; // the scope modal owns the keyboard
        if (e.key === "a" || e.key === "A") {
          if (!this.currentQuestion || !this.currentQuestion.audioUrl) return;
          e.preventDefault();
          this.playAudio();
          return;
        }
        if (e.key === "p" || e.key === "P") {
          if (!this.canPauseAutoAdvance) return;
          e.preventDefault();
          this.toggleAutoAdvancePause();
          return;
        }
        if (this.state === "graded" && e.key === " ") {
          e.preventDefault();
          this.next();
          return;
        }
        if (this.state === "playing") this._hooks.key?.call(this, e);
      },

      // canResume gates the reload-resume on renderers whose whole progress
      // is the question index. Everything else carries per-round state the
      // cached session does not describe, and - worse - every one of them
      // routes through beginSentences(), which resets idx
      // to 0 AFTER the resume set it. The round would then replay questions
      // the server has already solved, and SubmitAnswer answers a re-submit
      // with the stored verdict, so the learner would be shown the grade of
      // an answer they did not just give.
      //
      // Dictation is audio-to-text and pronunciation is speech, so
      // isAudioPrompt is what excludes those two.
      get canResume() {
        return !this.isMatching && !this.isSpellingBee && !this.isAudioPrompt;
      },

      // roundScope keys the remembered round on everything that shaped its
      // questions, so a resume can never hand back a differently-scoped round.
      // The header level is not part of it: a reload after a level switch
      // resumes the round, which keeps its own level. Course practice runs at
      // its lesson level, which is.
      currentRoundScope() {
        const root = getRoot();
        const isTrivia = this.type === "trivia";
        const variant = this.type === "regional-word" ? this.regions.join(",")
          : this.runScope ? "scope=" + this.runScope : "";
        return roundScope(
          this.type, root && root.coursePractice ? root.coursePractice.level : "",
          root && root.activeChallengeWordgroupId,
          isTrivia ? ((root && root.activeTriviaCategory) || "") : variant,
          (root && root.coursePractice && root.coursePractice.word_ids) || []);
      },

      // tryResume returns the remembered round when the server still has it,
      // and null otherwise. Never throws: an unavailable round is a normal
      // outcome (30-min TTL, sealed, cleared storage), not an error worth
      // showing, and the caller just starts a fresh one.
      async tryResume() {
        if (!this.canResume) return null;
        const saved = rememberedRound(this.currentRoundScope());
        if (!saved) return null;
        const run = this._dictRun;
        try {
          const r = await api.resumeChallenge(saved.challenge_id);
          if (!this._current(run)) return null;
          if (!r || !(r.questions || []).length) return null;
          // Nothing answered yet and the header level moved since the start:
          // the next round uses the new level, so start fresh.
          if (!r.resume_index && pointerHeaderDriven(saved)
            && saved.profile_level !== String(window.Alpine.store("ui").level).toUpperCase()) {
            forgetRound();
            return null;
          }
          this._resumePointer = saved;
          return r;
        } catch (e) {
          if (!this._current(run)) return null;
          // Terminal: the round is not coming back (expired past the 30-min
          // TTL, already sealed, or someone else's after a sign-out). Drop
          // the pointer and let the caller start a fresh round.
          if (e && (e.status === 404 || e.status === 403 || e.status === 401)) {
            forgetRound();
            return null;
          }
          // Retryable: offline, or the server is having a moment. The round
          // is probably still there, so keep the pointer and surface the
          // failure. Starting a fresh round here would orphan a live one and
          // put the learner back at 1/10 with no explanation - the exact
          // silent progress loss #666 is about.
          console.warn("resumeChallenge:", e);
          throw e;
        }
      },

      // The URL moved to another type or list while this player stayed
      // mounted (Back/Forward); the root already holds the new scope.
      rescope() {
        const root = getRoot();
        if (root && root.pendingChallengeType) this.type = root.pendingChallengeType;
        this.start();
      },

      abort() {
        this.clearAutoAdvance();
        this._hooks.reset.call(this);
        // Leaving or restarting ends the results break for the push opt-in (#986).
        if (this.$store?.notifications) this.$store.notifications.roundDone = false;
      },

      authChanged() {
        if (!["loading", "playing", "graded"].includes(this.state)) return;
        this.abort();
        forgetRound();
        const root = getRoot();
        if (root) {
          root.challengeDirty = false;
          root.challengeRoundLevel = "";
        }
        this.roundGone = true;
        this.error = t("challenge.submitForbidden");
        this.state = "idle";
      },

      async start(prefetched = null) {
        this.abort();
        const run = this._dictRun;
        this.state = "loading";
        this.idx = 0;
        this.questions = [];
        this.correctCount = 0;
        this.summary = null;
        this.lastOutcome = null;
        this.currentChoice = null;
        this.error = null;
        this.roundGone = false;
        this.readingBlocked = false;
        // Reset the navigation guard - a fresh session has no answers
        // submitted yet, so leaving is free until the user picks once.
        const root = getRoot();
        if (root) {
          root.challengeDirty = root.challengeFinished = false;
          root.challengeRoundLevel = "";
        }
        try {
          const isTrivia = this.type === "trivia";
          // Trivia plays at the profile level like every other challenge,
          // but the API's trivia levels are lowercase (a2/b1/b2) while
          // every other type's are uppercase; threads the active category too.
          const level = root && root.coursePractice
            ? root.coursePractice.level
            : isTrivia
            ? String(window.Alpine.store("ui").level).toLowerCase()
            : window.Alpine.store("ui").level;
          const sounds = window.Alpine.store("ui").audioEnabled;
          // Read before any await: a header switch while the round loads must
          // not become the start level.
          const headerLevel = String(window.Alpine.store("ui").level).toUpperCase();
          // Regional words needs the learner's Dialect regions: with none
          // (first start, or every stored one retired) ask instead.
          const regions = this.type === "regional-word" && root ? await root.regionalWordRegions() : [];
          if (!this._current(run)) return;
          this.regions = regions;
          if (root) root.roundRegions = regions;
          if (this.type === "regional-word" && !regions.length) {
            if (root) root.openRegionPicker();
            this.state = "idle";
            return;
          }
          // A reload lands here with the same type and a remembered id. Ask
          // the server for that round back before spending a new one; any
          // failure (expired, sealed, someone else's) falls through to a
          // fresh round, which is what used to happen unconditionally.
          const resumed = prefetched ? null : await this.tryResume();
          if (!this._current(run)) return;
          const c = prefetched || resumed || await api.startChallenge(
            this.type, level, sounds, root && root.activeChallengeWordgroupId,
            isTrivia ? ((root && root.activeTriviaCategory) || "") : "",
            (root && root.coursePractice && root.coursePractice.word_ids) || [], regions, this.runScope);
          if (!this._current(run)) return;
          this.challenge = c;
          if (this.type === "passive") this.loadPassiveScope();
          const pointer = resumed ? this._resumePointer : null;
          const startLevel = (pointer && pointer.profile_level)
            || (prefetched && prefetched.startLevel) || headerLevel;
          const headerDriven = pointer
            ? pointerHeaderDriven(pointer)
            : !(root && root.coursePractice)
              && (prefetched ? !!prefetched.headerDriven : this.type !== "reading-quiz");
          if (root) {
            root.challengeRoundLevel = String(c.level || level).toUpperCase();
            root.challengeStartLevel = startLevel;
            root.challengeHeaderDriven = headerDriven;
          }
          this.questions = (c.questions || []).map((q) =>
            normalizeQuestion(q, c.level || level));
          if (resumed) {
            // Clamp: the client must never index past its own question list
            // even if the two ever disagree about the round's length.
            this.idx = Math.min(resumed.resume_index || 0, this.questions.length);
            this.correctCount = resumed.correct_count || 0;
            // Answers already submitted, so leaving costs something again.
            if (this.idx > 0 && root) root.challengeDirty = true;
          }
          rememberRound(c.challenge_id, this.currentRoundScope(), startLevel, headerDriven);
          this.state = this.questions.length ? "playing" : "summary";
          // Every question was answered but /completions never landed (the
          // reload happened in that gap). Seal it rather than showing a
          // playing screen with nothing left to play.
          if (resumed && this.idx >= this.questions.length && this.questions.length) {
            await this.finish();
            if (!this._current(run)) return;
            // finish() leaves state on "playing" when /completions failed.
            // idx is past the last question, so the playing screen would
            // render a blank prompt card reading 11/10 with no way out.
            // Park on the summary instead; the error banner still shows and
            // the round stays remembered, so a reload retries the seal.
            if (this.state !== "summary") this.state = "summary";
            return;
          }
          this._shownAt = Date.now();
          this._hooks.begin?.call(this);
        } catch (e) {
          if (!this._current(run)) return;
          console.error("startChallenge:", e);
          // A reading-quiz reached by URL (reload, history) starts a new round
          // on the reading route. Its refusals have client copy, and a retry
          // cannot help: the way out is the feed (readingBlocked).
          const slug = problemSlug(e);
          const reading = this.type === "reading-quiz";
          if (reading && readingSignInLost(e)) {
            this.error = t("lesen_choice.signIn.title");
            this.readingBlocked = true;
          } else if (reading && slug === "reading-not-enough-ready") {
            this.error = t("reading.choice.notEnough", { count: READING_QUESTION_PIECES });
            this.readingBlocked = true;
          } else if (slug === "challenge-content-unavailable" && root && !root.coursePractice) {
            root.setScreen("challenge_unavailable");
            root.pushRouteForScreen();
          } else {
            this.error = (e.problem && e.problem.detail) || e.message || "Failed to start challenge";
          }
          this.state = "idle";
        }
      },

      async pick(choice) {
        // One answer per Question: a click while the submit (or its retry)
        // is in flight must not send a second one.
        if (this.state !== "playing" || this._picking) return;
        this.error = null; // a prior transient submit error must not persist into a fresh pick
        this.currentChoice = choice;
        const q = this.questions[this.idx];
        // _dictRun is the shared session-generation token (bumped by
        // _resetDictation on start/abort): a late submit from a torn-down
        // round must not forgetRound() the replacement.
        const run = this._dictRun;
        this._picking = true;
        try {
          const outcome = await api.submitAnswer(
            this.challenge.challenge_id, q.questionId, choice, () => this._current(run),
            this._shownAt ? Date.now() - this._shownAt : undefined);
          if (!this._current(run)) return;
          this.lastOutcome = outcome;
          if (this.lastOutcome && this.lastOutcome.correct) this.correctCount++;
          // Any submitted answer marks the session "dirty" so a stray
          // nav click or type switch asks for confirmation. Cleared on
          // start() (new round) and finish() (completed).
          const root = getRoot();
          if (root) root.challengeDirty = true;
          // Fill the answer slot so the template's graded styling can
          // diff "user picked X, correct is Y" without a second round-
          // trip. Mutates the cached question in-place; safe because the
          // session is one-way.
          q.answer = this.lastOutcome && this.lastOutcome.correct_answer;
          // A question can have several right answers: a gender-ambiguous noun
          // (der/das Blog) or an adjectival noun (der/die Angestellte) accepts
          // any of its articles. correct_answers is absent when there is only
          // one, so fall back to the canonical answer.
          q.answers = (this.lastOutcome && this.lastOutcome.correct_answers)
            || (q.answer ? [q.answer] : []);
          this.state = "graded";
          this.startAutoAdvance();
        } catch (e) {
          if (!this._current(run)) return;
          console.error("submitAnswer:", e);
          this.error = this.submitError(e, "Failed to submit");
        } finally {
          // A stale submit must not release the replacement round's guard.
          if (this._current(run)) this._picking = false;
        }
      },

      // A 403 (changed identity) or 404 (expired session) needs a fresh round.
      // A session-store-unavailable that outlived the submit retry means the
      // answer was not saved. Completion passes answerSubmit = false: its
      // answers already are.
      submitError(e, fallback, answerSubmit = true) {
        if (e instanceof ApiError && (e.status === 403 || e.status === 404)) {
          forgetRound();
          this.roundGone = true;
          return t(e.status === 403 ? "challenge.submitForbidden" : "challenge.submitExpired");
        }
        if (answerSubmit && isSessionStoreUnavailable(e)) return t("challenge.submitUnsaved");
        return (e.problem && e.problem.detail) || e.message || fallback;
      },

      // Display label for a multi-choice option. The plural challenge emits
      // "die -" for nouns with no plural form; show a translatable label
      // instead. The raw value is still what gets submitted/graded.
      optionLabel(option) {
        return option === "die -" ? t("challenge.noPlural") : option;
      },
      // isAccepted reports whether an option is one of the right answers, so
      // the graded view marks every one instead of greening the canonical
      // answer and reddening an equally correct pick.
      isAccepted(option) {
        const q = this.currentQuestion;
        if (!q) return false;
        if (Array.isArray(q.answers) && q.answers.length) return q.answers.includes(option);
        return q.answer === option;
      },
      // vulgarOption: a choice that shows a vulgar Meaning, highlighted for
      // learners with 18+ content on, and only once graded: earlier it would
      // be a cue.
      vulgarOption(option) {
        const out = this.lastOutcome;
        return this.state === "graded" && Boolean(out && (out.vulgar_choices || []).includes(option))
          && Boolean(window.Alpine.store("ui").matureContent);
      },
      // answersLabel lists the right answers for the "correct answer was..."
      // line, so a multi-answer question names all of them.
      answersLabel() {
        const q = this.currentQuestion;
        const all = (q && Array.isArray(q.answers) && q.answers.length) ? q.answers : [q && q.answer];
        return all.filter(Boolean).map((a) => this.optionLabel(a)).join(" / ");
      },

      // ---- Passive voice: wrong-tap lines, scope pill and modal, summary ----

      // The chosen and the canonical sentence, each with its differing words marked.
      get passiveDiff() {
        const q = this.currentQuestion;
        const segs = diffTokens(this.currentChoice, q && q.answer);
        return {
          chosen: segs.filter((s) => s.cls !== DIFF_SUCCESS),
          correct: segs.filter((s) => s.cls !== DIFF_ERROR),
        };
      },
      // The scope marker is per ladder level, which the start response names
      // (C2 plays the C1 ladder).
      async loadPassiveScope() {
        const run = this._dictRun;
        const level = this.challenge && this.challenge.passive && this.challenge.passive.level;
        if (!level) return;
        try {
          const scope = await api.getScopeMarker("PASSIVE_VOICE", level);
          if (this._current(run)) this.passiveScope = scope;
        } catch (e) {
          console.warn("getScopeMarker:", e); // the pill stays hidden; the round runs anyway
        }
      },
      // One dot per form of the level, filled up to the scope this round serves
      // (the start response); the saved scope moves the pill from the next round.
      passiveDots() {
        const s = this.passiveScope;
        if (!s) return "";
        const n = s.forms.length;
        const served = this.challenge && this.challenge.passive && this.challenge.passive.scope;
        const k = Math.max(0, Math.min(served || this.runScope || s.effective, n));
        return "●".repeat(k) + "○".repeat(n - k);
      },
      // The modal rows: Automatic (value 0), then one per form.
      get passiveScopeRows() {
        const s = this.passiveScope;
        if (!s) return [];
        const n = s.forms.length;
        const auto = { value: 0, label: "", name: t("challenge.passive.scope.auto", { k: s.marker, n }), stats: "", recommended: false };
        return [auto, ...s.forms.map((r) => {
          const st = (s.stats || []).find((x) => x.form === r.form);
          return {
            value: r.index,
            label: `${r.index}/${n}`,
            name: r.name,
            stats: st && st.recent_count > 0 ? `${st.recent_correct}/${st.recent_count}` : "",
            recommended: r.index === s.marker,
          };
        })].map((row) => ({ ...row, selected: row.value === s.chosen }));
      },
      openPassiveScope() {
        this.passiveScopeError = "";
        this.passiveScopeOpen = true;
      },
      closePassiveScope() {
        this.passiveScopeOpen = false;
      },
      // Saves the setting (0 = automatic); it applies from the next round.
      async choosePassiveScope(n) {
        if (!this.passiveScope) return;
        this.passiveScopeError = "";
        try {
          this.passiveScope = await api.patchScopeMarker("PASSIVE_VOICE", this.passiveScope.level, n);
          this.passiveScopeOpen = false;
        } catch (e) {
          console.warn("patchScopeMarker:", e);
          this.passiveScopeError = (e.problem && e.problem.detail) || e.message || "Failed to save";
        }
      },
      get passivePractised() {
        const p = this.summary && this.summary.passive;
        return p ? t("challenge.passive.practised", { forms: (p.forms_practised || []).join(", ") }) : "";
      },
      // The form the result screen recommends adding, while the learner's
      // explicit choice stays below the marker; "" otherwise.
      get passiveRecommendation() {
        const p = this.summary && this.summary.passive;
        const s = this.passiveScope;
        if (!p || !p.recommend_up || !s || !(s.chosen > 0 && s.chosen < p.marker)) return "";
        const form = s.forms.find((r) => r.index === p.marker);
        return form ? form.name : "";
      },

      next() {
        // Idempotent guard: the auto-advance timer and a Space/click can
        // race when the countdown hits 0 at the same moment the user
        // hits the key. Ignoring the second call keeps idx correct.
        if (this.state !== "graded") return;
        this.clearAutoAdvance();
        // On the last question, hand straight off to finish() WITHOUT
        // clearing the graded display state first. finish() is async (it
        // awaits the completion call), and the graded card stays mounted
        // until state flips to "summary"; nulling currentChoice/lastOutcome
        // here would leave a render window where the card shows currentChoice ===
        // null while currentQuestion.answer is still set, which the template
        // renders as a false "wrong" mark (red ✗ + "Correct:" hint).
        // Stop any in-flight audio so a clip does not bleed into the summary.
        if (this.idx + 1 >= this.questions.length) {
          this._stopClip();
          this.finish();
          return;
        }
        this.currentChoice = null;
        this.lastOutcome = null;
        this.showHelp = false;
        this._hooks.reset.call(this);
        this.idx++;
        this._shownAt = Date.now();
        this.state = "playing";
        this.maybeAutoplay();
      },

      async finish() {
        if (this._finishing) return;
        this._finishing = true;
        const run = this._dictRun;
        try {
          await this._finish();
        } finally {
          // A stale finish must not release the replacement round's guard.
          if (this._current(run)) this._finishing = false;
        }
      },

      async _finish() {
        // _dictRun doubles as a session-generation token: if the round is torn
        // down (abort / restart / type switch, all via _resetDictation) while
        // completeChallenge is in flight, the stale response must not force the
        // summary over the new session.
        const run = this._dictRun;
        this.clearAutoAdvance();
        // Dictation grades each segment in the background; wait for every grade
        // to land (server-persisted) before completeChallenge reads the score.
        if (this._pending && this._pending.length) {
          await Promise.allSettled(this._pending);
          if (!this._current(run)) return;
        }
        // Retry any submit that was dropped, so the score is not computed from a
        // missing answer. A session-store-unavailable that outlives the submit
        // retry keeps the entry (and its payload) for "Retry finish" and stops
        // before completion; any other failure falls back to the server scoring
        // that segment 0.
        if (this._failedSubmits && this._failedSubmits.length) {
          const retries = this._failedSubmits;
          this._failedSubmits = [];
          this.error = null; // hides "Retry finish", shows the spinner while resending
          const stillCurrent = () => this._current(run);
          await Promise.allSettled(retries.map((f) =>
            (f.audio
              ? api.submitAnswerAudio(this.challenge.challenge_id, f.questionId, f.blob, f.durationMs, stillCurrent)
              : api.submitAnswer(this.challenge.challenge_id, f.questionId, f.typed, stillCurrent)
            ).then((out) => {
              if (!this._current(run)) return;
              if (!out) { f.entry.pending = false; f.entry.verdict = "fail"; return; }
              f.entry.verdict = out.verdict || "fail";
              f.entry.reference = out.reference || out.correct_answer || "";
              f.entry.diff = out.diff || null;
              if (f.audio) f.entry.typed = out.transcription || "";
              f.entry.pending = false;
              if (out.correct) this.correctCount++;
            }).catch((e) => {
              console.error("submitPart retry:", e);
              if (this._current(run) && isSessionStoreUnavailable(e)) {
                this._failedSubmits.push(f);
                return;
              }
              f.entry.pending = false;
              // The server now replays the stored verdict (2xx) on a lost-
              // response retry. A 409 only happens against an old instance mid
              // rolling-deploy that still uses the pre-replay contract; the
              // segment WAS persisted there, so leave its verdict unknown rather
              // than a false fail. Any other error: genuinely unanswered -> fail.
              if (!(e && e.status === 409)) f.entry.verdict = "fail";
            })));
          if (!this._current(run)) return;
          if (this._failedSubmits.length) {
            this.error = t("challenge.submitUnsaved");
            return;
          }
        }
        this.error = null;
        let summary = null;
        try {
          summary = await api.completeChallenge(this.challenge.challenge_id);
        } catch (e) {
          console.warn("completeChallenge:", e);
          if (!this._current(run)) return; // session torn down mid-completion
          this.error = this.submitError(e, "Failed to complete challenge", false);
          return;
        }
        if (!this._current(run)) {
          // Session torn down mid-completion: no summary to show, but the
          // round still scored, and the stats are root state (#968).
          const root = getRoot();
          if (root && typeof root.refreshAllStats === "function") root.refreshAllStats();
          return;
        }
        forgetRound(); // sealed: a later reload starts a new round, not this one
        this.summary = summary; // publish only for the still-current session
        this.state = "summary";
        if (this.$store?.notifications) this.$store.notifications.roundDone = true;
        const root = getRoot();
        if (root) {
          // Session is done - no more "in progress" guard. Summary
          // screen is free to leave without a prompt.
          root.challengeDirty = false;
          root.challengeFinished = true;
          if (root.coursePractice) root.coursePractice.completed = true;
          // A deep-link scope ran its round: Play again and a reload use the setting.
          if (this.type === "passive" && this.scopeParam) {
            this.scopeParam = 0;
            if (root.replaceRouteForScreen) root.replaceRouteForScreen();
          }
          if (root.refreshAllStats) {
            // refreshAllStats owns the before/after comparison (it is the only
            // place holding both scores); the summary just consumes the
            // verdict. Promise.resolve wraps it because the refresh lands well
            // after the summary paints, and the round can be torn down in
            // between - the generation check drops a verdict that no longer
            // belongs to the mounted session.
            this.goalJustMet = false;
            Promise.resolve(root.refreshAllStats()).then((crossedGoal) => {
              if (!this._current(run)) return; // session torn down mid-refresh
              this.goalJustMet = !!crossedGoal;
            });
          }
        }
      },

      // dayGoal mirrors the root component's method so the summary can show
      // where the round left the day. Reads the store, so it repaints when
      // the post-finish refreshAllStats() lands - the bar visibly fills by
      // this round's points instead of arriving pre-filled.
      dayGoal() {
        return dayGoalProgress(this.$store.session.statsToday.score, this.$store.ui.dailyGoal);
      },

      // Trivia scores 0 only below half correct (stats.ScoreChallenge), so the
      // hint names exactly that rule; empty for any other result.
      get zeroPointsHint() {
        if (this.type !== "trivia" || !this.summary || this.summary.score !== 0) return "";
        const { total } = this.summary;
        return t("challenge.summary.triviaNoPoints", { needed: Math.ceil(total / 2), total });
      },

      // reviewHeadingKey names what the recap's missed list holds. Trivia and
      // reading questions miss whole questions (the full clue or prompt plus
      // the answers), word order and passive miss sentences; every other recap
      // lists words (issue #973).
      get reviewHeadingKey() {
        if (this.type === "trivia" || this.type === "reading-quiz") return "challenge.questionsToReview";
        if (this.type === "word-order" || this.type === "passive") return "challenge.sentencesToReview";
        return "challenge.wordsToReview";
      },

      summaryPct() {
        if (!this.questions.length) return 0;
        const total = (this.summary && this.summary.total) || this.questions.length;
        const correct = (this.summary && this.summary.correct) ?? this.correctCount;
        if (!total) return 0;
        return Math.round((correct / total) * 100);
      },

      restart() {
        const root = getRoot();
        // Sticky Random mode: each Restart rolls a fresh real type (excluding
        // the last) so consecutive sessions differ. Mirrored on root so the
        // challenge header re-renders with the new icon / label.
        if (root && root.randomMode) {
          const next = pickRandomChallengeType(this.type, root.$store.ui.level);
          this.type = next;
          root.pendingChallengeType = next;
          this.start();
          return;
        }
        const type = GAME_TYPES.find((candidate) => candidate.id === this.type);
        if (root && challengeAvailability(type, root.$store.ui.level)) {
          root._performStartChallenge(this.type);
          return;
        }
        if (this.type) {
          this.start();
          return;
        }
        // No type set yet - try to pick one off the root component.
        if (root && root.pendingChallengeType) {
          this.type = root.pendingChallengeType;
          this.start();
        } else {
          this.state = "idle";
        }
      },

      home() {
        const root = getRoot();
        if (root) root.go("home");
      },

      // The reading-quiz summary goes back to the feed instead of "Play
      // again": a new round needs 3 newly read Pieces.
      keepReading() {
        const root = getRoot();
        if (root) root.go(readingFeedScreen());
      },
    };
  };

  // ------------------------------------------------------------------
  // Compete (real-time head-to-head, GH #48)
  // ------------------------------------------------------------------

  // competePlayer drives the whole Compete screen: setup (mode / type /
  // level + quick match / room create / room join), the matchmaking
  // queue, and live play. Live play is poll-driven: the server is the
  // sole source of truth and every ~1s poll of the match-state endpoint
  // returns the per-viewer view (opponent picks land live; the correct
  // answer only appears once the question resolves for both players).
  // Countdowns and timer bars use the server clock via serverOffsetMs
  // so a skewed local clock cannot lie about remaining time.
  const COMPETE_POLL_MS = 1000;
  const COMPETE_QUEUE_POLL_MS = 1500;
  // Mirrors roomCodeAlphabet in lib/compete/service.go.
  const COMPETE_ROOM_CODE_RE = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}$/;

  window.competePlayer = function () {
    return {
      // "loading" | "setup" | "queue" | "match"
      phase: "loading",
      mode: "speed",
      typeId: "vocabulary",
      level: null,
      joinCode: "",
      inviteFriend: null, // {user_id, display_name} target for a room invite
      inviteSentTo: null, // display name once a room invite went out
      match: null,        // last StateResponse from the server
      serverOffsetMs: 0,
      nowMs: Date.now(),
      pollFailures: 0,
      _pollBusy: false, // one in-flight state fetch at a time
      _stateSeq: 0,     // bumps on every applied state; stale polls are dropped
      busy: false,
      error: "",
      _pollTimer: null,
      _queueTimer: null,
      _queueBusy: false,
      _tickTimer: null,
      _gone: false,

      // Only the levels the chosen type is offered at; the server answers
      // 503 challenge-content-unavailable for the rest.
      get levels() {
        const g = GAME_TYPES.find((x) => x.id === this.typeId);
        return LEVELS.filter((l) => !challengeAvailability(g, l.id));
      },

      // Multi-choice types only - Compete has no grader pipeline for
      // speech/LLM types. Mirrors the server-side allowlist.
      get competeTypes() {
        return GAME_TYPES.filter((g) => g.input_mode === "multi-choice" && g.id !== "random" && !g.standalone && !g.needs_regions)
          .map((g) => ({ ...g, label: t(`challengeType.${g.id}.label`) }));
      },
      get queueTypeLabel() {
        const g = this.competeTypes.find((x) => x.id === this.typeId);
        return g ? g.label : this.typeId;
      },
      // The CHALLENGE_TYPE value of the chosen type.
      get backendType() {
        const g = GAME_TYPES.find((x) => x.id === this.typeId);
        return g ? g.challenge_type : "";
      },

      async init() {
        this.level = this.$store.ui.level || "A1";
        if (typeof this.$watch === "function") {
          this.$watch("typeId", () => {
            const ids = this.levels.map((l) => l.id);
            if (ids.includes(this.level)) return;
            const order = LEVELS.map((l) => l.id);
            this.level = order.indexOf(this.level) < order.indexOf(ids[0]) ? ids[0] : ids[ids.length - 1];
          });
        }
        // Friends-screen handoff: pre-target the friend for an invite.
        const root = window.__dtRoot;
        if (root && root.competeInviteFriend) {
          this.inviteFriend = root.competeInviteFriend;
          root.competeInviteFriend = null;
        }
        const deepLinkCode = root && root.competeJoinCode;
        if (deepLinkCode) {
          root.competeJoinCode = null;
          try { history.replaceState({}, "", "/compete"); } catch { /* ignore */ }
        }
        // Alpine 3.17 calls destroy() on x-if unmount; the screen watch is a
        // second trigger. Navigating off the Compete screen must stop every
        // timer or the 1 Hz match poll would keep hitting the API forever.
        if (typeof this.$watch === "function") {
          this.$watch("screen", (value) => {
            if (value !== "compete") this.destroy();
          });
        }
        // Server-clock ticker for countdown / timer rendering.
        this._tickTimer = setInterval(() => { this.nowMs = Date.now(); }, 200);
        // Reconnect: resume a live match if one exists. The user may
        // navigate away while this request is in flight - a late
        // response must not restart the poll loop after destroy().
        try {
          const state = await api.competeActiveMatch();
          if (this._gone) return;
          // Recent results are also returned by the reconnect endpoint.
          // Only a live match should take priority over a room invitation.
          if (state && state.duel_id && (!deepLinkCode || !this.isStateTerminal(state))) {
            this.enterMatch(state);
            return;
          }
        } catch (e) {
          // 404 = no active match; anything else falls through to setup.
          if (!(e instanceof ApiError) || e.status !== 404) {
            console.warn("compete active-match probe failed:", e);
          }
        }
        if (this._gone) return;
        this.phase = "setup";
        if (deepLinkCode) {
          this.joinCode = deepLinkCode;
          if (root.flags && root.flags.compete_enabled) await this.joinByCode();
        }
      },

      // Idempotent teardown: called by Alpine on x-if unmount and by
      // the screen watcher in init().
      destroy() {
        const wasQueued = !this._gone && this.phase === "queue";
        this._gone = true;
        this.stopPolling();
        this.stopQueuePolling();
        if (this._tickTimer) { clearInterval(this._tickTimer); this._tickTimer = null; }
        // Navigating away mid-queue means leaving it: retract the server
        // entry (best-effort - the 60s TTL is the backstop) so the user
        // can't be paired into a match they never see.
        if (wasQueued) { api.competeLeaveQueue().catch(() => {}); }
      },

      stopPolling() {
        if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; }
      },
      stopQueuePolling() {
        if (this._queueTimer) { clearInterval(this._queueTimer); this._queueTimer = null; }
      },

      friendlyError(e) {
        if (e instanceof ApiError) {
          return e.problem.detail || t("settings.error.requestFailed", { status: e.status });
        }
        return t("settings.error.network");
      },

      // The one-active-match rule surfaces as a 409 whose detail is the
      // live match id: resume it instead of erroring out.
      async resumeFromConflict(e) {
        if (e instanceof ApiError && e.status === 409 &&
            (e.problem.type || "").endsWith("duel-already-running") && e.problem.detail) {
          try {
            const state = await api.competeMatchState(e.problem.detail);
            this.enterMatch(state);
            return true;
          } catch { /* fall through to the generic error */ }
        }
        return false;
      },

      // ----- setup actions -------------------------------------------

      async quickMatch() {
        if (this.busy) return;
        this.busy = true;
        this.error = "";
        this.inviteSentTo = null;
        try {
          const resp = await api.competeQuickMatch(this.mode, this.backendType, this.level);
          if (this._gone) {
            // Torn down mid-request; destroy() saw phase "setup" so it
            // did not dequeue - retract the entry this response created.
            if (!(resp.matched && resp.duel)) api.competeLeaveQueue().catch(() => {});
            return;
          }
          if (resp.matched && resp.duel) {
            this.enterMatch(resp.duel);
          } else {
            this.phase = "queue";
            this.startQueuePolling();
          }
        } catch (e) {
          if (!(await this.resumeFromConflict(e))) this.error = this.friendlyError(e);
        } finally {
          this.busy = false;
        }
      },

      startQueuePolling() {
        this.stopQueuePolling();
        this._queueBusy = false;
        this._queueTimer = setInterval(async () => {
          if (this._gone || this.phase !== "queue") { this.stopQueuePolling(); return; }
          if (this._queueBusy) return; // previous poll still in flight
          this._queueBusy = true;
          try {
            const resp = await api.competeQueueStatus();
            // A slow response can land after a faster poll already
            // entered the match (or the user cancelled); a stale answer
            // must not act on the departed queue phase.
            if (this._gone || this.phase !== "queue") return;
            if (resp.matched && resp.duel) {
              this.stopQueuePolling();
              this.enterMatch(resp.duel);
            }
          } catch (e) {
            if (this._gone || this.phase !== "queue") return;
            if (e instanceof ApiError && e.status === 404) {
              // Queue entry expired server-side.
              this.stopQueuePolling();
              this.phase = "setup";
              this.error = t("compete.error.queueExpired");
            }
          } finally {
            this._queueBusy = false;
          }
        }, COMPETE_QUEUE_POLL_MS);
      },

      // Leave the queue server-side FIRST: only a confirmed dequeue may
      // stop the polling and return to setup, otherwise the entry would
      // linger and the user could be paired into a match they think
      // they cancelled. The DELETE answers 204 even when the entry was
      // already consumed, so always probe for the Duel the user may have
      // just been pulled into instead of hiding it.
      async cancelQueue() {
        if (this.busy) return;
        this.busy = true;
        this.error = "";
        try {
          await api.competeLeaveQueue();
          // The entry may already be consumed - possibly by a concurrent
          // pairing - so probe for the Duel the user may have just been
          // pulled into instead of hiding it.
          try {
            const state = await api.competeActiveMatch();
            if (state && state.duel_id && !this.isStateTerminal(state)) {
              this.stopQueuePolling();
              this.enterMatch(state);
              return;
            }
          } catch { /* no live Duel - fall through to setup */ }
          this.stopQueuePolling();
          this.phase = "setup";
        } catch (e) {
          this.error = this.friendlyError(e);
        } finally {
          this.busy = false;
        }
      },

      isStateTerminal(state) {
        return !!state && (state.status === "finished" || state.status === "abandoned");
      },

      async createRoom() {
        if (this.busy) return;
        this.busy = true;
        this.error = "";
        try {
          const invitee = this.inviteFriend ? this.inviteFriend.user_id : null;
          const state = await api.competeCreateRoom(this.mode, this.backendType, this.level, invitee);
          this.inviteSentTo = this.inviteFriend ? this.inviteFriend.display_name : null;
          this.enterMatch(state);
        } catch (e) {
          if (!(await this.resumeFromConflict(e))) this.error = this.friendlyError(e);
        } finally {
          this.busy = false;
        }
      },

      async joinByCode() {
        const code = (this.joinCode || "").trim().toUpperCase();
        if (!code || this.busy) return;
        if (!COMPETE_ROOM_CODE_RE.test(code)) {
          this.error = t("compete.error.invalidCode");
          return;
        }
        this.busy = true;
        this.error = "";
        try {
          const state = await api.competeJoinRoom(code);
          this.enterMatch(state);
        } catch (e) {
          if (!(await this.resumeFromConflict(e))) {
            this.error = (e instanceof ApiError && e.status === 404)
              ? t("compete.error.noParty")
              : this.friendlyError(e);
          }
        } finally {
          this.busy = false;
        }
      },

      // ----- live match ----------------------------------------------

      enterMatch(state) {
        // A terminal state without a result is a cancelled lobby (the
        // server only builds results for matches that started): nothing
        // to show, straight back to setup.
        if (this.isStateTerminal(state) && !state.result) {
          this.stopPolling();
          this.phase = "setup";
          return;
        }
        this.applyState(state);
        this.phase = "match";
        this.error = "";
        // A terminal state (e.g. the reconnect endpoint serving a
        // just-ended match's result) renders without a poll loop.
        if (!this.isTerminal) this.startPolling();
      },

      startPolling() {
        if (this._gone) return; // torn down: never re-arm the loop
        this.stopPolling();
        this._pollTimer = setInterval(() => this.poll(), COMPETE_POLL_MS);
      },

      async poll() {
        if (this._gone || this.phase !== "match" || !this.match) return;
        if (this.isTerminal) { this.stopPolling(); return; }
        if (this._pollBusy) return; // previous tick's response still in flight
        this._pollBusy = true;
        const seq = this._stateSeq;
        try {
          const state = await api.competeMatchState(this.match.duel_id);
          // Drop out-of-order responses: if fresher state landed while
          // this request was in flight (an answer submission, a faster
          // poll), applying this one would rewind the UI.
          if (!this._gone && seq === this._stateSeq) this.applyState(state);
          this.pollFailures = 0;
          // Terminal without a result = the lobby was cancelled before
          // the game started; there is no result screen to park on.
          if (this.isTerminal && this.match && !this.match.result) {
            this.stopPolling();
            this.phase = "setup";
            this.error = t("compete.error.cancelled");
          }
        } catch (e) {
          this.pollFailures++;
          if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
            // Match evaporated (or access revoked): back to setup.
            this.stopPolling();
            this.phase = "setup";
            this.error = t("compete.error.gone");
          }
        } finally {
          this._pollBusy = false;
        }
      },

      applyState(state) {
        this._stateSeq++; // invalidates any poll response still in flight
        this.match = state;
        if (state && state.server_now) {
          this.serverOffsetMs = new Date(state.server_now).getTime() - Date.now();
        }
        if (this.isTerminal) this.stopPolling();
      },

      async ready() {
        if (this.busy || !this.match) return;
        this.busy = true;
        try {
          this.applyState(await api.competeReady(this.match.duel_id));
        } catch (e) {
          this.error = this.friendlyError(e);
        } finally {
          this.busy = false;
        }
      },

      async pickOption(choice) {
        const m = this.match;
        if (!m || m.status !== "playing" || !m.question || this.busy) return;
        if (m.reveal || (m.you && m.you.picked)) return;
        this.busy = true;
        try {
          this.applyState(await api.competeAnswer(m.duel_id, m.question.index, choice));
        } catch (e) {
          // Conflicts (too late / already answered / question advanced)
          // resolve themselves on the next poll - no error banner needed.
          if (!(e instanceof ApiError && e.status === 409)) {
            this.error = this.friendlyError(e);
          }
          await this.poll();
        } finally {
          this.busy = false;
        }
      },

      async leaveMatch() {
        if (!this.match || this.busy) return;
        // busy locks out pickOption() and the number-key shortcuts, so a
        // final answer can't race the forfeit it would contradict.
        this.busy = true;
        this.stopPolling();
        try {
          const state = await api.competeLeave(this.match.duel_id);
          this.applyState(state);
          // A pre-game cancel has nothing to show - jump straight back.
          if (!state.result || state.result.outcome === "abandoned") {
            this.backToSetup();
            return;
          }
          this.phase = "match"; // stay to show the forfeit result
        } catch (e) {
          if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
            // The match is already gone (or access revoked): nothing to
            // forfeit, back to setup.
            this.backToSetup();
            return;
          }
          // Transient failure: the server still holds the match live
          // (one-active-match rule), so stay on it, surface the error,
          // and resume polling instead of silently stranding the player.
          this.error = this.friendlyError(e);
          this.startPolling();
        } finally {
          this.busy = false;
        }
      },

      backToSetup() {
        this.stopPolling();
        this.match = null;
        this.inviteSentTo = null;
        this.joinCode = "";
        this.phase = "setup";
        this.error = "";
      },

      async copyRoomCode() {
        const code = this.match && this.match.duel_code;
        if (!code || !navigator.clipboard) return;
        try { await navigator.clipboard.writeText(code); } catch { /* best-effort */ }
      },

      playQuestionAudio() {
        const url = this.match && this.match.question && this.match.question.audio_url;
        if (!url) return;
        const lvl = (this.match && this.match.level) || this.level;
        try { newLevelAudio(url, lvl).play().catch(() => {}); } catch { /* no audio */ }
      },

      // Number keys 1-4 pick the matching option while a question is open.
      handleKey(ev) {
        if (this.phase !== "match" || !this.match || this.match.status !== "playing") return;
        const q = this.match.question;
        if (!q || this.match.reveal) return;
        const n = parseInt(ev.key, 10);
        if (Number.isInteger(n) && n >= 1 && n <= (q.choices || []).length) {
          this.pickOption(q.choices[n - 1]);
        }
      },

      // ----- derived rendering state ---------------------------------

      get serverNowMs() {
        return this.nowMs + this.serverOffsetMs;
      },
      get isTerminal() {
        const s = this.match && this.match.status;
        return s === "finished" || s === "abandoned";
      },
      get countdownSeconds() {
        if (!this.match || !this.match.countdown_ends_at) return 0;
        const left = new Date(this.match.countdown_ends_at).getTime() - this.serverNowMs;
        return Math.max(0, Math.ceil(left / 1000));
      },
      get questionTimeLeftMs() {
        const q = this.match && this.match.question;
        if (!q || !q.deadline_at) return 0;
        return Math.max(0, new Date(q.deadline_at).getTime() - this.serverNowMs);
      },
      get questionTimeLeftPct() {
        const limit = (this.match && this.match.question_time_limit_ms) || 1;
        return Math.max(0, Math.min(100, (this.questionTimeLeftMs / limit) * 100));
      },
      get questionTimeLeftSeconds() {
        return Math.ceil(this.questionTimeLeftMs / 1000);
      },
      // Big score readout: rounds in speed mode, points in points mode.
      get yourMetric() {
        if (!this.match) return 0;
        const p = this.match.you || {};
        return this.match.mode === "speed" ? (p.rounds_won || 0) : (p.score || 0);
      },
      get otherPlayerMetric() {
        if (!this.match || !this.match.other_player) return 0;
        const p = this.match.other_player;
        return this.match.mode === "speed" ? (p.rounds_won || 0) : (p.score || 0);
      },
      modeLabel(mode) {
        return (mode === "speed" ? "⚡ " : "🎯 ") + t(`compete.mode.${mode}`);
      },
      get metricLabel() {
        return this.match && this.match.mode === "speed" ? t("compete.rounds") : t("compete.points");
      },
      // Option button styling shared by the playing + reveal states.
      optionClass(choice) {
        const m = this.match;
        if (!m || !m.question) return "btn-outline";
        const myPick = m.you && m.you.picked ? m.you.pick : null;
        if (m.reveal) {
          if (choice === m.reveal.correct_answer) return "btn-success";
          if (choice === myPick) return "btn-error";
          return "btn-outline opacity-60";
        }
        if (choice === myPick) return "btn-primary";
        return "btn-outline";
      },
      otherPlayerPicked(choice) {
        const opp = this.match && this.match.other_player;
        return !!(opp && opp.picked && opp.pick === choice);
      },
      optionLabel(option) {
        return option === "die -" ? t("challenge.noPlural") : option;
      },
      get typeLabel() {
        const g = GAME_TYPES.find((x) => x.challenge_type === (this.match && this.match.challenge_type));
        return g ? t(`challengeType.${g.id}.label`) : (this.match ? this.match.challenge_type : "");
      },
      get resultHeadline() {
        const r = this.match && this.match.result;
        if (!r) return "";
        switch (r.outcome) {
          case "win": return t("compete.youWon");
          case "loss": return t("compete.youLost");
          case "draw": return t("compete.draw");
          default: return t("compete.abandoned");
        }
      },
    };
  };

  // ------------------------------------------------------------------
  // Chat (LLM role-play scenarios) - /chat
  // ------------------------------------------------------------------

  // Reply keys the server sends when a moderation strike-out sealed the
  // session. They are the only in-band signal that the chat is over, since
  // the message response carries no session status.
  const CHAT_TERMINAL_REPLY_KEYS = [
    "chat.moderation.terminated",
    "chat.moderation.output_terminated",
  ];

  const CHAT_POLL_MS = 2000;
  // A completion settled after the pane moved on (Back or leaving /chat)
  // re-reads at most this many times, CHAT_POLL_MS apart: 20 reads, 2 s apart
  // (about 38 s), past the server's 35 s completion lease, after which the
  // session is either completed or no longer in flight.
  const CHAT_SETTLE_ATTEMPTS = 20;
  // Answers that will not change on a retry: the session is gone, not ours,
  // or the request itself is wrong. Everything else (5xx, 408, 429, network)
  // is retried.
  const CHAT_SETTLE_FINAL_STATUSES = [400, 401, 403, 404];

  function chatMessageId() {
    return (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : "00000000-0000-4000-8000-" + String(Date.now()).padStart(12, "0").slice(-12);
  }

  // chatPlayer drives the whole /chat screen: scenario picker, the turn
  // loop (typed or spoken), render-on-tap reply audio, and the recap.
  //
  // Every server string here is learner-influenced (the learner's own turns,
  // the partner's replies) and is rendered with x-text only - never x-html.
  // DOMPurify is an XSS control elsewhere; it is not the safety mechanism
  // for this content.
  window.chatPlayer = function () {
    return {
      // loading | picker | chatting | ended | recap
      phase: "loading",
      scenarios: [],
      allowance: null,
      scenario: null,
      sessionId: "",
      status: "",
      terminationReason: "",
      turns: [],
      turnsUsed: 0,
      turnsRemaining: 0,
      hardZeroReason: "",
      inFlight: false,
      recap: null,
      draft: "",
      pending: "",     // optimistic learner bubble while a turn is in flight
      busy: false,
      error: "",
      announce: "",    // aria-live text
      serverDisabled: false,
      signInRequired: false,  // anonymous visitor: the screen becomes the pitch

      // Confirm strip: idle | recording | transcribing | confirm | empty
      recPhase: "idle",
      transcript: "",
      recError: "",
      audioKey: null,       // message key currently rendering / playing ("opening" for the opener)
      audioFailed: {},      // message key -> true ("no audio", turn untouched)

      _pendingMessageId: "",
      _pendingText: "",
      _mic: null, // createMicCapture(), made on first use
      _loadSeq: 0,
      _finishing: false,
      _pollTimer: null,
      _pollBusy: false,
      _gone: false,
      _audio: null,
      _audioUrl: "",
      _autoPlayed: new Set(),
      _statsRefreshedFor: "", // session whose completion already refreshed the shared stats

      get audioEnabled() {
        const root = getRoot();
        return !!(root && root.flags && root.flags.chat_audio_enabled);
      },
      get micSupported() {
        return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
      },
      // Remaining plays, shown before any start attempt: a rejected start
      // must never be the learner's first quota signal.
      get dailyRemaining() {
        return this.allowance && this.allowance.daily_remaining !== null
          ? this.allowance.daily_remaining : null;
      },
      get monthlyRemaining() {
        return this.allowance && this.allowance.monthly_remaining !== null
          ? this.allowance.monthly_remaining : null;
      },
      get unlimited() {
        return !!(this.allowance && this.allowance.unlimited);
      },
      get outOfPlays() {
        if (!this.allowance || this.unlimited) return false;
        return this.dailyRemaining === 0 || this.monthlyRemaining === 0;
      },
      // The opening line is not a turn row - it is curated scenario content
      // with pre-rendered durable audio - so it is prepended here.
      get messages() {
        const out = [];
        if (this.scenario && this.scenario.opening_line) {
          out.push({
            key: "opening", speaker: "partner",
            text: this.scenario.opening_line, warning: "",
            audioUrl: this.audioEnabled ? (this.scenario.audio_url || "") : "",
          });
        }
        for (const turn of this.turns) {
          out.push({
            key: turn.message_id, speaker: turn.speaker,
            text: turn.text_key ? t(turn.text_key) : turn.text,
            warning: turn.warning_key ? t(turn.warning_key) : "",
            // A key-only reply is localized client copy, not partner speech:
            // there is nothing to render as audio.
            audioUrl: "",
            speakable: turn.speaker === "partner" && !turn.text_key,
          });
        }
        if (this.pending) {
          out.push({ key: "pending", speaker: "learner", text: this.pending, warning: "", pending: true });
        }
        return out;
      },
      get maxTurns() {
        return (this.scenario && this.scenario.max_turns) || 0;
      },
      get minTurns() {
        return (this.scenario && this.scenario.min_turns) || 0;
      },
      // How many more messages before the conversation can be graded at all.
      // Ending below min_turns is a server-side hard zero (no judge call, no
      // feedback, no points), and the turn counter only ever counted down
      // toward max_turns - so a learner could see turns to spare and still
      // end one short of being graded, with nothing having said so.
      get turnsUntilGradable() {
        return Math.max(0, this.minTurns - this.turnsUsed);
      },
      // A running session keeps the level it was started at, so switching
      // level and reloading drops the learner back into the old chat with
      // its old turn budget. Say so instead of letting a B2 "6 more to be
      // graded" look like a bug on an A2 account.
      get levelMismatch() {
        return !!(this.scenario && this.scenario.level && this.$store.ui.level
          && this.scenario.level !== this.$store.ui.level);
      },
      // The learner's level, not a chat-local choice: the picker used to
      // carry its own selector, which asked again for something the app
      // already knows. GET /api/scenarios requires exactly one valid
      // level, so this is the filter for the whole picker. Read live, not
      // copied at mount, so a header switch and again() see the new level.
      get level() {
        return this.$store.ui.level || "A1";
      },
      // One interaction lock for the whole pane. `inFlight` matters as much
      // as `busy`: a recovered session can have a turn processing that this
      // tab never started, and the server rejects both a new message and a
      // completion while one is running.
      get locked() {
        return this.busy || this.inFlight || this._finishing || this.recPhase === "transcribing";
      },
      get canSend() {
        return this.phase === "chatting" && !this.locked && !!this.draft.trim();
      },

      async init() {
        // Alpine 3.17 calls destroy() on x-if unmount; the screen watch is a
        // second trigger so leaving /chat always stops the poll loop and
        // releases the mic.
        if (typeof this.$watch === "function") {
          this.$watch("screen", (value) => { if (value !== "chat") this.destroy(); });
          // A header level switch refilters the picker; a running chat keeps
          // its own level (levelMismatch says so) until the learner ends it.
          // A switch during a pending fetch is caught by loadScenarios itself.
          this.$watch("$store.ui.level", () => { if (this.phase === "picker") this.loadScenarios(); });
        }
        // A chat started on the Lesen picker plays like start(): opener
        // audio, the announcement and the composer focus.
        const root = getRoot();
        const handed = root && root.pendingChatState;
        if (root) root.pendingChatState = null;
        if (handed && handed.chat_id && handed.chat_id === root.activeChatSessionId) {
          this.applyState(handed);
          this.announce = t("chat.announce.started");
          this.focusComposer();
          return;
        }
        // The flag only hides the entry point, and it may still be in
        // flight when a deep link lands here, so the server is the gate:
        // every route 503s with chat-disabled while the switch is off, and
        // 403s with chat-signin-required for a visitor without an account.
        // Session recovery: a new tab or a reload must find the running
        // chat, or the recap of one that just finished.
        try {
          const state = await this.recoverSession();
          if (this._gone) return;
          if (state && state.chat_id) {
            this.applyState(state, { silentAudio: true });
            if (this.inFlight) this.startPolling();
            return;
          }
        } catch (e) {
          if (this.noteGate(e)) { this.phase = "picker"; return; }
          if (!(e instanceof ApiError) || e.status !== 404) {
            console.warn("chat active probe failed:", e);
          }
        }
        if (this._gone) return;
        // Nothing to recover: a /chat/<id> deep link that no longer resolves
        // must not leave its id in the address bar over the picker.
        this.syncSessionUrl(null);
        await this.loadScenarios();
      },

      // A deep link names the session it wants, so ask for that one first.
      // The active-session probe is the fallback for an id that is finished,
      // pruned, or from another device; without it a stale link would show
      // the picker instead of the conversation that is actually running.
      async recoverSession() {
        const root = getRoot();
        const wanted = root && root.activeChatSessionId;
        if (wanted) {
          try {
            return await api.chatState(wanted);
          } catch { /* stale or foreign id: fall through to the probe */ }
        }
        return api.chatActive();
      },

      destroy() {
        this._gone = true;
        this.stopPolling();
        this.cancelRecording();
        this.stopReplyAudio();
      },

      // The session id rides the URL so a conversation is reloadable and
      // Back leaves the chat instead of the whole screen. Entering a chat
      // pushes an entry; recovery on load and the return to the picker
      // replace, so Back never lands on a session that is already over.
      syncSessionUrl(sessionId, push = false) {
        const root = getRoot();
        if (!root || typeof root.pathForScreen !== "function") return;
        root.activeChatSessionId = sessionId || null;
        if (push) {
          root.pushRouteForScreen();
          return;
        }
        const want = root.pathForScreen();
        if (window.location.pathname === want) return;
        try { history.replaceState({}, "", want); } catch { /* ignore */ }
      },

      // Back / forward inside the chat screen: the router says which
      // session the URL now means, null being the picker.
      async onRouteSession(sessionId) {
        if (this._gone) return;
        if (!sessionId) {
          if (!this.sessionId) return;
          await this.again();
          return;
        }
        if (sessionId === this.sessionId) return;
        try {
          this.applyState(await api.chatState(sessionId));
        } catch {
          await this.again();
        }
      },

      stopPolling() {
        if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; }
      },

      // The turn a lost response left running finishes server-side; poll
      // until in_flight clears so the pane catches up on its own.
      startPolling() {
        this.stopPolling();
        this._pollTimer = setInterval(async () => {
          if (this._gone || !this.inFlight) { this.stopPolling(); return; }
          if (this._pollBusy) return;
          this._pollBusy = true;
          try {
            await this.refresh();
          } catch { /* transient; the next tick retries */ } finally {
            this._pollBusy = false;
          }
        }, CHAT_POLL_MS);
      },

      // A gate the whole screen turns on: the master switch is off, or chat
      // wants an account this visitor does not have. Both get a panel rather
      // than an error banner. Returns true when one applied.
      //
      // The server is the gate for both, so neither is read off client state:
      // the flag can still be in flight when a deep link lands here, and
      // $store.auth resolves after the screen mounts, so an early read would
      // pitch sign-in to someone who is signed in.
      noteGate(e) {
        if (!(e instanceof ApiError)) return false;
        const type = e.problem.type || "";
        if (type.endsWith("chat-disabled")) {
          this.serverDisabled = true;
          return true;
        }
        if (type.endsWith("chat-signin-required")) {
          this.signInRequired = true;
          return true;
        }
        return false;
      },

      friendlyError(e) {
        if (e instanceof ApiError) {
          const type = e.problem.type || "";
          if (type.endsWith("chat-allowance-exhausted")) return t("chat.error.allowance");
          if (type.endsWith("chat-disabled")) return t("chat.error.disabled");
          if (type.endsWith("chat-budget-exhausted")) return t("chat.error.busy");
          if (type.endsWith("chat-partner-unavailable")) return t("chat.error.busy");
          if (type.endsWith("chat-moderation-cooldown")) return t("chat.error.cooldown");
          if (type.endsWith("chat-turn-limit")) return t("chat.error.turnLimit");
          if (type.endsWith("chat-turn-in-flight")) return t("chat.error.inFlight");
          if (type.endsWith("chat-transcription-quota-exhausted")) return t("chat.error.transcriptionQuota");
          if (type.endsWith("chat-transcription-unavailable")) return t("chat.error.transcriptionUnavailable");
          if (type.endsWith("chat-audio-disabled") || type.endsWith("chat-audio-unavailable")) return t("chat.error.audioUnavailable");
          if (type.endsWith("chat-completion-in-flight")) return t("chat.error.completionInFlight");
          if (type.endsWith("chat-completion-unavailable")) return t("chat.error.busy");
          if (e.status === 404) return t("chat.error.gone");
          // Everything else falls back to the localized generic message:
          // problem.title is server-side English and would bypass all three
          // catalogs.
          return t("chat.error.generic");
        }
        return t("chat.error.network");
      },

      // ----- picker ---------------------------------------------------

      async loadScenarios() {
        this.error = "";
        this.phase = "loading";
        // A fast level switch (or a start racing a reload) must not let an
        // older response repaint the picker.
        const seq = ++this._loadSeq;
        const level = this.level;
        try {
          const resp = await api.chatScenarios(level);
          if (this._gone || seq !== this._loadSeq) return;
          this.scenarios = (resp && resp.scenarios) || [];
          this.allowance = (resp && resp.allowance) || null;
        } catch (e) {
          if (this._gone || seq !== this._loadSeq) return;
          this.scenarios = [];
          if (!this.noteGate(e)) this.error = this.friendlyError(e);
        } finally {
          if (!this._gone && seq === this._loadSeq) {
            if (this.level !== level && !this.serverDisabled && !this.signInRequired) {
              // The header moved while this fetch was out (the level
              // watcher stays quiet during loading), so fetch again rather
              // than paint the old level, whether this one succeeded or not.
              this.loadScenarios();
            } else if (this.phase === "loading") {
              // Only claim the phase if nothing else took it meanwhile: a
              // start that landed during the fetch owns it now.
              this.phase = "picker";
            }
          }
        }
      },

      async start(scenario) {
        if (this.busy || !scenario) return;
        this.busy = true;
        this.error = "";
        // Supersede any scenario load still in flight so its `finally` cannot
        // drop the picker back over the session we are about to enter.
        this._loadSeq++;
        try {
          const state = await api.chatStart(scenario.scenario_id);
          if (this._gone) return;
          // Push before applyState: the entry has to be added while the URL
          // is still the picker, or Back has nothing to return to.
          this.syncSessionUrl(state.chat_id, true);
          this.applyState(state);
          this.announce = t("chat.announce.started");
          this.focusComposer();
        } catch (e) {
          if (this._gone) return;
          // One-active-session: the server hands back the live id, so
          // resume it rather than telling the learner they cannot start.
          if (e instanceof ApiError && e.status === 409 &&
              (e.problem.type || "").endsWith("chat-active-session") && e.problem.detail) {
            try {
              const state = await api.chatState(e.problem.detail);
              this.syncSessionUrl(state.chat_id, true);
              this.applyState(state, { silentAudio: true });
              if (this.inFlight) this.startPolling();
              return;
            } catch { /* fall through to the generic error */ }
          }
          if (!this.noteGate(e)) this.error = this.friendlyError(e);
        } finally {
          this.busy = false;
        }
      },

      // ----- session state --------------------------------------------

      applyState(state, { silentAudio = false } = {}) {
        if (!state) return;
        // A recap arriving for the session this pane was already showing
        // without one is the moment the conversation completed (a poll, or
        // the reconcile after a lost or concurrent completion; finish()
        // refreshes for its own response first). A recap that is simply
        // opened (reload recovery, a deep link, Back to another session) or
        // read again was already counted by the stats the page holds.
        const completedHere = !!state.recap && !this.recap &&
          !!this.sessionId && this.sessionId === (state.chat_id || "");
        this.sessionId = state.chat_id || "";
        this.scenario = state.scenario || null;
        this.status = state.status || "";
        this.terminationReason = state.termination_reason || "";
        this.turns = (state.messages || []).filter((message) => message.status === "completed");
        this.turnsUsed = state.turns_used || 0;
        this.turnsRemaining = state.turns_remaining || 0;
        this.inFlight = !!state.in_flight;
        this.recap = state.recap || null;
        this.hardZeroReason = state.hard_zero_reason || "";
        if (state.allowance) this.allowance = state.allowance;
        // Covers every entry point that is not a fresh start: reload
        // recovery, a poll, a completion.
        this.syncSessionUrl(this.sessionId);
        // A recovered session is not new audio: a reload must not speak a
        // turn the learner already heard.
        if (silentAudio) this.silenceAutoplay();
        else this.maybeAutoplay();
        if (this.recap) {
          this.phase = "recap";
          this.error = "";
          this.stopPolling();
          if (completedHere) this.refreshSharedStats(this.sessionId);
        } else if (this.status === "active") {
          this.phase = "chatting";
        } else {
          this.phase = "ended";
          this.stopPolling();
        }
      },

      // The completion awarded points, and every response that carries its
      // recap (the completion itself and a state read) waits for the stats
      // fan-out first, so re-read Today/Week/Overall and the streak the way a
      // challenge summary does. The guard makes it once per session where
      // two paths see the same completion: finish() refreshes for its
      // response, then applyState sees the same recap arrive.
      refreshSharedStats(sessionId) {
        if (!sessionId || this._statsRefreshedFor === sessionId) return;
        this._statsRefreshedFor = sessionId;
        const root = getRoot();
        if (root && typeof root.refreshAllStats === "function") root.refreshAllStats();
      },

      async refresh() {
        const sessionId = this.sessionId;
        if (!sessionId) return;
        const hadRecap = !!this.recap;
        const state = await api.chatState(sessionId);
        if (this._gone || this.sessionId !== sessionId) {
          // The pane moved on mid-read (Back, leaving /chat): do not paint
          // this session over it, but a completion the read caught still
          // awarded points to the root stats.
          if (state && state.recap && !hadRecap) this.refreshSharedStats(sessionId);
          return;
        }
        this.applyState(state);
      },

      // ----- turn loop -------------------------------------------------

      async send() {
        const text = this.draft.trim();
        if (!text || this.locked || this.phase !== "chatting") return;
        this.busy = true;
        this.error = "";
        // Typing wins over a live capture: the mic must not keep recording
        // behind a sent turn.
        this.cancelRecording();
        // Reuse the id across a retry so a turn the server already persisted
        // replays instead of being charged twice. The server rejects the same
        // id with different text (400, permanently), so an edited retry has
        // to mint a fresh one.
        if (!this._pendingMessageId || this._pendingText !== text) {
          this._pendingMessageId = chatMessageId();
          this._pendingText = text;
        }
        this.pending = text;
        this.draft = "";
        this.announce = t("chat.announce.sending");
        let resp;
        try {
          resp = await api.chatMessage(this.sessionId, this._pendingMessageId, text);
        } catch (e) {
          this.busy = false;
          if (this._gone) return;
          this.pending = "";
          this.draft = text;
          this.error = this.friendlyError(e);
          this.announce = this.error;
          // A lost response left the turn running server-side: poll until it
          // lands instead of stranding the learner on the error.
          if (e instanceof ApiError && (e.problem.type || "").endsWith("chat-turn-in-flight")) {
            this.inFlight = true;
            this.startPolling();
          }
          return;
        }
        // Past this point the turn is spent. A failing state read must not
        // put the text back in the composer or drop the id - that would
        // charge the learner a second turn for the same message.
        this.pending = "";
        try {
          // The message response carries no session status, so the
          // authoritative state (Message ids, terminal status, in-flight)
          // comes from one cheap read after the expensive LLM round trip.
          await this.refresh();
        } catch (e) {
          this.busy = false;
          if (this._gone) return;
          this.error = this.friendlyError(e);
          this.announce = this.error;
          this.inFlight = true;
          this.startPolling();
          return;
        }
        this.busy = false;
        if (this._gone) return;
        this._pendingMessageId = "";
        this._pendingText = "";
        // A moderation warning rides alongside the reply, so it has to be
        // announced too - it is the only signal that the turn was flagged.
        const reply = (resp && resp.message) || {};
        this.announce = [
          reply.warning_key ? t(reply.warning_key) : "",
          reply.text_key ? t(reply.text_key) : (reply.text || ""),
        ].filter(Boolean).join(" ");
        if (CHAT_TERMINAL_REPLY_KEYS.includes(reply.text_key)) return;
        if (this.phase === "chatting" && this.turnsRemaining <= 0) {
          // Nothing else the learner can do: the server rejects further
          // messages once max_turns is spent.
          await this.finish();
          return;
        }
        this.focusComposer();
      },

      focusComposer() {
        this.$nextTick(() => {
          const el = this.$refs && this.$refs.composer;
          if (el && el.focus) el.focus();
        });
      },

      // Insert a German character at the composer caret (for keyboards
      // without them), mirroring the dictation input.
      // Caret-aware rather than append-only: a chat turn is a sentence the
      // learner edits, not a single word typed left to right.
      insertChar(ch) {
        const el = this.$refs && this.$refs.composer;
        if (!el) { this.draft += ch; return; }
        const s = el.selectionStart ?? this.draft.length;
        const e = el.selectionEnd ?? s;
        this.draft = this.draft.slice(0, s) + ch + this.draft.slice(e);
        this.$nextTick(() => {
          el.focus();
          const pos = s + ch.length;
          if (el.setSelectionRange) el.setSelectionRange(pos, pos);
        });
      },

      // "Gespräch beenden" / max_turns: grade the transcript and show the
      // recap. The completion route blocks through the judge, so one call
      // is enough; a concurrent one 409s and the state read picks it up.
      async finish() {
        if (this._finishing || !this.sessionId) return;
        // The completion outlives the pane: Back (again() clears sessionId)
        // or leaving /chat (destroy) can land while the judge runs.
        const sessionId = this.sessionId;
        this._finishing = true;
        this.cancelRecording();
        this.error = "";
        this.announce = t("chat.announce.grading");
        try {
          const state = await api.chatComplete(sessionId);
          // The points are awarded either way and the stats are root state,
          // so refresh before deciding whether this pane still shows it.
          if (state && state.recap) this.refreshSharedStats(sessionId);
          // A pane that moved on keeps what it shows now (the picker or
          // another session) instead of flipping back to this recap.
          if (this._gone || this.sessionId !== sessionId) return;
          this.applyState(state);
          this.announce = t("chat.announce.graded");
          this.focusRecap();
        } catch (e) {
          if (this._gone || this.sessionId !== sessionId) {
            // The response was lost after the pane moved on (Back or leaving
            // /chat), but the completion may still land (or already has):
            // settle it for the root stats in the background, without
            // painting anything here.
            this._settlePromise = this.settleCompletion(sessionId);
            return;
          }
          this.error = this.friendlyError(e);
          this.announce = this.error;
          // Completion may have landed even though this response did not, and
          // a concurrent one answers chat-completion-in-flight while still
          // grading. Either way the session is the authority: it carries the
          // recap once grading finished and the in-flight flag while it runs,
          // so reconcile rather than leaving a finished recap hidden behind
          // an error the learner would have to clear by reloading.
          try {
            await this.refresh();
            if (this._gone || this.sessionId !== sessionId) return;
            if (this.recap) {
              this.announce = t("chat.announce.graded");
              this.focusRecap();
            } else if (this.inFlight) {
              this.startPolling();
            }
          } catch { /* keep the original error; the learner can retry */ }
        } finally {
          this._finishing = false;
        }
      },

      // Re-reads a session whose completion response was lost after the pane
      // moved on (Back or leaving /chat), until its recap arrives (one stats
      // refresh) or it settles without one (no longer in flight, or a
      // permanent 400/401/403/404), for at most CHAT_SETTLE_ATTEMPTS reads.
      // Stops once the learner signs out. Never paints: the pane shows
      // something else by now.
      async settleCompletion(sessionId) {
        for (let attempt = 0; attempt < CHAT_SETTLE_ATTEMPTS; attempt++) {
          if (attempt > 0) await this._sleep(CHAT_POLL_MS);
          const root = getRoot();
          if (!root || (root.$store && root.$store.auth && !root.$store.auth.signedIn)) return;
          let state;
          try {
            state = await api.chatState(sessionId);
          } catch (e) {
            if (e instanceof ApiError && CHAT_SETTLE_FINAL_STATUSES.includes(e.status)) return;
            continue; // transient (5xx, 408, 429, network): the next attempt retries
          }
          if (state && state.recap) {
            this.refreshSharedStats(sessionId);
            return;
          }
          if (!state || !state.in_flight) return;
        }
      },

      _sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
      },

      focusRecap() {
        this.$nextTick(() => {
          const el = this.$refs && this.$refs.recapHeading;
          if (el && el.focus) el.focus();
        });
      },

      // Back to the picker after a finished session.
      async again() {
        this.stopPolling();
        this.cancelRecording();
        this.stopReplyAudio();
        this.inFlight = false;
        this._pendingMessageId = "";
        this._pendingText = "";
        this.sessionId = "";
        this.scenario = null;
        this.turns = [];
        this.recap = null;
        this.hardZeroReason = "";
        this.status = "";
        this.terminationReason = "";
        this.pending = "";
        this.draft = "";
        this.error = "";
        this.audioFailed = {};
        this._autoPlayed = new Set();
        this.syncSessionUrl(null);
        await this.loadScenarios();
      },

      // ----- spoken turns ---------------------------------------------

      resetConfirm() {
        this.recPhase = "idle";
        this.transcript = "";
        this.recError = "";
      },

      toggleRecording() {
        if (this.recPhase === "recording") this.stopRecording();
        else this.startRecording();
      },

      async startRecording() {
        if (this.locked || (this._mic && this._mic.starting) || this.recPhase === "recording" || this.phase !== "chatting") return;
        this.recError = "";
        if (!this.micSupported) {
          this.recError = t("chat.mic.unsupported");
          this.announce = this.recError;
          return;
        }
        this._mic ||= createMicCapture();
        try {
          if (!await this._mic.start((blob) => this._onRecStop(blob))) return; // a typed send, finish or navigation cancelled it
          this.recPhase = "recording";
          this.announce = t("chat.announce.recording");
        } catch (e) {
          this.recError = (e && e.name === "NotAllowedError")
            ? t("chat.mic.denied") : t("chat.mic.error");
          this.announce = this.recError;
        }
      },

      // Drop a live capture without sending it (typed send, finish, reset).
      // The teardown also invalidates a permission prompt that has not
      // resolved yet, so its stream is released rather than started.
      cancelRecording() {
        this._teardownRecorder();
        this.resetConfirm();
      },

      stopRecording() {
        if (this._mic && this._mic.stop()) return; // _onRecStop follows
        this.recPhase = "idle";
      },

      _onRecStop(blob) {
        if (!blob) { this.recPhase = "idle"; return; }
        this.transcribe(blob);
      },

      _teardownRecorder() {
        if (this._mic) this._mic.teardown();
      },

      async transcribe(blob) {
        this.recPhase = "transcribing";
        this.recError = "";
        try {
          const resp = await api.chatTranscribe(this.sessionId, blob);
          if (this._gone) return;
          const text = ((resp && resp.transcript) || "").trim();
          if (!text) {
            // A silent or unintelligible clip is a normal outcome, not an
            // error: offer another take instead of an error banner.
            this.recPhase = "empty";
            this.announce = t("chat.mic.empty");
            return;
          }
          this.transcript = text;
          this.recPhase = "confirm";
          this.announce = t("chat.announce.transcribed", { text });
        } catch (e) {
          if (this._gone) return;
          this.recPhase = "idle";
          this.recError = this.friendlyError(e);
          this.announce = this.recError;
        }
      },

      // Confirm strip: send the transcript as this turn.
      sendTranscript() {
        if (!this.transcript) return;
        this.draft = this.transcript;
        this.resetConfirm();
        return this.send();
      },
      // Edit drops the transcript into the composer so a spoken turn and a
      // typed turn converge on one send path.
      editTranscript() {
        this.draft = this.transcript;
        this.resetConfirm();
        this.focusComposer();
      },
      reRecord() {
        this.resetConfirm();
        return this.startRecording();
      },

      // ----- reply audio (render on tap, never autoplay) ---------------

      async playReply(messageId) {
        if (!this.audioEnabled || this.audioKey !== null) return;
        this.audioKey = messageId;
        const cleared = { ...this.audioFailed };
        delete cleared[messageId];
        this.audioFailed = cleared;
        try {
          const blob = await api.chatReplyAudio(this.sessionId, messageId);
          if (this._gone) return;
          this.stopReplyAudio();
          this._audioUrl = URL.createObjectURL(blob);
          const audio = new Audio(this._audioUrl);
          this._audio = audio;
          audio.addEventListener("ended", () => this.stopReplyAudio());
          await audio.play();
        } catch {
          if (this._gone) return;
          // TTS failure shows as "no audio"; the turn itself is untouched.
          this.audioFailed = { ...this.audioFailed, [messageId]: true };
          this.stopReplyAudio();
        } finally {
          if (!this._gone) this.audioKey = null;
        }
      },

      // Opening-line playback uses the pre-rendered durable clip directly.
      // Its key is "opening", so one player state covers both buttons and
      // neither can start over the other.
      playOpening() {
        const url = this.scenario && this.scenario.audio_url;
        if (!this.audioEnabled || !url || this.audioKey !== null) return;
        this.stopReplyAudio();
        this.audioKey = "opening";
        const audio = new Audio(url);
        this._audio = audio;
        audio.addEventListener("ended", () => {
          this.audioKey = null;
          this.stopReplyAudio();
        });
        audio.play().catch(() => {
          this.audioFailed = { ...this.audioFailed, opening: true };
          this.audioKey = null;
        });
      },

      // Autoplay follows the learner's audio mode, like the challenge player:
      // "auto" speaks the opening line and every partner reply as it lands,
      // "manual" and "off" wait for the button. Keyed by message, so a poll,
      // a re-render or a recovered session cannot replay what was heard.
      maybeAutoplay() {
        if (!this.audioEnabled || !Alpine.store("ui").audioAuto) return;
        // Something is already speaking. Leave the turn unmarked and let a
        // later state change pick it up: playReply would refuse the overlap,
        // and marking it here would silence that reply for good.
        if (this.audioKey !== null) return;
        const speakable = this.messages.filter((m) => m.key === "opening" || m.speakable);
        const last = speakable[speakable.length - 1];
        if (!last || this._autoPlayed.has(last.key)) return;
        this._autoPlayed.add(last.key);
        if (last.key === "opening") this.playOpening();
        else this.playReply(last.key);
      },

      // Marks everything currently on screen as already spoken.
      silenceAutoplay() {
        for (const m of this.messages) {
          if (m.key === "opening" || m.speakable) this._autoPlayed.add(m.key);
        }
      },

      stopReplyAudio() {
        if (this._audio) { try { this._audio.pause(); } catch { /* already stopped */ } this._audio = null; }
        if (this._audioUrl) { URL.revokeObjectURL(this._audioUrl); this._audioUrl = ""; }
      },

      // ----- recap -----------------------------------------------------

      get recapVerdictLabel() {
        if (!this.recap) return "";
        return t(`chat.recap.verdict.${this.recap.verdict}`);
      },
      // Every weakness the judge listed. Sessions graded before the judge
      // returned a list, and clean ones, have none - hence the guard rather
      // than a bare recap.issues read.
      get recapIssues() {
        const issues = this.recap && this.recap.issues;
        return Array.isArray(issues) ? issues : [];
      },
      // A hard zero skips the judge, so the recap has no rationale and no
      // model answer to show. Without this the learner cannot tell a harsh
      // grade from a conversation that was never graded at all.
      get recapHardZeroLabel() {
        if (!this.hardZeroReason) return "";
        if (this.hardZeroReason === "minimum_turns") {
          return t("chat.recap.hardZero.minimum_turns", { used: this.turnsUsed, min: this.minTurns });
        }
        return t(`chat.recap.hardZero.${this.hardZeroReason}`);
      },
    };
  };

  // ------------------------------------------------------------------
  // Public profile (/u/{username-or-user-id})
  // ------------------------------------------------------------------

  // dtUserProfile drives the public profile screen: fetch by @username or,
  // for anonymous users, user_id (from root.activeProfileUsername), then
  // best-effort load achievements by user_id. load() is guarded by loadedFor
  // so init() and profile -> profile navigation don't double-fetch.
  window.dtUserProfile = function () {
    return {
      loading: true,
      // "" | "notfound" | "generic". notfound covers unknown handles and
      // anonymous users (who have no public profile -> 404).
      errorKind: "",
      profile: null,
      achievements: [],
      achievementsLoaded: false,
      loadedFor: null,
      followBusy: false,
      requestNumber: 0,

      init() {
        const root = window.__dtRoot;
        this.load(root && root.activeProfileUsername);
        this.$watch("$store.ui.effectiveLang", () => {
          this.loadedFor = null;
          this.load(root && root.activeProfileUsername);
        });
      },

      // retry re-runs the load for the username currently on the route -
      // used by the "Try again" button in the generic-error state. load()
      // cleared loadedFor on the failure, so this is not short-circuited.
      retry() {
        const root = window.__dtRoot;
        this.load(root && root.activeProfileUsername);
      },

      async load(username) {
        if (!username) {
          this.loading = false;
          this.errorKind = "notfound";
          return;
        }
        if (username === this.loadedFor) return;
        const request = ++this.requestNumber;
        this.loadedFor = username;
        this.loading = true;
        this.errorKind = "";
        this.profile = null;
        this.achievements = [];
        this.achievementsLoaded = false;
        this.showAllAchievements = false;
        try {
          const profile = await api.getProfile(username, { silence: [404] });
          if (request !== this.requestNumber) return;
          this.profile = profile;
        } catch (e) {
          if (request !== this.requestNumber) return;
          this.loading = false;
          this.errorKind = e instanceof ApiError && e.status === 404 ? "notfound" : "generic";
          // Clear loadedFor so the same username can be retried (the load
          // didn't complete); the generic-error state offers a retry button.
          this.loadedFor = null;
          return;
        }
        this.loading = false;
        // Own profile hosts the fading-words widget (merged from the old
        // stats screen); ask the root component to load them.
        if (this.profile.is_self && window.__dtRoot) window.__dtRoot.refreshFadingWords();
        // Achievements are part of the public surface but are keyed by
        // user_id; a failure here must not blank the whole profile.
        try {
          const res = await api.achievements(this.profile.user_id);
          if (request === this.requestNumber) {
            this.achievements = (res && res.achievements) || [];
            this.achievementsLoaded = true;
          }
        } catch (e) {
          if (request === this.requestNumber) this.achievements = [];
        }
      },

      get wordgroups() {
        return (this.profile && this.profile.favorite_word_lists) || [];
      },
      openWordgroup(group, event) {
        const root = window.__dtRoot;
        if (root && group) root.openWordgroup(group.slug, event);
      },
      practiceWordgroup(group) {
        const root = window.__dtRoot;
        if (root && group) root.startWordgroupChallenge(group.word_list_id, group.slug);
      },

      // Initials avatar fallback derived from the display name (we store
      // no avatar images). "Sophie Müller" -> "SM"; "sophie" -> "SO".
      get initials() {
        const n = ((this.profile && this.profile.display_name) || "").trim();
        if (!n) return "?";
        const parts = n.split(/\s+/).filter(Boolean);
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      },
      get earnedAchievements() {
        return this.achievements
          .filter((a) => a.earned)
          .sort((a, b) => String(b.awarded_at || "").localeCompare(String(a.awarded_at || "")));
      },
      get lockedAchievements() {
        return this.achievements.filter((a) => !a.earned);
      },
      get achievementGroups() {
        const visible = this.isSelf ? this.achievements : this.earnedAchievements;
        return ACHIEVEMENT_GROUPS
          .map((group) => ({
            key: group.key,
            items: visible.filter((a) => group.kinds.includes(a.kind)),
          }))
          .filter((group) => group.items.length);
      },
      achievementIcon(a) {
        return ACHIEVEMENT_ICON_PATHS[a.kind] || ACHIEVEMENT_ICON_PATHS.hidden;
      },
      achievementText(a, field) { return localizedAchievement(a, field); },
      achievementDate(a) {
        if (!a.awarded_at) return localizedAchievement(a, "description");
        return new Date(a.awarded_at).toLocaleDateString(this.$store.i18n.lang, { year: "numeric", month: "short", day: "numeric" });
      },
      get isSelf() {
        return !!(this.profile && this.profile.is_self);
      },
      get isFollowing() {
        const r = this.profile && this.profile.viewer_relationship;
        return r === "following" || r === "friend";
      },
      get isFriend() {
        return !!(this.profile && this.profile.viewer_relationship === "friend");
      },
      // Member-since string from the self-only private block (other
      // users don't expose created_at).
      get memberSince() {
        const c = this.profile && this.profile.private && this.profile.private.created_at;
        if (!c) return "";
        try {
          return new Date(c).toLocaleDateString(this.$store.i18n.lang, { year: "numeric", month: "short" });
        } catch {
          return "";
        }
      },

      // streakAtRisk drives the "at risk" nudge. Only surfaced on the
      // viewer's own profile - it's a personal reminder to practise today,
      // not a status broadcast about other people.
      get streakAtRisk() {
        return !!(this.profile && this.profile.is_self && this.profile.at_risk);
      },

      // activityCalendar builds a contribution-style grid ending today: 14
      // week-columns x 7 weekday-rows, laid out column-major to match the
      // CSS grid-auto-flow. Cells beyond today in the current week are marked
      // empty so the grid stays rectangular.
      get activityCalendar() {
        const active = new Set((this.profile && this.profile.activity_dates) || []);
        const weeks = 14;
        // activity_dates are in the profile owner's zone (#1483). Only the
        // owner sees it (private.timezone); for someone else's profile the
        // viewer's zone is the best guess.
        const zone = this.profile?.private?.timezone || this.$store.auth?.me?.timezone || this.$store.ui.timezone || "";
        const today = zoneToday(zone);
        const dow = today.getUTCDay(); // 0 = Sunday
        const cells = [];
        for (let c = 0; c < weeks; c++) {
          for (let r = 0; r < 7; r++) {
            const daysBack = (weeks - 1 - c) * 7 + (dow - r);
            if (daysBack < 0) {
              cells.push({ key: "f" + c + "-" + r, empty: true });
              continue;
            }
            const key = dayKey(new Date(today.getTime() - daysBack * 86_400_000));
            cells.push({ key, active: active.has(key), today: daysBack === 0 });
          }
        }
        return cells;
      },

      // toggleFollow optimistically flips the relationship + counts. Anon
      // viewers can't follow (the button is replaced by a sign-in prompt
      // in the template); guarded here too as defense-in-depth.
      async toggleFollow() {
        if (!this.profile || this.profile.is_self || this.followBusy) return;
        if (!window.Alpine.store("auth").signedIn) {
          window.Alpine.store("auth").openSignIn();
          return;
        }
        if (!window.Alpine.store("ui").userId) return;
        const target = this.profile.user_id;
        this.followBusy = true;
        try {
          if (this.isFollowing) {
            const wasFriend = this.profile.viewer_relationship === "friend";
            await api.unfollow(target);
            this.profile.viewer_relationship = "none";
            this.profile.followers_count = Math.max(0, (this.profile.followers_count || 0) - 1);
            if (wasFriend) {
              this.profile.friends_count = Math.max(0, (this.profile.friends_count || 0) - 1);
            }
          } else {
            const r = await api.follow(target);
            const becameFriend = !!(r && r.became_friend);
            this.profile.viewer_relationship = becameFriend ? "friend" : "following";
            this.profile.followers_count = (this.profile.followers_count || 0) + 1;
            if (becameFriend) {
              this.profile.friends_count = (this.profile.friends_count || 0) + 1;
            }
          }
        } catch (e) {
          console.warn("follow toggle failed:", e);
        } finally {
          this.followBusy = false;
        }
      },
    };
  };

  // ------------------------------------------------------------------
  // Courses + Lessons
  // ------------------------------------------------------------------

  const COURSE_DEMOS = [
    {
      course_id: "cafe_am_morgen", title: "Café am Morgen",
      description_md: "A gentle slice-of-life story set in a Berlin Kaffeehaus. Order, chat, and people-watch your way through A1 essentials.",
      level_min: "A1", level_max: "A1", requires_paid: false,
      lessons: [
        ["c1", "Guten Morgen", "Begrüßung im Café"],
        ["c2", "Eine Tasse Kaffee, bitte", "Bestellen und bezahlen"],
        ["c3", "Das Frühstücksgespräch", "Small Talk am Tisch"],
        ["c4", "Die Rechnung, bitte", "Zahlen und Trinkgeld"],
      ].map(([lesson_id, title, words], i) => ({ lesson_id, title, words, order_index: i + 1, unlocked: true, completed_at: "2026-04-22" })),
    },
    {
      course_id: "cyber_express", title: "Der Cyber-Express",
      description_md: "A neon-lit night train from Neo-Berlin to Hamburg-Zentrum. Board, listen, decode - and learn A1-A2 German through a four-part mystery.",
      level_min: "A1", level_max: "A2", requires_paid: false,
      lessons: [
        { lesson_id: "l1", order_index: 1, title: "Abfahrt aus Neo-Berlin", words: "Boarding and first encounter", unlocked: true, completed_at: "2026-05-08" },
        { lesson_id: "l2", order_index: 2, title: "Der erste Hack", words: "Hacks, signals and screens", unlocked: true, completed_at: "2026-05-09" },
        { lesson_id: "l3", order_index: 3, title: "Mysterium im Wagen 7", words: "A mysterious passenger", unlocked: true, last_started_at: "2026-05-10", recap_md: "The screens went dark and Mira received an encrypted message. She still needs a code, and the stranger seems to know more than he should." },
        { lesson_id: "l4", order_index: 4, title: "Datenleck am Hamburg-Zentrum", words: "Arrival and data leak", unlocked: false },
      ],
    },
    {
      course_id: "wanderlust", title: "Wanderlust im Schwarzwald",
      description_md: "Hike through the Black Forest with two old friends. Practise A2 directions, weather, and small talk on the trail.",
      level_min: "A2", level_max: "A2", requires_paid: false,
      lessons: [
        { lesson_id: "w1", order_index: 1, title: "Der erste Tag", words: "Aufbruch und Ausrüstung", unlocked: true },
        { lesson_id: "w2", order_index: 2, title: "Verirrt im Wald", words: "Richtungen und Wege", unlocked: false },
        { lesson_id: "w3", order_index: 3, title: "Die alte Hütte", words: "Unterkunft und Rast", unlocked: false },
        { lesson_id: "w4", order_index: 4, title: "Heimweg im Regen", words: "Wetter und Heimkehr", unlocked: false },
      ],
    },
    {
      course_id: "krimi_spree", title: "Krimi an der Spree",
      description_md: "A Berlin-noir detective story along the Spree. Practise B1-B2 grammar through interrogations, leads, and red herrings.",
      level_min: "B1", level_max: "B2", requires_paid: true,
      lessons: [
        { lesson_id: "k1", order_index: 1, title: "Der Anruf um Mitternacht", words: "Anruf und erste Spur", unlocked: false },
        { lesson_id: "k2", order_index: 2, title: "Spuren am Ufer", words: "Ermittlung am Wasser", unlocked: false },
        { lesson_id: "k3", order_index: 3, title: "Das verschwundene Notizbuch", words: "Verhör und Verdacht", unlocked: false },
      ],
    },
  ].map((course) => ({ ...course, demo: true }));

  function courseProgress(course) {
    const lessons = course.lessons || [];
    const done = lessons.filter((lesson) => lesson.completed_at).length;
    return { done, total: lessons.length, percent: lessons.length ? Math.round(done * 100 / lessons.length) : 0 };
  }

  function courseState(course) {
    const progress = courseProgress(course);
    if (course.requires_paid) return "locked";
    if (progress.total && progress.done === progress.total) return "completed";
    if ((course.lessons || []).some((lesson) => lesson.completed_at || lesson.last_started_at)) return "in_progress";
    return "available";
  }

  function courseStateLabel(state) {
    return ({ completed: "Abgeschlossen", in_progress: "Läuft", available: "Verfügbar", locked: "PRO" })[state] || state;
  }

  function courseStateClass(state) {
    return ({
      completed: "border-success/40 bg-success/10 text-success",
      in_progress: "border-primary/40 bg-primary/10 text-primary",
      available: "border-base-300 bg-base-300 text-base-content/70",
      locked: "border-warning/40 bg-warning/10 text-warning",
    })[state] || "border-base-300";
  }

  function demoLessonView(course, lesson) {
    return {
      demo: true,
      course,
      lesson: {
        course_id: course.course_id, lesson_id: lesson.lesson_id, order_index: lesson.order_index,
        title: lesson.title, recap_md: lesson.recap_md || "Previously, our travellers found their way into a new part of the story.",
        grammar_rule_md: "Separable verbs split in main clauses. The prefix moves to the end.",
        grammar_examples: [
          { de: "Sie steigt ein.", en: "She boards." },
          { de: "Der Zug fährt um 23 Uhr ab.", en: "The train departs at 11 p.m." },
        ],
        grammar_focus_tag: "trennbare-verben", production_question_md: "Schreibe einen Satz über deine Reise.",
      },
      scenes: [{
        scene_id: "demo-scene", image_alt_text: "A neon-lit train waits at a city platform.",
        segments: [
          { segment_id: "demo-de-1", language: "de", text: "Der Bahnhof leuchtet. Es ist 23 Uhr in Neo-Berlin." },
          { segment_id: "demo-en-1", language: "en", text: "The station glows. It is 11 p.m. in Neo-Berlin." },
          { segment_id: "demo-de-2", language: "de", text: "Mira sucht ihren Zug. Gleis sieben, Wagen drei." },
          { segment_id: "demo-en-2", language: "en", text: "Mira is looking for her train. Platform seven, car three." },
        ],
      }],
      questions: [{ order_index: 1, question_text: "Wo sucht Mira ihren Zug?", options: ["Gleis sieben, Wagen drei", "Am Ausgang", "Im Café", "Gleis drei"], answer_index: 0 }],
      words: [
        { word_id: "demo-bahnhof", articles: "der", german: "Bahnhof", meanings: [{ text: "station", listed: false }] },
        { word_id: "demo-zug", articles: "der", german: "Zug", meanings: [{ text: "train", listed: false }] },
        { word_id: "demo-gleis", articles: "das", german: "Gleis", meanings: [{ text: "platform", listed: false }] },
        { word_id: "demo-einsteigen", german: "einsteigen", meanings: [{ text: "to board", listed: false }] },
      ],
    };
  }

  // The v2 sample catalogue is the empty-database fallback. Live rows remain
  // authoritative as soon as an admin seeds course content.
  window.dtCoursesIndex = function () {
    return {
      courses: [],
      loading: true,
      error: null,
      async init() {
        try {
          const resp = await api.listCourses();
          const courses = resp.courses || [];
          this.courses = await Promise.all(courses.map(async (course) => {
            const detail = await api.getCourse(course.course_id);
            return { ...course, lessons: detail.lessons || [] };
          }));
          if (this.courses.length === 0) this.courses = COURSE_DEMOS;
        } catch (e) { this.error = String(e); }
        this.loading = false;
      },
      progress: courseProgress,
      state: courseState,
      stateLabel: courseStateLabel,
      stateClass: courseStateClass,
      level(course) { return course.level_min === course.level_max ? course.level_min : `${course.level_min}-${course.level_max}`; },
      open(course) {
        const root = getRoot();
        if (root) {
          root.activeCourseId = course.course_id;
          root.activeCourseDemo = !!course.demo;
          root.go("course");
        }
      },
    };
  };

  // dtCourseDetail drives the /courses/{id} screen. The tile grid shows
  // lock state based on the linear-unlock rule the server computed.
  window.dtCourseDetail = function () {
    return {
      course: null,
      lessons: [],
      loading: true,
      error: null,
      requestNumber: 0,
      // Leaving the screen must not let a late reply set the shared band.
      destroy() { this.requestNumber++; },
      async init() {
        const root = getRoot();
        const courseId = root && root.activeCourseId;
        if (!courseId) { this.loading = false; return; }
        const request = ++this.requestNumber;
        const demo = root.activeCourseDemo && COURSE_DEMOS.find((course) => course.course_id === courseId);
        if (demo) {
          this.course = demo;
          this.lessons = demo.lessons;
        } else try {
          const resp = await api.getCourse(courseId);
          if (request !== this.requestNumber) return;
          this.course = resp.course;
          this.lessons = resp.lessons || [];
        } catch (e) {
          if (request !== this.requestNumber) return;
          this.error = String(e);
        }
        if (this.course) root.courseBandTitle = this.course.title;
        this.loading = false;
      },
      openLesson(lessonId, unlocked) {
        if (!unlocked) return;
        const root = getRoot();
        if (root) {
          root.activeLessonId = lessonId;
          root.go("lesson");
        }
      },
      back() {
        const root = getRoot();
        if (root) root.go("courses");
      },
      progress() { return courseProgress({ ...this.course, lessons: this.lessons }); },
      state() { return courseState({ ...this.course, lessons: this.lessons }); },
      stateLabel: courseStateLabel,
      stateClass: courseStateClass,
      level() { return this.course && (this.course.level_min === this.course.level_max ? this.course.level_min : `${this.course.level_min}-${this.course.level_max}`); },
      lessonState(lesson) { return lesson.completed_at ? "completed" : lesson.last_started_at ? "in_progress" : lesson.unlocked ? "available" : "locked"; },
      lessonLabel(lesson) { return ({ completed: "Abgeschlossen", in_progress: "Fortsetzen", available: "Bereit", locked: "Gesperrt" })[this.lessonState(lesson)]; },
      nextLesson() { return this.lessons.find((lesson) => lesson.unlocked && !lesson.completed_at) || this.lessons.find((lesson) => lesson.unlocked); },
      openNext() {
        if (this.course.requires_paid) return;
        const lesson = this.nextLesson();
        if (lesson) this.openLesson(lesson.lesson_id, true);
      },
    };
  };

  // dtLessonPlayer is the 8-step lesson player. State machine:
  //   loading -> {recap|listen_de|listen_en|repeat|words|production|
  //               comprehend|rule|practice|translate|replay|finish} -> done
  // Steps 4 (Repeat), 6 (Production), and 10 (Translate) are stubbed;
  // the frontend renders them as skip-through screens.
  window.dtLessonPlayer = function () {
    return {
      view: null,
      loading: true,
      error: null,
      stepIndex: 0,
      steps: [],
      // Comprehension step state.
      compQuestionIdx: 0,
      compPick: null,
      compFeedback: null, // { pass, answer_index, replay_segment_id }
      compWrongCount: 0,
      // Production / translate step text.
      prodText: "",
      transText: "",
      // Finish summary.
      summary: null,
      requestNumber: 0,
      destroy() { this.requestNumber++; },

      async init() {
        const root = getRoot();
        const courseId = root && root.activeCourseId;
        const lessonId = root && root.activeLessonId;
        if (!courseId || !lessonId) { this.loading = false; return; }
        const request = ++this.requestNumber;
        const resume = root.lessonResumeStep;
        root.lessonResumeStep = null;
        try {
          const demoCourse = root.activeCourseDemo && COURSE_DEMOS.find((course) => course.course_id === courseId);
          const demoLesson = demoCourse && demoCourse.lessons.find((lesson) => lesson.lesson_id === lessonId);
          const resp = demoLesson ? demoLessonView(demoCourse, demoLesson) : await api.getLesson(courseId, lessonId);
          if (request !== this.requestNumber) return;
          this.view = resp;
          root.courseBandTitle = resp.lesson.title;
          // Build the step list based on what the lesson actually has.
          const s = [];
          if (resp.lesson.recap_md) s.push("recap");
          s.push("listen_de");
          s.push("listen_en");
          s.push("repeat"); // stubbed paid
          s.push("words");
          if (resp.lesson.production_question_md) s.push("production");
          if (resp.questions && resp.questions.length > 0) s.push("comprehend");
          s.push("rule");
          if (resp.lesson.practice_challenge_type) s.push("practice");
          s.push("translate"); // stubbed LLM
          s.push("replay");
          s.push("finish");
          this.steps = s;
          if (resume) {
            const practice = s.indexOf("practice");
            if (practice >= 0) this.stepIndex = resume === "after-practice" ? Math.min(practice + 1, s.length - 1) : practice;
          }
          if (!resp.demo) await api.lessonVisit(courseId, lessonId);
        } catch (e) {
          if (request !== this.requestNumber) return;
          this.error = String(e);
        }
        if (request === this.requestNumber) this.loading = false;
      },

      get currentStep() { return this.steps[this.stepIndex] || ""; },
      stepLabel(step) {
        return ({ recap: "Rückblick", listen_de: "Lesen", listen_en: "Übersetzung", repeat: "Sprechen", words: "Wortschatz", production: "Schreiben", comprehend: "Verstehen", rule: "Grammatik", practice: "Üben", translate: "Übersetzen", replay: "Wiederholen", finish: "Fertig" })[step] || step;
      },

      next() {
        this.compPick = null;
        this.compFeedback = null;
        this.compWrongCount = 0;
        if (this.stepIndex < this.steps.length - 1) {
          this.stepIndex++;
        }
      },

      back() {
        if (this.stepIndex > 0) this.stepIndex--;
      },

      skip() { this.next(); },

      startPractice() {
        const root = getRoot();
        if (root) root.startCoursePractice(this.view);
      },

      segmentsByScene(language) {
        if (!this.view) return [];
        const out = [];
        for (const sc of this.view.scenes) {
          const segs = (sc.segments || []).filter((s) => s.language === language);
          if (segs.length > 0) out.push({ scene: sc, segments: segs });
        }
        return out;
      },

      async pickComp(idx) {
        if (this.compFeedback && this.compFeedback.pass) return;
        const root = getRoot();
        const courseId = root && root.activeCourseId;
        const lessonId = root && root.activeLessonId;
        try {
          const question = this.view.questions[this.compQuestionIdx];
          const r = this.view.demo ? { pass: idx === question.answer_index, answer_index: question.answer_index } : await api.lessonAnswer(
            courseId, lessonId,
            { answer_type: "COMPREHENSION", question_index: question.order_index, choice: idx }
          );
          this.compFeedback = r;
          this.compPick = idx;
          if (!r.pass) this.compWrongCount++;
        } catch (e) {
          this.error = String(e);
        }
      },

      compNextQuestion() {
        this.compPick = null;
        this.compFeedback = null;
        this.compWrongCount = 0;
        if (this.compQuestionIdx < this.view.questions.length - 1) {
          this.compQuestionIdx++;
        } else {
          this.next();
        }
      },

      async submitProduction() {
        if (!this.prodText) return;
        const root = getRoot();
        if (!this.view.demo) await api.lessonAnswer(
          root.activeCourseId, root.activeLessonId, { answer_type: "PRODUCTION", text: this.prodText }
        );
        this.next();
      },

      async submitTranslate() {
        if (!this.transText) return;
        const root = getRoot();
        if (!this.view.demo) await api.lessonAnswer(
          root.activeCourseId, root.activeLessonId, { answer_type: "SENTENCE_TRANSLATION", text: this.transText }
        );
        this.next();
      },

      async finish() {
        const root = getRoot();
        try {
          const r = this.view.demo ? { course_completed: false } : await api.lessonComplete(root.activeCourseId, root.activeLessonId);
          this.summary = r;
        } catch (e) { this.error = String(e); }
        if (root && root.refreshAllStats) root.refreshAllStats();
        if (root && root.refreshContinue) root.refreshContinue();
      },

      home() {
        const root = getRoot();
        if (root) root.go("home");
      },

      backToCourse() {
        const root = getRoot();
        if (root) root.go("course");
      },
    };
  };

  // ------------------------------------------------------------------
  // Public word groups
  // ------------------------------------------------------------------

  function searchPrepare(raw) {
    const nfc = String(raw ?? "").trim().normalize("NFC");
    return nfc.replace(/^(?:der|die|das) /i, "");
  }

  function searchQueryLength(raw) {
    return Array.from(searchPrepare(raw)).length;
  }

  function stripSearchMarks(s) {
    return s.normalize("NFD").replace(/\p{Mn}/gu, "");
  }

  function mapSearchFold(s, expand) {
    let out = "";
    for (const ch of s) {
      if (ch === "ä") out += expand ? "ae" : "a";
      else if (ch === "ö") out += expand ? "oe" : "o";
      else if (ch === "ü") out += expand ? "ue" : "u";
      else if (ch === "ß") out += "ss";
      else out += ch;
    }
    return stripSearchMarks(out);
  }

  function foldForms(raw) {
    const prepared = searchPrepare(raw).toLowerCase();
    const expanded = mapSearchFold(prepared, true);
    const stripped = mapSearchFold(prepared, false);
    return expanded === stripped ? [expanded] : [expanded, stripped];
  }

  function titleMatches(query, title) {
    if (searchPrepare(query) === "") return true;
    const haystacks = foldForms(title);
    return foldForms(query).some((needle) => haystacks.some((haystack) => haystack.includes(needle)));
  }

  function headwordMatch(query, headword) {
    const prepared = searchPrepare(query);
    if (prepared === "") return { match: false, exact: false };
    const needles = foldForms(query);
    const forms = foldForms(headword);
    const exact = needles.some((needle) => forms.some((form) => form === needle));
    if (searchQueryLength(query) <= 2) return { match: exact, exact };
    if (exact) return { match: true, exact: true };
    const match = needles.some((needle) => forms.some((form) => form.startsWith(needle)));
    return { match, exact: false };
  }

  window.dtSearchFold = {
    searchPrepare, searchQueryLength, foldForms, titleMatches, headwordMatch,
  };

  const WORDGROUP_HEADWORD_MAX = 100;

  const WORDGROUP_CATEGORIES = [
    "Everyday", "Food", "Home", "People", "Health", "Work", "Travel",
    "Animals", "Nature", "Sports", "Culture", "Tech", "Society",
    "Education", "Clothing", "Language",
  ];

  function updateWordgroupSelection(group, event) {
    const change = event && event.detail;
    if (group && change && group.word_list_id === change.id) group.selected = change.selected;
  }

  async function toggleWordgroupSelection(component, group) {
    const root = getRoot();
    const userId = root && root.$store.ui.userId;
    if (!userId || !group || root.wordgroupSelectionPending[group.word_list_id]) return;
    const selected = !group.selected;
    group.selected = selected;
    root.wordgroupSelectionPending[group.word_list_id] = true;
    if (root.wordgroupStats) root.wordgroupStats.selected += selected ? 1 : -1;
    component.$dispatch("dt-wordgroup-selection", { id: group.word_list_id, selected });
    try {
      await api.setWordgroupSelected(group.word_list_id, selected);
      root.selectedWordgroups = selected
        ? [...root.selectedWordgroups.filter((item) => item.word_list_id !== group.word_list_id), group]
        : root.selectedWordgroups.filter((item) => item.word_list_id !== group.word_list_id);
    } catch (e) {
      group.selected = !selected;
      if (root.wordgroupStats) root.wordgroupStats.selected += selected ? -1 : 1;
      component.$dispatch("dt-wordgroup-selection", { id: group.word_list_id, selected: !selected });
      component.error = t("wordgroups.selectionError");
    } finally {
      delete root.wordgroupSelectionPending[group.word_list_id];
    }
  }

  function wordgroupSelectionPending(group) {
    const root = getRoot();
    return Boolean(root && group && root.wordgroupSelectionPending[group.word_list_id]);
  }

  window.dtWordgroupBrowse = function () {
    return {
      all: [],
      groups: [],
      query: "",
      category: "",
      loading: true,
      error: null,
      categories: WORDGROUP_CATEGORIES,
      categoryCounts: {},
      requestNumber: 0,
      words: [],
      wordMore: 0,
      wordsExpanded: false,
      wordsLoading: false,
      wordsError: null,
      wordRequest: 0,
      prefixNote: false,
      prefixTimer: null,
      init() {
        const root = getRoot();
        if (root && root.wordgroupQuery) this.query = root.wordgroupQuery;
        this.load();
        const reload = () => {
          this.invalidateHeadwords();
          this.load();
        };
        this.$watch("$store.ui.effectiveLang", reload);
        this.$watch("$store.ui.matureContentSaved", reload);
      },
      // Title matches stay in the browser. Headword matches are a second
      // request. Category chips already filter by topic, so typing "Food"
      // must not list every Food-category deck.
      search() {
        this.groups = this.all.filter((group) => this.matchesQuery(group));
        const root = getRoot();
        if (root) {
          root.wordgroupQuery = this.query;
          if (typeof root.replaceRouteForScreen === "function") root.replaceRouteForScreen();
        }
        this.scheduleHeadwords();
      },
      onQuery(event) {
        const next = event && typeof event.detail === "string" ? event.detail : "";
        if (next === this.query) return;
        this.query = next;
        this.search();
      },
      matchesQuery(group) {
        return titleMatches(this.query, group.name);
      },
      hasWordQuery() {
        return searchPrepare(this.query) !== "";
      },
      setCategory(category) {
        this.category = category;
        this.invalidateHeadwords();
        this.load();
      },
      invalidateHeadwords() {
        this.wordRequest += 1;
        if (this.prefixTimer) {
          clearTimeout(this.prefixTimer);
          this.prefixTimer = null;
        }
        this.prefixNote = false;
        this.words = [];
        this.wordMore = 0;
        this.wordsError = null;
        this.wordsLoading = false;
      },
      scheduleHeadwords() {
        const request = ++this.wordRequest;
        if (this.prefixTimer) {
          clearTimeout(this.prefixTimer);
          this.prefixTimer = null;
        }
        this.prefixNote = false;
        if (!this.hasWordQuery()) {
          this.words = [];
          this.wordMore = 0;
          this.wordsError = null;
          this.wordsLoading = false;
          return;
        }
        // Drop the previous cards before the new request returns. Their links
        // already carry the new query.
        this.words = [];
        this.wordMore = 0;
        this.wordsExpanded = false;
        this.wordsLoading = true;
        this.wordsError = null;
        if (searchQueryLength(this.query) < 3 && this.groups.length > 0) {
          this.prefixTimer = setTimeout(() => {
            if (request !== this.wordRequest) return;
            this.prefixNote = true;
          }, 300);
        }
        this.fetchHeadwords(request);
      },
      showMoreWords() {
        this.wordsExpanded = true;
        this.wordsLoading = true;
        this.fetchHeadwords(++this.wordRequest, WORDGROUP_HEADWORD_MAX);
      },
      async fetchHeadwords(request, limit = 0) {
        const query = this.query;
        const category = this.category;
        try {
          if (typeof api.searchWordgroupHeadwords !== "function") return;
          const response = await api.searchWordgroupHeadwords(query, category, limit);
          if (request !== this.wordRequest || query !== this.query || category !== this.category) return;
          const hits = (response && response.words) || [];
          this.words = hits.map((word) => ({
            word_id: word.word_id,
            german: word.german,
            articles: word.articles || "",
            word_lists: word.word_lists || [],
          }));
          this.wordMore = (response && response.more) || 0;
          this.wordsError = null;
          if (searchQueryLength(this.query) < 3 && this.groups.length === 0 && this.words.length === 0) {
            this.prefixNote = true;
          }
        } catch (e) {
          if (request !== this.wordRequest || query !== this.query || category !== this.category) return;
          this.words = [];
          this.wordMore = 0;
          this.wordsError = t("wordgroups.wordsError");
        } finally {
          if (request === this.wordRequest) this.wordsLoading = false;
        }
      },
      listCard(card) {
        return this.all.find((group) => group.word_list_id === card.word_list_id) || card;
      },
      // One card per list, in the order of the ranked words; within one word
      // the selected lists come first. The title grid's lists are skipped.
      // headwordWords gives each card its matched chips.
      headwordCards() {
        const titled = new Set(this.groups.map((group) => group.word_list_id));
        const lists = new Map();
        for (const word of this.words) {
          const cards = word.word_lists.map((list) => this.listCard(list));
          for (const card of cards.filter((c) => c.selected).concat(cards.filter((c) => !c.selected))) {
            if (!titled.has(card.word_list_id) && !lists.has(card.word_list_id)) lists.set(card.word_list_id, card);
          }
        }
        return [...lists.values()];
      },
      showMoreCount() {
        return Math.min(this.wordMore, WORDGROUP_HEADWORD_MAX - this.words.length);
      },
      headwordWords(group) {
        return this.words.filter((word) => word.word_lists.some((list) => list.word_list_id === group.word_list_id));
      },
      wordLabel(word) {
        if (!word || !word.articles) return (word && word.german) || "";
        return word.articles + " " + word.german;
      },
      cardHref(group) {
        const path = "/word-groups/" + encodeURIComponent((group && group.slug) || "");
        if (!this.query) return path;
        return path + "?" + new URLSearchParams({ q: this.query }).toString();
      },
      async load() {
        const request = ++this.requestNumber;
        this.loading = true;
        this.error = null;
        try {
          const response = await api.listWordgroups("", this.category);
          if (request !== this.requestNumber) return;
          this.all = response.groups || [];
          this.search();
          if (!this.category) {
            this.categoryCounts = this.all.reduce((counts, group) => {
              counts[group.theme] = (counts[group.theme] || 0) + 1;
              return counts;
            }, {});
            const root = getRoot();
            if (root) {
              root.wordgroupStats = {
                selected: this.all.filter((group) => group.selected).length,
                total: this.all.length,
              };
            }
          }
        } catch (e) {
          if (request !== this.requestNumber) return;
          this.error = t("wordgroups.loadError");
        } finally {
          if (request === this.requestNumber) this.loading = false;
        }
      },
      open(slug, event) {
        const root = getRoot();
        if (root) root.openWordgroup(slug, event);
      },
      async toggle(group) {
        await toggleWordgroupSelection(this, group);
      },
      selectionPending(group) { return wordgroupSelectionPending(group); },
      syncSelection(event) {
        const change = event && event.detail;
        const group = change && this.all.find((item) => item.word_list_id === change.id);
        updateWordgroupSelection(group, event);
      },
      categoryLabel(category) {
        return t(`wordgroups.category.${category}`);
      },
      categoryCount(category) { return this.categoryCounts[category] || ""; },
      totalCount() {
        return Object.values(this.categoryCounts).reduce((total, count) => total + count, 0);
      },
    };
  };

  // levelAbove: a Meaning's level is above the learner's own (the word-list
  // and challenge gloss card share it).
  window.levelAbove = function (level, mine) {
    return Boolean(level && mine && level > mine);
  };

  window.dtWordgroupDetail = function () {
    return {
      group: null,
      words: [],
      loading: true,
      errorKind: "",
      error: null,
      requestNumber: 0,
      init() {
        this.load();
        this.$watch("$store.ui.effectiveLang", () => this.load());
        this.$watch("$store.ui.matureContentSaved", () => this.load());
      },
      // Back unmounts this screen; a late reply must not touch the shared band.
      destroy() { this.requestNumber++; },
      async load() {
        const root = getRoot();
        const slug = root && root.activeWordgroupSlug;
        if (!slug) return;
        const request = ++this.requestNumber;
        this.loading = true;
        this.errorKind = "";
        this.error = null;
        try {
          const response = await api.getWordgroup(slug);
          if (request !== this.requestNumber) return;
          if (!response || !response.word_list_id) throw Object.assign(new Error("not found"), { status: 404 });
          this.group = response;
          this.words = response.words || [];
          this.applyQueryReveal();
          if (root) root.wordgroupBand = response;
        } catch (e) {
          if (request !== this.requestNumber) return;
          this.group = null;
          this.words = [];
          if (root) root.wordgroupBand = null;
          this.errorKind = e && e.status === 404 ? "notfound" : "generic";
        } finally {
          if (request === this.requestNumber) this.loading = false;
        }
      },
      back() {
        const root = getRoot();
        if (root) root.go("wordgroups");
      },
      practice() {
        const root = getRoot();
        if (root && this.group) root.startWordgroupChallenge(this.group.word_list_id, this.group.slug);
      },
      async toggle() {
        await toggleWordgroupSelection(this, this.group);
      },
      selectionPending() { return wordgroupSelectionPending(this.group); },
      syncSelection(event) { updateWordgroupSelection(this.group, event); },
      categoryLabel() {
        return this.group ? t(`wordgroups.category.${this.group.theme}`) : "";
      },
      article(word) {
        return word.articles || "";
      },
      // Words above the user's level are hidden by default (display-only;
      // the full list is already loaded). CEFR codes sort lexically.
      // The toggle persists across visits.
      savedAboveLevel: lsGet("dt_wordgroup_show_above", false),
      showAboveLevel: lsGet("dt_wordgroup_show_above", false),
      toggleAboveLevel() {
        this.showAboveLevel = !this.showAboveLevel;
        this.savedAboveLevel = this.showAboveLevel;
        lsSet("dt_wordgroup_show_above", this.showAboveLevel);
      },
      // A matching above-level headword reveals the whole list for this
      // visit. The saved control is left alone, and a query that matches
      // nothing puts that saved control back.
      onQuery() {
        this.applyQueryReveal();
      },
      applyQueryReveal() {
        const root = getRoot();
        const query = (root && root.wordgroupQuery) || "";
        const reveal = (this.words || []).some((word) =>
          headwordMatch(query, word && word.german).match && this.isAboveLevel(word));
        this.showAboveLevel = reveal ? true : this.savedAboveLevel;
      },
      levelAbove(level) {
        return window.levelAbove(level, this.$store.ui.level);
      },
      // Meanings of one word sit at different levels (Krippe: B2 "nursery",
      // C1 "manger"), so the toggle hides them one by one, not only whole
      // words. A word drops out only when every meaning is too hard.
      shownMeanings(word) {
        const all = (word && word.meanings) || [];
        return this.showAboveLevel ? all : all.filter((m) => !this.levelAbove(m.level));
      },
      isAboveLevel(word) {
        const all = (word && word.meanings) || [];
        if (all.length === 0) return this.levelAbove(word && word.level);
        return all.every((m) => this.levelAbove(m.level));
      },
      // Counts the meanings the toggle would reveal: the ones hidden inside a
      // visible word plus every meaning of a hidden one.
      hiddenAboveCount() {
        let n = 0;
        for (const w of this.words) {
          const all = (w.meanings || []);
          n += all.length ? all.filter((m) => this.levelAbove(m.level)).length
            : (this.levelAbove(w.level) ? 1 : 0);
        }
        return n;
      },
      hiddenWordCount() {
        return this.words.filter((w) => this.isAboveLevel(w)).length;
      },
      visibleCount() {
        return this.showAboveLevel ? this.words.length : this.words.length - this.hiddenWordCount();
      },
      openWord(word) {
        window.Alpine.store("wordDetail").open(word.word_id);
      },
    };
  };

  // ------------------------------------------------------------------
  // Lesen: Superhirn and News feeds and the Piece screen (docs/reading.md
  // section 2)
  // ------------------------------------------------------------------

  const READING_SCREENS = ["lesen_superhirn", "lesen_news", "lesen_piece", "lesen_choice"];
  // A questions Challenge takes exactly this many ready Pieces (#728).
  const READING_QUESTION_PIECES = 3;
  const READING_KINDS = ["friend", "interview", "debate"];
  const READING_LEVELS = ["A2", "B1", "B2"];
  const READING_PAGE = 20;
  const NEWS_TOPICS = ["curiosities", "world", "tech", "domestic"];
  // AT and CH have no Source yet (#1101).
  const NEWS_SCOPES = [
    { id: "international", enabled: true },
    { id: "DE", enabled: true },
    { id: "AT", enabled: false },
    { id: "CH", enabled: false },
  ];

  // The feed last shown ({origin, topic, scope, pieces, done}), so the Piece
  // screen can find the next Piece and the feed comes back as it was left.
  // readingFeedGen counts replacements, so a slow page cannot bring back a
  // feed that a filter change or leaving Lesen already replaced.
  let readingFeedCache = null;
  let readingFeedGen = 0;
  function forgetReadingFeed() {
    readingFeedCache = null;
    readingFeedGen++;
  }
  // Late responses never reach this after the learner left Lesen: the feed
  // drops them on destroy, Piece paging on a changed readingFeedGen.
  function keepReadingFeed(feed) {
    readingFeedCache = feed;
    readingFeedGen++;
  }

  // The Origin of the feed or Piece last shown, so "Überspringen" and the
  // reading-quiz summary go back to that feed. sessionStorage carries it over
  // a reload of /lesen/was-jetzt or /challenges/reading-quiz.
  const READING_ORIGIN_KEY = "dt_reading_origin";
  let readingOrigin = "trivia";
  function setReadingOrigin(origin) {
    readingOrigin = origin;
    try { sessionStorage.setItem(READING_ORIGIN_KEY, origin); } catch { /* private mode */ }
  }
  function readingFeedScreen() {
    let origin = readingOrigin;
    try { origin = sessionStorage.getItem(READING_ORIGIN_KEY) || origin; } catch { /* private mode */ }
    return origin === "news" ? "lesen_news" : "lesen_superhirn";
  }

  // A new Read mark can change the ready count; the dock listens.
  function readingReadyChanged() {
    if (typeof window.dispatchEvent === "function") window.dispatchEvent(new CustomEvent("dt-reading-ready-changed"));
  }

  function defaultReadingFeed(origin) {
    return { origin, topic: "", scope: origin === "news" ? "international" : "", pieces: [], done: false };
  }

  // Passages exist for A2, B1 and B2 only: A1 reads A2, C1 and C2 read B2.
  function readingLevel(level) {
    if (READING_LEVELS.includes(level)) return level;
    return String(level || "") < "A2" ? "A2" : "B2";
  }

  function readingDate(iso, lang) {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    try {
      return date.toLocaleDateString(lang || undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch { return ""; }
  }

  // Source links come from scraped feeds; anything but http(s) is dropped.
  function safeHttpUrl(u) {
    try {
      const url = new URL(String(u || ""));
      return url.protocol === "http:" || url.protocol === "https:" ? url.href : "";
    } catch { return ""; }
  }

  function readingTopicLabel(origin, topic) {
    return origin === "news" ? t(`reading.topic.${topic}`) : t(`trivia.category.${topic}`);
  }

  // A lapsed session answers 401, a device-only chat start 403
  // chat-signin-required. Either way the screen asks for sign-in, and the
  // auth store re-probes so the rest of the app catches up.
  function readingSignInLost(e) {
    const status = e && (e.status || (e.problem && e.problem.status));
    const type = String((e && e.problem && e.problem.type) || "");
    if (status !== 401 && !(status === 403 && type.endsWith("chat-signin-required"))) return false;
    try {
      const auth = window.Alpine && window.Alpine.store && window.Alpine.store("auth");
      if (auth && typeof auth.refresh === "function") auth.refresh({ quiet: true });
    } catch { /* the card shows either way */ }
    return true;
  }

  async function fetchReadingPage(feed) {
    const response = await api.listPieces({
      origin: feed.origin, topic: feed.topic, scope: feed.scope,
      limit: READING_PAGE, offset: feed.pieces.length,
    });
    return (response && response.pieces) || [];
  }

  // walkReadingFeed pages `feed` (in place) until it can say whether it holds
  // `id` and, if so, which unblocked Piece follows it.
  // ponytail: pages one by one, fine while a feed is a few hundred Pieces.
  async function walkReadingFeed(feed, id) {
    for (;;) {
      const i = feed.pieces.findIndex((p) => p.piece_id === id);
      const next = i < 0 ? null : feed.pieces.slice(i + 1).find((p) => !p.blocked);
      if (next) return { holds: true, next };
      if (feed.done) return { holds: i >= 0, next: null };
      const page = await fetchReadingPage(feed);
      feed.pieces = feed.pieces.concat(page);
      feed.done = page.length < READING_PAGE;
    }
  }

  // nextReadingPiece finds the first unblocked Piece after `id` in the feed
  // the learner came from, or null at the end. When that feed does not hold
  // the Piece (opened by URL or history), the default feed of its Origin is
  // walked instead, but not kept: "Zurueck zur Liste" still shows the list
  // the learner left.
  async function nextReadingPiece(id, origin) {
    const gen = readingFeedGen;
    const cached = readingFeedCache && readingFeedCache.origin === origin ? readingFeedCache : null;
    const feed = cached ? { ...cached } : defaultReadingFeed(origin);
    const found = await walkReadingFeed(feed, id);
    const isDefault = feed.topic === "" && feed.scope === defaultReadingFeed(origin).scope;
    if (!found.holds && cached && !isDefault) {
      return (await walkReadingFeed(defaultReadingFeed(origin), id)).next;
    }
    if (found.holds && gen === readingFeedGen) keepReadingFeed(feed);
    return found.next;
  }

  function markCachedRead(pieceId) {
    const cached = readingFeedCache && readingFeedCache.pieces.find((p) => p.piece_id === pieceId);
    if (cached) cached.read = true;
  }

  window.dtReadingFeed = function (origin) {
    return {
      origin,
      topic: "",
      scope: origin === "news" ? "international" : "",
      pieces: [],
      done: false,
      loading: false,
      loadingMore: false,
      error: null,
      moreError: null,
      signInRequired: false,
      requestNumber: 0,
      scopes: NEWS_SCOPES,
      regions: [],
      topics: origin === "news" ? NEWS_TOPICS : TRIVIA_CATEGORIES.map((c) => c.id),
      init() {
        setReadingOrigin(origin);
        this.loadRegions();
        const cached = readingFeedCache;
        if (cached && cached.origin === origin) {
          this.topic = cached.topic;
          this.scope = cached.scope;
          this.pieces = cached.pieces;
          this.done = cached.done;
          return;
        }
        this.load();
      },
      destroy() { this.requestNumber++; },
      // The Region list is the DE regional Scopes of the enabled Coverages;
      // without it the Region filter stays hidden.
      async loadRegions() {
        if (origin !== "news") return;
        try {
          const response = await api.listRegions();
          this.regions = (response && response.regions) || [];
          if (this.scope.startsWith("DE-") && !this.regions.includes(this.scope)) this.setScope("international");
        } catch { /* the feed works without the Region filter */ }
      },
      remember() {
        keepReadingFeed({ origin, topic: this.topic, scope: this.scope, pieces: this.pieces, done: this.done });
      },
      async load() {
        const request = ++this.requestNumber;
        this.loading = true;
        this.loadingMore = false;
        this.error = null;
        this.moreError = null;
        this.signInRequired = false;
        this.pieces = [];
        try {
          const page = await fetchReadingPage(this);
          if (request !== this.requestNumber) return;
          this.pieces = page;
          this.done = page.length < READING_PAGE;
          this.remember();
        } catch (e) {
          if (request !== this.requestNumber) return;
          if (readingSignInLost(e)) this.signInRequired = true;
          else this.error = t("reading.loadError");
        } finally {
          if (request === this.requestNumber) this.loading = false;
        }
      },
      async more() {
        if (this.loading || this.loadingMore || this.done) return;
        const request = this.requestNumber;
        this.loadingMore = true;
        this.moreError = null;
        try {
          const page = await fetchReadingPage(this);
          if (request !== this.requestNumber) return;
          this.pieces = this.pieces.concat(page);
          this.done = page.length < READING_PAGE;
          this.remember();
        } catch (e) {
          if (request !== this.requestNumber) return;
          if (readingSignInLost(e)) this.signInRequired = true;
          else this.moreError = t("reading.loadError");
        } finally {
          if (request === this.requestNumber) this.loadingMore = false;
        }
      },
      setTopic(topic) {
        if (this.topic === topic) return;
        this.topic = topic;
        this.load();
      },
      setScope(scope) {
        const known = NEWS_SCOPES.find((s) => s.id === scope);
        if (!known || !known.enabled || this.scope === scope) return;
        this.scope = scope;
        this.load();
      },
      // A DE-XX Region is a sub-filter of the DE Scope, so DE stays on inside it.
      scopeOn(id) { return this.scope === id || (id === "DE" && this.scope.startsWith("DE-")); },
      setRegion(region) {
        if (!this.regions.includes(region) || this.scope === region) return;
        this.scope = region;
        this.load();
      },
      topicLabel(topic) { return readingTopicLabel(origin, topic); },
      pieceDate(piece) {
        return readingDate(origin === "news" ? piece.published_at || piece.created_at : piece.created_at,
          this.$store.i18n.lang);
      },
      pieceHref(piece) { return "/lesen/" + encodeURIComponent(piece.piece_id); },
      open(piece, event) {
        const root = getRoot();
        if (!root) return;
        root.readingPieceOrigin = origin;
        root.openPiece(piece.piece_id, event);
      },
    };
  };

  window.dtReadingPiece = function () {
    // Kept off the reactive object: Alpine's proxy breaks HTMLMediaElement methods.
    let audio = null;
    let audioToken = 0;
    return {
      piece: null,
      level: "A2",
      loading: true,
      error: null,
      signInRequired: false,
      requestNumber: 0,
      readLevels: {},
      marking: false,
      markError: null,
      next: null,
      playing: false,
      disclosureOpen: false,
      levels: READING_LEVELS,
      init() { this.load(); },
      // Bumping the request drops every pending completion of this screen.
      destroy() {
        this.requestNumber++;
        this.stopAudio();
      },
      // current reports whether a completion of `request` for `pieceId` still
      // belongs to what the learner sees.
      current(request, pieceId) {
        const root = getRoot();
        return request === this.requestNumber
          && (!root || (root.screen === "lesen_piece" && root.activePieceId === pieceId));
      },
      async load() {
        const root = getRoot();
        const id = root && root.activePieceId;
        if (!id) return;
        this.stopAudio();
        const request = ++this.requestNumber;
        this.loading = true;
        this.error = null;
        this.signInRequired = false;
        this.piece = null;
        this.next = null;
        this.readLevels = {};
        this.marking = false;
        this.markError = null;
        this.disclosureOpen = false;
        try {
          const piece = await api.getPiece(id);
          if (!this.current(request, id)) return;
          this.piece = piece;
          this.readLevels = Object.fromEntries(
            (piece.passages || []).filter((p) => p.read).map((p) => [p.level, true]));
          this.level = this.startLevel();
          if (root) root.readingPieceOrigin = piece.origin;
          setReadingOrigin(piece.origin);
        } catch (e) {
          if (!this.current(request, id)) return;
          if (readingSignInLost(e)) this.signInRequired = true;
          else this.error = t("reading.pieceError");
          return;
        } finally {
          if (request === this.requestNumber) this.loading = false;
        }
        if (typeof this.$nextTick === "function") {
          this.$nextTick(() => {
            if (this.$refs && this.$refs.headline) this.$refs.headline.focus();
          });
        }
        try {
          const next = await nextReadingPiece(id, this.piece.origin);
          if (this.current(request, id)) this.next = next;
        } catch { /* no "Weiter" button without the feed */ }
      },
      // The Home level clamped to A2..B2, or the nearest level the Piece has.
      startLevel() {
        const want = READING_LEVELS.indexOf(readingLevel(this.$store.ui.level));
        const have = READING_LEVELS.filter((l) => this.hasLevel(l));
        if (!have.length) return READING_LEVELS[want];
        return have.reduce((best, l) =>
          Math.abs(READING_LEVELS.indexOf(l) - want) < Math.abs(READING_LEVELS.indexOf(best) - want) ? l : best);
      },
      passage() {
        const passages = (this.piece && this.piece.passages) || [];
        return passages.find((p) => p.level === this.level) || passages[0] || null;
      },
      hasLevel(level) {
        return ((this.piece && this.piece.passages) || []).some((p) => p.level === level);
      },
      setLevel(level) {
        if (this.level === level || !this.hasLevel(level)) return;
        this.stopAudio();
        this.level = level;
        this.markError = null;
      },
      headline() {
        const passage = this.passage();
        return (passage && passage.title) || (this.piece && this.piece.headline) || "";
      },
      // One string per paragraph: the parts (sentences) that share a
      // paragraph index, joined with a space.
      paragraphs() {
        const passage = this.passage();
        const out = [];
        let last = null;
        for (const part of passage ? passage.parts : []) {
          const paragraph = part.paragraph || 0;
          if (paragraph === last) out[out.length - 1] += ` ${part.text}`;
          else out.push(part.text);
          last = paragraph;
        }
        return out;
      },
      topicLabel() { return this.piece ? readingTopicLabel(this.piece.origin, this.piece.topic) : ""; },
      isNews() { return !!(this.piece && this.piece.origin === "news" && this.piece.source); },
      pieceDate() {
        return this.piece ? readingDate(this.piece.published_at || this.piece.created_at, this.$store.i18n.lang) : "";
      },
      sourceHref() { return this.isNews() ? safeHttpUrl(this.piece.source.homepage_url) : ""; },
      articleHref() { return this.isNews() ? safeHttpUrl(this.piece.source.article_url) : ""; },
      // The #720 AI disclosure behind the AI badge toggle (#1331, #1332).
      // Without a date the sentence drops it instead of rendering ", ,".
      attribution() {
        if (!this.isNews()) return "";
        const source = this.piece.source;
        const date = this.pieceDate();
        const key = source.byline
          ? (date ? "reading.attributionByline" : "reading.attributionBylineNoDate")
          : (date ? "reading.attribution" : "reading.attributionNoDate");
        return t(key, { source: source.name, author: source.byline || "", date });
      },
      toggleDisclosure() { this.disclosureOpen = !this.disclosureOpen; },
      // The visible Original line split around {url}, which renders as a link.
      original() {
        if (!this.isNews()) return ["", ""];
        const line = t("reading.original", { headline: this.piece.source.source_headline });
        const at = line.indexOf("{url}");
        return at < 0 ? [line, ""] : [line.slice(0, at), line.slice(at + 5)];
      },
      audioUrls() {
        const passage = this.passage();
        return passage ? passage.parts.map((part) => part.audio_url).filter(Boolean) : [];
      },
      hasAudio() { return this.audioUrls().length > 0; },
      toggleAudio() {
        if (this.playing) { this.stopAudio(); return; }
        const urls = this.audioUrls();
        if (!urls.length) return;
        const token = ++audioToken;
        const level = this.level;
        this.playing = true;
        const playAt = (i) => {
          if (token !== audioToken) return;
          if (i >= urls.length) { this.stopAudio(); return; }
          audio = newLevelAudio(urls[i], level);
          audio.onended = () => playAt(i + 1);
          audio.onerror = () => playAt(i + 1);
          const started = audio.play();
          if (started && typeof started.catch === "function") {
            started.catch(() => { if (token === audioToken) this.stopAudio(); });
          }
        };
        playAt(0);
      },
      stopAudio() {
        audioToken++;
        if (audio) {
          try { audio.pause(); } catch { /* already gone */ }
          audio = null;
        }
        this.playing = false;
      },
      isRead() { return !!this.readLevels[this.level]; },
      // Opening a Piece never sets a Read mark; only this does (#728). A
      // completion that lands after the learner moved on still updates the
      // feed tick of its own Piece, and nothing else.
      async markRead() {
        const passage = this.passage();
        if (!passage || this.marking) return false;
        const request = this.requestNumber;
        const pieceId = this.piece.piece_id;
        this.marking = true;
        this.markError = null;
        try {
          await api.markPassageRead(passage.passage_id);
          markCachedRead(pieceId);
          readingReadyChanged();
          if (!this.current(request, pieceId)) return false;
          this.readLevels = { ...this.readLevels, [passage.level]: true };
          return true;
        } catch (e) {
          if (!this.current(request, pieceId)) return false;
          if (readingSignInLost(e)) this.signInRequired = true;
          else this.markError = t("reading.markError");
          return false;
        } finally {
          if (request === this.requestNumber) this.marking = false;
        }
      },
      async continueNext() {
        const next = this.next;
        if (!next || !(await this.markRead())) return;
        const root = getRoot();
        if (root) root.openPiece(next.piece_id);
      },
      back() {
        const root = getRoot();
        if (!root) return;
        const origin = (this.piece && this.piece.origin) || root.readingPieceOrigin;
        root.go(origin === "news" ? "lesen_news" : "lesen_superhirn");
      },
    };
  };

  // ------------------------------------------------------------------
  // Lesen: read-count dock, "Was jetzt?" and the Scenario kind picker
  // (docs/reading.md sections 2 and 4)
  // ------------------------------------------------------------------

  async function fetchReadyPieces() {
    const response = await api.getReadyPieces();
    return (response && response.pieces) || [];
  }

  // Chat problems the start can hit, as the chat screen words them.
  const READING_CHAT_ERRORS = {
    "chat-disabled": "chat.error.disabled",
    "chat-allowance-exhausted": "chat.error.allowance",
    "chat-moderation-cooldown": "chat.error.cooldown",
    "chat-budget-exhausted": "chat.error.busy",
    "chat-partner-unavailable": "chat.error.busy",
  };

  // The dock counts ready Pieces (GET .../ready-pieces) on every Lesen screen
  // but "Was jetzt?" and refetches after each new Read mark.
  window.dtReadingDock = function () {
    return {
      count: null,
      error: false,
      requestNumber: 0,
      init() { this.load(); },
      destroy() { this.requestNumber++; },
      async load() {
        const request = ++this.requestNumber;
        try {
          const pieces = await fetchReadyPieces();
          if (request !== this.requestNumber) return;
          this.count = pieces.length;
          this.error = false;
        } catch (e) {
          if (request !== this.requestNumber) return;
          // The screen below shows the sign-in card; the dock steps aside.
          this.count = null;
          this.error = !readingSignInLost(e);
        }
      },
      shown() { return this.count !== null || this.error; },
      dots() { return Array.from({ length: READING_QUESTION_PIECES }, (_, i) => ({ id: i, on: (this.count || 0) > i })); },
      label() {
        if (this.error) return t("reading.dock.error");
        return this.count ? t("reading.dock.count", { count: this.count }) : t("reading.dock.none");
      },
      // Open on an error too: "Was jetzt?" loads the count again and offers
      // a retry.
      canOpen() { return this.error || this.count > 0; },
      open() {
        const root = getRoot();
        if (root && this.canOpen()) root.go("lesen_choice");
      },
    };
  };

  window.dtReadingChoice = function () {
    return {
      pieces: [],
      loading: true,
      error: null,
      signInRequired: false,
      chatSignIn: false,
      view: "choice", // choice | chat
      chatPiece: null,
      chatKind: null,
      starting: false,
      startError: null,
      activeChat: false,
      gone: false,
      requestNumber: 0,
      kinds: READING_KINDS,
      questionPieces: READING_QUESTION_PIECES,
      init() { this.load(); },
      destroy() {
        this.gone = true;
        this.requestNumber++;
      },
      // A start that lands after the learner left must not navigate.
      current() {
        const root = getRoot();
        return !this.gone && (!root || root.screen === "lesen_choice");
      },
      // load reports whether it applied a fresh list. A silent reload (after
      // a start error) keeps the screen mounted and leaves a failure to the
      // start error.
      async load(silent = false) {
        const request = ++this.requestNumber;
        if (!silent) this.loading = true;
        this.error = null;
        this.signInRequired = false;
        try {
          const pieces = await fetchReadyPieces();
          if (request !== this.requestNumber) return false;
          this.pieces = pieces;
          if (!pieces.some((p) => p.piece_id === this.chatPiece)) this.chatPiece = null;
          if (!pieces.length) this.view = "choice";
          return true;
        } catch (e) {
          if (request !== this.requestNumber) return false;
          if (readingSignInLost(e)) this.signInRequired = true;
          else if (!silent) this.error = t("reading.choice.loadError");
          return false;
        } finally {
          if (request === this.requestNumber) this.loading = false;
        }
      },
      canQuestions() { return this.pieces.length >= READING_QUESTION_PIECES; },
      questionsHint() {
        const missing = READING_QUESTION_PIECES - this.pieces.length;
        return missing > 0
          ? t(missing === 1 ? "reading.choice.questionsLockedOne" : "reading.choice.questionsLocked", { count: missing })
          : t("reading.choice.questionsDesc", { count: READING_QUESTION_PIECES });
      },
      // fail words a start error; when a Piece stopped being ready, the list
      // reloads.
      async fail(e) {
        if (readingSignInLost(e)) {
          // 403: the server sees a device-only learner at the chat start, so
          // the choice screen stays and shows the chat sign-in card.
          if (problemSlug(e) === "chat-signin-required") {
            this.chatSignIn = true;
            this.view = "choice";
          } else this.signInRequired = true;
          return;
        }
        const slug = problemSlug(e);
        if (slug === "reading-not-enough-ready" || slug === "reading-piece-not-ready") {
          this.startError = t(slug === "reading-not-enough-ready" ? "reading.choice.notEnough" : "reading.choice.notReady",
            { count: READING_QUESTION_PIECES });
          this.load(true);
        } else if (slug === "chat-active-session") {
          this.activeChat = true;
          this.startError = t("reading.choice.chatActive");
        } else if (READING_CHAT_ERRORS[slug]) {
          this.startError = t(READING_CHAT_ERRORS[slug]);
        } else if (e && e.status === 404) {
          // A Piece that is gone and a kind without a Scenario both answer
          // 404; the reloaded list tells them apart (load drops a gone pick).
          const fresh = await this.load(true);
          this.startError = t(!fresh ? "reading.choice.startError"
            : this.chatPiece ? "reading.choice.noScenario" : "reading.choice.notReady");
        } else {
          this.startError = t(e && e.status ? "reading.choice.startError" : "chat.error.network");
        }
      },
      async begin(start, done) {
        if (this.starting) return;
        this.starting = true;
        this.startError = null;
        this.activeChat = false;
        try {
          const result = await start();
          if (this.current()) done(result);
        } catch (e) {
          if (this.current()) await this.fail(e);
        } finally {
          this.starting = false;
        }
      },
      startQuestions() {
        if (!this.canQuestions()) return;
        this.begin(() => api.startReadingQuestions(), (challenge) => {
          const root = getRoot();
          if (root) root.playReadingQuiz(challenge);
        });
      },
      openChat() {
        if (!this.pieces.length) return;
        // Chat costs LLM tokens: a device-only learner is asked to sign in.
        this.chatSignIn = !this.$store.auth.signedIn;
        if (this.chatSignIn) return;
        this.view = "chat";
        this.startError = null;
        this.activeChat = false;
      },
      // Disabled while a start is pending, so its answer lands where it was
      // asked for.
      back() {
        if (this.starting) return;
        this.view = "choice";
        this.startError = null;
        this.activeChat = false;
      },
      // Locked while a start is pending, so the chat opens on what was sent.
      selectPiece(id) { if (!this.starting) this.chatPiece = id; },
      selectKind(kind) { if (!this.starting) this.chatKind = kind; },
      canStartChat() { return !!(this.chatPiece && this.chatKind) && !this.starting; },
      startChat() {
        if (!this.canStartChat()) return;
        this.begin(() => api.startPieceChat(this.chatPiece, this.chatKind), (state) => {
          const root = getRoot();
          if (!root) return;
          root.activeChatSessionId = state.chat_id;
          root.pendingChatState = state;
          root._performGo("chat");
        });
      },
      openActiveChat() {
        const root = getRoot();
        if (root) root.go("chat");
      },
      skip() {
        const root = getRoot();
        if (root) root.go(readingFeedScreen());
      },
    };
  };

  // ------------------------------------------------------------------
  // German TV
  // ------------------------------------------------------------------

  // dtTvBrowse drives the /tv index. The catalogue is fetched once; the detail
  // popup is a sibling component (dtTvDetail) that pulls the active show from
  // root.activeShowId. Filter chips and the modal's genre / CEFR badges both
  // funnel through setFilter().
  window.dtTvBrowse = function () {
    return {
      shows: [],
      loading: true,
      error: null,
      get filterContentType() { return getRoot()?.tvContentType || ""; },
      get filterLevelCohort() { return getRoot()?.tvLevelCohort || ""; },
      async init() {
        try {
          const resp = await api.listTvShows({});
          this.shows = resp.shows || [];
        } catch (e) { this.error = String(e); }
        this.loading = false;
      },
      // A vote in the detail modal moves the card behind it (issue #688).
      applyVote({ show_id, effective_rating, user_vote_count }) {
        const show = this.shows.find((s) => s.show_id === show_id);
        if (!show) return;
        show.effective_rating = effective_rating;
        show.user_vote_count = user_vote_count;
      },
      prettyType(type) { return t(`tv.type.${type}`); },
      // Lists every content_type present in the catalogue so the chip row only
      // offers filters that would yield non-empty results.
      get availableContentTypes() {
        const set = new Set();
        for (const s of this.shows) {
          for (const t of (s.content_types || [])) set.add(t);
        }
        return Array.from(set).sort();
      },
      // Applies the chip filters. The rails compute over this filtered set so
      // a show tagged "drama" + "crime" still appears in both rails when only
      // CEFR is filtered.
      get filteredShows() {
        return this.shows.filter((s) => {
          if (this.filterContentType &&
              !(s.content_types || []).includes(this.filterContentType)) return false;
          if (this.filterLevelCohort && s.level_cohort !== this.filterLevelCohort) return false;
          return true;
        });
      },
      get rails() {
        const root = getRoot();
        const flags = (root && root.flags) || {};
        const out = [];
        if (flags.tv_most_voted_enabled) {
          out.push({
            id: "voted", title: t("tv.mostVoted"),
            subtitle: t("tv.topRated"),
            filter: () => true,
            sort: (a, b) => (b.effective_rating - a.effective_rating)
              || (b.user_vote_count - a.user_vote_count),
            limit: 8,
          });
        }
        // One rail per content_type present in the filtered shows.
        const counts = {};
        for (const s of this.filteredShows) {
          for (const t of (s.content_types || [])) {
            counts[t] = (counts[t] || 0) + 1;
          }
        }
        const cats = Object.keys(counts).sort((a, b) =>
          counts[b] - counts[a] || a.localeCompare(b));
        for (const t of cats) {
          out.push({
            id: "ct-" + t,
            title: this.prettyType(t),
            subtitle: "",
            filter: (s) => (s.content_types || []).includes(t),
          });
        }
        return out;
      },
      railShows(rail) {
        let list = this.filteredShows.filter(rail.filter);
        if (rail.sort) list = [...list].sort(rail.sort);
        if (rail.limit) list = list.slice(0, rail.limit);
        return list;
      },
      setFilter(axis, value) {
        const root = getRoot();
        if (!root) return;
        if (axis === "content_type") root.tvContentType = TV_CONTENT_TYPES.includes(value) ? value : "";
        else if (axis === "level_cohort") root.tvLevelCohort = TV_LEVEL_COHORTS.includes(value) ? value : "";
        else return;
        // Detail badges push once when closing onto the filtered catalogue.
        if (root.screen === "tv") root.pushRouteForScreen();
      },
      open(showId, ev) {
        // <a>-as-card: cancel default navigation so the SPA router
        // (not the browser) handles the screen change. The href is
        // still there for middle-click / right-click "open in new
        // tab", which serves the real URL via the server's SPA
        // catch-all.
        if (ev && typeof ev.preventDefault === "function") ev.preventDefault();
        const root = getRoot();
        if (root) {
          root.activeShowId = showId;
          root.go("tv_show");
        }
      },
    };
  };

  // dtTvDetail drives the popup. The template gates it on screen === 'tv_show',
  // so it mounts on open (load() reads root.activeShowId and fetches; Retry
  // calls it again) and tears down on close - no stale-detail caching.
  window.dtTvDetail = function () {
    return {
      detail: null,
      detailLoading: true,
      errorKind: "",
      detailMessage: null,
      // Optimistic star tracking; rolls back on 429 / network failure.
      pendingStars: null,
      pendingLevel: null,
      init() { return this.load(); },
      async load() {
        const root = getRoot();
        const showId = root && root.activeShowId;
        this.errorKind = "";
        if (!showId) { this.detailLoading = false; return; }
        this.detailLoading = true;
        try {
          this.detail = await api.getTvShow(showId);
        } catch (e) {
          this.errorKind = e.status === 404 ? "notfound" : "generic";
        }
        this.detailLoading = false;
      },
      close() {
        const root = getRoot();
        if (root) root.go("tv");
      },
      // applyFilterAndClose is called by the modal's genre / CEFR
      // badges. Dispatches to the parent dtTvBrowse scope, then closes
      // the modal so the listing underneath shows the filtered result.
      applyFilterAndClose(axis, value) {
        this.$dispatch("dt-tv-filter", { axis, value });
        const root = getRoot();
        if (root) root.go("tv");
      },
      // voteFailure: 403 tv-signin-required means the auth state was stale;
      // re-probe it so the sign-in button shows (as readingSignInLost does).
      voteFailure(e, rateLimited) {
        if (problemSlug(e) === "tv-signin-required") {
          this.$store.auth.refresh({ quiet: true });
          return this.$store.i18n.t("tv.signInToVote");
        }
        return e && e.status === 429 ? rateLimited : this.$store.i18n.t("tv.voteFailed");
      },
      async voteQuality(stars) {
        const root = getRoot();
        const uid = root && root.$store.ui.userId;
        if (!root || !root.authReady) return; // signedIn is false for everyone until /api/sessions/current resolves
        if (!uid || !this.$store.auth.signedIn) { this.detailMessage = this.$store.i18n.t("tv.signInToVote"); return; }
        const previous = this.detail.my_quality_vote;
        this.pendingStars = stars;
        this.detail.my_quality_vote = stars;
        try {
          const r = await api.voteTvQuality(this.detail.show_id, stars);
          this.detail.effective_rating = r.effective_rating;
          this.detail.user_vote_count = r.user_vote_count;
          this.detail.has_my_vote = true;
          // The listing under the modal stays mounted and renders the same
          // two numbers, so it has to hear about the vote too.
          this.$dispatch("dt-tv-vote", {
            show_id: this.detail.show_id,
            effective_rating: r.effective_rating,
            user_vote_count: r.user_vote_count,
          });
        } catch (e) {
          this.detail.my_quality_vote = previous;
          this.detailMessage = this.voteFailure(e, this.$store.i18n.t("tv.voteLimitQuality"));
        }
        this.pendingStars = null;
      },
      async voteLevel(range) {
        const root = getRoot();
        const uid = root && root.$store.ui.userId;
        if (!root || !root.authReady) return; // signedIn is false for everyone until /api/sessions/current resolves
        if (!uid || !this.$store.auth.signedIn) { this.detailMessage = this.$store.i18n.t("tv.signInToVote"); return; }
        const previous = this.detail.my_level_vote;
        this.pendingLevel = range;
        this.detail.my_level_vote = range;
        try {
          const r = await api.voteTvLevel(this.detail.show_id, range);
          this.detail.level_cohort = r.level_cohort;
          this.detail.has_my_vote = true;
        } catch (e) {
          this.detail.my_level_vote = previous;
          this.detailMessage = this.voteFailure(e, this.$store.i18n.t("tv.voteLimitLevel"));
        }
        this.pendingLevel = null;
      },
    };
  };
})();
